import 'bootstrap/dist/css/bootstrap.min.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import React, { useState, createRef, useCallback, useRef, useEffect, Children, createContext, Fragment, useContext, forwardRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import styled, { ThemeProvider, css, keyframes } from 'styled-components';
import { colorStyle, space, color, variant } from 'styled-system';
import useDeepCompareEffect from 'use-deep-compare-effect';
import tinycolor from 'tinycolor2';
import { CircularProgressbar } from 'react-circular-progressbar';
import mergeClassNames from 'merge-class-names';
import Calendar from 'react-calendar';
import Fit from 'react-fit';
import { getDaysInMonth, getYear, getMonthHuman, getDate, getISOLocalDate, getISOLocalMonth, getDayStart, getMonthStart, getYearStart, getDecadeStart, getCenturyStart, getDayEnd, getMonthEnd, getYearEnd, getDecadeEnd, getCenturyEnd } from '@wojtekmaj/date-utils';
import updateInputWidth, { getFontShorthand } from 'update-input-width';
import getUserLocale from 'get-user-locale';
import { Manager, Reference, Popper } from 'react-popper';
import { Transition } from 'react-spring/renderprops';
import { Link as Link$1, NavLink as NavLink$1 } from 'react-router-dom';
import { Transition as Transition$1, TransitionGroup, CSSTransition } from 'react-transition-group';
import FocusTrap from 'focus-trap-react';
import Popper$1 from 'popper.js';
import SlideDown from 'react-slidedown';
import { createPopper } from '@popperjs/core';
import ResizeObserver from 'resize-observer-polyfill';
import 'react-bootstrap-range-slider/dist/react-bootstrap-range-slider.css';
import RangeSlider from 'react-bootstrap-range-slider';
import { Link as Link$2 } from 'react-scroll';
import ReactTooltip from 'react-tooltip';
function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}
function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
function _taggedTemplateLiteralLoose(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }
  strings.raw = raw;
  return strings;
}
function _templateObject() {
  var data = _taggedTemplateLiteralLoose([""]);
  _templateObject = function _templateObject() {
    return data;
  };
  return data;
}
var Component = styled.div(_templateObject());
var colors = {
  white: '#FFFFFF',
  light: '#EEEEEE',
  primary: '#3b82f6',
  primary50: '#eff6ff',
  primary100: '#dbeafe',
  primary200: '#bfdbfe',
  primary300: '#93c5fd',
  primary400: '#60a5fa',
  primary500: '#3b82f6',
  primary600: '#2563eb',
  primary700: '#1d4ed8',
  primary800: '#1e40af',
  primary900: '#1e3a8a',
  secondary: '#6366f1',
  secondary50: '#eef2ff',
  secondary100: '#e0e7ff',
  secondary200: '#c7d2fe',
  secondary300: '#a5b4fc',
  secondary400: '#818cf8',
  secondary500: '#6366f1',
  secondary600: '#4f46e5',
  secondary700: '#4338ca',
  secondary800: '#3730a3',
  secondary900: '#312e81',
  danger: '#ef4444',
  danger50: '#fef2f2',
  danger100: '#fee2e2',
  danger200: '#fecaca',
  danger300: '#fca5a5',
  danger400: '#f87171',
  danger500: '#ef4444',
  danger600: '#dc2626',
  danger700: '#b91c1c',
  danger800: '#991b1b',
  danger900: '#7f1d1d',
  success: '#22c55e',
  success50: '#f0fdf4',
  success100: '#dcfce7',
  success200: '#bbf7d0',
  success300: '#86efac',
  success400: '#4ade80',
  success500: '#22c55e',
  success600: '#16a34a',
  success700: '#15803d',
  success800: '#166534',
  success900: '#14532d',
  warning: '#eab308',
  warning50: '#fefce8',
  warning100: '#fef9c3',
  warning200: '#fef08a',
  warning300: '#fde047',
  warning400: '#facc15',
  warning500: '#eab308',
  warning600: '#ca8a04',
  warning700: '#a16207',
  warning800: '#854d0e',
  warning900: '#713f12',
  brown: '#bfa094',
  brown50: '#fdf8f6',
  brown100: '#f2e8e5',
  brown200: '#eaddd7',
  brown300: '#e0cec7',
  brown400: '#d2bab0',
  brown500: '#bfa094',
  brown600: '#a18072',
  brown700: '#977669',
  brown800: '#846358',
  brown900: '#43302b',
  dark: '#111827',
  dark50: '#f9fafb',
  dark100: '#f3f4f6',
  dark200: '#e5e7eb',
  dark300: '#d1d5db',
  dark400: '#9ca3af',
  dark500: '#6b7280',
  dark600: '#4b5563',
  dark700: '#374151',
  dark800: '#1f2937',
  dark900: '#111827',
  info: '#17A2B8',
  info100: '#17A2B8BD',
  primaryHover: '#276EF1BD',
  secondaryHover: '#7356BFBD',
  successHover: '#05944FBD',
  warningHover: '#FFE975BD',
  dangerHover: '#E11900BD',
  whiteHover: '#FFFFFFD5',
  darkHover: '#000000BD',
  infoHover: '#17A2B8BD',
  lightHover: '#EEEEEEBD'
};
var theme = {
  colors: colors,
  colorStyles: {
    primary: {
      color: colors.white,
      borderColor: colors.primary,
      backgroundColor: colors.primary,
      "&:hover": {
        color: colors.white,
        backgroundColor: colors.primaryHover
      }
    },
    secondary: {
      color: colors.white,
      borderColor: colors.secondary,
      backgroundColor: colors.secondary,
      "&:hover": {
        color: colors.white,
        backgroundColor: colors.secondaryHover
      }
    },
    light: {
      color: colors.dark,
      borderColor: colors.light,
      backgroundColor: colors.light,
      "&:hover": {
        color: colors.dark,
        backgroundColor: colors.lightHover
      }
    },
    success: {
      color: colors.white,
      borderColor: colors.success,
      backgroundColor: colors.success,
      "&:hover": {
        color: colors.white,
        backgroundColor: colors.successHover
      }
    },
    danger: {
      color: colors.white,
      borderColor: colors.danger,
      backgroundColor: colors.danger,
      "&:hover": {
        color: colors.white,
        backgroundColor: colors.dangerHover
      }
    },
    warning: {
      color: colors.dark,
      borderColor: colors.warning,
      backgroundColor: colors.warning,
      "&:hover": {
        color: colors.dark,
        backgroundColor: colors.warningHover
      }
    },
    dark: {
      color: colors.white,
      borderColor: colors.dark,
      backgroundColor: colors.dark,
      "&:hover": {
        color: colors.white,
        backgroundColor: colors.darkHover
      }
    },
    white: {
      color: colors.dark,
      borderColor: colors.white,
      backgroundColor: colors.white,
      "&:hover": {
        color: colors.dark,
        backgroundColor: colors.whiteHover
      }
    },
    info: {
      color: colors.white,
      borderColor: colors.info,
      backgroundColor: colors.info,
      "&:hover": {
        color: colors.white,
        backgroundColor: colors.infoHover
      }
    }
  },
  buttonStyle: {
    primary: {
      color: colors.white,
      borderColor: colors.primary,
      backgroundColor: colors.primary
    },
    secondary: {
      color: colors.white,
      borderColor: colors.secondary,
      backgroundColor: colors.secondary
    },
    light: {
      color: colors.dark,
      borderColor: colors.light,
      backgroundColor: colors.light
    },
    success: {
      color: colors.white,
      borderColor: colors.success,
      backgroundColor: colors.success
    },
    danger: {
      color: colors.white,
      borderColor: colors.danger,
      backgroundColor: colors.danger
    },
    warning: {
      color: colors.dark,
      borderColor: colors.warning,
      backgroundColor: colors.warning
    },
    dark: {
      color: colors.white,
      borderColor: colors.dark,
      backgroundColor: colors.dark
    },
    white: {
      color: colors.dark,
      borderColor: colors.white,
      backgroundColor: colors.white
    },
    info: {
      color: colors.white,
      borderColor: colors.info,
      backgroundColor: colors.info
    }
  },
  lightStyle: {
    primary: {
      color: colors.primary,
      borderColor: colors.primary,
      backgroundColor: "#E6E6FF"
    },
    secondary: {
      color: colors.secondary,
      borderColor: colors.secondary,
      backgroundColor: "#F0EDF8"
    },
    success: {
      color: colors.success,
      borderColor: colors.success,
      backgroundColor: "#E7FAE7"
    },
    danger: {
      color: colors.danger,
      borderColor: colors.danger,
      backgroundColor: "#FCE9E9"
    },
    warning: {
      color: colors.dark,
      borderColor: colors.warning,
      backgroundColor: "#FFFBE6"
    },
    dark: {
      color: colors.white,
      borderColor: colors.dark,
      backgroundColor: "#333333"
    },
    white: {
      color: colors.dark,
      borderColor: colors.dark,
      backgroundColor: "#F9F9F9"
    },
    light: {
      color: colors.dark,
      borderColor: colors.light,
      backgroundColor: colors.light
    },
    info: {
      color: colors.white,
      borderColor: colors.info,
      backgroundColor: colors.info
    }
  }
};
var AccordionItem = function AccordionItem(props) {
  var _useState = useState(false),
    opened = _useState[0],
    setOpened = _useState[1];
  var title = props.title,
    content = props.content;
  var accordionItemClassName = classNames('accordion', opened ? 'accordion-item--opened' : false);
  var handleAccordionToggle = function handleAccordionToggle() {
    setOpened(!opened);
  };
  var accordionItemComp = React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component, {
    className: accordionItemClassName,
    onClick: handleAccordionToggle
  }, React.createElement("div", {
    className: 'accordion-item__line'
  }, React.createElement("h3", {
    className: 'accordion-item__title'
  }, title), React.createElement("span", {
    className: 'accordion-item__icon'
  })), React.createElement("div", {
    className: 'accordion-item__inner'
  }, React.createElement("div", {
    className: 'accordion-item__content'
  }, React.createElement("p", {
    className: 'accordion-item__paragraph'
  }, content)))));
  return accordionItemComp;
};
AccordionItem.defaultProps = {};
function _templateObject2() {
  var data = _taggedTemplateLiteralLoose(["\n          .accordion-item__icon {\n            display: none;\n          }\n        "]);
  _templateObject2 = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$1() {
  var data = _taggedTemplateLiteralLoose(["\n  width: 100%;\n  ", ";\n  .accordion-list {\n    list-style: none;\n    margin: 0;\n    padding: 0;\n    overflow: hidden;\n    border: 1px solid #f4f4f4;\n  }\n  .accordion-list__item + .accordion-list__item {\n    border-top: 1px solid #f4f4f4;\n  }\n  .accordion-item--opened .accordion-item__icon {\n    transform: rotate(180deg);\n  }\n  .accordion-item--opened .accordion-item__inner {\n    max-height: 100rem;\n    transition-timing-function: cubic-bezier(0.895, 0.03, 0.685, 0.22);\n    transition-duration: 0.5s;\n    transition-property: max-height;\n  }\n  .accordion-item--opened .accordion-item__content {\n    opacity: 1;\n    transform: translateY(0);\n    transition-delay: 0.2s;\n    transition-timing-function: ease-in-out;\n    transition-duration: 0.2s;\n    transition-property: opacity, transform;\n  }\n  .accordion-item__line {\n    display: block;\n    padding: 0.8rem 1.2rem;\n    display: flex;\n    justify-content: space-between;\n    align-items: center;\n    background-color: #fff;\n    z-index: 2;\n    position: relative;\n  }\n  .accordion-item__title {\n    font-size: 1.2rem;\n    margin: 0;\n    font-weight: 500;\n    color: #121212;\n  }\n  .accordion-item__icon {\n    width: 1.5rem;\n    height: 1.5rem;\n    transition: transform 0.3s ease-in-out;\n    background-size: contain;\n    background-repeat: no-repeat;\n    background-image: url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGAAAABgCAQAAABIkb+zAAABGklEQVR4Ae3RAcZCQRiF4buDfwshBGi+2UQgcIGAVtpSIuS/KyilG+UTcbk6zIH3GQBm3mM6AAAAAAAAAACA+eqf/yZBXcV/2XeCVPYx1FXj/FjGUMd45AQp/1HHGGLZNL+e61jHnKDmv8652YT1IvPfE2LX/Sh27/ycsF60yT/lk58JYn6eU4MJccjnlAmZ/33i0OAH4jg9Qcw/5g9YJpS+m6n0xvzpCfVe+nn59S7kGyYo+YYJWz3fO+E2PaFs9XzPhMy/6fmWCXq+YUJs9HzrhLh+JsQmrnq+bYKeb52g53snXPR88wQ93z9Bz/dP0PP9E/R89wQ93zpBz7dO0POtE/R86wQ93zpBzzdP+MoHAAAAAAAAAADAExTnTW20AtjhAAAAAElFTkSuQmCC);\n    opacity: 0.6;\n  }\n  .accordion-item__inner {\n    max-height: 0;\n    overflow: hidden;\n    text-transform: cubic-bezier(0.95, 0.05, 0.795, 0.035);\n    transition-duration: 0.5s;\n    transition-property: max-height;\n    z-index: 1;\n    position: relative;\n  }\n  .accordion-item__content {\n    opacity: 0;\n    transform: translateY(-1rem);\n    transition-timing-function: linear, ease;\n    transition-duration: 0.1s;\n    transition-property: opacity, transform;\n    transition-delay: 0.5s;\n    padding: 0 1.2rem 1.2rem;\n  }\n  .accordion-item__paragraph {\n    margin: 0;\n    font-size: 1rem;\n    color: #333;\n    font-weight: 300;\n    line-height: 1.3;\n  }\n"]);
  _templateObject$1 = function _templateObject() {
    return data;
  };
  return data;
}
var Component$1 = styled.div(_templateObject$1(), function (props) {
  return props.hideIcon ? css(_templateObject2()) : null;
});
var Accordion = function Accordion(props) {
  var data = props.data,
    accordionClass = props.accordionClass,
    accordionHeaderClass = props.accordionHeaderClass,
    accordionBodyClass = props.accordionBodyClass,
    _props$hideIcon = props.hideIcon,
    hideIcon = _props$hideIcon === void 0 ? false : _props$hideIcon;
  var accordionClassName = classNames("wrapper", accordionClass);
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$1, {
    className: accordionClassName,
    hideIcon: hideIcon
  }, React.createElement("ul", {
    className: "accordion-list"
  }, data.map(function (data, key) {
    return React.createElement("li", {
      className: "accordion-list__item",
      key: key
    }, React.createElement(AccordionItem, Object.assign({}, data, {
      accordionHeaderClass: accordionHeaderClass,
      accordionBodyClass: accordionBodyClass
    })));
  }))));
};
Accordion.prototype = {
  accordionClass: PropTypes.string,
  accordionHeaderClass: PropTypes.string,
  accordionBodyClass: PropTypes.string,
  hideIcon: PropTypes.bool,
  tag: PropTypes.string,
  data: PropTypes.arrayOf(PropTypes.any)
};
function _templateObject2$1() {
  var data = _taggedTemplateLiteralLoose(["\n\tcolor:inherit;\n\tborder:none;\n\tbackground-color:transparent;\n    right: 0px;\n    position: absolute;\n    padding: 0px 1.2em;\n\t:focus { outline:none;\t}\n"]);
  _templateObject2$1 = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$2() {
  var data = _taggedTemplateLiteralLoose(["\n    ", "\n    border-radius: 0px;\n    align-items:center;\n    justify-content:space-between;\n    min-width:500px;\n"]);
  _templateObject$2 = function _templateObject() {
    return data;
  };
  return data;
}
var Component$2 = styled.div(_templateObject$2(), colorStyle);
var Button = styled.button(_templateObject2$1());
var Alert = function Alert(props) {
  var className = props.className,
    tag = props.tag,
    color = props.color,
    children = props.children,
    dismiss = props.dismiss,
    attributes = _objectWithoutPropertiesLoose(props, ["className", "tag", "color", "children", "dismiss"]);
  var alertClasses = classNames("alert", className);
  var close = function close(e) {
    e.target.parentNode.remove();
  };
  var alertComponent = React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$2, Object.assign({}, attributes, {
    className: alertClasses,
    role: "alert",
    as: tag,
    colors: color
  }), children, dismiss ? React.createElement(Button, {
    onClick: close
  }, "\xD7") : null));
  return alertComponent;
};
Alert.defaultProps = {
  color: "primary",
  tag: "div"
};
function _templateObject2$2() {
  var data = _taggedTemplateLiteralLoose(["\n  .animated {\n    animation-duration: 1s;\n    animation-fill-mode: both;\n  }\n  .animated.infinite {\n    animation-iteration-count: infinite;\n  }\n  .animated.delay-1s {\n    animation-delay: 1s;\n  }\n  .animated.delay-2s {\n    animation-delay: 2s;\n  }\n  .animated.delay-3s {\n    animation-delay: 3s;\n  }\n  .animated.delay-4s {\n    animation-delay: 4s;\n  }\n  .animated.delay-5s {\n    animation-delay: 5s;\n  }\n  .animated.fast {\n    animation-duration: 800ms;\n  }\n  .animated.faster {\n    animation-duration: 500ms;\n  }\n  .animated.slow {\n    animation-duration: 2s;\n  }\n  .animated.slower {\n    animation-duration: 3s;\n  }\n  @media (prefers-reduced-motion) {\n    .animated {\n      transition: none !important;\n      animation: unset !important;\n    }\n  }\n\n  @keyframes fadeIn {\n    from {\n      opacity: 0;\n    }\n    to {\n      opacity: 1;\n    }\n  }\n  .fadeIn {\n    animation-name: fadeIn;\n  }\n  @keyframes fadeInDown {\n    from {\n      opacity: 0;\n      transform: translate3d(0, -100%, 0);\n    }\n    to {\n      opacity: 1;\n      transform: translate3d(0, 0, 0);\n    }\n  }\n  .fadeInDown {\n    animation-name: fadeInDown;\n  }\n  @keyframes fadeInLeft {\n    from {\n      opacity: 0;\n      transform: translate3d(-100%, 0, 0);\n    }\n    to {\n      opacity: 1;\n      transform: translate3d(0, 0, 0);\n    }\n  }\n  .fadeInLeft {\n    animation-name: fadeInLeft;\n  }\n  @keyframes fadeInRight {\n    from {\n      opacity: 0;\n      transform: translate3d(100%, 0, 0);\n    }\n    to {\n      opacity: 1;\n      transform: translate3d(0, 0, 0);\n    }\n  }\n  .fadeInRight {\n    animation-name: fadeInRight;\n  }\n  @keyframes fadeInUp {\n    from {\n      opacity: 0;\n      transform: translate3d(0, 100%, 0);\n    }\n    to {\n      opacity: 1;\n      transform: translate3d(0, 0, 0);\n    }\n  }\n  .fadeInUp {\n    animation-name: fadeInUp;\n  }\n  @keyframes fadeOut {\n    from {\n      opacity: 1;\n    }\n    to {\n      opacity: 0;\n    }\n  }\n  .fadeOut {\n    animation-name: fadeOut;\n  }\n  @keyframes fadeOutDown {\n    from {\n      opacity: 1;\n    }\n    to {\n      opacity: 0;\n      transform: translate3d(0, 100%, 0);\n    }\n  }\n  .fadeOutDown {\n    animation-name: fadeOutDown;\n  }\n  @keyframes fadeOutLeft {\n    from {\n      opacity: 1;\n    }\n    to {\n      opacity: 0;\n      transform: translate3d(-100%, 0, 0);\n    }\n  }\n  .fadeOutLeft {\n    animation-name: fadeOutLeft;\n  }\n  @keyframes fadeOutRight {\n    from {\n      opacity: 1;\n    }\n    to {\n      opacity: 0;\n      transform: translate3d(100%, 0, 0);\n    }\n  }\n  .fadeOutRight {\n    animation-name: fadeOutRight;\n  }\n  @keyframes fadeOutUp {\n    from {\n      opacity: 1;\n    }\n    to {\n      opacity: 0;\n      transform: translate3d(0, -100%, 0);\n    }\n  }\n  .fadeOutUp {\n    animation-name: fadeOutUp;\n  } /*!\n * animate.css -http://daneden.me/animate\n * Version - 3.7.0\n * Licensed under the MIT license - http://opensource.org/licenses/MIT\n *\n * Copyright (c) 2018 Daniel Eden\n */\n  @keyframes bounce {\n    from,\n    20%,\n    53%,\n    80%,\n    to {\n      transform: translate3d(0, 0, 0);\n      animation-timing-function: cubic-bezier(0.215, 0.61, 0.355, 1);\n    }\n    40%,\n    43% {\n      transform: translate3d(0, -30px, 0);\n      animation-timing-function: cubic-bezier(0.755, 0.05, 0.855, 0.06);\n    }\n    70% {\n      transform: translate3d(0, -15px, 0);\n      animation-timing-function: cubic-bezier(0.755, 0.05, 0.855, 0.06);\n    }\n    90% {\n      transform: translate3d(0, -4px, 0);\n    }\n  }\n  .bounce {\n    transform-origin: center bottom;\n    animation-name: bounce;\n  }\n  @keyframes flash {\n    from,\n    50%,\n    to {\n      opacity: 1;\n    }\n    25%,\n    75% {\n      opacity: 0;\n    }\n  }\n  .flash {\n    animation-name: flash;\n  }\n  @keyframes pulse {\n    from {\n      transform: scale3d(1, 1, 1);\n    }\n    50% {\n      transform: scale3d(1.05, 1.05, 1.05);\n    }\n    to {\n      transform: scale3d(1, 1, 1);\n    }\n  }\n  .pulse {\n    animation-name: pulse;\n  }\n  @keyframes rubberBand {\n    from {\n      transform: scale3d(1, 1, 1);\n    }\n    30% {\n      transform: scale3d(1.25, 0.75, 1);\n    }\n    40% {\n      transform: scale3d(0.75, 1.25, 1);\n    }\n    50% {\n      transform: scale3d(1.15, 0.85, 1);\n    }\n    65% {\n      transform: scale3d(0.95, 1.05, 1);\n    }\n    75% {\n      transform: scale3d(1.05, 0.95, 1);\n    }\n    to {\n      transform: scale3d(1, 1, 1);\n    }\n  }\n  .rubberBand {\n    animation-name: rubberBand;\n  }\n  @keyframes shake {\n    from,\n    to {\n      transform: translate3d(0, 0, 0);\n    }\n    10%,\n    30%,\n    50%,\n    70%,\n    90% {\n      transform: translate3d(-10px, 0, 0);\n    }\n    20%,\n    40%,\n    60%,\n    80% {\n      transform: translate3d(10px, 0, 0);\n    }\n  }\n  .shake {\n    animation-name: shake;\n  }\n  @keyframes headShake {\n    0% {\n      transform: translateX(0);\n    }\n    6.5% {\n      transform: translateX(-6px) rotateY(-9deg);\n    }\n    18.5% {\n      transform: translateX(5px) rotateY(7deg);\n    }\n    31.5% {\n      transform: translateX(-3px) rotateY(-5deg);\n    }\n    43.5% {\n      transform: translateX(2px) rotateY(3deg);\n    }\n    50% {\n      transform: translateX(0);\n    }\n  }\n  .headShake {\n    animation-name: headShake;\n    animation-timing-function: ease-in-out;\n  }\n  @keyframes swing {\n    20% {\n      transform: rotate3d(0, 0, 1, 15deg);\n    }\n    40% {\n      transform: rotate3d(0, 0, 1, -10deg);\n    }\n    60% {\n      transform: rotate3d(0, 0, 1, 5deg);\n    }\n    80% {\n      transform: rotate3d(0, 0, 1, -5deg);\n    }\n    to {\n      transform: rotate3d(0, 0, 1, 0deg);\n    }\n  }\n  .swing {\n    transform-origin: top center;\n    animation-name: swing;\n  }\n  @keyframes tada {\n    from {\n      transform: scale3d(1, 1, 1);\n    }\n    10%,\n    20% {\n      transform: scale3d(0.9, 0.9, 0.9) rotate3d(0, 0, 1, -3deg);\n    }\n    30%,\n    50%,\n    70%,\n    90% {\n      transform: scale3d(1.1, 1.1, 1.1) rotate3d(0, 0, 1, 3deg);\n    }\n    40%,\n    60%,\n    80% {\n      transform: scale3d(1.1, 1.1, 1.1) rotate3d(0, 0, 1, -3deg);\n    }\n    to {\n      transform: scale3d(1, 1, 1);\n    }\n  }\n  .tada {\n    animation-name: tada;\n  }\n  @keyframes wobble {\n    from {\n      transform: translate3d(0, 0, 0);\n    }\n    15% {\n      transform: translate3d(-25%, 0, 0) rotate3d(0, 0, 1, -5deg);\n    }\n    30% {\n      transform: translate3d(20%, 0, 0) rotate3d(0, 0, 1, 3deg);\n    }\n    45% {\n      transform: translate3d(-15%, 0, 0) rotate3d(0, 0, 1, -3deg);\n    }\n    60% {\n      transform: translate3d(10%, 0, 0) rotate3d(0, 0, 1, 2deg);\n    }\n    75% {\n      transform: translate3d(-5%, 0, 0) rotate3d(0, 0, 1, -1deg);\n    }\n    to {\n      transform: translate3d(0, 0, 0);\n    }\n  }\n  .wobble {\n    animation-name: wobble;\n  }\n  @keyframes jello {\n    from,\n    11.1%,\n    to {\n      transform: translate3d(0, 0, 0);\n    }\n    22.2% {\n      transform: skewX(-12.5deg) skewY(-12.5deg);\n    }\n    33.3% {\n      transform: skewX(6.25deg) skewY(6.25deg);\n    }\n    44.4% {\n      transform: skewX(-3.125deg) skewY(-3.125deg);\n    }\n    55.5% {\n      transform: skewX(1.5625deg) skewY(1.5625deg);\n    }\n    66.6% {\n      transform: skewX(-0.78125deg) skewY(-0.78125deg);\n    }\n    77.7% {\n      transform: skewX(0.39063deg) skewY(0.39063deg);\n    }\n    88.8% {\n      transform: skewX(-0.19531deg) skewY(-0.19531deg);\n    }\n  }\n  .jello {\n    transform-origin: center;\n    animation-name: jello;\n  }\n  @keyframes heartBeat {\n    0% {\n      transform: scale(1);\n    }\n    14% {\n      transform: scale(1.3);\n    }\n    28% {\n      transform: scale(1);\n    }\n    42% {\n      transform: scale(1.3);\n    }\n    70% {\n      transform: scale(1);\n    }\n  }\n  .heartBeat {\n    animation-name: heartBeat;\n    animation-duration: 1.3s;\n    animation-timing-function: ease-in-out;\n  }\n  @keyframes bounceIn {\n    from,\n    20%,\n    40%,\n    60%,\n    80%,\n    to {\n      animation-timing-function: cubic-bezier(0.215, 0.61, 0.355, 1);\n    }\n    0% {\n      opacity: 0;\n      transform: scale3d(0.3, 0.3, 0.3);\n    }\n    20% {\n      transform: scale3d(1.1, 1.1, 1.1);\n    }\n    40% {\n      transform: scale3d(0.9, 0.9, 0.9);\n    }\n    60% {\n      opacity: 1;\n      transform: scale3d(1.03, 1.03, 1.03);\n    }\n    80% {\n      transform: scale3d(0.97, 0.97, 0.97);\n    }\n    to {\n      opacity: 1;\n      transform: scale3d(1, 1, 1);\n    }\n  }\n  .bounceIn {\n    animation-name: bounceIn;\n    animation-duration: 0.75s;\n  }\n  @keyframes bounceInDown {\n    from,\n    60%,\n    75%,\n    90%,\n    to {\n      animation-timing-function: cubic-bezier(0.215, 0.61, 0.355, 1);\n    }\n    0% {\n      opacity: 0;\n      transform: translate3d(0, -3000px, 0);\n    }\n    60% {\n      opacity: 1;\n      transform: translate3d(0, 25px, 0);\n    }\n    75% {\n      transform: translate3d(0, -10px, 0);\n    }\n    90% {\n      transform: translate3d(0, 5px, 0);\n    }\n    to {\n      transform: translate3d(0, 0, 0);\n    }\n  }\n  .bounceInDown {\n    animation-name: bounceInDown;\n  }\n  @keyframes bounceInLeft {\n    from,\n    60%,\n    75%,\n    90%,\n    to {\n      animation-timing-function: cubic-bezier(0.215, 0.61, 0.355, 1);\n    }\n    0% {\n      opacity: 0;\n      transform: translate3d(-3000px, 0, 0);\n    }\n    60% {\n      opacity: 1;\n      transform: translate3d(25px, 0, 0);\n    }\n    75% {\n      transform: translate3d(-10px, 0, 0);\n    }\n    90% {\n      transform: translate3d(5px, 0, 0);\n    }\n    to {\n      transform: translate3d(0, 0, 0);\n    }\n  }\n  .bounceInLeft {\n    animation-name: bounceInLeft;\n  }\n  @keyframes bounceInRight {\n    from,\n    60%,\n    75%,\n    90%,\n    to {\n      animation-timing-function: cubic-bezier(0.215, 0.61, 0.355, 1);\n    }\n    from {\n      opacity: 0;\n      transform: translate3d(3000px, 0, 0);\n    }\n    60% {\n      opacity: 1;\n      transform: translate3d(-25px, 0, 0);\n    }\n    75% {\n      transform: translate3d(10px, 0, 0);\n    }\n    90% {\n      transform: translate3d(-5px, 0, 0);\n    }\n    to {\n      transform: translate3d(0, 0, 0);\n    }\n  }\n  .bounceInRight {\n    animation-name: bounceInRight;\n  }\n  @keyframes bounceInUp {\n    from,\n    60%,\n    75%,\n    90%,\n    to {\n      animation-timing-function: cubic-bezier(0.215, 0.61, 0.355, 1);\n    }\n    from {\n      opacity: 0;\n      transform: translate3d(0, 3000px, 0);\n    }\n    60% {\n      opacity: 1;\n      transform: translate3d(0, -20px, 0);\n    }\n    75% {\n      transform: translate3d(0, 10px, 0);\n    }\n    90% {\n      transform: translate3d(0, -5px, 0);\n    }\n    to {\n      transform: translate3d(0, 0, 0);\n    }\n  }\n  .bounceInUp {\n    animation-name: bounceInUp;\n  }\n  @keyframes bounceOut {\n    20% {\n      transform: scale3d(0.9, 0.9, 0.9);\n    }\n    50%,\n    55% {\n      opacity: 1;\n      transform: scale3d(1.1, 1.1, 1.1);\n    }\n    to {\n      opacity: 0;\n      transform: scale3d(0.3, 0.3, 0.3);\n    }\n  }\n  .bounceOut {\n    animation-name: bounceOut;\n    animation-duration: 0.75s;\n  }\n  @keyframes bounceOutDown {\n    20% {\n      transform: translate3d(0, 10px, 0);\n    }\n    40%,\n    45% {\n      opacity: 1;\n      transform: translate3d(0, -20px, 0);\n    }\n    to {\n      opacity: 0;\n      transform: translate3d(0, 2000px, 0);\n    }\n  }\n  .bounceOutDown {\n    animation-name: bounceOutDown;\n  }\n  @keyframes bounceOutLeft {\n    20% {\n      opacity: 1;\n      transform: translate3d(20px, 0, 0);\n    }\n    to {\n      opacity: 0;\n      transform: translate3d(-2000px, 0, 0);\n    }\n  }\n  .bounceOutLeft {\n    animation-name: bounceOutLeft;\n  }\n  @keyframes bounceOutRight {\n    20% {\n      opacity: 1;\n      transform: translate3d(-20px, 0, 0);\n    }\n    to {\n      opacity: 0;\n      transform: translate3d(2000px, 0, 0);\n    }\n  }\n  .bounceOutRight {\n    animation-name: bounceOutRight;\n  }\n  @keyframes bounceOutUp {\n    20% {\n      transform: translate3d(0, -10px, 0);\n    }\n    40%,\n    45% {\n      opacity: 1;\n      transform: translate3d(0, 20px, 0);\n    }\n    to {\n      opacity: 0;\n      transform: translate3d(0, -2000px, 0);\n    }\n  }\n  .bounceOutUp {\n    animation-name: bounceOutUp;\n  }\n  @keyframes fadeInDownBig {\n    from {\n      opacity: 0;\n      transform: translate3d(0, -2000px, 0);\n    }\n    to {\n      opacity: 1;\n      transform: translate3d(0, 0, 0);\n    }\n  }\n  .fadeInDownBig {\n    animation-name: fadeInDownBig;\n  }\n  @keyframes fadeInLeftBig {\n    from {\n      opacity: 0;\n      transform: translate3d(-2000px, 0, 0);\n    }\n    to {\n      opacity: 1;\n      transform: translate3d(0, 0, 0);\n    }\n  }\n  .fadeInLeftBig {\n    animation-name: fadeInLeftBig;\n  }\n  @keyframes fadeInRightBig {\n    from {\n      opacity: 0;\n      transform: translate3d(2000px, 0, 0);\n    }\n    to {\n      opacity: 1;\n      transform: translate3d(0, 0, 0);\n    }\n  }\n  .fadeInRightBig {\n    animation-name: fadeInRightBig;\n  }\n  @keyframes fadeInUpBig {\n    from {\n      opacity: 0;\n      transform: translate3d(0, 2000px, 0);\n    }\n    to {\n      opacity: 1;\n      transform: translate3d(0, 0, 0);\n    }\n  }\n  .fadeInUpBig {\n    animation-name: fadeInUpBig;\n  }\n  @keyframes fadeOutDownBig {\n    from {\n      opacity: 1;\n    }\n    to {\n      opacity: 0;\n      transform: translate3d(0, 2000px, 0);\n    }\n  }\n  .fadeOutDownBig {\n    animation-name: fadeOutDownBig;\n  }\n  @keyframes fadeOutLeftBig {\n    from {\n      opacity: 1;\n    }\n    to {\n      opacity: 0;\n      transform: translate3d(-2000px, 0, 0);\n    }\n  }\n  .fadeOutLeftBig {\n    animation-name: fadeOutLeftBig;\n  }\n  @keyframes fadeOutRightBig {\n    from {\n      opacity: 1;\n    }\n    to {\n      opacity: 0;\n      transform: translate3d(2000px, 0, 0);\n    }\n  }\n  .fadeOutRightBig {\n    animation-name: fadeOutRightBig;\n  }\n  @keyframes fadeOutUpBig {\n    from {\n      opacity: 1;\n    }\n    to {\n      opacity: 0;\n      transform: translate3d(0, -2000px, 0);\n    }\n  }\n  .fadeOutUpBig {\n    animation-name: fadeOutUpBig;\n  }\n  @keyframes flip {\n    from {\n      transform: perspective(400px) scale3d(1, 1, 1) translate3d(0, 0, 0)\n        rotate3d(0, 1, 0, -360deg);\n      animation-timing-function: ease-out;\n    }\n    40% {\n      transform: perspective(400px) scale3d(1, 1, 1) translate3d(0, 0, 150px)\n        rotate3d(0, 1, 0, -190deg);\n      animation-timing-function: ease-out;\n    }\n    50% {\n      transform: perspective(400px) scale3d(1, 1, 1) translate3d(0, 0, 150px)\n        rotate3d(0, 1, 0, -170deg);\n      animation-timing-function: ease-in;\n    }\n    80% {\n      transform: perspective(400px) scale3d(0.95, 0.95, 0.95)\n        translate3d(0, 0, 0) rotate3d(0, 1, 0, 0deg);\n      animation-timing-function: ease-in;\n    }\n    to {\n      transform: perspective(400px) scale3d(1, 1, 1) translate3d(0, 0, 0)\n        rotate3d(0, 1, 0, 0deg);\n      animation-timing-function: ease-in;\n    }\n  }\n  .flip {\n    backface-visibility: visible;\n    animation-name: flip;\n  }\n  @keyframes flipInX {\n    from {\n      opacity: 0;\n      transform: perspective(400px) rotate3d(1, 0, 0, 90deg);\n      animation-timing-function: ease-in;\n    }\n    40% {\n      transform: perspective(400px) rotate3d(1, 0, 0, -20deg);\n      animation-timing-function: ease-in;\n    }\n    60% {\n      opacity: 1;\n      transform: perspective(400px) rotate3d(1, 0, 0, 10deg);\n    }\n    80% {\n      transform: perspective(400px) rotate3d(1, 0, 0, -5deg);\n    }\n    to {\n      transform: perspective(400px);\n    }\n  }\n  .flipInX {\n    backface-visibility: visible;\n    animation-name: flipInX;\n  }\n  @keyframes flipInY {\n    from {\n      opacity: 0;\n      transform: perspective(400px) rotate3d(0, 1, 0, 90deg);\n      animation-timing-function: ease-in;\n    }\n    40% {\n      transform: perspective(400px) rotate3d(0, 1, 0, -20deg);\n      animation-timing-function: ease-in;\n    }\n    60% {\n      opacity: 1;\n      transform: perspective(400px) rotate3d(0, 1, 0, 10deg);\n    }\n    80% {\n      transform: perspective(400px) rotate3d(0, 1, 0, -5deg);\n    }\n    to {\n      transform: perspective(400px);\n    }\n  }\n  .flipInY {\n    backface-visibility: visible;\n    animation-name: flipInY;\n  }\n  @keyframes flipOutX {\n    from {\n      transform: perspective(400px);\n    }\n    30% {\n      opacity: 1;\n      transform: perspective(400px) rotate3d(1, 0, 0, -20deg);\n    }\n    to {\n      opacity: 0;\n      transform: perspective(400px) rotate3d(1, 0, 0, 90deg);\n    }\n  }\n  .flipOutX {\n    animation-name: flipOutX;\n    animation-duration: 0.75s;\n    backface-visibility: visible;\n  }\n  @keyframes flipOutY {\n    from {\n      transform: perspective(400px);\n    }\n    30% {\n      opacity: 1;\n      transform: perspective(400px) rotate3d(0, 1, 0, -15deg);\n    }\n    to {\n      opacity: 0;\n      transform: perspective(400px) rotate3d(0, 1, 0, 90deg);\n    }\n  }\n  .flipOutY {\n    animation-name: flipOutY;\n    animation-duration: 0.75s;\n    backface-visibility: visible;\n  }\n  @keyframes lightSpeedIn {\n    from {\n      opacity: 0;\n      transform: translate3d(100%, 0, 0) skewX(-30deg);\n    }\n    60% {\n      opacity: 1;\n      transform: skewX(20deg);\n    }\n    80% {\n      transform: skewX(-5deg);\n    }\n    to {\n      transform: translate3d(0, 0, 0);\n    }\n  }\n  .lightSpeedIn {\n    animation-name: lightSpeedIn;\n    animation-timing-function: ease-out;\n  }\n  @keyframes lightSpeedOut {\n    from {\n      opacity: 1;\n    }\n    to {\n      opacity: 0;\n      transform: translate3d(100%, 0, 0) skewX(30deg);\n    }\n  }\n  .lightSpeedOut {\n    animation-name: lightSpeedOut;\n    animation-timing-function: ease-in;\n  }\n  @keyframes rotateIn {\n    from {\n      opacity: 0;\n      transform: rotate3d(0, 0, 1, -200deg);\n      transform-origin: center;\n    }\n    to {\n      opacity: 1;\n      transform: translate3d(0, 0, 0);\n      transform-origin: center;\n    }\n  }\n  .rotateIn {\n    animation-name: rotateIn;\n  }\n  @keyframes rotateInDownLeft {\n    from {\n      opacity: 0;\n      transform: rotate3d(0, 0, 1, -45deg);\n      transform-origin: left bottom;\n    }\n    to {\n      opacity: 1;\n      transform: translate3d(0, 0, 0);\n      transform-origin: left bottom;\n    }\n  }\n  .rotateInDownLeft {\n    animation-name: rotateInDownLeft;\n  }\n  @keyframes rotateInDownRight {\n    from {\n      opacity: 0;\n      transform: rotate3d(0, 0, 1, 45deg);\n      transform-origin: right bottom;\n    }\n    to {\n      opacity: 1;\n      transform: translate3d(0, 0, 0);\n      transform-origin: right bottom;\n    }\n  }\n  .rotateInDownRight {\n    animation-name: rotateInDownRight;\n  }\n  @keyframes rotateInUpLeft {\n    from {\n      opacity: 0;\n      transform: rotate3d(0, 0, 1, 45deg);\n      transform-origin: left bottom;\n    }\n    to {\n      opacity: 1;\n      transform: translate3d(0, 0, 0);\n      transform-origin: left bottom;\n    }\n  }\n  .rotateInUpLeft {\n    animation-name: rotateInUpLeft;\n  }\n  @keyframes rotateInUpRight {\n    from {\n      opacity: 0;\n      transform: rotate3d(0, 0, 1, -90deg);\n      transform-origin: right bottom;\n    }\n    to {\n      opacity: 1;\n      transform: translate3d(0, 0, 0);\n      transform-origin: right bottom;\n    }\n  }\n  .rotateInUpRight {\n    animation-name: rotateInUpRight;\n  }\n  @keyframes rotateOut {\n    from {\n      opacity: 1;\n      transform-origin: center;\n    }\n    to {\n      opacity: 0;\n      transform: rotate3d(0, 0, 1, 200deg);\n      transform-origin: center;\n    }\n  }\n  .rotateOut {\n    animation-name: rotateOut;\n  }\n  @keyframes rotateOutDownLeft {\n    from {\n      opacity: 1;\n      transform-origin: left bottom;\n    }\n    to {\n      opacity: 0;\n      transform: rotate3d(0, 0, 1, 45deg);\n      transform-origin: left bottom;\n    }\n  }\n  .rotateOutDownLeft {\n    animation-name: rotateOutDownLeft;\n  }\n  @keyframes rotateOutDownRight {\n    from {\n      opacity: 1;\n      transform-origin: right bottom;\n    }\n    to {\n      opacity: 0;\n      transform: rotate3d(0, 0, 1, -45deg);\n      transform-origin: right bottom;\n    }\n  }\n  .rotateOutDownRight {\n    animation-name: rotateOutDownRight;\n  }\n  @keyframes rotateOutUpLeft {\n    from {\n      opacity: 1;\n      transform-origin: left bottom;\n    }\n    to {\n      opacity: 0;\n      transform: rotate3d(0, 0, 1, -45deg);\n      transform-origin: left bottom;\n    }\n  }\n  .rotateOutUpLeft {\n    animation-name: rotateOutUpLeft;\n  }\n  @keyframes rotateOutUpRight {\n    from {\n      opacity: 1;\n      transform-origin: right bottom;\n    }\n    to {\n      opacity: 0;\n      transform: rotate3d(0, 0, 1, 90deg);\n      transform-origin: right bottom;\n    }\n  }\n  .rotateOutUpRight {\n    animation-name: rotateOutUpRight;\n  }\n  @keyframes hinge {\n    0% {\n      transform-origin: top left;\n      animation-timing-function: ease-in-out;\n    }\n    20%,\n    60% {\n      transform: rotate3d(0, 0, 1, 80deg);\n      transform-origin: top left;\n      animation-timing-function: ease-in-out;\n    }\n    40%,\n    80% {\n      opacity: 1;\n      transform: rotate3d(0, 0, 1, 60deg);\n      transform-origin: top left;\n      animation-timing-function: ease-in-out;\n    }\n    to {\n      opacity: 0;\n      transform: translate3d(0, 700px, 0);\n    }\n  }\n  .hinge {\n    animation-name: hinge;\n    animation-duration: 2s;\n  }\n  @keyframes jackInTheBox {\n    from {\n      opacity: 0;\n      transform: scale(0.1) rotate(30deg);\n      transform-origin: center bottom;\n    }\n    50% {\n      transform: rotate(-10deg);\n    }\n    70% {\n      transform: rotate(3deg);\n    }\n    to {\n      opacity: 1;\n      transform: scale(1);\n    }\n  }\n  .jackInTheBox {\n    animation-name: jackInTheBox;\n  }\n  @keyframes rollIn {\n    from {\n      opacity: 0;\n      transform: translate3d(-100%, 0, 0) rotate3d(0, 0, 1, -120deg);\n    }\n    to {\n      opacity: 1;\n      transform: translate3d(0, 0, 0);\n    }\n  }\n  .rollIn {\n    animation-name: rollIn;\n  }\n  @keyframes rollOut {\n    from {\n      opacity: 1;\n    }\n    to {\n      opacity: 0;\n      transform: translate3d(100%, 0, 0) rotate3d(0, 0, 1, 120deg);\n    }\n  }\n  .rollOut {\n    animation-name: rollOut;\n  }\n  @keyframes zoomIn {\n    from {\n      opacity: 0;\n      transform: scale3d(0.3, 0.3, 0.3);\n    }\n    50% {\n      opacity: 1;\n    }\n  }\n  .zoomIn {\n    animation-name: zoomIn;\n  }\n  @keyframes zoomInDown {\n    from {\n      opacity: 0;\n      transform: scale3d(0.1, 0.1, 0.1) translate3d(0, -1000px, 0);\n      animation-timing-function: cubic-bezier(0.55, 0.055, 0.675, 0.19);\n    }\n    60% {\n      opacity: 1;\n      transform: scale3d(0.475, 0.475, 0.475) translate3d(0, 60px, 0);\n      animation-timing-function: cubic-bezier(0.175, 0.885, 0.32, 1);\n    }\n  }\n  .zoomInDown {\n    animation-name: zoomInDown;\n  }\n  @keyframes zoomInLeft {\n    from {\n      opacity: 0;\n      transform: scale3d(0.1, 0.1, 0.1) translate3d(-1000px, 0, 0);\n      animation-timing-function: cubic-bezier(0.55, 0.055, 0.675, 0.19);\n    }\n    60% {\n      opacity: 1;\n      transform: scale3d(0.475, 0.475, 0.475) translate3d(10px, 0, 0);\n      animation-timing-function: cubic-bezier(0.175, 0.885, 0.32, 1);\n    }\n  }\n  .zoomInLeft {\n    animation-name: zoomInLeft;\n  }\n  @keyframes zoomInRight {\n    from {\n      opacity: 0;\n      transform: scale3d(0.1, 0.1, 0.1) translate3d(1000px, 0, 0);\n      animation-timing-function: cubic-bezier(0.55, 0.055, 0.675, 0.19);\n    }\n    60% {\n      opacity: 1;\n      transform: scale3d(0.475, 0.475, 0.475) translate3d(-10px, 0, 0);\n      animation-timing-function: cubic-bezier(0.175, 0.885, 0.32, 1);\n    }\n  }\n  .zoomInRight {\n    animation-name: zoomInRight;\n  }\n  @keyframes zoomInUp {\n    from {\n      opacity: 0;\n      transform: scale3d(0.1, 0.1, 0.1) translate3d(0, 1000px, 0);\n      animation-timing-function: cubic-bezier(0.55, 0.055, 0.675, 0.19);\n    }\n    60% {\n      opacity: 1;\n      transform: scale3d(0.475, 0.475, 0.475) translate3d(0, -60px, 0);\n      animation-timing-function: cubic-bezier(0.175, 0.885, 0.32, 1);\n    }\n  }\n  .zoomInUp {\n    animation-name: zoomInUp;\n  }\n  @keyframes zoomOut {\n    from {\n      opacity: 1;\n    }\n    50% {\n      opacity: 0;\n      transform: scale3d(0.3, 0.3, 0.3);\n    }\n    to {\n      opacity: 0;\n    }\n  }\n  .zoomOut {\n    animation-name: zoomOut;\n  }\n  @keyframes zoomOutDown {\n    40% {\n      opacity: 1;\n      transform: scale3d(0.475, 0.475, 0.475) translate3d(0, -60px, 0);\n      animation-timing-function: cubic-bezier(0.55, 0.055, 0.675, 0.19);\n    }\n    to {\n      opacity: 0;\n      transform: scale3d(0.1, 0.1, 0.1) translate3d(0, 2000px, 0);\n      transform-origin: center bottom;\n      animation-timing-function: cubic-bezier(0.175, 0.885, 0.32, 1);\n    }\n  }\n  .zoomOutDown {\n    animation-name: zoomOutDown;\n  }\n  @keyframes zoomOutLeft {\n    40% {\n      opacity: 1;\n      transform: scale3d(0.475, 0.475, 0.475) translate3d(42px, 0, 0);\n    }\n    to {\n      opacity: 0;\n      transform: scale(0.1) translate3d(-2000px, 0, 0);\n      transform-origin: left center;\n    }\n  }\n  .zoomOutLeft {\n    animation-name: zoomOutLeft;\n  }\n  @keyframes zoomOutRight {\n    40% {\n      opacity: 1;\n      transform: scale3d(0.475, 0.475, 0.475) translate3d(-42px, 0, 0);\n    }\n    to {\n      opacity: 0;\n      transform: scale(0.1) translate3d(2000px, 0, 0);\n      transform-origin: right center;\n    }\n  }\n  .zoomOutRight {\n    animation-name: zoomOutRight;\n  }\n  @keyframes zoomOutUp {\n    40% {\n      opacity: 1;\n      transform: scale3d(0.475, 0.475, 0.475) translate3d(0, 60px, 0);\n      animation-timing-function: cubic-bezier(0.55, 0.055, 0.675, 0.19);\n    }\n    to {\n      opacity: 0;\n      transform: scale3d(0.1, 0.1, 0.1) translate3d(0, -2000px, 0);\n      transform-origin: center bottom;\n      animation-timing-function: cubic-bezier(0.175, 0.885, 0.32, 1);\n    }\n  }\n  .zoomOutUp {\n    animation-name: zoomOutUp;\n  }\n  @keyframes slideInDown {\n    from {\n      visibility: visible;\n      transform: translate3d(0, -100%, 0);\n    }\n    to {\n      transform: translate3d(0, 0, 0);\n    }\n  }\n  .slideInDown {\n    animation-name: slideInDown;\n  }\n  @keyframes slideInLeft {\n    from {\n      visibility: visible;\n      transform: translate3d(-100%, 0, 0);\n    }\n    to {\n      transform: translate3d(0, 0, 0);\n    }\n  }\n  .slideInLeft {\n    animation-name: slideInLeft;\n  }\n  @keyframes slideInRight {\n    from {\n      visibility: visible;\n      transform: translate3d(100%, 0, 0);\n    }\n    to {\n      transform: translate3d(0, 0, 0);\n    }\n  }\n  .slideInRight {\n    animation-name: slideInRight;\n  }\n  @keyframes slideInUp {\n    from {\n      visibility: visible;\n      transform: translate3d(0, 100%, 0);\n    }\n    to {\n      transform: translate3d(0, 0, 0);\n    }\n  }\n  .slideInUp {\n    animation-name: slideInUp;\n  }\n  @keyframes slideOutDown {\n    from {\n      transform: translate3d(0, 0, 0);\n    }\n    to {\n      visibility: hidden;\n      transform: translate3d(0, 100%, 0);\n    }\n  }\n  .slideOutDown {\n    animation-name: slideOutDown;\n  }\n  @keyframes slideOutLeft {\n    from {\n      transform: translate3d(0, 0, 0);\n    }\n    to {\n      visibility: hidden;\n      transform: translate3d(-100%, 0, 0);\n    }\n  }\n  .slideOutLeft {\n    animation-name: slideOutLeft;\n  }\n  @keyframes slideOutRight {\n    from {\n      transform: translate3d(0, 0, 0);\n    }\n    to {\n      visibility: hidden;\n      transform: translate3d(100%, 0, 0);\n    }\n  }\n  .slideOutRight {\n    animation-name: slideOutRight;\n  }\n  @keyframes slideOutUp {\n    from {\n      transform: translate3d(0, 0, 0);\n    }\n    to {\n      visibility: hidden;\n      transform: translate3d(0, -100%, 0);\n    }\n  }\n  .slideOutUp {\n    animation-name: slideOutUp;\n  }\n"]);
  _templateObject2$2 = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$3() {
  var data = _taggedTemplateLiteralLoose([""]);
  _templateObject$3 = function _templateObject() {
    return data;
  };
  return data;
}
var Component$3 = styled.div(_templateObject$3());
var Container = styled.div(_templateObject2$2());
var Animation = function Animation(props) {
  var tag = props.tag,
    children = props.children,
    className = props.className,
    count = props.count,
    delay = props.delay,
    duration = props.duration,
    infinite = props.infinite,
    reveal = props.reveal,
    style = props.style,
    type = props.type,
    countIterations = props.countIterations,
    onAnimationStart = props.onAnimationStart,
    onAnimationIteration = props.onAnimationIteration,
    onAnimationEnd = props.onAnimationEnd,
    attributes = _objectWithoutPropertiesLoose(props, ["tag", "children", "className", "count", "delay", "duration", "infinite", "reveal", "style", "currentRef", "clientHeight", "type", "countIterations", "onAnimationStart", "onAnimationIteration", "onAnimationEnd"]);
  var _useState = useState({
      isVisible: false,
      revealed: false,
      countIterations: 0
    }),
    state = _useState[0],
    setState = _useState[1];
  var elemRef = createRef();
  var handleStart = function handleStart() {
    setState(_extends({}, state, {
      countIterations: countIterations + 1
    }));
    if (onAnimationStart) {
      onAnimationStart();
    }
  };
  var handleIteration = function handleIteration() {
    if (onAnimationIteration) {
      setState(_extends({}, state, {
        countIterations: countIterations + 1
      }));
      onAnimationIteration();
    }
  };
  var handleEnd = function handleEnd() {
    setState(_extends({}, state, {
      countIterations: countIterations + 1
    }));
    if (onAnimationEnd && count === countIterations) {
      onAnimationEnd();
    }
  };
  var getOffset = function getOffset(elem) {
    var box = elem.getBoundingClientRect();
    var _document = document,
      body = _document.body;
    var docEl = document.documentElement;
    var scrollTop = window.pageYOffset || docEl.scrollTop || body.scrollTop;
    var clientTop = docEl.clientTop || body.clientTop || 0;
    var top = box.top + scrollTop - clientTop;
    return Math.round(top);
  };
  var updatePredicate = useCallback(function () {
    var windowHeight = window.innerHeight;
    var scroll = window.scrollY;
    var docHeight = document.documentElement.offsetHeight;
    var currentRef = elemRef.current;
    if (windowHeight + scroll - 100 > getOffset(currentRef) && scroll < getOffset(currentRef) || windowHeight + scroll - 100 > getOffset(currentRef) + currentRef.clientHeight && scroll < getOffset(currentRef) + currentRef.clientHeight || windowHeight + scroll === docHeight && getOffset(currentRef) + 100 > docHeight) {
      setState(_extends({}, state, {
        isVisible: true,
        revealed: true
      }));
    } else if (!state.revealed) {
      setState(_extends({}, state, {
        isVisible: false,
        revealed: true
      }));
    }
  }, [state, elemRef]);
  useDeepCompareEffect(function () {
    setState(_extends({}, state, {
      isVisible: !reveal,
      revealed: !reveal
    }));
    if (state.revealed) {
      window.addEventListener("scroll", updatePredicate);
      updatePredicate();
    }
    return function () {
      if (state.revealed) {
        window.removeEventListener("scroll", updatePredicate);
      }
    };
  }, [state, reveal]);
  var styleObject = {
    animationDuration: duration.toString(),
    animationDelay: delay,
    animationIterationCount: infinite ? "infinite" : count,
    visibility: state.isVisible ? "visible" : "hidden",
    animationName: type
  };
  var hiddenStyles = {
    animationName: "none",
    visibility: "hidden"
  };
  var getAllStyles = Object.assign(styleObject, style);
  var classes = classNames(state.isVisible && "animated", type && type, infinite && "infinite", className);
  var animationComponent = React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Container, null, React.createElement(Component$3, Object.assign({
    as: tag,
    "data-test": "animation",
    className: classes,
    onAnimationEnd: handleEnd,
    onAnimationIteration: handleIteration,
    onAnimationStart: handleStart,
    ref: elemRef,
    style: state.isVisible && state.revealed ? getAllStyles : hiddenStyles
  }, attributes), children)));
  return animationComponent;
};
Animation.defaultProps = {
  tag: "div",
  reveal: false,
  duration: 1,
  count: 1
};
function _templateObject3() {
  var data = _taggedTemplateLiteralLoose([""]);
  _templateObject3 = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$3() {
  var data = _taggedTemplateLiteralLoose([""]);
  _templateObject2$3 = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$4() {
  var data = _taggedTemplateLiteralLoose(["\n  // icon container\n  .icon {\n    position: absolute;\n    height: 100%;\n    width: 30px;\n    top: 0;\n    left: 0;\n    display: flex;\n    justify-content: center;\n    align-items: center;\n  }\n  .icon + input:not([type='checkbox']):not([type='radio']),\n  textarea {\n    padding-left: 30px;\n  }\n  &.input-group label {\n    top: 0;\n    margin-bottom: 0;\n  }\n  &.input-group .input-group-text {\n    background-color: #e0e0e0;\n  }\n  &.input-group .input-group-text.md-addon {\n    font-weight: 500;\n    background-color: transparent;\n    border: none;\n  }\n  &.input-group .form-control {\n    padding: 0.375rem 0.75rem;\n    margin: 0;\n  }\n  & {\n    position: relative;\n    margin-top: 0.625rem;\n    margin-bottom: 0.625rem;\n  }\n  & {\n    input:not([type='checkbox']):not([type='radio']),\n    textarea {\n      border-radius: 4px;\n      border: 1px solid #dadce0;\n      box-sizing: border-box;\n      border-radius: 4px;\n      box-shadow: none;\n      padding: 4px 10px;\n      min-height: 40px;\n      width: 100%;\n      transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;\n      font-size: ", ";\n      &::placeholder {\n        font-size: ", ";\n      }\n      &:focus {\n        outline: none;\n        &::placeholder {\n          font-weight: 700 !important;\n        }\n      }\n      &::placeholder {\n        color: #757575;\n        font-weight: 400;\n        font-size: inherit;\n        color: #757575;\n        cursor: text;\n        transition: color 0.2s ease-out;\n      }\n    }\n    textarea {\n      padding: 10px !important;\n    }\n  }\n  &.size-lg {\n    input:not([type='checkbox']):not([type='radio']),\n    textarea {\n      min-height: 50px;\n      font-size: ", ";\n      &::placeholder {\n        font-size: ", ";\n      }\n    }\n  }\n  &.size-sm {\n    input:not([type='checkbox']):not([type='radio']),\n    textarea {\n      min-height: 30px;\n      font-size: ", ";\n      &::placeholder {\n        font-size: ", ";\n      }\n    }\n  }\n  &.checkbox,\n  &.radio {\n    margin: 0;\n    height: 20px;\n    width: 20px;\n    &.size-lg {\n      transform: scale(1.4);\n    }\n    &.size-sm {\n      transform: scale(0.6);\n    }\n    position: relative;\n    label {\n      background-color: #fff;\n      border: 1px solid #ccc;\n      // border-radius: 4px;\n      cursor: pointer;\n      height: 20px;\n      left: 0;\n      position: absolute;\n      top: 0;\n      width: 20px;\n    }\n\n    input[type='checkbox'],\n    input[type='radio'] {\n      width: 20px;\n      height: 20px;\n      visibility: hidden;\n    }\n    input[type='radio'] + label {\n      border-radius: 50%;\n    }\n    input[type='checkbox'] + label:after {\n      border: 2px solid ", ";\n      border-top: none;\n      border-right: none;\n      content: '';\n      height: 6px;\n      left: 2px;\n      opacity: 0;\n      position: absolute;\n      top: 2px;\n      transform: rotate(-45deg);\n      width: 12px;\n    }\n    input[type='radio']:not(.browser) + label:after {\n      border: 2px solid ", ";\n      border-radius: 50%;\n      content: '';\n      height: 10px;\n      left: 50%;\n      opacity: 0;\n      position: absolute;\n      top: 50%;\n      transform: translate(-50%, -50%);\n      width: 10px;\n    }\n\n    input[type='checkbox']:not(.browser-default):checked + label,\n    input[type='radio']:not(.browser-default):checked + label {\n      background-color: ", ";\n      border: 2px solid ", ";\n    }\n    input[type='checkbox']:not(.browser-default):checked + label:after,\n    input[type='radio']:not(.browser-default):checked + label:after {\n      opacity: 1;\n    }\n    &.input-color {\n      &-primary {\n        input[type='checkbox']:not(.browser-default):checked + label,\n        input[type='radio']:not(.browser-default):checked + label {\n          background-color: ", ";\n          border: 2px solid ", ";\n        }\n\n        input[type='checkbox']:not(.browser-default):checked + label:after,\n        input[type='radio']:not(.browser-default):checked + label:after {\n          border: 2px solid ", ";\n        }\n        input[type='checkbox']:not(.browser-default):checked + label:after {\n          border-top: none;\n          border-right: none;\n        }\n      }\n      &-secondary {\n        input[type='checkbox']:not(.browser-default):checked + label,\n        input[type='radio']:not(.browser-default):checked + label {\n          background-color: ", ";\n          border: 2px solid ", ";\n        }\n        input[type='checkbox']:not(.browser-default):checked + label:after,\n        input[type='radio']:not(.browser-default):checked + label:after {\n          border: 2px solid ", ";\n        }\n        input[type='checkbox']:not(.browser-default):checked + label:after {\n          border-top: none;\n          border-right: none;\n        }\n      }\n      &-success {\n        input[type='checkbox']:not(.browser-default):checked + label,\n        input[type='radio']:not(.browser-default):checked + label {\n          background-color: ", ";\n          border: 2px solid ", ";\n        }\n        input[type='checkbox']:not(.browser-default):checked + label:after,\n        input[type='radio']:not(.browser-default):checked + label:after {\n          border: 2px solid ", ";\n        }\n        input[type='checkbox']:not(.browser-default):checked + label:after {\n          border-top: none;\n          border-right: none;\n        }\n      }\n      &-danger {\n        input[type='checkbox']:not(.browser-default):checked + label,\n        input[type='radio']:not(.browser-default):checked + label {\n          background-color: ", ";\n          border: 2px solid ", ";\n        }\n        input[type='checkbox']:not(.browser-default):checked + label:after,\n        input[type='radio']:not(.browser-default):checked + label:after {\n          border: 2px solid ", ";\n        }\n        input[type='checkbox']:not(.browser-default):checked + label:after {\n          border-top: none;\n          border-right: none;\n        }\n      }\n      &-warning {\n        input[type='checkbox']:not(.browser-default):checked + label,\n        input[type='radio']:not(.browser-default):checked + label {\n          background-color: ", ";\n          border: 2px solid ", ";\n        }\n        input[type='checkbox']:not(.browser-default):checked + label:after,\n        input[type='radio']:not(.browser-default):checked + label:after {\n          border: 2px solid ", ";\n        }\n        input[type='checkbox']:not(.browser-default):checked + label:after {\n          border-top: none;\n          border-right: none;\n        }\n      }\n      &-info {\n        input[type='checkbox']:not(.browser-default):checked + label,\n        input[type='radio']:not(.browser-default):checked + label {\n          background-color: ", ";\n          border: 2px solid ", ";\n        }\n        input[type='checkbox']:not(.browser-default):checked + label:after,\n        input[type='radio']:not(.browser-default):checked + label:after {\n          border: 2px solid ", ";\n        }\n        input[type='checkbox']:not(.browser-default):checked + label:after {\n          border-top: none;\n          border-right: none;\n        }\n      }\n      &-dark {\n        input[type='checkbox']:not(.browser-default):checked + label,\n        input[type='radio']:not(.browser-default):checked + label {\n          background-color: ", ";\n          border: 2px solid ", ";\n        }\n        input[type='checkbox']:not(.browser-default):checked + label:after,\n        input[type='radio']:not(.browser-default):checked + label:after {\n          border: 2px solid ", ";\n        }\n        input[type='checkbox']:not(.browser-default):checked + label:after {\n          border-top: none;\n          border-right: none;\n        }\n      }\n    }\n  }\n  &.input-color-primary {\n    &.bg {\n      input:not([type='checkbox']),\n      input:not([type='radio']),\n      textarea {\n        &:focus,\n        &:focus-visible {\n          background: ", ";\n        }\n      }\n    }\n    input:not([type='checkbox']):not([type='radio']),\n    textarea {\n      &:focus,\n      &:focus-visible {\n        border: 1px solid ", ";\n        box-shadow: inset 0 0 0 1px ", ";\n        &::placeholder {\n          color: ", ";\n          font-weight: 700;\n        }\n      }\n    }\n  }\n\n  &.input-color-secondary {\n    &.bg {\n      input:not([type='checkbox']),\n      input:not([type='radio']),\n      textarea {\n        &:focus,\n        &:focus-visible {\n          background: ", ";\n        }\n      }\n    }\n    input:not([type='checkbox']):not([type='radio']),\n    textarea {\n      &:focus,\n      &:focus-visible {\n        border: 1px solid ", ";\n        box-shadow: inset 0 0 0 1px ", ";\n        &::placeholder {\n          color: ", ";\n          font-weight: 700;\n        }\n      }\n    }\n  }\n  &.input-color-success {\n    &.bg {\n      input:not([type='checkbox']),\n      input:not([type='radio']),\n      textarea {\n        &:focus,\n        &:focus-visible {\n          background: ", ";\n        }\n      }\n    }\n    input:not([type='checkbox']):not([type='radio']),\n    textarea {\n      &:focus,\n      &:focus-visible {\n        border: 1px solid ", ";\n        box-shadow: inset 0 0 0 1px ", ";\n        &::placeholder {\n          color: ", ";\n          font-weight: 700;\n        }\n      }\n    }\n  }\n\n  &.input-color-danger {\n    &.bg {\n      input:not([type='checkbox']),\n      input:not([type='radio']),\n      textarea {\n        &:focus,\n        &:focus-visible {\n          background: ", ";\n        }\n      }\n    }\n    input:not([type='checkbox']):not([type='radio']),\n    textarea {\n      &:focus,\n      &:focus-visible {\n        border: 1px solid ", ";\n        box-shadow: inset 0 0 0 1px ", ";\n        &::placeholder {\n          color: ", ";\n          font-weight: 700;\n        }\n      }\n    }\n    input[type='checkbox']:not(.browser-default),\n    input[type='radio']:not(.browser-default) {\n      background: red;\n    }\n  }\n  &.input-color-warning {\n    &.bg {\n      input:not([type='checkbox']),\n      input:not([type='radio']),\n      textarea {\n        &:focus,\n        &:focus-visible {\n          background: ", ";\n        }\n      }\n    }\n    input:not([type='checkbox']):not([type='radio']),\n    textarea {\n      &:focus,\n      &:focus-visible {\n        border: 1px solid ", ";\n        box-shadow: inset 0 0 0 1px ", ";\n        &::placeholder {\n          color: ", ";\n          font-weight: 700;\n        }\n      }\n    }\n  }\n  &.input-color-info {\n    &.bg {\n      input:not([type='checkbox']),\n      input:not([type='radio']),\n      textarea {\n        &:focus,\n        &:focus-visible {\n          background: ", ";\n        }\n      }\n    }\n    input:not([type='checkbox']):not([type='radio']),\n    textarea {\n      &:focus,\n      &:focus-visible {\n        border: 1px solid ", ";\n        box-shadow: inset 0 0 0 1px ", ";\n        &::placeholder {\n          color: ", ";\n          font-weight: 700;\n        }\n      }\n    }\n  }\n  &.input-color-dark {\n    &.bg {\n      input:not([type='checkbox']),\n      input:not([type='radio']),\n      textarea {\n        &:focus,\n        &:focus-visible {\n          background: ", ";\n        }\n      }\n    }\n    input:not([type='checkbox']):not([type='radio']),\n    textarea {\n      &:focus,\n      &:focus-visible {\n        border: 1px solid ", ";\n        box-shadow: inset 0 0 0 1px ", ";\n        &::placeholder {\n          color: ", ";\n          font-weight: 700;\n        }\n      }\n    }\n  }\n\n  & label.active {\n    font-weight: 700;\n  }\n  & .prefix {\n    top: 0.25rem;\n    font-size: 1.75rem;\n  }\n  & .prefix ~ input,\n  & .prefix ~ textarea {\n    width: calc(100% - 2.5rem);\n    margin-left: 2.5rem;\n  }\n  & .prefix ~ label {\n    margin-left: 2.5rem;\n  }\n  & .prefix ~ .form-text {\n    margin-left: 2.6rem;\n  }\n  & .prefix {\n    position: absolute;\n    transition: color 0.2s;\n  }\n  & .prefix.active {\n    color: #4285f4;\n  }\n  textarea {\n    padding: 0;\n    overflow-y: hidden;\n  }\n\n  & .form-control.is-valid,\n  .was-validated & .form-control:valid {\n    padding-right: 0;\n  }\n  .needs-validation & label {\n    left: 0.3rem;\n  }\n  .custom-file-input:lang(es) ~ .custom-file-label::after {\n    content: 'Elegir';\n  }\n  .custom-file-input:lang(pl-pl) ~ .custom-file-label::after {\n    content: 'Wybierz';\n  }\n  .custom-file-input:lang(fr) ~ .custom-file-label::after {\n    content: 'Choisir';\n  }\n  .custom-file-input:lang(in) ~ .custom-file-label::after {\n    content: 'Pilih';\n  }\n  .custom-file-input:lang(zh) ~ .custom-file-label::after {\n    content: '\u9078\u64C7';\n  }\n  .custom-file-input:lang(de) ~ .custom-file-label::after {\n    content: 'W\xE4hlen';\n  }\n  .custom-file-input:lang(ru) ~ .custom-file-label::after {\n    content: '\u0412\u044B\u0431\u0440\u0430\u0442\u044C';\n  }\n  & > label {\n    max-width: 100%;\n    white-space: nowrap;\n    overflow: hidden;\n    text-overflow: ellipsis;\n  }\n  & .input-prefix {\n    position: absolute;\n    top: 50%;\n    transform: translateY(-50%);\n    transition: color 0.2s;\n    color: rgba(0, 0, 0, 0.87);\n    pointer-events: none;\n  }\n  & .input-prefix.active {\n    color: #4285f4;\n  }\n  &.input-with-pre-icon label {\n    left: 36px;\n    right: initial;\n  }\n  &.input-with-pre-icon .input-prefix {\n    left: 16px;\n    right: initial;\n  }\n  &.input-with-pre-icon .form-control {\n    padding-left: 2.7rem !important;\n  }\n  &.input-with-post-icon .input-prefix {\n    right: 16px;\n    left: initial;\n  }\n  &.input-with-post-icon .form-control {\n    padding-right: 2.7rem !important;\n  }\n  &.input-with-post-icon.input-with-pre-icon .input-prefix {\n    right: 16px;\n    left: initial;\n  }\n  &.input-with-post-icon.input-with-pre-icon .input-prefix:first-of-type {\n    left: 16px;\n    right: initial;\n  }\n  &.input-with-post-icon.input-with-pre-icon .form-control {\n    padding-left: 2.5rem !important;\n    padding-right: 2.5rem !important;\n  }\n"]);
  _templateObject$4 = function _templateObject() {
    return data;
  };
  return data;
}
var Component$4 = styled.div(_templateObject$4(), function (_ref) {
  var fontSize = _ref.fontSize;
  return fontSize + "px";
}, function (_ref2) {
  var fontSize = _ref2.fontSize;
  return fontSize + "px";
}, function (_ref3) {
  var fontSize = _ref3.fontSize;
  return fontSize && fontSize * 1.2 + "px";
}, function (_ref4) {
  var fontSize = _ref4.fontSize;
  return fontSize && fontSize * 1.2 + "px";
}, function (_ref5) {
  var fontSize = _ref5.fontSize;
  return fontSize && fontSize * 0.8 + "px";
}, function (_ref6) {
  var fontSize = _ref6.fontSize;
  return fontSize && fontSize * 0.8 + "px";
}, colors.primary, colors.primary, colors.primary100, colors.primary, colors.primary100, colors.primary, colors.primary, colors.secondary100, colors.secondary, colors.secondary, colors.success100, colors.success, colors.success, colors.danger100, colors.danger, colors.danger, colors.warning100, colors.warning, colors.warning, colors.info, colors.info, colors.info, colors.dark100, colors.dark, colors.dark, colors.primary100, colors.primary, colors.primary, colors.primary, colors.secondary100, colors.secondary, colors.secondary, colors.secondary, colors.success100, colors.success, colors.success, colors.success, colors.danger100, colors.danger, colors.danger, colors.danger, colors.warning100, colors.warning, colors.warning, colors.warning, colors.info, colors.info, colors.info, colors.info, colors.dark100, colors.dark, colors.dark, colors.dark);
var StyledTextArea = styled.textarea(_templateObject2$3());
var StyledInput = styled.input(_templateObject3());
function _templateObject$5() {
  var data = _taggedTemplateLiteralLoose(["\n  padding: 0px 4px;\n"]);
  _templateObject$5 = function _templateObject() {
    return data;
  };
  return data;
}
var Component$5 = styled.i(_templateObject$5());
var Icon = function Icon(props) {
  var border = props.border,
    brand = props.brand,
    className = props.className,
    fab = props.fab,
    duotone = props.duotone,
    fal = props.fal,
    fad = props.fad,
    far = props.far,
    solid = props.solid,
    fixed = props.fixed,
    fas = props.fas,
    flip = props.flip,
    icon = props.icon,
    inverse = props.inverse,
    light = props.light,
    list = props.list,
    pull = props.pull,
    pulse = props.pulse,
    regular = props.regular,
    rotate = props.rotate,
    size = props.size,
    spin = props.spin,
    stack = props.stack,
    attributes = _objectWithoutPropertiesLoose(props, ["border", "brand", "className", "fab", "duotone", "fal", "fad", "onClick", "far", "solid", "fixed", "fas", "flip", "icon", "inverse", "light", "list", "pull", "pulse", "regular", "rotate", "size", "spin", "stack"]);
  var iconPrefix = regular || far ? 'far' : solid || fas ? 'fas' : light || fal ? 'fal' : duotone || fad ? 'fad' : brand || fab ? 'fab' : 'fa';
  var classes = classNames(iconPrefix, list ? 'fa-li' : false, icon ? "fa-" + icon : false, size ? "fa-" + size : false, fixed ? 'fa-fw' : false, pull ? "fa-pull-" + pull : false, border ? 'fa-border' : false, spin ? 'fa-spin' : false, pulse ? 'fa-pulse' : false, rotate ? "fa-rotate-" + rotate : false, flip ? "fa-flip-" + flip : false, inverse ? 'fa-inverse' : false, stack ? "fa-" + stack : false, className);
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$5, Object.assign({
    "data-test": 'fa'
  }, attributes, {
    className: classes
  })));
};
Icon.defaultProps = {
  border: false,
  brand: false,
  className: '',
  fab: false,
  fal: false,
  far: false,
  fixed: false,
  flip: '',
  inverse: false,
  light: false,
  list: false,
  pull: '',
  pulse: false,
  regular: false,
  rotate: '',
  size: '',
  spin: false,
  stack: '',
  duotone: false,
  fad: false,
  solid: false,
  fas: false
};
var Input = function Input(props) {
  var background = props.background,
    className = props.className,
    color = props.color,
    disabled = props.disabled,
    focused = props.focused,
    fontSize = props.fontSize,
    getValue = props.getValue,
    placeholder = props.placeholder,
    id = props.id,
    indeterminate = props.indeterminate,
    inputClassName = props.inputClassName,
    labelClassName = props.labelClassName,
    onBlur = props.onBlur,
    onChange = props.onChange,
    onFocus = props.onFocus,
    onInput = props.onInput,
    size = props.size,
    icon = props.icon,
    type = props.type,
    value = props.value,
    valueDefault = props.valueDefault,
    checked = props.checked,
    label = props.label,
    material = props.material,
    attributes = _objectWithoutPropertiesLoose(props, ["background", "className", "color", "disabled", "focused", "fontSize", "getValue", "placeholder", "id", "indeterminate", "inputClassName", "labelClassName", "onKeyDown", "onBlur", "onChange", "onFocus", "onInput", "size", "icon", "type", "value", "valueDefault", "checked", "label", "material"]);
  {
    var _useState = useState(value || valueDefault),
      innerValue = _useState[0],
      setInnerValue = _useState[1];
    var _useState2 = useState(focused),
      isFocused = _useState2[0],
      setIsFocused = _useState2[1];
    var inputRef = useRef();
    useEffect(function () {
      isFocused && inputRef.current.focus();
      if (indeterminate && inputRef.current instanceof HTMLInputElement) inputRef.current.indeterminate = true;
    }, [isFocused]);
    useEffect(function () {
      setInnerValue(value);
    }, [value]);
    var handleBlur = function handleBlur(event) {
      event.stopPropagation();
      setIsFocused(false);
      onBlur && onBlur(event);
    };
    var handleFocus = function handleFocus(event) {
      event.stopPropagation();
      setIsFocused(false);
      onFocus && onFocus(event);
    };
    var handleChange = function handleChange(event) {
      event.stopPropagation();
      var _event$target = event.target,
        value = _event$target.value,
        checked = _event$target.checked;
      if (type !== 'checkbox' && type !== 'radio') {
        setInnerValue(value);
        getValue && getValue(value);
      } else {
        getValue && getValue(checked);
      }
      onChange && onChange(event);
    };
    var handleInput = function handleInput(event) {
      event.stopPropagation();
      if (type !== 'checkbox' && type !== 'radio') {
        setInnerValue(event.target.value);
      }
      onInput && onInput(event);
    };
    var isNotEmpty = (!!innerValue || !!placeholder || isFocused || typeof innerValue === 'number' && innerValue === 0) && type !== 'checkbox' && type !== 'radio';
    var classes = classNames('contrast-input', className, background && "bg", color && "input-color-" + color, size === 'lg' && 'size-lg', size === 'sm' && 'size-sm', type === 'checkbox' && 'checkbox', type === 'radio' && 'radio', material && "mb-0");
    var labelClassNames = classNames(isNotEmpty || placeholder ? 'active' : false, disabled ? 'disabled' : false, labelClassName);
    var inputClassNames = classNames(material && "pt-0  border-top-0 border-end-0 border-start-0 ", inputClassName);
    return React.createElement(ThemeProvider, {
      theme: theme
    }, !material && label && type !== 'checkbox' && type !== 'radio' && React.createElement("label", {
      className: labelClassNames,
      htmlFor: id,
      id: id,
      "aria-labelledby": id
    }, " ", label), React.createElement(Component$4, {
      className: classes,
      fontSize: fontSize
    }, icon && React.createElement("span", {
      className: 'icon'
    }, typeof icon === 'string' ? React.createElement(Icon, {
      icon: icon
    }) : icon), type === 'textarea' ? React.createElement(StyledTextArea, Object.assign({
      fontSize: fontSize,
      ref: inputRef,
      onBlur: handleBlur,
      onChange: handleChange,
      onInput: handleInput,
      onFocus: handleFocus,
      placeholder: placeholder
    }, attributes, {
      className: inputClassNames,
      id: id,
      value: innerValue,
      "aria-disabled": disabled,
      rows: 10
    })) : React.createElement(StyledInput, Object.assign({
      ref: inputRef,
      onBlur: handleBlur,
      onChange: handleChange,
      onInput: handleInput,
      onFocus: handleFocus
    }, attributes, {
      className: inputClassNames,
      id: id,
      type: type,
      placeholder: placeholder,
      value: innerValue,
      "aria-disabled": disabled,
      checked: checked,
      disabled: disabled
    })), (type === 'checkbox' || type === 'radio') && React.createElement("label", {
      className: labelClassNames,
      htmlFor: id,
      id: id,
      "aria-labelledby": id
    })), material && label && type !== 'checkbox' && type !== 'radio' && React.createElement("label", {
      className: labelClassNames,
      htmlFor: id,
      id: id,
      "aria-labelledby": id
    }, " ", label));
  }
};
Input.defaultProps = {
  valueDefault: '',
  background: PropTypes.string,
  className: '',
  color: 'dark',
  disabled: false,
  focused: false,
  fontSize: 14,
  placeholder: '',
  id: Date.now().toString(),
  indeterminate: false,
  inputClassName: '',
  labelClassName: '',
  type: 'text',
  value: '',
  getValue: function getValue() {},
  onBlur: function onBlur() {},
  onFocus: function onFocus() {},
  onInput: function onInput() {},
  size: '',
  icon: null
};
function _templateObject$6() {
  var data = _taggedTemplateLiteralLoose(["\n  .suggestions {\n    list-style: none;\n    position: absolute;\n    right: 0;\n    left: 0;\n    z-index: 100;\n    padding-left: 0;\n    overflow-y: auto;\n    list-style-type: none;\n    background: #fff;\n    -webkit-box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.16),\n      0 2px 10px 0 rgba(0, 0, 0, 0.12);\n    box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.16),\n      0 2px 10px 0 rgba(0, 0, 0, 0.12);\n  }\n  .suggestions li {\n    padding: 12px 15px;\n    font-size: 0.875rem;\n    cursor: pointer;\n  }\n  .no-suggestions {\n    list-style: none;\n    position: absolute;\n    right: 0;\n    left: 0;\n    z-index: 100;\n    padding-left: 0;\n    overflow-y: auto;\n    list-style-type: none;\n    background: #fff;\n    -webkit-box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.16),\n      0 2px 10px 0 rgba(0, 0, 0, 0.12);\n    box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.16),\n      0 2px 10px 0 rgba(0, 0, 0, 0.12);\n  }\n  .no-suggestions em {\n    padding: 12px 15px;\n    font-size: 0.87rem;\n  }\n"]);
  _templateObject$6 = function _templateObject() {
    return data;
  };
  return data;
}
var StyledComponent = styled.span(_templateObject$6());
var Autocomplete = function Autocomplete(props) {
  var label = props.label,
    getValue = props.getValue,
    suggestions = props.suggestions,
    attributes = _objectWithoutPropertiesLoose(props, ["label", "getValue", "suggestions"]);
  var _useState = useState({
      activeSuggestion: 0,
      filteredSuggestions: [],
      showSuggestions: false,
      userInput: ""
    }),
    state = _useState[0],
    setState = _useState[1];
  var onChange = function onChange(e) {
    var userInput = e.target.value;
    if (suggestions) {
      var filteredSuggestions = suggestions.filter(function (suggestion) {
        return suggestion.toLowerCase().indexOf(userInput.toLowerCase()) > -1;
      });
      setState({
        activeSuggestion: 0,
        filteredSuggestions: filteredSuggestions,
        showSuggestions: true,
        userInput: e.target.value
      });
      console.log(state);
      getValue && getValue(e.target.value);
    } else {
      return null;
    }
  };
  var onClick = function onClick(e) {
    setState({
      activeSuggestion: 0,
      filteredSuggestions: [],
      showSuggestions: false,
      userInput: e.currentTarget.innerText
    });
    getValue && getValue(e.currentTarget.innerText);
  };
  var onKeyDown = function onKeyDown(e) {
    var filteredSuggestions = state.filteredSuggestions,
      activeSuggestion = state.activeSuggestion;
    if (e.keyCode === 13) {
      setState(function (prevState) {
        return _extends({}, prevState, {
          activeSuggestion: 0,
          showSuggestions: false,
          userInput: filteredSuggestions[activeSuggestion]
        });
      });
    } else if (e.keyCode === 38) {
      if (activeSuggestion === 0) {
        return;
      }
      setState(function (prevState) {
        return _extends({}, prevState, {
          activeSuggestion: activeSuggestion - 1
        });
      });
    } else if (e.keyCode === 40) {
      if (activeSuggestion - 1 === filteredSuggestions.length) {
        return;
      }
      setState(function (prevState) {
        return _extends({}, prevState, {
          activeSuggestion: activeSuggestion + 1
        });
      });
    }
  };
  var suggestionsListComponent;
  if (state.showSuggestions && state.userInput) {
    if (state.filteredSuggestions.length) {
      suggestionsListComponent = React.createElement("ul", {
        className: "suggestions"
      }, state.filteredSuggestions.map(function (suggestion, index) {
        return React.createElement("li", {
          key: suggestion,
          onClick: onClick
        }, suggestion);
      }));
    } else {
      suggestionsListComponent = null;
    }
  }
  var userInput = state.userInput;
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(StyledComponent, null, React.createElement(Input, Object.assign({
    type: "text",
    onChange: onChange,
    onKeyDown: onKeyDown,
    value: userInput,
    labelClassName: label
  }, attributes)), suggestionsListComponent));
};
Autocomplete.defaultProps = {
  suggestion: []
};
function _templateObject$7() {
  var data = _taggedTemplateLiteralLoose(["\n  display: flex;\n  align-items: center;\n  ", "\n  ", "\n  font-weight:200;\n  box-shadow: ", ";\n\n  ", ";\n  ", "\n  .icon {\n    ", "\n    i {\n      ", "\n      ", "\n    }\n  }\n"]);
  _templateObject$7 = function _templateObject() {
    return data;
  };
  return data;
}
var Component$6 = styled.span(_templateObject$7(), space, color, function (props) {
  return props.flat ? null : "0 2px 5px 0 rgba(0, 0, 0, 0.16), 0 2px 10px 0 rgba(0, 0, 0, 0.12)";
}, variant({
  prop: "borderType",
  variants: {
    box: {
      borderRadius: "0px"
    },
    pill: {
      borderRadius: "20px"
    }
  }
}), variant({
  prop: "size",
  variants: {
    large: {
      padding: "8.75px 15px",
      fontSize: "1rem"
    },
    medium: {
      padding: "6.25px 8.75px",
      fontSize: "0.85rem"
    },
    small: {
      padding: "4px 6px",
      fontSize: "0.625rem"
    }
  }
}), variant({
  prop: "size",
  variants: {
    large: {
      fontSize: "1rem",
      marginRight: "12px"
    },
    medium: {
      fontSize: "0.85rem",
      marginRight: "10px"
    },
    small: {
      fontSize: "0.625rem !important",
      marginRight: "7.5px"
    }
  }
}), space, variant({
  prop: "size",
  variants: {
    large: {
      fontSize: "1rem"
    },
    medium: {
      fontSize: "0.85rem"
    },
    small: {
      fontSize: "0.625rem !important"
    }
  }
}));
var Badge = function Badge(props) {
  var className = props.className,
    tag = props.tag,
    color = props.color,
    children = props.children,
    icon = props.icon,
    borderType = props.borderType,
    size = props.size,
    flat = props.flat,
    attributes = _objectWithoutPropertiesLoose(props, ["className", "tag", "color", "intensity", "children", "icon", "borderType", "size", "flat"]);
  var badgeClasses = classNames(className);
  var badgeComponent = React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$6, Object.assign({
    className: badgeClasses,
    role: "badge",
    as: tag,
    bg: color,
    borderType: borderType,
    size: size,
    flat: flat
  }, attributes), icon && React.createElement("span", {
    className: "icon"
  }, React.createElement("i", {
    className: "fa fa-" + icon
  })), children));
  return badgeComponent;
};
Badge.defaultProps = {
  tag: "span",
  color: "primary",
  borderType: "pill",
  size: "medium",
  intensity: "900"
};
function _templateObject$8() {
  var data = _taggedTemplateLiteralLoose(["\n  ", "\n  ", "\n"]);
  _templateObject$8 = function _templateObject() {
    return data;
  };
  return data;
}
var Component$7 = styled.div(_templateObject$8(), space, color);
var Box = function Box(props) {
  var tag = props.tag,
    className = props.className,
    children = props.children,
    display = props.display,
    justifyContent = props.justifyContent,
    flex = props.flex,
    alignItems = props.alignItems,
    alignContent = props.alignContent,
    alignSelf = props.alignSelf,
    color = props.color,
    space = props.space,
    bgColor = props.bgColor,
    m = props.m,
    mt = props.mt,
    mr = props.mr,
    mb = props.mb,
    ml = props.ml,
    mx = props.mx,
    my = props.my,
    p = props.p,
    pt = props.pt,
    pr = props.pr,
    pb = props.pb,
    pl = props.pl,
    px = props.px,
    py = props.py,
    attributes = _objectWithoutPropertiesLoose(props, ["tag", "className", "children", "display", "justifyContent", "flex", "alignItems", "alignContent", "alignSelf", "color", "space", "bgColor", "m", "mt", "mr", "mb", "ml", "mx", "my", "p", "pt", "pr", "pb", "pl", "px", "py"]);
  var marginOrPadding = function marginOrPadding(props, suffix) {
    if (props !== undefined) {
      return suffix + "-" + props;
    }
  };
  var classes = classNames(display && "d-" + display, justifyContent && "justify-content-" + justifyContent, flex && "flex-" + flex, alignItems && "align-items-" + alignItems, alignContent && "align-content-" + alignContent, alignSelf && "align-self-" + alignSelf, color && color + "-text", bgColor && "bg-" + bgColor, marginOrPadding(m, "m"), marginOrPadding(mt, "mt"), marginOrPadding(mr, "mr"), marginOrPadding(mb, "mb"), marginOrPadding(ml, "ml"), marginOrPadding(mx, "mx"), marginOrPadding(my, "my"), marginOrPadding(p, "p"), marginOrPadding(pt, "pt"), marginOrPadding(pr, "pr"), marginOrPadding(pb, "pb"), marginOrPadding(pl, "pl"), marginOrPadding(px, "px"), marginOrPadding(py, "py"), className);
  var isEmptyClass = classes !== "" ? classes : null;
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$7, Object.assign({
    as: tag,
    "data-test": "box"
  }, attributes, {
    className: isEmptyClass,
    color: color,
    space: space
  }), children));
};
Box.defaultProps = {
  tag: "div"
};
function _templateObject$9() {
  var data = _taggedTemplateLiteralLoose(["\n  ", ";\n  font-weight: ", ";\n  border-radius: ", ";\n  text-transform: ", ";\n"]);
  _templateObject$9 = function _templateObject() {
    return data;
  };
  return data;
}
var Component$8 = styled.ol(_templateObject$9(), colorStyle, function (props) {
  return props.bold ? 'bold' : props.light ? 'light' : null;
}, function (props) {
  return props.circle ? '50%' : '0';
}, function (props) {
  return props.uppercase ? 'uppercase' : 'capitalize';
});
var Breadcrumb = function Breadcrumb(props) {
  var className = props.className,
    bold = props.bold,
    _props$color = props.color,
    color = _props$color === void 0 ? "primary" : _props$color,
    attributes = _objectWithoutPropertiesLoose(props, ["className", "bold", "color"]);
  var classes = classNames("breadcrumb", className);
  var children;
  if (bold) {
    children = Children.map(props.children, function (child) {
      return React.cloneElement(child, {
        bold: true
      });
    });
  } else {
    children = props.children;
  }
  var breadcrumbComponent = React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement("nav", {
    "data-test": "breadcrumb"
  }, React.createElement(Component$8, Object.assign({}, attributes, {
    bold: bold,
    className: classes,
    colors: color
  }), children)));
  return breadcrumbComponent;
};
function _templateObject8() {
  var data = _taggedTemplateLiteralLoose(["\n            border: 2.4px solid ", ";\n            transform: scale(1.02);\n            color: ", ";\n          "]);
  _templateObject8 = function _templateObject8() {
    return data;
  };
  return data;
}
function _templateObject7() {
  var data = _taggedTemplateLiteralLoose(["\n          border: 2px solid ", ";\n          color: ", ";\n        "]);
  _templateObject7 = function _templateObject7() {
    return data;
  };
  return data;
}
function _templateObject6() {
  var data = _taggedTemplateLiteralLoose(["\n      background: transparent;\n      ", ";\n      border-width: 2px;\n      &:hover {\n        background: transparent;\n        ", ";\n      }\n    "]);
  _templateObject6 = function _templateObject6() {
    return data;
  };
  return data;
}
function _templateObject5() {
  var data = _taggedTemplateLiteralLoose(["\n          border-radius: 4px;\n        "]);
  _templateObject5 = function _templateObject5() {
    return data;
  };
  return data;
}
function _templateObject4() {
  var data = _taggedTemplateLiteralLoose(["\n          border-radius: 30px;\n        "]);
  _templateObject4 = function _templateObject4() {
    return data;
  };
  return data;
}
function _templateObject3$1() {
  var data = _taggedTemplateLiteralLoose(["\n            transform: scale(1.02);\n            color: #fff;\n          "]);
  _templateObject3$1 = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$4() {
  var data = _taggedTemplateLiteralLoose(["\n      background: ", ";\n      color: #fff;\n      &:hover {\n        ", ";\n    "]);
  _templateObject2$4 = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$a() {
  var data = _taggedTemplateLiteralLoose(["\nborder: none;\n  box-shadow: ", ";\n  box-sizing: border-box;\n  font-weight: 600;\n  display:flex;\n  justify-content:center;\n  align-items:center;\n  ", "}\n    \n", ";\n\n  ", ";\n\n  ", ";\n\n  ", ";\n\n"]);
  _templateObject$a = function _templateObject() {
    return data;
  };
  return data;
}
var Component$9 = styled.div(_templateObject$a(), function (props) {
  return props.flat ? 'none' : '0 2px 5px 0 rgba(0, 0, 0, 0.16), 0 2px 10px 0 rgba(0, 0, 0, 0.12)';
}, function (_ref) {
  var bg = _ref.bg;
  return bg && css(_templateObject2$4(), colors["" + bg], function (_ref2) {
    var bg = _ref2.bg;
    return bg && css(_templateObject3$1());
  });
}, colorStyle, variant({
  prop: 'size',
  variants: {
    xl: {
      padding: '12px 18.75px',
      fontSize: '1rem'
    },
    large: {
      padding: '8.75px 15px',
      fontSize: '1rem'
    },
    medium: {
      padding: '6px 12px',
      fontSize: '0.85rem'
    },
    small: {
      padding: '3px 6px',
      fontSize: '0.625rem'
    }
  }
}), function (props) {
  return props.circle === true ? css(_templateObject4()) : css(_templateObject5());
}, function (props) {
  return props.outline === true && css(_templateObject6(), function (_ref3) {
    var bg = _ref3.bg;
    return bg && css(_templateObject7(), colors["" + bg], tinycolor("" + colors["" + bg]).darken(10));
  }, function (_ref4) {
    var bg = _ref4.bg;
    return bg && css(_templateObject8(), colors["" + bg], tinycolor("" + colors["" + bg]).darken(10));
  });
});
var Button$1 = function Button(props) {
  var _classNames;
  var action = props.action,
    active = props.active,
    block = props.block,
    children = props.children,
    circle = props.circle,
    className = props.className,
    color = props.color,
    disabled = props.disabled,
    download = props.download,
    flat = props.flat,
    innerRef = props.innerRef,
    outline = props.outline,
    role = props.role,
    size = props.size,
    social = props.social,
    tag = props.tag,
    target = props.target,
    type = props.type,
    attributes = _objectWithoutPropertiesLoose(props, ["action", "active", "block", "children", "circle", "className", "color", "disabled", "download", "flat", "gradient", "innerRef", "outline", "role", "rounded", "size", "social", "tag", "target", "type"]);
  var _ref = useRef();
  var buttonClasses = classNames('Ripple-parent', (_classNames = {
    active: active,
    'btn-block': block,
    'btn-action': action
  }, _classNames["btn-" + social] = social, _classNames.disabled = disabled, _classNames), className);
  if (attributes.href && tag === 'button') {
    tag = 'a';
  }
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$9, Object.assign({
    "data-test": 'button',
    type: tag === 'button' && !type ? 'button' : type,
    as: tag,
    target: target,
    role: tag === 'a' && !role ? 'button' : role,
    className: buttonClasses,
    colors: color,
    ref: innerRef,
    size: size,
    flat: flat
  }, attributes, {
    download: download,
    circle: circle,
    disabled: disabled,
    outline: outline,
    bg: color
  }), children));
};
Button$1.defaultProps = {
  color: 'primary',
  tag: 'button',
  size: 'medium',
  circle: false,
  innerRef: undefined
};
function _templateObject$b() {
  var data = _taggedTemplateLiteralLoose(["\n  ", "\n  \n  .btn {\n  \tborder-radius: 0px;\n  }\n\n"]);
  _templateObject$b = function _templateObject() {
    return data;
  };
  return data;
}
var Component$a = styled.div(_templateObject$b(), colorStyle);
var ButtonGroup = function ButtonGroup(_ref) {
  var className = _ref.className,
    size = _ref.size,
    vertical = _ref.vertical,
    children = _ref.children,
    attributes = _objectWithoutPropertiesLoose(_ref, ["className", "size", "vertical", "children"]);
  var classes = classNames(size ? "btn-group-" + size : false, vertical ? "btn-group-vertical" : "btn-group", className);
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$a, Object.assign({
    role: "group"
  }, attributes, {
    className: classes
  }), children));
};
ButtonGroup.defaultProps = {
  role: "group"
};
var ButtonToolbar = function ButtonToolbar(_ref) {
  var className = _ref.className,
    children = _ref.children,
    attributes = _objectWithoutPropertiesLoose(_ref, ["className", "children"]);
  var classes = classNames('btn-toolbar', className);
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement("div", Object.assign({}, attributes, {
    className: classes
  }), children));
};
ButtonToolbar.defaultProps = {
  role: "toolbar"
};
var Control = function Control(props) {
  var className = props.className,
    direction = props.direction,
    iconLeft = props.iconLeft,
    iconRight = props.iconRight,
    multiItem = props.multiItem,
    onClick = props.onClick,
    Tag = props.tag,
    testimonial = props.testimonial;
  var text;
  if (direction === "prev") {
    text = "Previous";
  } else if (direction === "next") {
    text = "Next";
  }
  var classes = classNames("carousel-control-" + direction, className);
  var caretClasses = classNames("carousel-control-" + direction + "-icon");
  if (testimonial) {
    var arrow = direction === "prev" ? "left" : "right";
    classes = classNames("carousel-control-" + direction, arrow, "carousel-control", className);
    caretClasses = classNames("icon-" + direction);
  }
  if (multiItem) {
    classes = classNames("btn-floating");
  }
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Tag, {
    "data-test": "carousel-control",
    className: classes,
    "data-slide": direction,
    onClick: onClick
  }, iconLeft ? React.createElement("i", {
    className: "fab fa-chevron-left"
  }) : iconRight ? React.createElement("i", {
    className: "fab fa-chevron-right"
  }) : React.createElement("div", null, React.createElement("span", {
    className: caretClasses,
    "aria-hidden": "true"
  }), React.createElement("span", {
    className: "sr-only"
  }, text))));
};
Control.defaultProps = {
  tag: "a",
  iconRight: false,
  iconLeft: false,
  className: ''
};
var CarouselIndicator = function CarouselIndicator(props) {
  var active = props.active,
    alt = props.alt,
    children = props.children,
    className = props.className,
    img = props.img,
    attributes = _objectWithoutPropertiesLoose(props, ["active", "alt", "children", "className", "img"]);
  var classes = classNames(active && "active", className);
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement("li", Object.assign({
    "data-test": "carousel-indicator"
  }, attributes, {
    className: classes
  }), img && React.createElement("img", {
    src: img,
    alt: alt,
    className: "img-fluid"
  }), children));
};
CarouselIndicator.defaultProps = {
  alt: "Carousel thumbnail",
  className: "",
  img: "",
  onClick: function onClick(event) {},
  children: null
};
var CarouselIndicators = function CarouselIndicators(props) {
  var children = props.children,
    className = props.className,
    attributes = _objectWithoutPropertiesLoose(props, ["children", "className"]);
  var classes = classNames("carousel-indicators", className);
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement("ol", Object.assign({
    "data-test": "carousel-indicators"
  }, attributes, {
    className: classes
  }), children));
};
CarouselIndicators.defaultProps = {
  className: ""
};
function _templateObject$c() {
  var data = _taggedTemplateLiteralLoose(["\n  .carousel-inner {\n    position: relative;\n    overflow: hidden;\n    width: 100%;\n    height: 100%;\n  }\n\n  &.carousel-fade .carousel-item {\n    position: absolute;\n    left: 0;\n    top: 0;\n    width: 100%;\n    height: 100%;\n    display: block !important;\n    opacity: 0;\n    z-index: 0;\n    transition: transform 0ms ease-in-out, opacity 0.8s ease-out;\n  }\n\n  &.carousel-fade .carousel-item.active {\n    position: relative;\n    z-index: 1;\n    opacity: 1;\n  }\n\n  &.carousel-multi-item .carousel-item {\n    display: inline-block !important;\n  }\n\n  &.carousel .carousel-slide-item {\n    transition: left 0.5s;\n  }\n\n  .carousel-control-prev,\n  .carousel-control-next,\n  .carousel-item-prev,\n  .carousel-item-next {\n    z-index: 2;\n  }\n"]);
  _templateObject$c = function _templateObject() {
    return data;
  };
  return data;
}
var Tag = styled.div(_templateObject$c());
var CarouselContext = createContext({
  activeItem: null,
  length: null,
  slide: null
});
var Carousel = function Carousel(props) {
  var length = props.length,
    activeItem = props.activeItem,
    children = props.children,
    className = props.className,
    multiItem = props.multiItem,
    onHoverStop = props.onHoverStop,
    showControls = props.showControls,
    showIndicators = props.showIndicators,
    tag = props.tag,
    testimonial = props.testimonial,
    thumbnails = props.thumbnails,
    attributes = _objectWithoutPropertiesLoose(props, ["length", "activeItem", "children", "className", "interval", "mobileGesture", "multiItem", "onHoverStop", "showControls", "showIndicators", "slide", "tag", "testimonial", "thumbnails"]);
  var _useState = useState({
      activeItem: activeItem,
      initialLength: length,
      srcArray: [],
      swipeAvailable: true,
      initialX: null,
      initialY: null
    }),
    state = _useState[0],
    setState = _useState[1];
  var _useState2 = useState(),
    cycleInterval = _useState2[0],
    setCycleInterval = _useState2[1];
  var carouselRef = useRef(null);
  var isMounted = useRef(false);
  useEffect(function () {
    if (!isMounted.current) {
      componentDidMountCallback();
      isMounted.current = true;
    } else {
      componentDidUpdateCallback();
    }
    return function () {
      var interval = props.interval;
      if (typeof interval === 'boolean' && interval === false) {
        return;
      }
      clearCycleIntervalHandler();
    };
  }, [state.initialLength]);
  function componentDidMountCallback() {
    var interval = props.interval,
      thumbnails = props.thumbnails,
      length = props.length;
    if (typeof interval === 'boolean' && interval === false) {
      return;
    }
    typeof interval === 'number' && setCycleInterval(setInterval(next, interval));
    if (thumbnails) {
      var CarouselItemsArray = carouselRef.current.querySelectorAll('.carousel-item img');
      var _srcArray = Array.prototype.map.call(CarouselItemsArray, function (item) {
        return item.src;
      });
      setState(function (prevState) {
        return _extends({}, prevState, {
          srcArray: _srcArray
        });
      });
    }
    setState(function (prevState) {
      return _extends({}, prevState, {
        initialLength: length
      });
    });
  }
  function componentDidUpdateCallback() {
    var length = props.length;
    var initialLength = length;
    if (state.initialLength !== length) {
      setState(_extends({}, state, {
        initialLength: initialLength
      }));
    }
  }
  var clearCycleIntervalHandler = function clearCycleIntervalHandler() {
    return clearInterval(cycleInterval);
  };
  var swipeAvailableHandler = function swipeAvailableHandler() {
    return setState(_extends({}, state, {
      swipeAvailable: true
    }));
  };
  var restartInterval = function restartInterval() {
    var interval = props.interval;
    if (typeof interval === 'number') {
      clearCycleIntervalHandler();
      setCycleInterval(setInterval(next, interval));
    }
  };
  var next = function next() {
    var activeItem = state.activeItem,
      initialLength = state.initialLength;
    var nextIndex = activeItem + 1;
    var nextItem = nextIndex > initialLength ? 1 : nextIndex;
    goToIndex(nextItem);
  };
  var prev = function prev() {
    var activeItem = state.activeItem,
      initialLength = state.initialLength;
    var prevIndex = activeItem - 1;
    var prevItem = prevIndex < 1 ? initialLength : prevIndex;
    goToIndex(prevItem);
  };
  var goToIndex = function goToIndex(item) {
    setState(_extends({}, state, {
      activeItem: item
    }));
    restartInterval();
  };
  var startTouch = function startTouch(e) {
    var mobileGesture = props.mobileGesture;
    if (mobileGesture !== false) {
      setState(_extends({}, state, {
        initialX: e.touches[0].clientX,
        initialY: e.touches[0].clientY
      }));
    }
  };
  var moveTouch = function moveTouch(e) {
    setState(_extends({}, state, {
      swipeAvailable: false
    }));
    var initialX = state.initialX,
      initialY = state.initialY;
    if (initialX === null || initialY === null) {
      return;
    }
    var currentX = e.touches[0].clientX;
    var currentY = e.touches[0].clientY;
    var diffX = initialX - currentX;
    var diffY = initialY - currentY;
    if (Math.abs(diffX) > Math.abs(diffY)) {
      if (diffX > 0) {
        next();
      } else {
        prev();
      }
    }
    setState(_extends({}, state, {
      initialX: null,
      initialY: null
    }));
  };
  var initialLength = state.initialLength,
    srcArray = state.srcArray,
    swipeAvailable = state.swipeAvailable;
  var ariaLabel = 'carousel';
  var classes = classNames('carousel', multiItem ? 'carousel-multi-item' : 'carousel-fade', thumbnails ? 'carousel-thumbnails' : '', testimonial ? 'testimonial-carousel' : '', className);
  var CarouselIndicatorsArray = [];
  var _loop = function _loop(i) {
    var activeItem = state.activeItem;
    CarouselIndicatorsArray.push(React.createElement(CarouselIndicator, {
      img: thumbnails ? srcArray[i - 1] : '',
      key: i,
      active: activeItem === i,
      onClick: function onClick(_e) {
        return goToIndex(i);
      }
    }));
  };
  for (var i = 1; i <= initialLength; i++) {
    _loop(i);
  }
  var isMultiItem = !!multiItem;
  var isTestimonial = !!testimonial;
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(CarouselContext.Provider, {
    value: {
      activeItem: state.activeItem,
      length: state.initialLength,
      slide: props.slide
    }
  }, React.createElement(Tag, Object.assign({
    "data-test": 'carousel',
    ref: carouselRef
  }, attributes, {
    className: classes,
    "aria-label": ariaLabel,
    onTouchStart: startTouch,
    onTouchMove: swipeAvailable ? moveTouch : undefined,
    onTouchEnd: swipeAvailableHandler,
    onMouseEnter: onHoverStop ? clearCycleIntervalHandler : undefined,
    onMouseLeave: onHoverStop ? restartInterval : undefined,
    as: tag
  }), showControls && multiItem && React.createElement("div", {
    className: 'controls-top'
  }, React.createElement(Control, {
    testimonial: isTestimonial,
    multiItem: isMultiItem,
    iconLeft: true,
    className: 'btn-floating',
    direction: 'prev',
    onClick: prev
  }), React.createElement(Control, {
    testimonial: isTestimonial,
    multiItem: isMultiItem,
    iconRight: true,
    className: 'btn-floating',
    direction: 'next',
    onClick: next
  })), children, showControls && !multiItem && React.createElement(Fragment, null, React.createElement(Control, {
    testimonial: isTestimonial,
    multiItem: isMultiItem,
    direction: 'prev',
    onClick: prev
  }), React.createElement(Control, {
    testimonial: isTestimonial,
    multiItem: isMultiItem,
    direction: 'next',
    onClick: next
  })), showIndicators && React.createElement(CarouselIndicators, null, CarouselIndicatorsArray))));
};
Carousel.defaultProps = {
  interval: 6000,
  mobileGesture: true,
  onHoverStop: true,
  showControls: true,
  showIndicators: true,
  tag: 'div'
};
function _templateObject3$2() {
  var data = _taggedTemplateLiteralLoose(["\n          border: 2px solid #e2e2e2;\n        "]);
  _templateObject3$2 = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$5() {
  var data = _taggedTemplateLiteralLoose(["\n          border-top: 8px solid #e2e2e2;\n          border-bottom: 8px solid #e2e2e2;\n        "]);
  _templateObject2$5 = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$d() {
  var data = _taggedTemplateLiteralLoose(["\n  position: relative;\n  background-color: #ffffff;\n  ", "\n"]);
  _templateObject$d = function _templateObject() {
    return data;
  };
  return data;
}
var Component$b = styled.div(_templateObject$d(), function (props) {
  return props.border ? css(_templateObject2$5()) : css(_templateObject3$2());
});
var Card = function Card(props) {
  var className = props.className,
    tag = props.tag,
    color = props.color,
    border = props.border,
    attributes = _objectWithoutPropertiesLoose(props, ["className", "tag", "color", "border"]);
  var classes = classNames(color, className);
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$b, Object.assign({
    "data-test": "card",
    as: tag
  }, attributes, {
    className: classes,
    border: border
  })));
};
Card.defaultProps = {
  tag: "div",
  border: false
};
function _templateObject$e() {
  var data = _taggedTemplateLiteralLoose(["\n  border-radius: 8px;\n"]);
  _templateObject$e = function _templateObject() {
    return data;
  };
  return data;
}
var Component$c = styled.div(_templateObject$e());
var CardBody = function CardBody(props) {
  var className = props.className,
    tag = props.tag,
    cascade = props.cascade,
    attributes = _objectWithoutPropertiesLoose(props, ["className", "tag", "cascade"]);
  var classes = classNames('card-body', cascade && 'card-body-cascade', className);
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$c, Object.assign({
    "data-test": 'card-body',
    as: tag
  }, attributes, {
    className: classes
  })));
};
CardBody.defaultProps = {
  tag: 'div'
};
function _templateObject$f() {
  var data = _taggedTemplateLiteralLoose(["\n"]);
  _templateObject$f = function _templateObject() {
    return data;
  };
  return data;
}
var Component$d = styled.img(_templateObject$f());
function _templateObject4$1() {
  var data = _taggedTemplateLiteralLoose(["\n          .mask {\n            opacity: 0;\n            transition: all 0.4s ease-in-out;\n            &:hover {\n              opacity: 1;\n            }\n          }\n        "]);
  _templateObject4$1 = function _templateObject4() {
    return data;
  };
  return data;
}
function _templateObject3$3() {
  var data = _taggedTemplateLiteralLoose([""]);
  _templateObject3$3 = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$6() {
  var data = _taggedTemplateLiteralLoose(["\n          img,\n          video {\n            transition: all 0.2s linear;\n          }\n          &:hover img,\n          &:hover video {\n            transform: scale(1.1);\n          }\n        "]);
  _templateObject2$6 = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$g() {
  var data = _taggedTemplateLiteralLoose(["\n  position: relative;\n  overflow: hidden;\n  cursor: default;\n  ", "\n  .mask {\n    position: absolute;\n    top: 0;\n    right: 0;\n    bottom: 0;\n    left: 0;\n    width: 100%;\n    height: 100%;\n    overflow: hidden;\n    background-attachment: fixed;\n  }\n  img,\n  video {\n    position: relative;\n    display: block;\n  }\n  .video.video-intro {\n    top: 50%;\n    left: 50%;\n    z-index: -100;\n    width: auto;\n    min-width: 100%;\n    height: auto;\n    min-height: 100%;\n    transition: 1s opacity;\n    transform: translateX(-50%) translateY(-50%);\n  }\n  border-radius: ", ";\n  ", "\n"]);
  _templateObject$g = function _templateObject() {
    return data;
  };
  return data;
}
var Component$e = styled.div(_templateObject$g(), function (props) {
  return props.zoom ? css(_templateObject2$6()) : css(_templateObject3$3());
}, function (props) {
  return props.rounded ? "0.25rem !important" : "0";
}, function (props) {
  return props.hover ? css(_templateObject4$1()) : null;
});
var View = function View(props) {
  var children = props.children,
    className = props.className,
    fixed = props.fixed,
    hover = props.hover,
    rounded = props.rounded,
    src = props.src,
    tag = props.tag,
    waves = props.waves,
    zoom = props.zoom,
    attributes = _objectWithoutPropertiesLoose(props, ["children", "className", "fixed", "hover", "rounded", "src", "tag", "waves", "zoom", "cascade"]);
  var viewClasses = classNames("view", className, rounded && "rounded", waves ? "Ripple-parent" : false);
  var viewStyle = src ? {
    backgroundAttachment: fixed ? "fixed" : null,
    backgroundImage: "url(\"" + src + "\")",
    backgroundPosition: "center center",
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    height: "100vh"
  } : {};
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$e, Object.assign({
    as: tag
  }, attributes, {
    className: viewClasses,
    "data-test": "view",
    style: viewStyle,
    hover: hover,
    rounded: rounded,
    zoom: zoom
  }), children));
};
View.defaultProps = {
  className: "",
  hover: false,
  rounded: false,
  src: "",
  tag: "div"
};
var CardImage = function CardImage(props) {
  var cascade = props.cascade,
    className = props.className,
    hover = props.hover,
    src = props.src,
    tag = props.tag,
    top = props.top,
    zoom = props.zoom,
    attributes = _objectWithoutPropertiesLoose(props, ["cascade", "className", "hover", "overlay", "src", "tag", "top", "waves", "zoom"]);
  var classes = classNames(top && 'card-img-top', className);
  var innerContent = React.createElement(Component$d, Object.assign({
    "data-test": 'card-image',
    as: tag,
    src: src
  }, attributes, {
    className: classes
  }));
  if (src) {
    return React.createElement(ThemeProvider, {
      theme: theme
    }, React.createElement(View, {
      zoom: zoom,
      hover: hover,
      cascade: cascade
    }, React.createElement("div", {
      style: {
        touchAction: 'unset'
      }
    }, innerContent)));
  }
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement("div", null, innerContent));
};
CardImage.defaultProps = {
  tag: 'img',
  overlay: 'white-slight',
  waves: true,
  hover: false,
  cascade: false,
  zoom: false
};
function _templateObject$h() {
  var data = _taggedTemplateLiteralLoose(["\n"]);
  _templateObject$h = function _templateObject() {
    return data;
  };
  return data;
}
var Component$f = styled.p(_templateObject$h());
var CardText = function CardText(props) {
  var textNode = props.children,
    className = props.className,
    muted = props.muted,
    small = props.small,
    tag = props.tag,
    attributes = _objectWithoutPropertiesLoose(props, ["children", "className", "muted", "small", "tag"]);
  var classes = classNames('card-text', muted && 'text-muted', className);
  var children = small ? React.createElement("small", null, textNode) : textNode;
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$f, Object.assign({
    "data-test": 'card-text',
    as: tag
  }, attributes, {
    className: classes
  }), children));
};
CardText.defaultProps = {
  tag: 'p'
};
function _templateObject$i() {
  var data = _taggedTemplateLiteralLoose(["\n  margin: 10px 0;\n"]);
  _templateObject$i = function _templateObject() {
    return data;
  };
  return data;
}
var Component$g = styled.h4(_templateObject$i());
var CardTitle = function CardTitle(props) {
  var className = props.className,
    sub = props.sub,
    tag = props.tag,
    attributes = _objectWithoutPropertiesLoose(props, ["className", "sub", "tag"]);
  var classes = classNames(sub ? 'card-subtitle' : 'card-title', className);
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$g, Object.assign({
    "data-test": 'card-title',
    as: tag
  }, attributes, {
    className: classes
  })));
};
CardTitle.defaultProps = {
  tag: 'h4',
  sub: false
};
var CarouselInner = function CarouselInner(props) {
  var active = props.active,
    children = props.children,
    className = props.className,
    Tag = props.tag,
    attributes = _objectWithoutPropertiesLoose(props, ["active", "children", "childrenCount", "className", "tag"]);
  var classes = classNames("carousel-inner", active ? "active" : "", className);
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Tag, Object.assign({
    "data-test": "carousel-inner"
  }, attributes, {
    className: classes
  }), children));
};
CarouselInner.defaultProps = {
  tag: "div"
};
var CarouselItem = function CarouselItem(props) {
  var children = props.children,
    className = props.className,
    itemId = props.itemId,
    Tag = props.tag,
    attributes = _objectWithoutPropertiesLoose(props, ["active", "children", "className", "itemId", "tag"]);
  var _useState = useState({}),
    style = _useState[0],
    setStyle = _useState[1];
  var moveForward = function moveForward() {
    setStyle({
      position: "absolute",
      left: "100%"
    });
  };
  var moveBackwards = function moveBackwards() {
    setStyle({
      position: "absolute",
      left: "-100%"
    });
  };
  var makeVisible = function makeVisible() {
    setStyle({
      left: "0"
    });
  };
  var _useContext = useContext(CarouselContext),
    slide = _useContext.slide,
    activeItem = _useContext.activeItem;
  var _itemId = typeof itemId === "string" ? parseInt(itemId, 10) : itemId;
  var classes = classNames("carousel-item", {
    "active carousel-slide-item": slide,
    active: !slide && _itemId === activeItem
  }, className);
  useEffect(function () {
    var slideIndex = activeItem - _itemId;
    if (slide) {
      if (slideIndex < 0) {
        moveForward();
      } else if (slideIndex > 0) {
        moveBackwards();
      } else {
        makeVisible();
      }
    } else {
      makeVisible();
    }
  }, [activeItem]);
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Tag, Object.assign({
    "data-test": "carousel-item"
  }, attributes, {
    className: classes,
    style: style
  }), children));
};
CarouselItem.defaultProps = {
  tag: "div"
};
function _templateObject$j() {
  var data = _taggedTemplateLiteralLoose(["\n\twidth:18px;\n\theight:18px;\n\tcursor:pointer;\n\tdisplay-inline-block;\n\tborder:1px solid #888;\n\tposition:relative;\n\n\tdiv {\n\t\tposition:absolute;\n\t    top: 0px;\n\t    left: 10px;\n\t\ttransition:all 0.1s ease-in;\n\t}\n\n\t&.active {\n\t    border-color:#000;\n\t\tdiv {\n\t\t    top: 1px;\n\t\t    left: 5px;\n\t\t    width: 6px;\n\t\t\theight:12px;\n\t\t\tborder:solid #000;\n\t\t\tborder-width: 0px 2px 2px 0px;\n\t\t\ttransform:rotate(45deg);\n\t\t}\n\t}\n\n\t&.disabled {\n\t\tpointer-events:none;\n\t\tbackground-color:#eee;\n\t\tborder-color:#eee;\n\t}\n"]);
  _templateObject$j = function _templateObject() {
    return data;
  };
  return data;
}
var Component$h = styled.div(_templateObject$j());
var Checkbox = function Checkbox(props) {
  var tag = props.tag,
    className = props.className,
    disabled = props.disabled,
    attributes = _objectWithoutPropertiesLoose(props, ["tag", "className", "disabled"]);
  var _useState = useState(false),
    active = _useState[0],
    setActive = _useState[1];
  var classes = classNames({
    disabled: disabled,
    active: active
  }, className);
  var checkboxComponent = React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$h, Object.assign({
    "data-test": "input",
    className: classes,
    onClick: function onClick() {
      return setActive(!active);
    },
    as: tag
  }, attributes), React.createElement("div", null)));
  return checkboxComponent;
};
Checkbox.defaultProps = {
  tag: "div"
};
function _templateObject4$2() {
  var data = _taggedTemplateLiteralLoose(["\n          width: 75px;\n          height: 75px;\n        "]);
  _templateObject4$2 = function _templateObject4() {
    return data;
  };
  return data;
}
function _templateObject3$4() {
  var data = _taggedTemplateLiteralLoose(["\n          width: 50px;\n          height: 50px;\n        "]);
  _templateObject3$4 = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$7() {
  var data = _taggedTemplateLiteralLoose(["\n          width: 100px;\n          height: 100px;\n        "]);
  _templateObject2$7 = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$k() {
  var data = _taggedTemplateLiteralLoose(["\n  ", "\n\n  .CircularProgressbar {\n    width: inherit;\n    vertical-align: middle;\n  }\n\n  .CircularProgressbar .CircularProgressbar-path {\n    stroke: ", ";\n    stroke-linecap: round;\n    transition: stroke-dashoffset 0.5s ease 0s;\n  }\n\n  .CircularProgressbar .CircularProgressbar-trail {\n    stroke: #d6d6d6;\n    stroke-linecap: round;\n  }\n\n  .CircularProgressbar .CircularProgressbar-text {\n    fill: ", ";\n    font-size: 20px;\n    dominant-baseline: middle;\n    text-anchor: middle;\n  }\n\n  .CircularProgressbar .CircularProgressbar-background {\n    fill: #d6d6d6;\n  }\n\n  .CircularProgressbar.CircularProgressbar-inverted\n    .CircularProgressbar-background {\n    fill: #3e98c7;\n  }\n\n  .CircularProgressbar.CircularProgressbar-inverted .CircularProgressbar-text {\n    fill: #fff;\n  }\n\n  .CircularProgressbar.CircularProgressbar-inverted .CircularProgressbar-path {\n    stroke: #fff;\n  }\n\n  .CircularProgressbar.CircularProgressbar-inverted .CircularProgressbar-trail {\n    stroke: transparent;\n  }\n"]);
  _templateObject$k = function _templateObject() {
    return data;
  };
  return data;
}
var Component$i = styled.span(_templateObject$k(), function (props) {
  return props.size === "lg" ? css(_templateObject2$7()) : props.size === "sm" ? css(_templateObject3$4()) : css(_templateObject4$2());
}, function (props) {
  return props.color === "primary" ? "#3e98c7" : props.color === "secondary" ? "#7356BF" : props.color === "danger" ? "#E11900" : props.color === "warning" ? "#66512C" : props.color === "info" ? "#17A2B8" : props.color === "success" ? "#05944F" : "#000000";
}, function (props) {
  return props.color === "primary" ? "#3e98c7" : props.color === "secondary" ? "#7356BF" : props.color === "danger" ? "#E11900" : props.color === "warning" ? "#66512C" : props.color === "info" ? "#17A2B8" : props.color === "success" ? "#05944F" : "#000000";
});
var CircularProgress = function CircularProgress(props) {
  var value = props.value,
    text = props.text,
    max = props.max,
    min = props.min,
    color = props.color,
    size = props.size,
    attributes = _objectWithoutPropertiesLoose(props, ["value", "text", "max", "min", "color", "tag", "size"]);
  var circularProgressComp = React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$i, {
    size: size,
    color: color
  }, React.createElement(CircularProgressbar, Object.assign({
    value: value,
    maxValue: max,
    minValue: min,
    text: text
  }, attributes))));
  return circularProgressComp;
};
CircularProgress.defaultProps = {
  tag: "span",
  className: "",
  max: 100,
  min: 0,
  value: 0,
  size: "md",
  color: "dark"
};
var CloseIcon = function CloseIcon(props) {
  var onClick = props.onClick,
    className = props.className,
    ariaLabel = props.ariaLabel,
    attributes = _objectWithoutPropertiesLoose(props, ["onClick", "className", "ariaLabel"]);
  var onClickHandler = function onClickHandler(e) {
    onClick && onClick(e);
  };
  var closeIconClasses = classNames("close", className);
  var closeIconComponent = React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement("button", Object.assign({
    "data-test": "close-button",
    type: "button"
  }, attributes, {
    className: closeIconClasses,
    onClick: onClickHandler,
    "aria-label": ariaLabel
  }), React.createElement("span", {
    "aria-hidden": "true"
  }, "\xD7")));
  return closeIconComponent;
};
CloseIcon.defaultProps = {
  ariaLabel: "Close"
};
function _templateObject$l() {
  var data = _taggedTemplateLiteralLoose([""]);
  _templateObject$l = function _templateObject() {
    return data;
  };
  return data;
}
var Component$j = styled.div(_templateObject$l());
var Col = function Col(props) {
  var xs = props.xs,
    sm = props.sm,
    md = props.md,
    lg = props.lg,
    xl = props.xl,
    top = props.top,
    bottom = props.bottom,
    middle = props.middle,
    size = props.size,
    children = props.children,
    className = props.className,
    attributes = _objectWithoutPropertiesLoose(props, ["xs", "sm", "md", "lg", "xl", "top", "bottom", "middle", "size", "children", "className", "tag"]);
  var colClasses = classNames(size && "col-" + size, xs && "col-xs-" + xs, sm && "col-sm-" + sm, md && "col-md-" + md, lg && "col-lg-" + lg, xl && "col-xl-" + xl, !size && !xs && !sm && !md && !lg && !xl ? "col" : "", top && "align-self-start", middle && "align-self-center", bottom && "align-self-end", className);
  var colComponent = React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$j, Object.assign({
    "data-test": "col"
  }, attributes, {
    className: colClasses
  }), children));
  return colComponent;
};
Col.defaultProps = {
  tag: "div",
  xs: null,
  sm: null,
  md: null,
  lg: null,
  xl: null
};
var SHOW = 'SHOW';
var SHOWN = 'SHOWN';
var HIDE = 'HIDE';
var HIDDEN = 'HIDDEN';
var DEFAULT_DELAYS = {
  show: 350,
  hide: 350
};
var Collapse = function Collapse(props) {
  var transitionTag;
  var _useState = useState({
      id: props.id,
      collapse: HIDDEN,
      height: 0
    }),
    state = _useState[0],
    setState = _useState[1];
  var element = useRef(null);
  var compRef = useRef(false);
  useEffect(function () {
    if (compRef.current === false) {
      handleComponentDidMount();
      compRef.current = true;
    } else {
      handleComponentDidUpdate();
    }
    return function () {
      clearTimeout(transitionTag);
    };
  }, [props.isOpen, state.collapse]);
  useEffect(function () {
    if (state.collapse === SHOW) {
      setState(function (prevState) {
        return _extends({}, prevState, {
          height: getHeight()
        });
      });
      setTransitionTag(SHOWN, props.onOpened, 'show');
    } else if (state.collapse === HIDE) {
      setState(function (prevState) {
        return _extends({}, prevState, {
          collapse: HIDE,
          height: getHeight()
        });
      });
    }
  }, [state.collapse]);
  function setTransitionTag(collapse, callback, delayType) {
    transitionTag = setTimeout(function () {
      setState(function (prevState) {
        return _extends({}, prevState, {
          collapse: collapse,
          height: 0
        });
      });
      callback();
    }, getDelay(delayType));
  }
  function getDelay(key) {
    var delay = props.delay;
    if (typeof delay === 'object' && delay !== null) {
      return isNaN(delay[key]) ? DEFAULT_DELAYS[key] : delay[key];
    }
    return delay;
  }
  function getHeight() {
    return element.current.scrollHeight;
  }
  function handleComponentDidMount() {
    var isOpen = props.isOpen;
    var collapse = state.collapse,
      id = state.id;
    if ((isOpen === id || isOpen === true) && collapse === HIDDEN) {
      openCollapse();
    }
  }
  function handleComponentDidUpdate() {
    var isOpen = props.isOpen;
    var collapse = state.collapse;
    var willOpen = typeof isOpen !== 'boolean' ? isOpen === state.id : isOpen;
    if (willOpen && collapse === HIDDEN) {
      openCollapse();
    } else if (!willOpen && state.collapse === SHOWN) {
      closeCollapse();
    }
  }
  function openCollapse() {
    setState(function (prevState) {
      return _extends({}, prevState, {
        collapse: SHOW
      });
    });
  }
  function closeCollapse() {
    setState(function (prevState) {
      return _extends({}, prevState, {
        height: getHeight()
      });
    });
  }
  var navbar = props.navbar,
    children = props.children,
    className = props.className,
    attributes = _objectWithoutPropertiesLoose(props, ["navbar", "children", "className", "isOpen", "delay", "onOpened", "onClosed"]);
  var collapse = state.collapse,
    height = state.height;
  var collapseClass;
  switch (collapse) {
    case SHOW:
      collapseClass = 'collapsing';
      break;
    case SHOWN:
      collapseClass = 'collapse show';
      break;
    case HIDE:
      collapseClass = 'collapsing';
      break;
    case HIDDEN:
      collapseClass = 'collapse';
      break;
    default:
      collapseClass = 'collapse';
  }
  var collapseClasses = classNames(collapseClass, navbar ? 'navbar-collapse' : false, className);
  var style = height === null ? null : {
    height: height
  };
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement("div", Object.assign({
    "data-test": 'collapse'
  }, attributes, {
    style: _extends({}, attributes.style, style),
    className: collapseClasses,
    ref: element
  }), children));
};
Collapse.defaultProps = {
  isOpen: '',
  delay: DEFAULT_DELAYS,
  onOpened: function onOpened() {},
  onClosed: function onClosed() {}
};
function _templateObject$m() {
  var data = _taggedTemplateLiteralLoose([""]);
  _templateObject$m = function _templateObject() {
    return data;
  };
  return data;
}
var Component$k = styled.div(_templateObject$m());
var Container$1 = function Container(props) {
  var className = props.className,
    fluid = props.fluid,
    size = props.size,
    tag = props.tag,
    children = props.children,
    attributes = _objectWithoutPropertiesLoose(props, ["className", "fluid", "size", "tag", "children"]);
  var containerClasses = classNames(fluid ? "container-fluid" : size ? "container-" + size : "container", className);
  var containerComponent = React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$k, Object.assign({
    as: tag,
    "data-test": "container"
  }, attributes, {
    className: containerClasses
  }), children));
  return containerComponent;
};
Container$1.defaultProps = {
  tag: "div",
  fluid: false
};
var DataTableHead = function DataTableHead(props) {
  var color = props.color,
    columns = props.columns,
    handleSort = props.handleSort,
    scrollX = props.scrollX,
    scrollY = props.scrollY,
    sortable = props.sortable,
    sorted = props.sorted,
    textWhite = props.textWhite;
  var theadClasses = classNames(color && (color !== 'dark' && color !== 'light' ? color : "thead-" + color), textWhite && 'text-white');
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(React.Fragment, null, (scrollY || scrollX) && React.createElement("colgroup", null, columns.map(function (col) {
    return React.createElement("col", {
      key: col.field,
      style: {
        width: col.width + "px" || 'auto',
        minWidth: col.width + "px" || 'auto'
      }
    });
  })), React.createElement("thead", {
    "data-test": 'datatable-head',
    className: theadClasses || undefined
  }, React.createElement("tr", null, columns.map(function (col) {
    return React.createElement("th", Object.assign({
      onClick: function onClick() {
        return sortable && handleSort(col.field, col.sort);
      },
      key: col.field,
      className: classNames(col.hasOwnProperty('minimal') ? "th-" + col.minimal : null, sortable && col.sort !== 'disabled' && (sorted && col.sort ? "sorting_" + (col.sort === 'asc' ? 'desc' : 'asc') : 'sorting'))
    }, col.attributes), col.label);
  })))));
};
DataTableHead.defaultProps = {
  scrollX: false,
  scrollY: false,
  sortable: true,
  textWhite: false
};
function _templateObject3$5() {
  var data = _taggedTemplateLiteralLoose([""]);
  _templateObject3$5 = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$8() {
  var data = _taggedTemplateLiteralLoose(["\n          display: block;\n          max-height: 200px;\n          overflow-y: auto;\n          -ms-overflow-style: -ms-autohiding-scrollbar;\n        "]);
  _templateObject2$8 = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$n() {
  var data = _taggedTemplateLiteralLoose(["\n  table-layout: ", ";\n  ", ";\n"]);
  _templateObject$n = function _templateObject() {
    return data;
  };
  return data;
}
var Component$l = styled.div(_templateObject$n(), function (props) {
  return props.autoWidth ? "auto" : null;
}, function (props) {
  return props.scrollY ? css(_templateObject2$8()) : null;
});
var Table = styled.table(_templateObject3$5());
var Table$1 = function Table$1(props) {
  var autoWidth = props.autoWidth,
    bordered = props.bordered,
    borderless = props.borderless,
    btn = props.btn,
    children = props.children,
    className = props.className,
    dark = props.dark,
    fixed = props.fixed,
    hover = props.hover,
    maxHeight = props.maxHeight,
    responsive = props.responsive,
    responsiveLg = props.responsiveLg,
    responsiveMd = props.responsiveMd,
    responsiveSm = props.responsiveSm,
    responsiveXl = props.responsiveXl,
    scrollY = props.scrollY,
    small = props.small,
    striped = props.striped,
    wrapperClassName = props.wrapperClassName,
    attributes = _objectWithoutPropertiesLoose(props, ["autoWidth", "bordered", "borderless", "btn", "children", "className", "dark", "fixed", "hover", "maxHeight", "responsive", "responsiveLg", "responsiveMd", "responsiveSm", "responsiveXl", "scrollY", "small", "striped", "theadColor", "wrapperClassName"]);
  var tableClasses = classNames("table", autoWidth && "w-auto", bordered && "table-bordered", borderless && "table-borderless", btn && "btn-table", fixed && "table-fixed", hover && "table-hover", striped && "table-striped", small && "table-sm", className);
  var wrapperClasses = classNames({
    "table-dark": dark,
    "table-responsive": responsive,
    "table-responsive-sm": responsiveSm,
    "table-responsive-md": responsiveMd,
    "table-responsive-lg": responsiveLg,
    "table-responsive-xl": responsiveXl,
    "table-wrapper-scroll-y": scrollY
  }, wrapperClassName);
  var wrapperStyles = {
    maxHeight: maxHeight
  };
  var tableComponent = React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$l, {
    "data-test": "table",
    className: wrapperClasses,
    style: wrapperStyles,
    autoWidth: autoWidth,
    scrollY: scrollY
  }, React.createElement(Table, Object.assign({}, attributes, {
    className: tableClasses
  }), children)));
  return tableComponent;
};
var TableBody = function TableBody(props) {
  var children = props.children,
    color = props.color,
    columns = props.columns,
    rows = props.rows,
    textWhite = props.textWhite,
    attributes = _objectWithoutPropertiesLoose(props, ["children", "color", "columns", "rows", "textWhite"]);
  var classes = classNames(color, {
    "text-white": textWhite
  });
  var renderTD = function renderTD(field, key, array, row) {
    if (field === "clickEvent") {
      return null;
    }
    if (field !== "colspan") {
      if (row.message) {
        return key === 0 && React.createElement("td", {
          key: key,
          colSpan: row.colspan
        }, row.message);
      } else {
        return array[key + 1] !== "colspan" && row[field] !== null && React.createElement("td", {
          key: key
        }, row[field]) || React.createElement("td", {
          key: key
        });
      }
    }
    return React.createElement("td", {
      key: key,
      colSpan: row.colspan
    }, row[array[key - 1]]);
  };
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement("tbody", Object.assign({
    "data-test": "table-body"
  }, attributes, {
    className: classes || undefined
  }), rows && rows.map(function (row, index) {
    return React.createElement("tr", {
      onClick: row.hasOwnProperty("clickEvent") ? row.clickEvent : undefined,
      key: index
    }, columns ? columns.map(function (_ref, key, array) {
      var field = _ref.field;
      return renderTD(field, key, array, row);
    }) : Object.keys(row).map(function (field, key, array) {
      return renderTD(field, key, array, row);
    }));
  }), children));
};
TableBody.defaultProps = {
  textWhite: false
};
var TableFooter = function TableFooter(props) {
  var _classNames;
  var children = props.children,
    color = props.color,
    columns = props.columns,
    textWhite = props.textWhite,
    attributes = _objectWithoutPropertiesLoose(props, ["children", "color", "columns", "textWhite"]);
  var isTheadColor = color === "dark" || color === "light";
  var classes = classNames((_classNames = {
    "text-white": textWhite
  }, _classNames["thead-" + color] = color && isTheadColor, _classNames["" + color] = color && !isTheadColor, _classNames));
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement("thead", Object.assign({
    "data-test": "table-foot"
  }, attributes, {
    className: classes || undefined
  }), columns && React.createElement("tr", null, columns.map(function (col) {
    return React.createElement("th", {
      key: col.field,
      className: col.hasOwnProperty("minimal") ? "th-" + col.minimal : undefined
    }, col.label);
  })), children));
};
TableFooter.defaultProps = {
  textWhite: false
};
var DataTableTable = function DataTableTable(props) {
  var autoWidth = props.autoWidth,
    bordered = props.bordered,
    borderless = props.borderless,
    btn = props.btn,
    children = props.children,
    columns = props.columns,
    dark = props.dark,
    fixed = props.fixed,
    handleSort = props.handleSort,
    hover = props.hover,
    noBottomColumns = props.noBottomColumns,
    responsive = props.responsive,
    responsiveLg = props.responsiveLg,
    responsiveMd = props.responsiveMd,
    responsiveSm = props.responsiveSm,
    responsiveXl = props.responsiveXl,
    rows = props.rows,
    small = props.small,
    sortable = props.sortable,
    sorted = props.sorted,
    striped = props.striped,
    tbodyColor = props.tbodyColor,
    tbodyTextWhite = props.tbodyTextWhite,
    theadColor = props.theadColor,
    theadTextWhite = props.theadTextWhite,
    attributes = _objectWithoutPropertiesLoose(props, ["autoWidth", "bordered", "borderless", "btn", "children", "columns", "dark", "fixed", "handleSort", "hover", "noBottomColumns", "noRecordsFoundLabel", "responsive", "responsiveLg", "responsiveMd", "responsiveSm", "responsiveXl", "rows", "small", "sortable", "sorted", "striped", "tbodyColor", "tbodyTextWhite", "theadColor", "theadTextWhite"]);
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement("div", {
    "data-test": "datatable-table",
    className: "col-sm-12"
  }, React.createElement(Table$1, Object.assign({
    maxHeight: "",
    scrollY: false,
    theadColor: "",
    wrapperClassName: "",
    autoWidth: autoWidth,
    bordered: bordered,
    borderless: borderless,
    btn: btn,
    dark: dark,
    fixed: fixed,
    hover: hover,
    responsive: responsive,
    responsiveSm: responsiveSm,
    responsiveMd: responsiveMd,
    responsiveLg: responsiveLg,
    responsiveXl: responsiveXl,
    small: small,
    striped: striped,
    className: "dataTable"
  }, attributes), React.createElement(DataTableHead, {
    color: theadColor,
    textWhite: theadTextWhite,
    columns: columns,
    handleSort: handleSort,
    sortable: sortable,
    sorted: sorted
  }), React.createElement(TableBody, {
    color: tbodyColor,
    textWhite: tbodyTextWhite,
    rows: rows,
    columns: columns,
    children: undefined
  }), !noBottomColumns && React.createElement(TableFooter, {
    color: theadColor,
    textWhite: theadTextWhite,
    columns: columns,
    children: undefined
  }), children)));
};
DataTableTable.defaultProps = {
  children: ""
};
var DataTableTableScroll = function DataTableTableScroll(props) {
  var autoWidth = props.autoWidth,
    bordered = props.bordered,
    borderless = props.borderless,
    btn = props.btn,
    children = props.children,
    columns = props.columns,
    dark = props.dark,
    fixed = props.fixed,
    handleSort = props.handleSort,
    handleTableBodyScroll = props.handleTableBodyScroll,
    hover = props.hover,
    maxHeight = props.maxHeight,
    responsive = props.responsive,
    responsiveLg = props.responsiveLg,
    responsiveMd = props.responsiveMd,
    responsiveSm = props.responsiveSm,
    responsiveXl = props.responsiveXl,
    rows = props.rows,
    scrollX = props.scrollX,
    scrollY = props.scrollY,
    small = props.small,
    sortable = props.sortable,
    sorted = props.sorted,
    striped = props.striped,
    tbodyColor = props.tbodyColor,
    tbodyTextWhite = props.tbodyTextWhite,
    theadColor = props.theadColor,
    theadTextWhite = props.theadTextWhite,
    translateScrollHead = props.translateScrollHead,
    attributes = _objectWithoutPropertiesLoose(props, ["autoWidth", "bordered", "borderless", "btn", "children", "columns", "dark", "fixed", "handleSort", "handleTableBodyScroll", "hover", "maxHeight", "responsive", "responsiveLg", "responsiveMd", "responsiveSm", "responsiveXl", "rows", "scrollX", "scrollY", "small", "sortable", "sorted", "striped", "tbodyColor", "tbodyTextWhite", "theadColor", "theadTextWhite", "translateScrollHead"]);
  var minWidth = scrollX ? columns.map(function (col) {
    return col.width;
  }).reduce(function (prev, curr) {
    return prev + curr;
  }, 0) + "px" : 'auto';
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement("div", {
    "data-test": 'datatable-table-scroll',
    className: 'col-sm-12'
  }, React.createElement("div", {
    className: 'dataTables_scroll'
  }, React.createElement("div", {
    className: 'dataTables_scrollHead',
    style: {
      overflow: 'hidden'
    }
  }, React.createElement("div", {
    className: 'dataTables_scrollHeadInner',
    style: {
      position: 'relative',
      transform: "translateX(-" + translateScrollHead + "px)",
      boxSizing: 'content-box',
      minWidth: minWidth
    }
  }, React.createElement(Table$1, Object.assign({
    theadColor: '',
    autoWidth: false,
    maxHeight: '',
    wrapperClassName: '',
    scrollY: false,
    bordered: bordered,
    borderless: borderless,
    btn: btn,
    dark: dark,
    fixed: fixed,
    hover: hover,
    responsive: responsive,
    responsiveSm: responsiveSm,
    responsiveMd: responsiveMd,
    responsiveLg: responsiveLg,
    responsiveXl: responsiveXl,
    small: small,
    striped: striped,
    className: 'dataTable'
  }, attributes), React.createElement(DataTableHead, {
    color: theadColor,
    textWhite: theadTextWhite,
    columns: columns,
    handleSort: handleSort,
    scrollX: scrollX,
    scrollY: scrollY,
    sortable: sortable,
    sorted: sorted
  })))), React.createElement("div", {
    className: 'dataTable_scrollBody',
    style: {
      overflow: 'auto'
    },
    onScroll: handleTableBodyScroll
  }, React.createElement(Table$1, Object.assign({
    theadColor: '',
    wrapperClassName: '',
    autoWidth: autoWidth,
    bordered: bordered,
    borderless: borderless,
    btn: btn,
    dark: dark,
    fixed: fixed,
    hover: hover,
    maxHeight: maxHeight,
    responsive: responsive,
    responsiveSm: responsiveSm,
    responsiveMd: responsiveMd,
    responsiveLg: responsiveLg,
    responsiveXl: responsiveXl,
    scrollY: scrollY,
    small: small,
    striped: striped,
    className: 'dataTable'
  }, attributes), React.createElement("colgroup", null, columns.map(function (col) {
    return React.createElement("col", {
      key: col.field,
      style: {
        width: col.width + "px" || 'auto',
        minWidth: col.width + "px" || 'auto'
      }
    });
  })), React.createElement(TableBody, {
    color: tbodyColor,
    textWhite: tbodyTextWhite,
    rows: rows,
    columns: columns,
    children: undefined
  }), children)))));
};
DataTableTableScroll.defaultProps = {
  children: ""
};
var DataTableSelect = function DataTableSelect(props) {
  var value = props.value,
    onChange = props.onChange,
    entries = props.entries,
    label = props.label;
  var getValue = function getValue(e) {
    var value = parseInt(e.target.value, 10);
    onChange(value);
  };
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement("div", {
    "data-test": 'datatable-select',
    className: 'dataTables_length bs-select',
    style: {
      display: "flex"
    }
  }, React.createElement("label", null, label), React.createElement("div", null, React.createElement("select", {
    value: value,
    onChange: getValue,
    className: 'custom-select custom-select-sm form-control form-control-sm',
    style: {
      marginLeft: '.5rem'
    }
  }, entries.map(function (entry) {
    return React.createElement("option", {
      key: entry,
      value: entry
    }, entry);
  })))));
};
var DataTableEntries = function DataTableEntries(props) {
  var handleEntriesChange = props.handleEntriesChange,
    displayEntries = props.displayEntries,
    entries = props.entries,
    entriesArr = props.entriesArr,
    paging = props.paging,
    label = props.label,
    barReverse = props.barReverse,
    proSelect = props.proSelect,
    attributes = _objectWithoutPropertiesLoose(props, ["handleEntriesChange", "displayEntries", "entries", "entriesArr", "paging", "onChange", "label", "barReverse", "proSelect"]);
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement("div", {
    "data-test": 'datatable-entries',
    className: 'col-sm-12 col-md-6',
    style: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: barReverse ? 'flex-end' : ''
    }
  }, paging && displayEntries && !proSelect && React.createElement(DataTableSelect, Object.assign({
    value: entries,
    onChange: handleEntriesChange,
    entries: entriesArr,
    label: label
  }, attributes))));
};
var DataTableInput = function DataTableInput(_ref) {
  var value = _ref.value,
    onChange = _ref.onChange,
    label = _ref.label,
    barReverse = _ref.barReverse,
    materialSearch = _ref.materialSearch;
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement("div", {
    "data-test": "datatable-input",
    className: classNames("mdb-datatable-filter", "flex-row", barReverse && "text-left")
  }, materialSearch ? React.createElement(Input, {
    value: value,
    onChange: onChange,
    type: "search",
    className: "form-control form-control-sm mt-0",
    placeholder: label || "Search"
  }) : React.createElement("input", {
    className: "form-control form-control-sm ml-0 my-1",
    type: "text",
    placeholder: label || "Search",
    "aria-label": "Search",
    value: value,
    onChange: onChange
  })));
};
var DataTableSearch = function DataTableSearch(props) {
  var handleSearchChange = props.handleSearchChange,
    search = props.search,
    searching = props.searching,
    label = props.label,
    barReverse = props.barReverse,
    wrapperSearchStyle = props.wrapperSearchStyle,
    wrapperSearchClasses = props.wrapperSearchClasses,
    materialSearch = props.materialSearch,
    attributes = _objectWithoutPropertiesLoose(props, ["handleSearchChange", "search", "searching", "onChange", "label", "barReverse", "wrapperSearchStyle", "wrapperSearchClasses", "materialSearch"]);
  var classes = classNames('col-sm-12 col-md-6', wrapperSearchClasses);
  return React.createElement(ThemeProvider, {
    theme: theme
  }, searching && React.createElement("div", {
    "data-test": 'datatable-search',
    style: _extends({
      display: 'flex',
      alignItems: 'center',
      justifyContent: barReverse ? 'flex-start' : 'flex-end'
    }, wrapperSearchStyle),
    className: classes
  }, React.createElement(DataTableInput, Object.assign({
    value: search,
    onChange: handleSearchChange,
    label: label,
    barReverse: barReverse,
    materialSearch: materialSearch
  }, attributes))));
};
DataTableSearch.defaultProps = {
  wrapperSearchStyle: {},
  wrapperSearchClasses: ""
};
var DataTableInfo = function DataTableInfo(props) {
  var activePage = props.activePage,
    entries = props.entries,
    filteredRows = props.filteredRows,
    info = props.info,
    label = props.label,
    noRecordsFoundLabel = props.noRecordsFoundLabel,
    pages = props.pages;
  var SHOWING_LABEL = label[0];
  var TO_LABEL = label[1];
  var OF_LABEL = label[2];
  var ENTRIES_LABEL = label[3];
  var NO_RECORDS = filteredRows.length > 0 && filteredRows[0].message === noRecordsFoundLabel;
  var RECORDS = activePage > 0 ? activePage * entries + 1 : activePage + 1;
  var RECORDS_ON_PAGE = pages.length - 1 > activePage ? pages[activePage].length * (activePage + 1) : filteredRows.length;
  var ENTRIES = filteredRows.length;
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement("div", {
    "data-test": 'datatable-info',
    className: 'col-sm-12 col-md-5'
  }, info && React.createElement("div", {
    className: 'dataTables_info',
    role: 'status',
    "aria-live": 'polite'
  }, !NO_RECORDS ? SHOWING_LABEL + " " + RECORDS + " " + TO_LABEL + " " + RECORDS_ON_PAGE + " " + OF_LABEL + " " + ENTRIES + " " + ENTRIES_LABEL : SHOWING_LABEL + " 0 " + ENTRIES_LABEL)));
};
DataTableInfo.defaultProps = {
  label: ['Showing', 'to', 'of', 'entries']
};
function _templateObject5$1() {
  var data = _taggedTemplateLiteralLoose(["\n            min-width: 50px;\n            min-height: 50px;\n          "]);
  _templateObject5$1 = function _templateObject5() {
    return data;
  };
  return data;
}
function _templateObject4$3() {
  var data = _taggedTemplateLiteralLoose(["\n            min-width: 30px;\n            min-height: 30px;\n          "]);
  _templateObject4$3 = function _templateObject4() {
    return data;
  };
  return data;
}
function _templateObject3$6() {
  var data = _taggedTemplateLiteralLoose(["\n            min-width: 50px;\n            min-height: 50px;\n          "]);
  _templateObject3$6 = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$9() {
  var data = _taggedTemplateLiteralLoose(["\n            min-width: 30px;\n            min-height: 30px;\n          "]);
  _templateObject2$9 = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$o() {
  var data = _taggedTemplateLiteralLoose(["\n  padding: 15px;\n  border: 0.75px solid #ccc;\n  border-radius: 0;\n  .page-link {\n    ", "\n    ", "\n    border-radius: ", ";\n    border: 0.5px solid #ccc;\n    display: flex;\n    align-items: center;\n    justify-content: center;\n  }\n  .page-item {\n    ", "\n    ", "\n    display: flex;\n    align-items: center;\n    justify-content: center;\n    text-align: center;\n    border: 0.5px solid #ccc;\n    border-radius: ", ";\n    font-size: ", ";\n  }\n"]);
  _templateObject$o = function _templateObject() {
    return data;
  };
  return data;
}
var Component$m = styled.ul(_templateObject$o(), colorStyle, function (props) {
  return props.sm ? css(_templateObject2$9()) : css(_templateObject3$6());
}, function (props) {
  return props.circle ? "50%" : "0";
}, colorStyle, function (props) {
  return props.sm ? css(_templateObject4$3()) : css(_templateObject5$1());
}, function (props) {
  return props.circle ? "50%" : "0";
}, function (props) {
  return props.size === "big" ? "1.25rem" : props.size === "small" ? "0.75rem" : "1rem";
});
var Pagination = function Pagination(props) {
  var children = props.children,
    className = props.className,
    color = props.color,
    tag = props.tag,
    shape = props.shape,
    size = props.size,
    sm = props.sm,
    circle = props.circle,
    attributes = _objectWithoutPropertiesLoose(props, ["children", "className", "color", "colors", "tag", "shape", "size", "sm", "circle"]);
  var paginationClasses = classNames("pagination", className);
  var paginationComponent = React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$m, Object.assign({
    as: tag,
    "data-test": "pagination",
    colors: color,
    circle: circle,
    shape: shape,
    size: size,
    sm: sm
  }, attributes, {
    className: paginationClasses
  }), children));
  return paginationComponent;
};
Pagination.defaultProps = {
  circle: false,
  className: "",
  color: "dark",
  colors: "dark",
  tag: "ul",
  shape: "",
  size: "lg",
  sm: false
};
function _templateObject$p() {
  var data = _taggedTemplateLiteralLoose(["\n  margin: 0px 7.5px;\n  cursor: pointer;\n  a {\n    text-decoration: none;\n    color: inherit;\n  }\n"]);
  _templateObject$p = function _templateObject() {
    return data;
  };
  return data;
}
var Component$n = styled.button(_templateObject$p());
var PageItem = function PageItem(props) {
  var active = props.active,
    className = props.className,
    children = props.children,
    disabled = props.disabled,
    tag = props.tag,
    attributes = _objectWithoutPropertiesLoose(props, ["active", "className", "children", "disabled", "tag"]);
  var pageItemClasses = classNames({
    disabled: disabled,
    active: active
  }, "page-item", className);
  var pageItemComponent = React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$n, Object.assign({
    "data-test": "page-item"
  }, attributes, {
    as: tag,
    className: pageItemClasses
  }), children));
  return pageItemComponent;
};
PageItem.defaultProps = {
  active: false,
  className: "",
  disabled: false,
  tag: "li"
};
function _templateObject$q() {
  var data = _taggedTemplateLiteralLoose(["\n  margin: 0px 7.5px;\n  cursor: pointer;\n\n\t&.disabled {\n\t\tpointer-events:none;\n\t\tbackground:#ccc;\n\t\tcolor:#555;\n\t}\n"]);
  _templateObject$q = function _templateObject() {
    return data;
  };
  return data;
}
var Component$o = styled.a(_templateObject$q());
var PageLink = function PageLink(props) {
  var children = props.children,
    className = props.className,
    tag = props.tag,
    disabled = props.disabled,
    attributes = _objectWithoutPropertiesLoose(props, ["children", "className", "tag", "disabled"]);
  var pageLinkClasses = classNames({
    disabled: disabled
  }, "page-link", className);
  var pageLinkComponent = React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$o, Object.assign({
    as: tag,
    "data-test": "page-link"
  }, attributes, {
    className: pageLinkClasses
  }), children));
  return pageLinkComponent;
};
PageLink.defaultProps = {
  tag: "a",
  disabled: false
};
var DataTablePagination = function DataTablePagination(props) {
  var activePage = props.activePage,
    changeActivePage = props.changeActivePage,
    pages = props.pages,
    label = props.label,
    attributes = _objectWithoutPropertiesLoose(props, ["activePage", "changeActivePage", "className", "pages", "label"]);
  var _useState = useState({
      pages: pages,
      pGroups: []
    }),
    state = _useState[0],
    setState = _useState[1];
  var mounted = useRef();
  useEffect(function () {
    if (!mounted.current) {
      groupPages();
      mounted.current = true;
    } else {
      var _pages = props.pages;
      Promise.resolve().then(function () {
        return setState(function (prev) {
          return _extends({}, prev, {
            pages: _pages
          });
        });
      }).then(function () {
        return groupPages();
      });
    }
  }, [pages]);
  var pagesIndexify = function pagesIndexify() {
    var pages = state.pages;
    var mutablePages = [].concat(pages);
    mutablePages.forEach(function (page, index) {
      return page.index = index;
    });
    return mutablePages;
  };
  var groupPages = function groupPages() {
    var pGroups = [];
    var pages = pagesIndexify();
    var pagesAmount = props.pagesAmount;
    while (pages.length > 0) {
      pGroups.push(pages.splice(0, pagesAmount));
    }
    setState(function (prev) {
      return _extends({}, prev, {
        pGroups: pGroups
      });
    });
  };
  var choosePagesGroup = function choosePagesGroup() {
    var activePage = props.activePage,
      pagesAmount = props.pagesAmount;
    var pGroups = state.pGroups;
    var pGroupNumber = Math.floor(activePage / pagesAmount);
    return pGroups.length ? pGroups[pGroupNumber] : [];
  };
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement("div", {
    "data-test": 'datatable-pagination',
    className: 'col-sm-12 col-md-7 d-flex justify-content-end'
  }, React.createElement("div", {
    className: 'dataTables_paginate'
  }, React.createElement(Pagination, {
    sm: true
  }, React.createElement(PageLink, {
    disabled: activePage <= 0,
    "aria-label": label[0],
    onClick: function onClick() {
      return changeActivePage(activePage - 1);
    },
    className: ""
  }, React.createElement("span", null, label[0])), choosePagesGroup().map(function (page) {
    return React.createElement(PageItem, {
      key: Object.keys(page[0])[0] + page.index,
      active: page.index === activePage,
      onClick: function onClick() {
        return changeActivePage(page.index);
      }
    }, page.index + 1, page.index === activePage && React.createElement("span", {
      className: 'sr-only'
    }, "(current)"));
  }), React.createElement(PageLink, {
    disabled: !pages.length || activePage === pages.length - 1,
    "aria-label": label[1],
    onClick: function onClick() {
      return changeActivePage(activePage + 1);
    },
    className: ""
  }, React.createElement("span", null, label[1]))))));
};
DataTablePagination.defaultProps = {
  children: "",
  className: ""
};
var DataTable = function DataTable(props) {
  var autoWidth = props.autoWidth,
    barReverse = props.barReverse,
    bordered = props.bordered,
    borderless = props.borderless,
    btn = props.btn,
    className = props.className,
    dark = props.dark,
    data = props.data,
    displayEntries = props.displayEntries,
    entriesLabel = props.entriesLabel,
    entriesOptions = props.entriesOptions,
    fixed = props.fixed,
    hover = props.hover,
    info = props.info,
    infoLabel = props.infoLabel,
    maxHeight = props.maxHeight,
    noBottomColumns = props.noBottomColumns,
    noRecordsFoundLabel = props.noRecordsFoundLabel,
    pagesAmount = props.pagesAmount,
    paginationLabel = props.paginationLabel,
    paging = props.paging,
    responsive = props.responsive,
    responsiveLg = props.responsiveLg,
    responsiveMd = props.responsiveMd,
    responsiveSm = props.responsiveSm,
    responsiveXl = props.responsiveXl,
    scrollX = props.scrollX,
    scrollY = props.scrollY,
    searching = props.searching,
    searchLabel = props.searchLabel,
    small = props.small,
    sortable = props.sortable,
    striped = props.striped,
    tbodyColor = props.tbodyColor,
    tbodyTextWhite = props.tbodyTextWhite,
    theadColor = props.theadColor,
    materialSearch = props.materialSearch,
    theadTextWhite = props.theadTextWhite,
    proSelect = props.proSelect,
    attributes = _objectWithoutPropertiesLoose(props, ["autoWidth", "barReverse", "bordered", "borderless", "btn", "children", "className", "dark", "data", "disableRetreatAfterSorting", "displayEntries", "entriesLabel", "entriesOptions", "exportToCSV", "filter", "fixed", "hover", "info", "infoLabel", "maxHeight", "noBottomColumns", "noRecordsFoundLabel", "onPageChange", "onSearch", "onSort", "order", "pagesAmount", "paginationLabel", "paging", "responsive", "responsiveLg", "responsiveMd", "responsiveSm", "responsiveXl", "scrollX", "scrollY", "searching", "searchLabel", "small", "sortable", "rows", "sortRows", "striped", "tbodyColor", "tbodyTextWhite", "theadColor", "materialSearch", "theadTextWhite", "proSelect"]);
  var _useState = useState({
      activePage: 0,
      columns: typeof props.data === 'object' && props.data.columns || [],
      entries: props.entries,
      filteredRows: typeof props.data === 'object' && props.data.rows || [],
      filterOptions: [],
      order: props.order || [],
      pages: [],
      rows: typeof props.data === 'object' && props.data.rows || [],
      search: '',
      searchSelect: '',
      sorted: false,
      translateScrollHead: 0,
      unsearchable: []
    }),
    state = _useState[0],
    setState = _useState[1];
  var mounted = useRef();
  useEffect(function () {
    if (!mounted.current) {
      componentDidMountCallback();
      mounted.current = true;
    } else {
      componentDidUpdateCallback();
    }
  }, [data]);
  useEffect(function () {
    return filterRows();
  }, [state.rows, state.columns, state.sorted]);
  var componentDidMountCallback = function componentDidMountCallback() {
    var data = props.data,
      paging = props.paging;
    var order = state.order,
      columns = state.columns,
      pages = state.pages,
      rows = state.rows;
    if (typeof data === 'string') {
      fetchData(data);
    }
    if (order.length > 0) {
      handleSort(order[0], order[1]);
    } else {
      handleSort();
    }
    setUnsearchable(columns);
    if (paging) {
      paginateRowsInitialy();
    } else {
      pages.push(rows);
    }
  };
  var componentDidUpdateCallback = function componentDidUpdateCallback() {
    var columns = state.columns;
    var data = props.data;
    typeof data === 'string' ? fetchData(data) : setData(data.rows, data.columns);
    setUnsearchable(columns);
    filterRows();
  };
  var setData = function setData(rows, columns, callback) {
    if (rows === void 0) {
      rows = [];
    }
    if (columns === void 0) {
      columns = [];
    }
    setState(function (prevState) {
      return _extends({}, prevState, {
        columns: columns,
        rows: rows,
        filteredRows: !props.disableRetreatAfterSorting ? rows : []
      });
    });
    props.disableRetreatAfterSorting && filterRows();
  };
  var setUnsearchable = function setUnsearchable(columns) {
    var unsearchable = [];
    columns.forEach(function (column) {
      if (column.searchable !== undefined && column.searchable === false) {
        unsearchable.push(column.field);
      }
    });
    setState(function (prevState) {
      return _extends({}, prevState, {
        unsearchable: unsearchable
      });
    });
  };
  var fetchData = function fetchData(link, isPaginateRows) {
    fetch(link).then(function (res) {
      return res.json();
    }).then(function (json) {
      return setData(json.rows, json.columns);
    }).catch(function (err) {
      return console.log(err);
    });
  };
  var getPagesAmount = function getPagesAmount() {
    return Math.ceil(state.filteredRows.length / state.entries);
  };
  var paginateRowsInitialy = function paginateRowsInitialy() {
    var rows = state.rows,
      entries = state.entries,
      pages = state.pages;
    var pagesAmount = getPagesAmount();
    for (var i = 1; i <= pagesAmount; i++) {
      var pageEndIndex = i * entries;
      pages.push(rows.slice(pageEndIndex - entries, pageEndIndex));
    }
  };
  var handleEntriesChange = function handleEntriesChange(value) {
    setState(function (prevState) {
      return _extends({}, prevState, {
        entries: Array.isArray(value) ? value[0] : value
      });
    });
    paginateRows();
  };
  var handleSearchChange = function handleSearchChange(e) {
    setState(function (prevState) {
      return _extends({}, prevState, {
        search: e.target.value
      });
    });
    filterRows();
    props.onSearch && typeof props.onSearch === 'function' && props.onSearch(e.target.value);
  };
  var checkFieldValue = function checkFieldValue(array, field) {
    return array[field] && typeof array[field] !== 'string' ? array[field].props.searchvalue : array[field];
  };
  var checkField = function checkField(field, a, b, direction) {
    var _ref = [checkFieldValue(a, field), checkFieldValue(b, field)],
      aField = _ref[0],
      bField = _ref[1];
    var comp = aField > bField ? -1 : 1;
    if (direction === 'asc') {
      comp *= -1;
    }
    return comp;
  };
  var sort = function sort(rows, sortRows, field, direction) {
    rows.sort(function (a, b) {
      if (sortRows && sortRows.includes(field)) {
        return checkField(field, a, b, direction);
      }
      return direction === 'asc' ? a[field] < b[field] ? -1 : 1 : a[field] > b[field] ? -1 : 1;
    });
  };
  var handleSort = function handleSort(field, sortType) {
    var onSort = props.onSort;
    if (sortType === 'disabled') {
      return;
    }
    setState(function (prevState) {
      var sortRows = props.sortRows;
      var rows = prevState.rows,
        columns = prevState.columns;
      var direction = sortType === 'desc' ? 'desc' : 'asc';
      sort(rows, sortRows, field, direction);
      columns.forEach(function (col) {
        if (col.sort === 'disabled') {
          return;
        }
        col.sort = col.field === field ? col.sort === 'desc' ? 'asc' : 'desc' : '';
      });
      return _extends({}, prevState, {
        rows: rows,
        columns: columns,
        sorted: true
      });
    });
    onSort && typeof onSort === 'function' && onSort({
      column: field,
      direction: sortType === 'desc' ? 'desc' : 'asc'
    });
  };
  var filterRows = function filterRows(search) {
    if (search === void 0) {
      search = state.search;
    }
    var unsearchable = state.unsearchable;
    var sortRows = props.sortRows,
      noRecordsFoundLabel = props.noRecordsFoundLabel;
    setState(function (prevState) {
      var filteredRows = prevState.rows.filter(function (row) {
        for (var key in row) {
          if ((!unsearchable.length || !unsearchable.includes(key)) && typeof row[key] !== 'function') {
            var stringValue = '';
            if (sortRows && typeof row[key] !== 'string') {
              (function () {
                var content = [];
                var getContent = function getContent(element) {
                  return typeof element === 'object' ? element.props.children && Array.from(element.props.children).map(function (el) {
                    return getContent(el);
                  }) : content.push(element);
                };
                getContent(row[key]);
                stringValue = content.join('');
              })();
            } else if (row[key]) {
              stringValue = row[key].toString();
            }
            if (stringValue.toLowerCase().includes(search.toLowerCase())) {
              return true;
            }
          }
        }
        return false;
      });
      if (filteredRows.length === 0) {
        var _prevState$columns;
        console.log(prevState);
        filteredRows.push({
          message: noRecordsFoundLabel,
          colspan: prevState === null || prevState === void 0 ? void 0 : (_prevState$columns = prevState.columns) === null || _prevState$columns === void 0 ? void 0 : _prevState$columns.length
        });
      }
      var test = _extends({}, state);
      if (props.disableRetreatAfterSorting) {
        test = _extends({}, state, {
          filteredRows: filteredRows,
          activePage: prevState.activePage = prevState.activePage < prevState.pages.length || prevState.activePage === 0 ? prevState.activePage : prevState.pages.length - 1
        });
      } else if (!props.disableRetreatAfterSorting) {
        test = _extends({}, state, {
          filteredRows: filteredRows,
          activePage: 0
        });
      }
      return test;
    });
    paginateRows();
  };
  var paginateRows = function paginateRows() {
    var pagesAmount = getPagesAmount();
    setState(function (prevState) {
      var pages = prevState.pages,
        entries = prevState.entries,
        filteredRows = prevState.filteredRows,
        activePage = prevState.activePage;
      var paging = props.paging,
        disableRetreatAfterSorting = props.disableRetreatAfterSorting;
      pages = [];
      if (paging) {
        for (var i = 1; i <= pagesAmount; i++) {
          var pageEndIndex = i * entries;
          pages.push(filteredRows.slice(pageEndIndex - entries, pageEndIndex));
        }
        if (!disableRetreatAfterSorting) {
          activePage = activePage < pages.length || activePage === 0 ? activePage : pages.length - 1;
        }
      } else {
        pages.push(filteredRows);
        activePage = 0;
      }
      return _extends({}, prevState, {
        pages: pages,
        filteredRows: filteredRows,
        activePage: activePage
      });
    });
  };
  var changeActivePage = function changeActivePage(page) {
    var onPageChange = props.onPageChange;
    setState(_extends({}, state, {
      activePage: page
    }));
    onPageChange && typeof onPageChange === 'function' && onPageChange({
      activePage: page + 1,
      pagesAmount: getPagesAmount()
    });
  };
  var handleTableBodyScroll = function handleTableBodyScroll(e) {
    setState(_extends({}, state, {
      translateScrollHead: e.target.scrollLeft
    }));
  };
  var columns = state.columns,
    entries = state.entries,
    filteredRows = state.filteredRows,
    pages = state.pages,
    activePage = state.activePage,
    search = state.search,
    sorted = state.sorted,
    translateScrollHead = state.translateScrollHead;
  var tableClasses = classNames('dataTables_wrapper', className);
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement("div", {
    "data-test": 'datatable',
    className: tableClasses
  }, React.createElement("div", {
    className: "d-flex flex-wrap justify-content-between px-2"
  }, barReverse ? React.createElement(React.Fragment, null, React.createElement(DataTableSearch, {
    handleSearchChange: handleSearchChange,
    search: search,
    searching: searching,
    label: searchLabel,
    barReverse: barReverse,
    materialSearch: materialSearch
  }), React.createElement(DataTableEntries, {
    paging: paging,
    displayEntries: displayEntries,
    entries: entries,
    handleEntriesChange: handleEntriesChange,
    entriesArr: entriesOptions,
    label: entriesLabel,
    barReverse: barReverse,
    proSelect: proSelect
  })) : React.createElement(React.Fragment, null, React.createElement(DataTableEntries, {
    paging: paging,
    displayEntries: displayEntries,
    entries: entries,
    handleEntriesChange: handleEntriesChange,
    entriesArr: entriesOptions,
    label: entriesLabel,
    barReverse: barReverse,
    proSelect: proSelect
  }), React.createElement(DataTableSearch, {
    handleSearchChange: handleSearchChange,
    search: search,
    searching: searching,
    label: searchLabel,
    barReverse: barReverse,
    materialSearch: materialSearch
  }))), !scrollY && !scrollX && React.createElement("div", {
    className: 'row'
  }, React.createElement(DataTableTable, Object.assign({
    autoWidth: autoWidth,
    bordered: bordered,
    borderless: borderless,
    btn: btn,
    dark: dark,
    fixed: fixed,
    hover: hover,
    noBottomColumns: noBottomColumns,
    noRecordsFoundLabel: noRecordsFoundLabel,
    responsive: responsive,
    responsiveSm: responsiveSm,
    responsiveMd: responsiveMd,
    responsiveLg: responsiveLg,
    responsiveXl: responsiveXl,
    small: small,
    striped: striped,
    theadColor: theadColor,
    theadTextWhite: theadTextWhite,
    columns: columns,
    handleSort: handleSort,
    sortable: sortable,
    tbodyColor: tbodyColor,
    tbodyTextWhite: tbodyTextWhite,
    rows: pages[activePage],
    sorted: sorted
  }, attributes))), (scrollY || scrollX) && React.createElement("div", {
    className: 'row'
  }, React.createElement(DataTableTableScroll, Object.assign({
    autoWidth: autoWidth,
    bordered: bordered,
    borderless: borderless,
    btn: btn,
    dark: dark,
    fixed: fixed,
    handleTableBodyScroll: handleTableBodyScroll,
    hover: hover,
    maxHeight: maxHeight,
    responsive: responsive,
    responsiveSm: responsiveSm,
    responsiveMd: responsiveMd,
    responsiveLg: responsiveLg,
    responsiveXl: responsiveXl,
    scrollX: scrollX,
    scrollY: scrollY,
    small: small,
    striped: striped,
    theadColor: theadColor,
    theadTextWhite: theadTextWhite,
    columns: columns,
    handleSort: handleSort,
    sortable: sortable,
    sorted: sorted,
    tbodyColor: tbodyColor,
    tbodyTextWhite: tbodyTextWhite,
    rows: pages[activePage],
    translateScrollHead: translateScrollHead
  }, attributes))), paging && React.createElement("div", {
    className: "d-flex flex-wrap justify-content-between px-2"
  }, React.createElement(DataTableInfo, {
    activePage: activePage,
    entries: entries,
    filteredRows: filteredRows,
    info: info,
    pages: pages,
    label: infoLabel,
    noRecordsFoundLabel: noRecordsFoundLabel
  }), React.createElement(DataTablePagination, {
    activePage: activePage,
    changeActivePage: changeActivePage,
    pages: pages,
    pagesAmount: pagesAmount,
    label: paginationLabel
  }))));
};
DataTable.defaultProps = {
  autoWidth: false,
  barReverse: false,
  bordered: false,
  borderless: false,
  btn: false,
  dark: false,
  data: {
    columns: [],
    rows: []
  },
  disableRetreatAfterSorting: false,
  displayEntries: true,
  entries: 10,
  entriesLabel: 'Show entries',
  entriesOptions: [10, 20, 50, 100],
  exportToCSV: false,
  fixed: false,
  hover: false,
  info: true,
  infoLabel: ['Showing', 'to', 'of', 'entries'],
  noRecordsFoundLabel: 'No matching records found',
  noBottomColumns: false,
  order: [],
  pagesAmount: 8,
  paging: true,
  paginationLabel: ['Prev', 'Next'],
  responsive: false,
  responsiveSm: false,
  responsiveMd: false,
  responsiveLg: false,
  responsiveXl: false,
  searching: true,
  searchLabel: 'Search',
  scrollX: false,
  scrollY: false,
  sortable: true,
  small: false,
  striped: false,
  theadColor: '',
  theadTextWhite: false,
  tbodyColor: '',
  tbodyTextWhite: false,
  proSelect: false,
  materialSearch: false
};
function onFocus(event) {
  var target = event.target;
  requestAnimationFrame(function () {
    return target.select();
  });
}
function updateInputWidthOnFontLoad(element) {
  if ((!document).fonts) {
    return;
  }
  var font = getFontShorthand(element);
  if (!font) {
    return;
  }
  var isFontLoaded = document.fonts.check(font);
  if (isFontLoaded) {
    return;
  }
  function onLoadingDone() {
    updateInputWidth(element);
  }
  document.fonts.addEventListener('loadingdone', onLoadingDone);
}
function getSelectionString() {
  if (typeof window === 'undefined') {
    return null;
  }
  return window.getSelection().toString();
}
function makeOnKeyPress(maxLength) {
  return function onKeyPress(event) {
    var key = event.key,
      input = event.target;
    var value = input.value;
    var isNumberKey = !isNaN(parseInt(key, 10));
    var selection = getSelectionString();
    if (isNumberKey && (selection || value.length < maxLength)) {
      return;
    }
    event.preventDefault();
  };
}
function Input$1(_ref) {
  var ariaLabel = _ref.ariaLabel,
    autoFocus = _ref.autoFocus,
    className = _ref.className,
    disabled = _ref.disabled,
    itemRef = _ref.itemRef,
    max = _ref.max,
    min = _ref.min,
    name = _ref.name,
    nameForClass = _ref.nameForClass,
    onChange = _ref.onChange,
    onKeyDown = _ref.onKeyDown,
    _onKeyUp = _ref.onKeyUp,
    _ref$placeholder = _ref.placeholder,
    placeholder = _ref$placeholder === void 0 ? '__' : _ref$placeholder,
    required = _ref.required,
    showLeadingZeros = _ref.showLeadingZeros,
    step = _ref.step,
    value = _ref.value;
  var hasLeadingZero = showLeadingZeros && value !== null && value < 10;
  var maxLength = max.toString().length;
  return React.createElement(React.Fragment, null, hasLeadingZero && React.createElement("span", {
    key: "leadingZero",
    className: className + "__leadingZero"
  }, "0"), React.createElement("input", {
    key: "input",
    "aria-label": ariaLabel,
    autoComplete: "off",
    autoFocus: autoFocus,
    className: mergeClassNames(className + "__input", className + "__" + (nameForClass || name), hasLeadingZero && className + "__input--hasLeadingZero"),
    "data-input": "true",
    disabled: disabled,
    max: max,
    min: min,
    name: name,
    onChange: onChange,
    onFocus: onFocus,
    onKeyDown: onKeyDown,
    onKeyPress: makeOnKeyPress(maxLength),
    onKeyUp: function onKeyUp(event) {
      updateInputWidth(event.target);
      if (_onKeyUp) {
        _onKeyUp(event);
      }
    },
    placeholder: placeholder,
    ref: function ref(_ref2) {
      if (_ref2) {
        updateInputWidth(_ref2);
        updateInputWidthOnFontLoad(_ref2);
      }
      if (itemRef) {
        itemRef(_ref2, name);
      }
    },
    required: required,
    step: step,
    type: "number",
    value: value !== null ? value : ''
  }), ",");
}
Input$1.defaultProps = {
  autoFocus: false,
  nameForClass: '',
  className: ''
};
var allViews = ['century', 'decade', 'year', 'month'];
function between(value, min, max) {
  if (min && min > value) {
    return min;
  }
  if (max && max < value) {
    return max;
  }
  return value;
}
function isValidNumber(num) {
  return num !== null && num !== false && !Number.isNaN(Number(num));
}
function safeMin() {
  for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
    args[_key] = arguments[_key];
  }
  return Math.min.apply(Math, args.filter(isValidNumber));
}
function safeMax() {
  for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
    args[_key2] = arguments[_key2];
  }
  return Math.max.apply(Math, args.filter(isValidNumber));
}
function DayInput(_ref) {
  var maxDate = _ref.maxDate,
    minDate = _ref.minDate,
    month = _ref.month,
    year = _ref.year,
    otherProps = _objectWithoutPropertiesLoose(_ref, ["maxDate", "minDate", "month", "year"]);
  var currentMonthMaxDays = function () {
    if (!month) {
      return 31;
    }
    return getDaysInMonth(new Date(year, month - 1, 1));
  }();
  function isSameMonth(date) {
    return date && year === getYear(date) && month === getMonthHuman(date);
  }
  var maxDay = safeMin(currentMonthMaxDays, isSameMonth(maxDate) && getDate(maxDate));
  var minDay = safeMax(1, isSameMonth(minDate) && getDate(minDate));
  return React.createElement(Input$1, Object.assign({
    max: maxDay,
    min: minDay,
    name: "day"
  }, otherProps));
}
function MonthInput(_ref) {
  var maxDate = _ref.maxDate,
    minDate = _ref.minDate,
    year = _ref.year,
    otherProps = _objectWithoutPropertiesLoose(_ref, ["maxDate", "minDate", "year"]);
  function isSameYear(date) {
    return date && year === getYear(date);
  }
  var maxMonth = safeMin(12, isSameYear(maxDate) && getMonthHuman(maxDate));
  var minMonth = safeMax(1, isSameYear(minDate) && getMonthHuman(minDate));
  return React.createElement(Input$1, Object.assign({
    max: maxMonth,
    min: minMonth,
    name: "month"
  }, otherProps));
}
function getFormatter(options) {
  return function (locale, date) {
    return date.toLocaleString(locale || getUserLocale(), options);
  };
}
function toSafeHour(date) {
  var safeDate = new Date(date);
  return new Date(safeDate.setHours(12));
}
function getSafeFormatter(options) {
  return function (locale, date) {
    return getFormatter(options)(locale, toSafeHour(date));
  };
}
var formatMonthOptions = {
  month: 'long'
};
var formatShortMonthOptions = {
  month: 'short'
};
var formatMonth = getSafeFormatter(formatMonthOptions);
var formatShortMonth = getSafeFormatter(formatShortMonthOptions);
function MonthSelect(_ref) {
  var ariaLabel = _ref.ariaLabel,
    className = _ref.className,
    itemRef = _ref.itemRef,
    locale = _ref.locale,
    maxDate = _ref.maxDate,
    minDate = _ref.minDate,
    _ref$placeholder = _ref.placeholder,
    placeholder = _ref$placeholder === void 0 ? "--" : _ref$placeholder,
    short = _ref.short,
    value = _ref.value,
    year = _ref.year,
    otherProps = _objectWithoutPropertiesLoose(_ref, ["ariaLabel", "className", "itemRef", "locale", "maxDate", "minDate", "placeholder", "short", "value", "year"]);
  function isSameYear(date) {
    return date && year === getYear(date);
  }
  var maxMonth = safeMin(12, isSameYear(maxDate) && getMonthHuman(maxDate));
  var minMonth = safeMax(1, isSameYear(minDate) && getMonthHuman(minDate));
  var dates = [].concat(Array(12)).map(function (el, index) {
    return new Date(2019, index, 1);
  });
  var name = "month";
  var formatter = short ? formatShortMonth : formatMonth;
  return React.createElement("select", Object.assign({
    "aria-label": ariaLabel,
    className: mergeClassNames(className + "__input", className + "__" + name),
    "data-input": "true",
    name: name,
    ref: function ref(_ref2) {
      if (itemRef) {
        itemRef(_ref2, name);
      }
    },
    value: value !== null ? value : ""
  }, otherProps), !value && React.createElement("option", {
    value: ""
  }, placeholder), dates.map(function (date) {
    var month = getMonthHuman(date);
    var disabled = month < minMonth || month > maxMonth;
    return React.createElement("option", {
      key: month,
      disabled: disabled,
      value: month
    }, formatter(locale, date));
  }));
}
function YearInput(_ref) {
  var maxDate = _ref.maxDate,
    minDate = _ref.minDate,
    _ref$placeholder = _ref.placeholder,
    placeholder = _ref$placeholder === void 0 ? '____' : _ref$placeholder,
    valueType = _ref.valueType,
    otherProps = _objectWithoutPropertiesLoose(_ref, ["maxDate", "minDate", "placeholder", "valueType"]);
  var maxYear = safeMin(275760, maxDate && getYear(maxDate));
  var minYear = safeMax(1, minDate && getYear(minDate));
  var yearStep = function () {
    if (valueType === 'century') {
      return 10;
    }
    return 1;
  }();
  return React.createElement(Input$1, Object.assign({
    max: maxYear,
    min: minYear,
    name: "year",
    placeholder: placeholder,
    step: yearStep
  }, otherProps));
}
function NativeInput(_ref) {
  var ariaLabel = _ref.ariaLabel,
    disabled = _ref.disabled,
    maxDate = _ref.maxDate,
    minDate = _ref.minDate,
    name = _ref.name,
    onChange = _ref.onChange,
    required = _ref.required,
    value = _ref.value,
    valueType = _ref.valueType;
  var nativeInputType = function () {
    switch (valueType) {
      case 'decade':
      case 'year':
        return 'number';
      case 'month':
        return 'month';
      case 'day':
        return 'date';
      default:
        throw new Error('Invalid valueType.');
    }
  }();
  var nativeValueParser = function () {
    switch (valueType) {
      case 'decade':
      case 'year':
        return getYear;
      case 'month':
        return getISOLocalMonth;
      case 'day':
        return getISOLocalDate;
      default:
        throw new Error('Invalid valueType.');
    }
  }();
  function stopPropagation(event) {
    event.stopPropagation();
  }
  return React.createElement("input", {
    "aria-label": ariaLabel,
    disabled: disabled,
    max: maxDate ? nativeValueParser(maxDate) : null,
    min: minDate ? nativeValueParser(minDate) : null,
    name: name,
    onChange: onChange,
    onFocus: stopPropagation,
    required: required,
    style: {
      visibility: 'hidden',
      position: 'absolute',
      top: '-9999px',
      left: '-9999px'
    },
    type: nativeInputType,
    value: value ? nativeValueParser(value) : ''
  });
}
function getBegin(rangeType, date) {
  switch (rangeType) {
    case 'century':
      return getCenturyStart(date);
    case 'decade':
      return getDecadeStart(date);
    case 'year':
      return getYearStart(date);
    case 'month':
      return getMonthStart(date);
    case 'day':
      return getDayStart(date);
    default:
      throw new Error("Invalid rangeType: " + rangeType);
  }
}
function getEnd(rangeType, date) {
  switch (rangeType) {
    case 'century':
      return getCenturyEnd(date);
    case 'decade':
      return getDecadeEnd(date);
    case 'year':
      return getYearEnd(date);
    case 'month':
      return getMonthEnd(date);
    case 'day':
      return getDayEnd(date);
    default:
      throw new Error("Invalid rangeType: " + rangeType);
  }
}
var defaultMinDate = new Date(-8.64e15);
var defaultMaxDate = new Date(8.64e15);
var allViews$1 = ['century', 'decade', 'year', 'month'];
var allValueTypes$1 = [].concat(allViews$1.slice(1), ['day']);
function datesAreDifferent(date1, date2) {
  return date1 && !date2 || !date1 && date2 || date1 && date2 && date1.getTime() !== date2.getTime();
}
function getValueType(maxDetail) {
  return allValueTypes$1[allViews$1.indexOf(maxDetail)];
}
function getValue(value, index) {
  if (!value) {
    return null;
  }
  var rawValue = value instanceof Array && value.length === 2 ? value[index] : value;
  if (!rawValue) {
    return null;
  }
  var valueDate = new Date(rawValue);
  if (isNaN(valueDate.getTime())) {
    throw new Error("Invalid date: " + value);
  }
  return valueDate;
}
function getDetailValue(_ref, index) {
  var value = _ref.value,
    minDate = _ref.minDate,
    maxDate = _ref.maxDate,
    maxDetail = _ref.maxDetail;
  var valuePiece = getValue(value, index);
  if (!valuePiece) {
    return null;
  }
  var valueType = getValueType(maxDetail);
  var detailValueFrom = [getBegin, getEnd][index](valueType, valuePiece);
  return between(detailValueFrom, minDate, maxDate);
}
var getDetailValueFrom = function getDetailValueFrom(args) {
  return getDetailValue(args, 0);
};
var getDetailValueTo = function getDetailValueTo(args) {
  return getDetailValue(args, 1);
};
var getDetailValueArray = function getDetailValueArray(args) {
  var value = args.value;
  if (value instanceof Array) {
    return value;
  }
  return [getDetailValueFrom, getDetailValueTo].map(function (fn) {
    return fn(args);
  });
};
function isInternalInput(element) {
  return element.getAttribute('data-input') === 'true';
}
function findInput(element, property) {
  var nextElement = element;
  do {
    nextElement = nextElement[property];
  } while (nextElement && !isInternalInput(nextElement));
  return nextElement;
}
function focus(element) {
  if (element) {
    element.focus();
  }
}
function _renderCustomInputs(getPlaceholder, elementFunctions, allowMultipleInstances) {
  var usedFunctions = [];
  var pattern = new RegExp(Object.keys(elementFunctions).map(function (el) {
    return el + "+";
  }).join('|'), 'g');
  var matches = getPlaceholder.match(pattern);
  return getPlaceholder.split(pattern).reduce(function (arr, element, index) {
    var res = [].concat(arr);
    var currentMatch = matches && matches[index];
    if (currentMatch) {
      var otherMatch = Object.keys(elementFunctions).find(function (elementFunction) {
        return currentMatch.match(elementFunction);
      });
      var renderFunction = elementFunctions[currentMatch] || otherMatch && elementFunctions[otherMatch];
      if (!allowMultipleInstances && usedFunctions.includes(renderFunction)) {
        res.push(currentMatch);
      } else {
        res.push(renderFunction(currentMatch, index));
        usedFunctions.push(renderFunction);
      }
    }
    return res;
  }, []);
}
function DateInput(props) {
  var _useState = useState({
      isCalendarOpen: null,
      value: null,
      year: null,
      month: null,
      day: null
    }),
    state = _useState[0],
    setState = _useState[1];
  var minDate = props.minDate,
    maxDate = props.maxDate,
    maxDetail = props.maxDetail,
    attributes = _objectWithoutPropertiesLoose(props, ["autoFocus", "dayAriaLabel", "daygetPlaceholder", "showLeadingZeros", "minDate", "maxDate", "maxDetail"]);
  useEffect(function () {
    if (props.isCalendarOpen !== state.isCalendarOpen) {
      setState(function (prev) {
        return _extends({}, prev, {
          isCalendarOpen: props.isCalendarOpen
        });
      });
    }
    var nextValue = getDetailValueFrom({
      value: props.value,
      minDate: minDate,
      maxDate: maxDate,
      maxDetail: maxDetail
    });
    var values = [nextValue, state.value];
    var detailValueFrom = values.map(function (value) {
      return getDetailValueFrom({
        value: value,
        minDate: minDate,
        maxDate: maxDate,
        maxDetail: maxDetail
      });
    });
    var detailValueTo = values.map(function (value) {
      return getDetailValueTo({
        value: value,
        minDate: minDate,
        maxDate: maxDate,
        maxDetail: maxDetail
      });
    });
    if (state.isCalendarOpen || datesAreDifferent(detailValueFrom[0], detailValueFrom[1]) || datesAreDifferent(detailValueTo[0], detailValueTo[1])) {
      if (nextValue) {
        setState(function (prev) {
          return _extends({}, prev, {
            year: getYear(nextValue),
            month: getMonthHuman(nextValue),
            day: getDate(nextValue)
          });
        });
      } else {
        setState(function (prev) {
          return _extends({}, prev, {
            year: null,
            month: null,
            day: null
          });
        });
      }
      setState(function (prev) {
        return _extends({}, prev, {
          value: nextValue
        });
      });
    }
  }, [props.minDate, props.maxDate, props.maxDetail]);
  var dayInput;
  var monthInput;
  var yearInput;
  function getFormatDate() {
    var maxDetail = props.maxDetail;
    var options = {
      year: 'numeric',
      month: '',
      day: ''
    };
    var level = allViews$1.indexOf(maxDetail);
    if (level >= 2) {
      options.month = 'numeric';
    }
    if (level >= 3) {
      options.day = 'numeric';
    }
    return getFormatter(options);
  }
  function getFormatNumber() {
    var options = {
      useGrouping: false
    };
    return getFormatter(options);
  }
  function getProcessedValue(value) {
    var minDate = props.minDate,
      maxDate = props.maxDate,
      maxDetail = props.maxDetail,
      returnValue = props.returnValue;
    var processFunction = function () {
      switch (returnValue) {
        case 'start':
          return getDetailValueFrom;
        case 'end':
          return getDetailValueTo;
        case 'range':
          return getDetailValueArray;
        default:
          throw new Error('Invalid returnValue.');
      }
    }();
    return processFunction({
      value: value,
      minDate: minDate,
      maxDate: maxDate,
      maxDetail: maxDetail
    });
  }
  function getPlaceholder() {
    var format = props.format,
      locale = props.locale;
    if (format) {
      return format;
    }
    var year = 2017;
    var monthIndex = 11;
    var day = 11;
    var date = new Date(year, monthIndex, day);
    return getFormatDate()(locale, date).replace(getFormatNumber()(locale, year), 'y').replace(getFormatNumber()(locale, monthIndex + 1), 'M').replace(getFormatNumber()(locale, day), 'd');
  }
  function getCommonInputProps() {
    var className = props.className,
      disabled = props.disabled,
      isCalendarOpen = props.isCalendarOpen,
      maxDate = props.maxDate,
      minDate = props.minDate,
      required = props.required;
    return {
      className: className,
      disabled: disabled,
      maxDate: maxDate || defaultMaxDate,
      minDate: minDate || defaultMinDate,
      onChange: onChange,
      onKeyDown: onKeyDown,
      onKeyUp: onKeyUp,
      required: required || isCalendarOpen,
      itemRef: function itemRef(ref, name) {
        if (name === "day") {
          dayInput = ref;
        } else if (name === "month") {
          monthInput = ref;
        } else if (name === "year") {
          yearInput = ref;
        }
      }
    };
  }
  function _getValueType() {
    var maxDetail = props.maxDetail;
    return getValueType(maxDetail);
  }
  var onClick = function onClick(event) {
    if (event.target === event.currentTarget) {
      var firstInput = event.target.children[1];
      focus(firstInput);
    }
  };
  var onKeyDown = function onKeyDown(event) {};
  var onKeyUp = function onKeyUp(event) {
    var key = event.key,
      input = event.target;
    var isNumberKey = !isNaN(parseInt(key, 10));
    if (!isNumberKey) {
      return;
    }
    var value = input.value;
    var max = input.getAttribute('max');
    if (value * 10 > max || value.length >= max.length) {
      var property = 'nextElementSibling';
      var nextInput = findInput(input, property);
      focus(nextInput);
    }
  };
  var onChange = function onChange(event) {
    var _event$target = event.target,
      name = _event$target.name,
      value = _event$target.value;
    Promise.resolve().then().then(function () {
      return onChangeExternal;
    });
    setState(function (prev) {
      var _extends2;
      return _extends({}, prev, (_extends2 = {}, _extends2[name] = value ? parseInt(value, 10) : null, _extends2));
    });
  };
  var onChangeNative = function onChangeNative(event) {
    var onChange = props.onChange;
    var value = event.target.value;
    if (!onChange) {
      return;
    }
    var processedValue = function () {
      if (!value) {
        return null;
      }
      var _value$split = value.split('-'),
        yearString = _value$split[0],
        monthString = _value$split[1],
        dayString = _value$split[2];
      var year = parseInt(yearString, 10);
      var monthIndex = parseInt(monthString, 10) - 1 || 0;
      var day = parseInt(dayString, 10) || 1;
      var proposedValue = new Date();
      proposedValue.setFullYear(year, monthIndex, day);
      proposedValue.setHours(0, 0, 0, 0);
      return proposedValue;
    }();
    onChange(processedValue, false);
  };
  var onChangeExternal = function onChangeExternal() {
    var onChange = props.onChange;
    if (!onChange) {
      return;
    }
    var formElements = [dayInput, monthInput, yearInput].filter(Boolean);
    var values = {
      year: '',
      month: '',
      day: ''
    };
    formElements.forEach(function (formElement) {
      values[formElement.name] = formElement.value;
    });
    if (formElements.every(function (formElement) {
      return !formElement.value;
    })) {
      onChange(null, false);
    } else if (formElements.every(function (formElement) {
      return formElement.value && formElement.validity.valid;
    })) {
      var year = parseInt(values.year, 10);
      var monthIndex = parseInt(values.month, 10) - 1 || 0;
      var day = parseInt(values.day || "1", 10);
      var proposedValue = new Date();
      proposedValue.setFullYear(year, monthIndex, day);
      proposedValue.setHours(0, 0, 0, 0);
      var processedValue = getProcessedValue(proposedValue);
      onChange(processedValue, false);
    }
  };
  var renderDay = function renderDay(currentMatch, index) {
    var autoFocus = props.autoFocus,
      dayAriaLabel = props.dayAriaLabel,
      daygetPlaceholder = props.daygetPlaceholder,
      showLeadingZeros = props.showLeadingZeros;
    var day = state.day,
      month = state.month,
      year = state.year;
    if (currentMatch && currentMatch.length > 2) {
      throw new Error("Unsupported token: " + currentMatch);
    }
    var showLeadingZerosFromFormat = currentMatch && currentMatch.length === 2;
    return React.createElement(DayInput, Object.assign({
      key: 'day'
    }, getCommonInputProps(), {
      ariaLabel: dayAriaLabel,
      autoFocus: index === 0 && autoFocus,
      month: month,
      getPlaceholder: daygetPlaceholder,
      showLeadingZeros: showLeadingZerosFromFormat || showLeadingZeros,
      value: day,
      year: year
    }));
  };
  var renderMonth = function renderMonth(currentMatch, index) {
    var autoFocus = props.autoFocus,
      locale = props.locale,
      monthAriaLabel = props.monthAriaLabel,
      monthgetPlaceholder = props.monthgetPlaceholder,
      showLeadingZeros = props.showLeadingZeros;
    var month = state.month,
      year = state.year;
    if (currentMatch && currentMatch.length > 4) {
      throw new Error("Unsupported token: " + currentMatch);
    }
    if (currentMatch.length > 2) {
      return React.createElement(MonthSelect, Object.assign({
        key: 'month'
      }, getCommonInputProps(), {
        ariaLabel: monthAriaLabel,
        autoFocus: index === 0 && autoFocus,
        locale: locale,
        getPlaceholder: monthgetPlaceholder,
        short: currentMatch.length === 3,
        value: month,
        year: year
      }));
    }
    var showLeadingZerosFromFormat = currentMatch && currentMatch.length === 2;
    return React.createElement(MonthInput, Object.assign({
      key: 'month'
    }, getCommonInputProps(), {
      ariaLabel: monthAriaLabel,
      autoFocus: index === 0 && autoFocus,
      getPlaceholder: monthgetPlaceholder,
      showLeadingZeros: showLeadingZerosFromFormat || showLeadingZeros,
      value: month,
      year: year
    }));
  };
  var renderYear = function renderYear(currentMatch, index) {
    var autoFocus = props.autoFocus,
      yearAriaLabel = props.yearAriaLabel,
      yeargetPlaceholder = props.yeargetPlaceholder;
    var year = state.year;
    return React.createElement(YearInput, Object.assign({
      key: 'year'
    }, getCommonInputProps(), {
      ariaLabel: yearAriaLabel,
      autoFocus: index === 0 && autoFocus,
      getPlaceholder: yeargetPlaceholder,
      value: year,
      valueType: _getValueType()
    }));
  };
  function renderCustomInputs() {
    var placeholder = getPlaceholder();
    var format = props.format;
    var elementFunctions = {
      d: renderDay,
      M: renderMonth,
      y: renderYear
    };
    var allowMultipleInstances = typeof format !== 'undefined';
    return _renderCustomInputs(placeholder, elementFunctions, allowMultipleInstances);
  }
  function renderNativeInput() {
    var disabled = props.disabled,
      maxDate = props.maxDate,
      minDate = props.minDate,
      name = props.name,
      nativeInputAriaLabel = props.nativeInputAriaLabel,
      required = props.required;
    var value = state.value;
    return React.createElement(NativeInput, {
      key: 'date',
      ariaLabel: nativeInputAriaLabel,
      disabled: disabled,
      maxDate: maxDate || defaultMaxDate,
      minDate: minDate || defaultMinDate,
      name: name,
      onChange: onChangeNative,
      required: required,
      value: value,
      valueType: _getValueType()
    });
  }
  var className = props.className;
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement("div", {
    className: className,
    onClick: onClick
  }, renderNativeInput(), renderCustomInputs()));
}
DateInput.defaultProps = {
  maxDetail: 'month',
  name: 'date',
  returnValue: 'start'
};
function _templateObject15() {
  var data = _taggedTemplateLiteralLoose(["\n        background: ", ";\n      "]);
  _templateObject15 = function _templateObject15() {
    return data;
  };
  return data;
}
function _templateObject14() {
  var data = _taggedTemplateLiteralLoose(["\n        background: ", ";\n      "]);
  _templateObject14 = function _templateObject14() {
    return data;
  };
  return data;
}
function _templateObject13() {
  var data = _taggedTemplateLiteralLoose(["\n        background: ", ";\n      "]);
  _templateObject13 = function _templateObject13() {
    return data;
  };
  return data;
}
function _templateObject12() {
  var data = _taggedTemplateLiteralLoose(["\n        background: ", " !important;\n      "]);
  _templateObject12 = function _templateObject12() {
    return data;
  };
  return data;
}
function _templateObject11() {
  var data = _taggedTemplateLiteralLoose(["\n        background: ", ";\n      "]);
  _templateObject11 = function _templateObject11() {
    return data;
  };
  return data;
}
function _templateObject10() {
  var data = _taggedTemplateLiteralLoose(["\n        background: ", " !important;\n      "]);
  _templateObject10 = function _templateObject10() {
    return data;
  };
  return data;
}
function _templateObject9() {
  var data = _taggedTemplateLiteralLoose(["\n            color: #eee !important;\n          "]);
  _templateObject9 = function _templateObject9() {
    return data;
  };
  return data;
}
function _templateObject8$1() {
  var data = _taggedTemplateLiteralLoose(["\n            color: #333 !important;\n          "]);
  _templateObject8$1 = function _templateObject8() {
    return data;
  };
  return data;
}
function _templateObject7$1() {
  var data = _taggedTemplateLiteralLoose(["\n        background: ", ";\n      "]);
  _templateObject7$1 = function _templateObject7() {
    return data;
  };
  return data;
}
function _templateObject6$1() {
  var data = _taggedTemplateLiteralLoose(["\n      display: none !important;\n    "]);
  _templateObject6$1 = function _templateObject6() {
    return data;
  };
  return data;
}
function _templateObject5$2() {
  var data = _taggedTemplateLiteralLoose(["\n  ", "\n  .react-calendar {\n    width: 550px !important;\n    max-width: 100%;\n    border: none !important;\n    line-height: 1.125em;\n    background: #ffffff;\n    box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.12);\n    border-radius: 20px;\n  }\n  .react-calendar--doubleView {\n    width: 700px;\n  }\n  .react-calendar--doubleView .react-calendar__viewContainer {\n    display: flex;\n    margin: -0.5em;\n  }\n  .react-calendar--doubleView .react-calendar__viewContainer > * {\n    width: 50%;\n    margin: 0.5em;\n  }\n  .react-calendar,\n  .react-calendar *,\n  .react-calendar *:before,\n  .react-calendar *:after {\n    -moz-box-sizing: border-box;\n    -webkit-box-sizing: border-box;\n    box-sizing: border-box;\n  }\n  .react-calendar button {\n    margin: 0;\n    border: 0;\n    outline: none;\n  }\n  .react-calendar button:enabled:hover {\n    cursor: pointer;\n  }\n  .react-calendar__navigation {\n    padding: 20px 10px;\n    margin-bottom: 0 !important;\n    display: flex;\n    align-items: center;\n    width: 100%;\n    height: 80px;\n    ", "\n  }\n  .react-calendar__navigation button {\n    min-width: 44px;\n    background: none;\n    font-size: 20px;\n    font-weight: bold;\n  }\n  .react-calendar__navigation button[disabled] {\n    background-color: #f0f0f0;\n  }\n  .react-calendar__navigation_label {\n    background: #000 !important;\n  }\n  .react-calendar__navigation__label__labelText,\n  .react-calendar__navigation__label__labelText--from,\n  .react-calendar__navigation__arrow {\n    font-weight: bold;\n    text-transform: capitalize;\n    font-size: 20px;\n    font-family: Montserrat, sans-serif;\n    ", ";\n  }\n  .react-calendar__month-view__days,\n  .react-calendar__year-view__months {\n    padding: 10px;\n  }\n  .react-calendar__month-view__days .react-calendar__tile {\n    height: 48.5px;\n    border-radius: 50%;\n  }\n  .react-calendar__tile--now {\n    color: #fff !important;\n    ", "\n  }\n  .react-calendar__tile--hasActive {\n    color: #fff !important;\n    ", "\n  }\n  .react-calendar__tile--active {\n    background: #f6f6f6 !important;\n    color: #000 !important;\n    border: 2px solid #e2e2e2 !important;\n  }\n  .react-calendar__month-view__weekdays {\n    text-align: center;\n    text-transform: uppercase;\n    font-weight: bold;\n    font-size: 0.75em;\n    padding: 10px 0;\n    margin-bottom: 15px;\n    ", "\n    color: #fff;\n  }\n  .react-calendar__month-view__weekdays__weekday {\n    padding: 0.5em;\n    text-transform: capitalize;\n  }\n\n  .react-calendar__month-view__weekdays__weekday abbr {\n    text-decoration: none;\n    text-transform: capitalize;\n  }\n  .react-calendar__month-view__weekNumbers {\n    font-weight: bold;\n    text-transform: capitalize;\n  }\n  .react-calendar__month-view__weekNumbers .react-calendar__tile {\n    display: flex;\n    align-items: center;\n    justify-content: center;\n    font-size: 0.75em;\n    padding: calc(1em) calc(0.6666666666666666em);\n  }\n  .react-calendar__month-view__days__day--weekend {\n    color: #d10000;\n  }\n  .react-calendar__month-view__days__day--neighboringMonth {\n    color: #757575;\n  }\n  .react-calendar__year-view .react-calendar__tile,\n  .react-calendar__decade-view .react-calendar__tile,\n  .react-calendar__century-view .react-calendar__tile {\n    padding: 2em 0.5em;\n  }\n  .react-calendar__tile {\n    max-width: 100%;\n    text-align: center;\n    padding: 0.75em 0.5em;\n    background: none;\n    border-radius: 4px !important;\n  }\n  .react-calendar__tile:disabled {\n    background-color: #f0f0f0;\n  }\n  .react-calendar__tile:enabled:hover,\n  .react-calendar__tile:enabled:focus {\n    background-color: #e6e6e6;\n  }\n  .react-calendar__tile--now {\n    background: #ffff76;\n  }\n  .react-calendar__tile--now:enabled:hover,\n  .react-calendar__tile--now:enabled:focus {\n    background: #ffffa9;\n  }\n  .react-calendar__tile--hasActive {\n    ", "\n  }\n  .react-calendar__tile--hasActive:enabled:hover,\n  .react-calendar__tile--hasActive:enabled:focus {\n    ", "\n  }\n  .react-calendar__tile--active {\n    ", "\n    color: white;\n  }\n  .react-calendar__tile--active:enabled:hover,\n  .react-calendar__tile--active:enabled:focus {\n    background: #1087ff;\n  }\n  .react-calendar--selectRange .react-calendar__tile--hover {\n    background-color: #e6e6e6;\n  }\n"]);
  _templateObject5$2 = function _templateObject5() {
    return data;
  };
  return data;
}
function _templateObject4$4() {
  var data = _taggedTemplateLiteralLoose(["\n        background: ", " !important;\n        color: ", " !important;\n        font-weight: 900;\n      "]);
  _templateObject4$4 = function _templateObject4() {
    return data;
  };
  return data;
}
function _templateObject3$7() {
  var data = _taggedTemplateLiteralLoose(["\n                fill: ", " !important;\n                fill-width: 3px;\n                stroke: ", " !important;\n                stroke-width: 3px;\n              "]);
  _templateObject3$7 = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$a() {
  var data = _taggedTemplateLiteralLoose(["\n        border: 3px solid ", ";\n        background: ", ";\n        * {\n          font-weight: bold !important;\n          color: ", " !important;\n        }\n        .react-date-picker__button {\n          svg {\n            ", "\n          }\n        }\n      "]);
  _templateObject2$a = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$r() {
  var data = _taggedTemplateLiteralLoose(["\n  .react-date-picker {\n    display: inline-flex;\n    position: relative;\n  }\n  .react-date-picker,\n  .react-date-picker *,\n  .react-date-picker *:before,\n  .react-date-picker *:after {\n    -moz-box-sizing: border-box;\n    -webkit-box-sizing: border-box;\n    box-sizing: border-box;\n  }\n  .react-date-picker--disabled {\n    background-color: #f0f0f0;\n    color: #6d6d6d;\n  }\n  .react-date-picker__wrapper {\n    display: flex;\n    flex-grow: 1;\n    flex-shrink: 0;\n    max-width: 350px;\n    margin-bottom: 10px;\n    border-radius: 4px;\n    padding: 5px 10px;\n    background: #eeeeee;\n    border: 1px solid #e7e7e7;\n    box-sizing: border-box;\n  }\n  .react-date-picker__wrapper:hover,\n  .react-date-picker__wrapper:focus {\n    ", "\n  }\n  .react-date-picker__inputGroup {\n    flex-grow: 1;\n    box-sizing: border-box;\n    border-radius: 4px;\n    height: 36px;\n    padding-right: 20px;\n  }\n  .react-date-picker__inputGroup__divider {\n    padding: 1px 0;\n    white-space: pre;\n  }\n  .react-date-picker__inputGroup__input {\n    min-width: 0.54em;\n    height: 100%;\n    margin: 0 2px;\n    position: relative;\n    padding: 0 1px;\n    border: 0;\n    background: none;\n    font: inherit;\n    box-sizing: content-box;\n  }\n  .react-date-picker__inputGroup__input:hover,\n  .react-date-picker__inputGroup__input:focus {\n    outline: none;\n    border: none !important;\n    background: #f0f0f0;\n    ", "\n  }\n  .react-date-picker__inputGroup__input::-webkit-outer-spin-button,\n  .react-date-picker__inputGroup__input::-webkit-inner-spin-button {\n    -webkit-appearance: none;\n    margin: 0;\n  }\n  .react-date-picker__inputGroup__input:invalid {\n    background: rgba(0, 0, 0, 0.1);\n  }\n  .react-date-picker__inputGroup__input--hasLeadingZero {\n    margin-left: -0.54em;\n    padding-left: calc(1px + 0.54em);\n  }\n  .react-date-picker__button {\n    border: 0;\n    background: transparent;\n    padding: 4px 6px;\n    svg {\n      fill: #cccccc;\n      stroke: #cccccc;\n    }\n  }\n  .react-date-picker__button:hover,\n  .react-date-picker__button:focus {\n    border: none;\n    outline: none;\n  }\n  .react-date-picker__button:enabled {\n    cursor: pointer;\n  }\n  .react-date-picker__button:enabled:hover .react-date-picker__button__icon,\n  .react-date-picker__button:enabled:focus .react-date-picker__button__icon {\n    stroke: #0078d7;\n    outline: none;\n    border: none !important;\n  }\n  .react-date-picker__button:disabled .react-date-picker__button__icon {\n    stroke: #6d6d6d;\n  }\n  .react-date-picker__button svg {\n    display: inherit;\n  }\n  .react-date-picker__calendar {\n    width: 350px;\n    max-width: 100vw;\n    position: absolute;\n    top: 50%;\n    left: 50%;\n    z-index: 1;\n    border-radius: 8px;\n  }\n  .react-date-picker__calendar--closed {\n    display: none;\n  }\n  .react-date-picker__calendar .react-calendar {\n    border-width: thin;\n  }\n"]);
  _templateObject$r = function _templateObject() {
    return data;
  };
  return data;
}
var Component$p = styled.div(_templateObject$r(), function (_ref) {
  var color = _ref.color;
  return color && css(_templateObject2$a(), colors[color], colors[color + "100"], colors[color], function (_ref2) {
    var color = _ref2.color;
    return color && css(_templateObject3$7(), colors[color], colors[color]);
  });
}, function (_ref3) {
  var color = _ref3.color;
  return color && css(_templateObject4$4(), colors[color + "200"], colors[color + "700"]);
});
var CalenderFit = styled.div(_templateObject5$2(), function (_ref4) {
  var isOpen = _ref4.isOpen;
  return !isOpen && css(_templateObject6$1());
}, function (_ref5) {
  var color = _ref5.color;
  return color && css(_templateObject7$1(), colors[color + "600"]);
}, function (_ref6) {
  var color = _ref6.color;
  return color === 'warning' || color === 'light' ? css(_templateObject8$1()) : css(_templateObject9());
}, function (_ref7) {
  var color = _ref7.color;
  return color && css(_templateObject10(), colors[color]);
}, function (_ref8) {
  var color = _ref8.color;
  return color && css(_templateObject11(), colors[color]);
}, function (_ref9) {
  var color = _ref9.color;
  return color && css(_templateObject12(), colors[color]);
}, function (_ref10) {
  var color = _ref10.color;
  return color && css(_templateObject13(), colors[color]);
}, function (_ref11) {
  var color = _ref11.color;
  return color && css(_templateObject14(), colors[color]);
}, function (_ref12) {
  var color = _ref12.color;
  return color && css(_templateObject15(), colors[color]);
});
var baseClassName = 'react-date-picker';
var DatePicker = function DatePicker(_ref) {
  var className = _ref.className,
    onCalendarClose = _ref.onCalendarClose,
    onCalendarOpen = _ref.onCalendarOpen,
    disabled = _ref.disabled,
    onFocus = _ref.onFocus,
    onChange = _ref.onChange,
    autoFocus = _ref.autoFocus,
    calendarAriaLabel = _ref.calendarAriaLabel,
    calendarIcon = _ref.calendarIcon,
    clearAriaLabel = _ref.clearAriaLabel,
    clearIcon = _ref.clearIcon,
    color = _ref.color,
    dayAriaLabel = _ref.dayAriaLabel,
    dayPlaceholder = _ref.dayPlaceholder,
    disableCalendar = _ref.disableCalendar,
    format = _ref.format,
    locale = _ref.locale,
    maxDate = _ref.maxDate,
    maxDetail = _ref.maxDetail,
    minDate = _ref.minDate,
    monthAriaLabel = _ref.monthAriaLabel,
    monthPlaceholder = _ref.monthPlaceholder,
    name = _ref.name,
    nativeInputAriaLabel = _ref.nativeInputAriaLabel,
    required = _ref.required,
    returnValue = _ref.returnValue,
    showLeadingZeros = _ref.showLeadingZeros,
    value = _ref.value,
    yearAriaLabel = _ref.yearAriaLabel,
    yearPlaceholder = _ref.yearPlaceholder,
    calendarClassName = _ref.calendarClassName,
    props = _objectWithoutPropertiesLoose(_ref, ["className", "onCalendarClose", "onCalendarOpen", "disabled", "onFocus", "onChange", "autoFocus", "calendarAriaLabel", "calendarIcon", "clearAriaLabel", "clearIcon", "color", "dayAriaLabel", "dayPlaceholder", "disableCalendar", "format", "locale", "maxDate", "maxDetail", "minDate", "monthAriaLabel", "monthPlaceholder", "name", "nativeInputAriaLabel", "required", "returnValue", "showLeadingZeros", "value", "yearAriaLabel", "yearPlaceholder", "calendarClassName"]);
  var calenderRef = useRef(null);
  var wrapperRef = useRef(null);
  var _useState = useState(false),
    isOpen = _useState[0],
    setIsOpen = _useState[1];
  useEffect(function () {
    var callback = isOpen ? onCalendarOpen : onCalendarClose;
    if (callback) callback();
    addSvgEvents();
  }, [isOpen]);
  function addSvgEvents() {
    var prev2 = calenderRef.current.querySelector('.react-calendar__navigation__arrow.react-calendar__navigation__prev2-button');
    prev2.innerHTML = "<i class=\"fa fa-angle-double-left\" style=\"font-size: 25px\" aria-hidden=\"true\"></i>";
    var prev = calenderRef.current.querySelector('.react-calendar__navigation__arrow.react-calendar__navigation__prev-button');
    prev.innerHTML = "<i class=\"fa fa-chevron-left\" aria-hidden=\"true\"></i>";
    var next = calenderRef.current.querySelector('.react-calendar__navigation__arrow.react-calendar__navigation__next-button');
    next.innerHTML = "<i class=\"fa fa-chevron-right\" aria-hidden=\"true\"></i>";
    var next2 = calenderRef.current.querySelector('.react-calendar__navigation__arrow.react-calendar__navigation__next2-button');
    next2.innerHTML = "<i class=\"fa fa-angle-double-right\" style=\"font-size: 25px\" aria-hidden=\"true\"></i>";
  }
  var handleChange = function handleChange(value, closeCalendar) {
    if (closeCalendar) {
      setIsOpen(false);
    }
    if (onChange) {
      onChange(value);
    }
  };
  var handleFocus = function handleFocus(event) {
    if (onFocus) {
      onFocus(event);
    }
    setIsOpen(true);
  };
  var stopPropagation = function stopPropagation(event) {
    return event.stopPropagation();
  };
  var clear = function clear() {
    return handleChange(null, null);
  };
  function renderInputs() {
    var _concat = [].concat(value),
      valueFrom = _concat[0];
    var ariaLabelProps = {
      dayAriaLabel: dayAriaLabel,
      monthAriaLabel: monthAriaLabel,
      nativeInputAriaLabel: nativeInputAriaLabel,
      yearAriaLabel: yearAriaLabel
    };
    var placeholderProps = {
      dayPlaceholder: dayPlaceholder,
      monthPlaceholder: monthPlaceholder,
      yearPlaceholder: yearPlaceholder
    };
    return React.createElement(ThemeProvider, {
      theme: theme
    }, React.createElement(Component$p, {
      color: color
    }, React.createElement("div", {
      className: baseClassName + "__wrapper"
    }, React.createElement(DateInput, Object.assign({}, ariaLabelProps, placeholderProps, {
      autoFocus: autoFocus,
      className: baseClassName + "__inputGroup",
      disabled: disabled,
      format: format,
      isCalendarOpen: isOpen,
      locale: locale,
      maxDate: maxDate,
      maxDetail: maxDetail,
      minDate: minDate,
      name: name,
      onChange: handleChange,
      required: required,
      returnValue: returnValue,
      showLeadingZeros: showLeadingZeros,
      value: valueFrom
    })), clearIcon !== null && React.createElement("button", {
      "aria-label": clearAriaLabel,
      className: baseClassName + "__clear-button " + baseClassName + "__button",
      disabled: disabled,
      onClick: clear,
      onFocus: stopPropagation,
      type: 'button'
    }, clearIcon), calendarIcon !== null && !disableCalendar && React.createElement("button", {
      "aria-label": calendarAriaLabel,
      className: baseClassName + "__calendar-button " + baseClassName + "__button",
      disabled: disabled,
      onClick: function onClick() {
        return setIsOpen(!isOpen);
      },
      onFocus: stopPropagation,
      type: 'button'
    }, calendarIcon))));
  }
  function renderCalendar() {
    if (isOpen === null || disableCalendar) {
      return null;
    }
    var className = baseClassName + "__calendar";
    return React.createElement(CalenderFit, {
      color: color,
      ref: calenderRef,
      isOpen: isOpen
    }, React.createElement(Fit, null, React.createElement("div", {
      style: {
        maxHeight: '213px'
      },
      className: mergeClassNames(className, className + "--" + (isOpen ? 'open' : 'closed'))
    }, React.createElement(Calendar, Object.assign({
      className: calendarClassName,
      onChange: handleChange,
      value: value || null
    }, props)))));
  }
  return React.createElement("div", Object.assign({
    className: mergeClassNames(baseClassName, baseClassName + "--" + (isOpen ? 'open' : 'closed'), baseClassName + "--" + (disabled ? 'disabled' : 'enabled'), className)
  }, props, {
    onFocus: handleFocus,
    ref: wrapperRef
  }), renderInputs(), renderCalendar());
};
var iconProps = {
  xmlns: 'http://www.w3.org/2000/svg',
  width: 19,
  height: 19,
  viewBox: '0 0 19 19',
  stroke: 'black',
  strokeWidth: 2,
  version: '1.1'
};
var CalendarIcon = React.createElement("svg", Object.assign({}, iconProps, {
  className: baseClassName + "__calendar-button__icon " + baseClassName + "__button__icon",
  viewBox: '0 0 24 24',
  enableBackground: 'new 0 0 24 24'
}), React.createElement("path", {
  d: 'M2,19c0,1.7,1.3,3,3,3h14c1.7,0,3-1.3,3-3v-8H2V19z M19,4h-2V3c0-0.6-0.4-1-1-1s-1,0.4-1,1v1H9V3c0-0.6-0.4-1-1-1S7,2.4,7,3v1H5C3.3,4,2,5.3,2,7v2h20V7C22,5.3,20.7,4,19,4z'
}));
var ClearIcon = React.createElement("svg", Object.assign({}, iconProps, {
  className: baseClassName + "__clear-button__icon " + baseClassName + "__button__icon"
}), React.createElement("line", {
  x1: '4',
  x2: '15',
  y1: '4',
  y2: '15'
}), React.createElement("line", {
  x1: '15',
  x2: '4',
  y1: '4',
  y2: '15'
}));
DatePicker.defaultProps = {
  calendarIcon: CalendarIcon,
  clearIcon: ClearIcon,
  closeCalendar: true,
  color: 'dark',
  isOpen: null,
  returnValue: 'start',
  className: ""
};
function _templateObject$s() {
  var data = _taggedTemplateLiteralLoose(["\n  ", "\n  display: flex;\n  flex-direction: column;\n  position: relative;\n  cursor: pointer;\n"]);
  _templateObject$s = function _templateObject() {
    return data;
  };
  return data;
}
var Component$q = styled.div(_templateObject$s(), colorStyle);
var DropDownContext = createContext({});
var DropDownProvider = function DropDownProvider(props) {
  var _useState = useState(false),
    isOpen = _useState[0],
    setIsOpen = _useState[1];
  return React.createElement(DropDownContext.Provider, {
    value: {
      isOpen: [isOpen, setIsOpen]
    }
  }, props.children);
};
var DropDown = function DropDown(props) {
  var className = props.className,
    tag = props.tag,
    children = props.children,
    dropleft = props.dropleft,
    dropright = props.dropright,
    dropup = props.dropup,
    attributes = _objectWithoutPropertiesLoose(props, ["className", "tag", "children", "dropleft", "dropright", "dropup"]);
  var dropdownClasses = classNames("dropdown", className);
  var dropdownComponent = React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(DropDownProvider, null, React.createElement(Manager, null, React.createElement(Component$q, Object.assign({
    className: dropdownClasses,
    role: "dropdown",
    as: tag,
    dropleft: dropleft,
    dropright: dropright,
    dropup: dropup
  }, attributes), children))));
  return dropdownComponent;
};
DropDown.defaultProps = {
  dropleft: false,
  dropright: false,
  dropup: false,
  tag: "span"
};
function _templateObject12$1() {
  var data = _taggedTemplateLiteralLoose(["\n              margin-bottom: 5px;\n            "]);
  _templateObject12$1 = function _templateObject12() {
    return data;
  };
  return data;
}
function _templateObject11$1() {
  var data = _taggedTemplateLiteralLoose(["\n              margin-bottom: 0;\n            "]);
  _templateObject11$1 = function _templateObject11() {
    return data;
  };
  return data;
}
function _templateObject10$1() {
  var data = _taggedTemplateLiteralLoose(["\n              margin-bottom: 0;\n            "]);
  _templateObject10$1 = function _templateObject10() {
    return data;
  };
  return data;
}
function _templateObject9$1() {
  var data = _taggedTemplateLiteralLoose(["\n              margin-bottom: 0;\n            "]);
  _templateObject9$1 = function _templateObject9() {
    return data;
  };
  return data;
}
function _templateObject8$2() {
  var data = _taggedTemplateLiteralLoose(["\n              transform: rotate(45deg);\n            "]);
  _templateObject8$2 = function _templateObject8() {
    return data;
  };
  return data;
}
function _templateObject7$2() {
  var data = _taggedTemplateLiteralLoose(["\n              transform: rotate(-45deg);\n            "]);
  _templateObject7$2 = function _templateObject7() {
    return data;
  };
  return data;
}
function _templateObject6$2() {
  var data = _taggedTemplateLiteralLoose(["\n              transform: rotate(135deg);\n            "]);
  _templateObject6$2 = function _templateObject6() {
    return data;
  };
  return data;
}
function _templateObject5$3() {
  var data = _taggedTemplateLiteralLoose(["\n              transform: rotate(-135deg);\n            "]);
  _templateObject5$3 = function _templateObject5() {
    return data;
  };
  return data;
}
function _templateObject4$5() {
  var data = _taggedTemplateLiteralLoose(["\n  .pro-arrow-wrapper {\n    position: relative;\n    min-width: 10px;\n    min-height: 10px;\n    .pro-arrow {\n      top: 50%;\n      left: 50%;\n      transform: translate(-50%, -50%);\n      display: inline-block;\n      border-style: solid;\n      border-color: #fff;\n      border-width: 0 2px 2px 0;\n      padding: 2.5px;\n      vertical-align: middle;\n      transition: transform 0.3s;\n\n      ", "\n      ", "\n    }\n  }\n"]);
  _templateObject4$5 = function _templateObject4() {
    return data;
  };
  return data;
}
function _templateObject3$8() {
  var data = _taggedTemplateLiteralLoose(["\n          border-radius: 0px;\n        "]);
  _templateObject3$8 = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$b() {
  var data = _taggedTemplateLiteralLoose(["\n          border-radius: 30px;\n        "]);
  _templateObject2$b = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$t() {
  var data = _taggedTemplateLiteralLoose(["\n  padding: 0.8rem 2rem;\n  border: none;\n  ", "\n  ", "\n  :hover, :focus {\n    outline: none;\n    border: none; \n  }\n  ", ";\n  ", "\n"]);
  _templateObject$t = function _templateObject() {
    return data;
  };
  return data;
}
var Component$r = styled.button(_templateObject$t(), colorStyle, space, function (props) {
  return props.circle === true ? css(_templateObject2$b()) : css(_templateObject3$8());
}, variant({
  prop: "size",
  variants: {
    lg: {
      fontSize: "1.25rem",
      padding: "20px"
    },
    sm: {
      fontSize: "0.75rem",
      padding: "5px"
    }
  }
}));
var Caret = styled.span(_templateObject4$5(), function (props) {
  return props.dropup ? css(_templateObject5$3()) : props.dropleft ? css(_templateObject6$2()) : props.dropright ? css(_templateObject7$2()) : css(_templateObject8$2());
}, function (props) {
  return props.dropup ? css(_templateObject9$1()) : props.dropleft ? css(_templateObject10$1()) : props.dropright ? css(_templateObject11$1()) : css(_templateObject12$1());
});
var DropdownToggle = function DropdownToggle(props) {
  var _useContext = useContext(DropDownContext),
    isOpen = _useContext.isOpen;
  var isOpenValue = isOpen[0],
    setIsOpenValue = isOpen[1];
  var className = props.className,
    tag = props.tag,
    color = props.color,
    children = props.children,
    caret = props.caret,
    nav = props.nav,
    size = props.size,
    disabled = props.disabled,
    circle = props.circle,
    dropleft = props.dropleft,
    dropright = props.dropright,
    dropup = props.dropup,
    dropdown = props.dropdown,
    attributes = _objectWithoutPropertiesLoose(props, ["className", "tag", "color", "children", "caret", "nav", "size", "disabled", "circle", "dropleft", "dropright", "dropup", "dropdown"]);
  var dropdownToggleClasses = classNames({
    "nav-link": nav
  }, className);
  var toggleDropdown = function toggleDropdown(e) {
    var disabled = props.disabled,
      nav = props.nav,
      tag = props.tag,
      onClick = props.onClick;
    if (disabled) {
      return;
    }
    if (nav && !tag) {
      return;
    }
    if (onClick) {
      onClick(e);
    }
    setIsOpenValue(!isOpenValue);
  };
  var caretClasses = classNames(dropleft && "mr-3", dropright || dropdown || dropup ? "ml-3" : "", className);
  var CaretIcon = caret ? React.createElement(Caret, Object.assign({
    className: caretClasses,
    dropleft: dropleft,
    dropright: dropright,
    dropdown: dropdown,
    dropup: dropup
  }, attributes), React.createElement("span", {
    className: "pro-arrow-wrapper"
  }, React.createElement("span", {
    className: "pro-arrow"
  }))) : null;
  var dropdownToggleComponent = React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Reference, null, function (_ref) {
    var ref = _ref.ref;
    return React.createElement(Component$r, {
      className: dropdownToggleClasses,
      ref: ref,
      as: tag,
      colors: color,
      size: size,
      disabled: disabled,
      circle: circle,
      onClick: function onClick(e) {
        toggleDropdown(e);
      }
    }, dropleft && CaretIcon, children, dropright || dropdown || dropup ? CaretIcon : null);
  }));
  return dropdownToggleComponent;
};
DropdownToggle.defaultProps = {
  "aria-haspopup": true,
  color: "primary",
  tag: "button"
};
function _templateObject$u() {
  var data = _taggedTemplateLiteralLoose(["\n  display: flex;\n  flex-direction: column;\n  min-height: 20px;\n  min-width: 20px;\n  background-color: #fff;\n  border-radius: 0;\n  color: #232323;\n  padding: 0.3rem;\n  ", ";\n"]);
  _templateObject$u = function _templateObject() {
    return data;
  };
  return data;
}
var Component$s = styled("div")(_templateObject$u(), function (props) {
  return props.popperStyle;
});
var Null = function Null() {
  return null;
};
var modifiers = [{
  name: 'flip',
  enabled: false
}, {
  name: 'hide',
  enabled: false
}];
var popperModifiers = [].concat(modifiers, [{
  name: 'arrow',
  options: {
    padding: 5
  }
}, {
  name: 'offset',
  options: {
    offset: [0, 14]
  }
}]);
var animatedModifiers = [].concat(popperModifiers, [{
  name: 'computeStyles',
  options: {
    adaptive: false
  }
}, {
  name: 'computeStyles',
  options: {
    gpuAcceleration: false
  }
}]);
var DropDownMenu = function DropDownMenu(props) {
  var className = props.className,
    children = props.children,
    right = props.right,
    attrs = _objectWithoutPropertiesLoose(props, ["className", "tag", "color", "children", "right", "dropleft", "dropright", "top", "dropdown", "dropup"]);
  var _useContext = useContext(DropDownContext),
    isOpen = _useContext.isOpen;
  var isOpenValue = isOpen[0];
  useEffect(function () {
    var dropup = props.dropup,
      dropleft = props.dropleft,
      dropright = props.dropright;
    var position1 = dropup ? 'top' : dropright ? 'right' : dropleft ? 'left' : 'bottom';
    var position2 = right ? 'end' : 'start';
    attrs.placement = position1 + "-" + position2;
  });
  var dropDownMenuClasses = classNames('dropdown-menu', className);
  var dropDownMenuComponent = React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Transition, {
    items: isOpenValue,
    from: {
      opacity: 0,
      scale: 0.8,
      top: 0
    },
    enter: {
      opacity: 1,
      scale: 1,
      top: 0
    },
    leave: {
      opacity: 0,
      scale: 0.8,
      top: 0
    },
    config: {
      mass: 1,
      tension: 500,
      friction: 0,
      duration: 200
    }
  }, function (show) {
    return show ? function (_ref) {
      var scale = _ref.scale,
        opacity = _ref.opacity,
        topOffset = _ref.top;
      return React.createElement(Popper, {
        placement: attrs.placement,
        modifiers: animatedModifiers
      }, function (_ref2) {
        var ref = _ref2.ref,
          _ref2$style = _ref2.style,
          top = _ref2$style.top,
          left = _ref2$style.left,
          position = _ref2$style.position,
          placement = _ref2.placement;
        var newTop = typeof top === 'string' && top ? parseInt(top) : top;
        var topVal = (!isNaN(+newTop) ? +newTop : 0) + topOffset;
        return React.createElement(Component$s, {
          className: dropDownMenuClasses,
          ref: ref,
          style: {
            opacity: opacity,
            top: 0,
            left: 0,
            position: position,
            padding: '1em',
            transform: "translate3d(" + left + ", " + topVal + "px, 0) scale(" + scale + ")",
            transformOrigin: 'top center'
          },
          "data-placement": placement
        }, children);
      });
    } : Null;
  }));
  return dropDownMenuComponent;
};
DropDownMenu.defaultProps = {
  colors: 'primary',
  right: false,
  tag: 'span',
  dropleft: false,
  dropright: false,
  dropup: false
};
function omit(obj, omitKeys) {
  var result = {};
  Object.keys(obj).forEach(function (key) {
    if (omitKeys.indexOf(key) === -1) {
      result[key] = obj[key];
    }
  });
  return result;
}
var returnAttributes = function returnAttributes(attributes) {
  var newAttributesObject = Object.keys(attributes).reduce(function (previousValue, currentElement) {
    if (attributes[currentElement]) {
      previousValue[currentElement] = attributes[currentElement];
    }
    return previousValue;
  }, {});
  return newAttributesObject;
};
function _templateObject$v() {
  var data = _taggedTemplateLiteralLoose(["\n  color: #000 !important;\n"]);
  _templateObject$v = function _templateObject() {
    return data;
  };
  return data;
}
var Component$t = styled.div(_templateObject$v());
var DropDownItem = function DropDownItem(props) {
  var _useContext = useContext(DropDownContext),
    isOpen = _useContext.isOpen;
  var isOpenValue = isOpen[0],
    setIsOpenValue = isOpen[1];
  var _useState = useState(""),
    tagType = _useState[0],
    setTagType = _useState[1];
  var _omit = omit(props, ["toggle"]),
    disabled = _omit.disabled,
    className = _omit.className,
    header = _omit.header,
    divider = _omit.divider,
    onClick = _omit.onClick,
    href = _omit.href,
    active = _omit.active,
    attrs = _objectWithoutPropertiesLoose(_omit, ["disabled", "className", "header", "divider", "onClick", "toggle", "href", "active", "tag"]);
  var dropDownItemClasses = classNames({
    active: active,
    disabled: disabled,
    "dropdown-item": !divider && !header,
    "dropdown-header": header,
    "dropdown-divider": divider
  }, className);
  useEffect(function () {
    var header = props.header,
      divider = props.divider,
      href = props.href;
    var _tagType = header ? "h6" : divider ? "div" : href ? "a" : "button";
    setTagType(_tagType);
  });
  var toggleDropDown = function toggleDropDown(e) {
    if (disabled || header || divider) {
      return;
    }
    if (onClick) {
      onClick(e);
    }
    setIsOpenValue(!isOpenValue);
  };
  var getTabIndex = function getTabIndex() {
    if (disabled || header || divider) {
      return "-1";
    }
    return "0";
  };
  var tabIndex = getTabIndex();
  var dropDownItemComponent = React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$t, Object.assign({
    "data-test": "dropdown-item"
  }, attrs, {
    type: tagType,
    tabIndex: parseInt(tabIndex),
    className: dropDownItemClasses,
    onClick: toggleDropDown,
    href: href,
    disabled: disabled,
    header: header,
    divider: divider
  })));
  return dropDownItemComponent;
};
DropDownItem.defaultProps = {
  tag: "button",
  toggle: true
};
var TableHeader = function TableHeader(props) {
  var _classNames;
  var children = props.children,
    color = props.color,
    columns = props.columns,
    textWhite = props.textWhite,
    attributes = _objectWithoutPropertiesLoose(props, ["children", "color", "columns", "textWhite"]);
  var isTheadColor = color === "dark" || color === "light";
  var classes = classNames((_classNames = {
    "text-white": textWhite
  }, _classNames["thead-" + color] = color && isTheadColor, _classNames["" + color] = color && !isTheadColor, _classNames));
  var tableHeaderComponent = React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement("thead", Object.assign({
    "data-test": "table-head"
  }, attributes, {
    className: classes
  }), columns && React.createElement("tr", null, columns.map(function (col) {
    return React.createElement("th", {
      key: col.field,
      className: col.hasOwnProperty("minimal") ? "th-" + col.minimal : ""
    }, col.label);
  })), children));
  return tableHeaderComponent;
};
TableHeader.defaultProps = {
  textWhite: false
};
function _templateObject3$9() {
  var data = _taggedTemplateLiteralLoose([""]);
  _templateObject3$9 = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$c() {
  var data = _taggedTemplateLiteralLoose(["\n          display: block;\n          max-height: 200px;\n          overflow-y: auto;\n          -ms-overflow-style: -ms-autohiding-scrollbar;\n        "]);
  _templateObject2$c = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$w() {
  var data = _taggedTemplateLiteralLoose(["\n  table-layout: ", ";\n  ", ";\n"]);
  _templateObject$w = function _templateObject() {
    return data;
  };
  return data;
}
var Component$u = styled.div(_templateObject$w(), function (props) {
  return props.autoWidth ? "auto" : null;
}, function (props) {
  return props.scrollY ? css(_templateObject2$c()) : null;
});
var Table$2 = styled.table(_templateObject3$9());
var EditableTable = function EditableTable(props) {
  var autoWidth = props.autoWidth,
    bordered = props.bordered,
    borderless = props.borderless,
    btn = props.btn,
    data = props.data,
    columns = props.columns,
    className = props.className,
    dark = props.dark,
    fixed = props.fixed,
    hover = props.hover,
    maxHeight = props.maxHeight,
    responsive = props.responsive,
    responsiveLg = props.responsiveLg,
    responsiveMd = props.responsiveMd,
    responsiveSm = props.responsiveSm,
    responsiveXl = props.responsiveXl,
    scrollY = props.scrollY,
    small = props.small,
    striped = props.striped,
    wrapperClassName = props.wrapperClassName,
    attributes = _objectWithoutPropertiesLoose(props, ["autoWidth", "bordered", "borderless", "btn", "data", "columns", "className", "dark", "fixed", "hover", "maxHeight", "responsive", "responsiveLg", "responsiveMd", "responsiveSm", "responsiveXl", "scrollY", "small", "striped", "theadColor", "wrapperClassName"]);
  var _useState = useState(data),
    tableData = _useState[0];
  var _useState2 = useState(true),
    clicked = _useState2[0],
    setClicked = _useState2[1];
  var tableClasses = classNames('table', autoWidth && 'w-auto', bordered && 'table-bordered', borderless && 'table-borderless', btn && 'btn-table', fixed && 'table-fixed', hover && 'table-hover', striped && 'table-striped', small && 'table-sm', className);
  var wrapperClasses = classNames({
    'table-dark': dark,
    'table-responsive': responsive,
    'table-responsive-sm': responsiveSm,
    'table-responsive-md': responsiveMd,
    'table-responsive-lg': responsiveLg,
    'table-responsive-xl': responsiveXl,
    'table-wrapper-scroll-y': scrollY
  }, wrapperClassName);
  var wrapperStyles = {
    maxHeight: maxHeight
  };
  var add = function add() {
    var test = data[0].map(function (sample) {
      return 'Example';
    });
    tableData.push(test);
    setClicked(!clicked);
  };
  var del = function del(e) {
    e.target.parentNode.parentNode.remove();
  };
  var editableTableComponent = React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$u, {
    "data-test": "table",
    className: wrapperClasses,
    style: wrapperStyles,
    scrollY: scrollY,
    autoWidth: autoWidth
  }, React.createElement("div", {
    onClick: add
  }, React.createElement(Icon, {
    className: 'float-right m-2 text-success',
    fas: true,
    icon: 'plus'
  })), React.createElement(Table$2, Object.assign({}, attributes, {
    className: tableClasses
  }), React.createElement(TableHeader, {
    color: ''
  }, columns && React.createElement("tr", null, columns.map(function (column, index) {
    return React.createElement("th", {
      key: index
    }, column);
  }), React.createElement("th", null, "Remove"))), React.createElement(TableBody, {
    color: ''
  }, data && tableData.map(function (row, index) {
    return React.createElement("tr", {
      key: index
    }, row.map(function (data, i) {
      return React.createElement("td", {
        contentEditable: true,
        suppressContentEditableWarning: true,
        key: i
      }, data);
    }), React.createElement("td", {
      className: 'text-center'
    }, React.createElement("div", {
      onClick: del
    }, React.createElement(Badge, {
      flat: true,
      className: 'bg-danger font-weight-bold'
    }, "X"))));
  })))));
  return editableTableComponent;
};
function _templateObject$x() {
  var data = _taggedTemplateLiteralLoose(["\n  .uploader {\n    display: block;\n    clear: both;\n    margin: 0 auto;\n    width: 100%;\n    max-width: 600px;\n  }\n  .uploader label {\n    float: left;\n    clear: both;\n    width: 100%;\n    padding: 2rem 1.5rem;\n    text-align: center;\n    background: #fff;\n    border-radius: 7px;\n    border: 3px solid #eee;\n    -webkit-transition: all 0.2s ease;\n    transition: all 0.2s ease;\n    -webkit-user-select: none;\n    -moz-user-select: none;\n    -ms-user-select: none;\n    user-select: none;\n  }\n  .uploader label:hover {\n    border-color: #454cad;\n  }\n  .uploader label.hover {\n    border: 3px solid #454cad;\n    box-shadow: inset 0 0 0 6px #eee;\n  }\n  .uploader label.hover #start i.fa {\n    -webkit-transform: scale(0.8);\n    transform: scale(0.8);\n    opacity: 0.3;\n  }\n  .uploader #start {\n    float: left;\n    clear: both;\n    width: 100%;\n  }\n  .uploader #start.hidden {\n    display: none;\n  }\n  .uploader #start i.fa {\n    font-size: 50px;\n    margin-bottom: 1rem;\n    -webkit-transition: all 0.2s ease-in-out;\n    transition: all 0.2s ease-in-out;\n  }\n  .uploader #response {\n    float: left;\n    clear: both;\n    width: 100%;\n  }\n  .uploader #response.hidden {\n    display: none;\n  }\n  .uploader #response #messages {\n    margin-bottom: 0.5rem;\n  }\n  .uploader #file-image {\n    display: inline;\n    margin: 0 auto 0.5rem auto;\n    width: auto;\n    height: auto;\n    max-width: 180px;\n  }\n  .uploader #file-image.hidden {\n    display: none;\n  }\n  .uploader #notimage {\n    display: block;\n    float: left;\n    clear: both;\n    width: 100%;\n  }\n  .uploader #notimage.hidden {\n    display: none;\n  }\n  .uploader progress,\n  .uploader .progress {\n    display: inline;\n    clear: both;\n    margin: 0 auto;\n    width: 100%;\n    max-width: 180px;\n    height: 8px;\n    border: 0;\n    border-radius: 4px;\n    background-color: #eee;\n    overflow: hidden;\n  }\n  .uploader .progress[value]::-webkit-progress-bar {\n    border-radius: 4px;\n    background-color: #eee;\n  }\n  .uploader .progress[value]::-webkit-progress-value {\n    background: -webkit-gradient(\n      linear,\n      left top,\n      right top,\n      from(#393f90),\n      color-stop(50%, #454cad)\n    );\n    background: linear-gradient(to right, #393f90 0%, #454cad 50%);\n    border-radius: 4px;\n  }\n  .uploader .progress[value]::-moz-progress-bar {\n    background: linear-gradient(to right, #393f90 0%, #454cad 50%);\n    border-radius: 4px;\n  }\n  .uploader input[type=\"file\"] {\n    display: none;\n  }\n  .uploader div {\n    margin: 0 0 0.5rem 0;\n    color: #5f6982;\n  }\n  .uploader .btn {\n    display: inline-block;\n    margin: 0.5rem 0.5rem 1rem 0.5rem;\n    clear: both;\n    font-family: inherit;\n    font-weight: 700;\n    font-size: 14px;\n    text-decoration: none;\n    text-transform: initial;\n    border: none;\n    border-radius: 0.2rem;\n    outline: none;\n    padding: 0 1rem;\n    height: 36px;\n    line-height: 36px;\n    color: #fff;\n    -webkit-transition: all 0.2s ease-in-out;\n    transition: all 0.2s ease-in-out;\n    box-sizing: border-box;\n    background: #454cad;\n    border-color: #454cad;\n    cursor: pointer;\n  }\n"]);
  _templateObject$x = function _templateObject() {
    return data;
  };
  return data;
}
var Component$v = styled.div(_templateObject$x());
function _templateObject2$d() {
  var data = _taggedTemplateLiteralLoose(["\n  display: block;\n  width: 100%;\n  .progress-text {\n    display: flex;\n    width: 100%;\n    justify-content: center;\n    color: ", ";\n  }\n"]);
  _templateObject2$d = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$y() {
  var data = _taggedTemplateLiteralLoose(["\n  width: 100%;\n  background: ", ";\n  border-radius: 20px;\n  .progress-bar {\n    ", "\n    border-radius: 20px;\n  }\n"]);
  _templateObject$y = function _templateObject() {
    return data;
  };
  return data;
}
var Component$w = styled.div(_templateObject$y(), function (props) {
  return props.colors === "primary" ? "#3e98c728" : props.colors === "secondary" ? "#7356BF28" : props.colors === "danger" ? "#E1190028" : props.colors === "warning" ? "#66512C28" : props.colors === "info" ? "#17A2B828" : props.colors === "success" ? "#05944F28" : "#00000028";
}, colorStyle);
var Container$2 = styled.div(_templateObject2$d(), function (props) {
  return props.colors === "primary" ? "#3e98c7" : props.colors === "secondary" ? "#7356BF" : props.colors === "danger" ? "#E11900" : props.colors === "warning" ? "#66512C" : props.colors === "info" ? "#17A2B8" : props.colors === "success" ? "#05944F" : "#000000";
});
var Progress = function Progress(props) {
  var tag = props.tag,
    children = props.children,
    className = props.className,
    color = props.color,
    max = props.max,
    min = props.min,
    height = props.height,
    value = props.value,
    text = props.text,
    wrapperStyle = props.wrapperStyle,
    barClassName = props.barClassName,
    attributes = _objectWithoutPropertiesLoose(props, ["tag", "children", "id", "className", "color", "max", "min", "height", "value", "text", "wrapperStyle", "barClassName"]);
  var percent = (value - min) / (max - min) * 100;
  var progressClasses = classNames("progress", className);
  var progressBarClasses = classNames("progress-bar", barClassName);
  var computedHeight = height || children && "1rem";
  var computedWrapperStyle = _extends({}, wrapperStyle, {
    height: computedHeight
  });
  var progressComponent = React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Container$2, Object.assign({}, attributes), React.createElement("div", {
    className: "progress-text"
  }, text), React.createElement(Component$w, Object.assign({
    as: tag,
    "data-test": "progress"
  }, attributes, {
    className: progressClasses,
    style: computedWrapperStyle
  }), React.createElement("div", {
    className: progressBarClasses,
    style: {
      width: percent + "%",
      height: computedHeight
    },
    color: color,
    role: "progressbar",
    "aria-valuenow": value,
    "aria-valuemin": min,
    "aria-valuemax": max
  }, children))));
  return progressComponent;
};
Progress.defaultProps = {
  tag: "div",
  barClassName: "",
  className: "",
  colors: "dark",
  height: 10,
  max: 100,
  min: 0,
  value: 0,
  wrapperStyle: {},
  text: "",
  color: "",
  children: null,
  id: ""
};
var FileUploader = function FileUploader(props) {
  var fileSelect = useRef(null);
  var fileDrag = useRef(null);
  var submitButton = useRef(null);
  var messageBar = useRef(null);
  var start = useRef(null);
  var response = useRef(null);
  var notImage = useRef(null);
  var fileImage = useRef(null);
  var fileUploadForm = useRef(null);
  var _useState = useState(0),
    value = _useState[0],
    setValue = _useState[1];
  useEffect(function () {
    if (window.File && window.FileList && window.FileReader) {
      fileUpload();
    } else {
      fileDrag.current.style.display = "none";
    }
  });
  var fileUpload = function fileUpload() {
    fileSelect.current.addEventListener("change", fileSelectHandler, false);
    var xhr = new XMLHttpRequest();
    if (xhr.upload) {
      fileDrag.current.addEventListener("dragover", fileDragHover, false);
      fileDrag.current.addEventListener("dragleave", fileDragHover, false);
      fileDrag.current.addEventListener("drop", fileSelectHandler, false);
    }
  };
  var fileDragHover = function fileDragHover(e) {
    e.stopPropagation();
    e.preventDefault();
    fileDrag.current.className = e.type === "dragover" ? "hover" : "modal-body file-upload";
  };
  var fileSelectHandler = function fileSelectHandler(e) {
    var files = e.target.files || e.dataTransfer.files;
    fileDragHover(e);
    for (var i = 0, f; f = files[i]; i++) {
      parseFile(f);
      uploadFile(f);
    }
  };
  var output = function output(msg) {
    messageBar.current.innerHTML = msg;
  };
  var parseFile = function parseFile(file) {
    output("<strong>" + encodeURI(file.name) + "</strong>");
    var imageName = file.name;
    var isGood = /\.(?=gif|jpg|png|jpeg)/gi.test(imageName);
    if (isGood) {
      start.current.classList.add("hidden");
      response.current.classList.remove("hidden");
      notImage.current.classList.add("hidden");
      fileImage.current.classList.remove("hidden");
      fileImage.current.src = URL.createObjectURL(file);
    } else {
      fileImage.current.classList.add("hidden");
      notImage.current.classList.remove("hidden");
      start.current.classList.remove("hidden");
      response.current.classList.add("hidden");
      fileUploadForm.current.reset();
    }
  };
  var setProgressMaxValue = function setProgressMaxValue(e) {
    if (e.lengthComputable) {
      document.getElementById("file-progress").max = e.total;
    }
  };
  var updateFileProgress = function updateFileProgress(e) {
    if (e.lengthComputable) {
      setValue(e.loaded * (100 / e.total));
    }
  };
  var uploadFile = function uploadFile(file) {
    var xhr = new XMLHttpRequest(),
      fileSizeLimit = 1024;
    if (xhr.upload) {
      if (file.size <= fileSizeLimit * 1024 * 1024) {
        document.getElementById("file-progress").style.display = "inline";
        xhr.upload.addEventListener("loadstart", setProgressMaxValue, false);
        xhr.upload.addEventListener("progress", updateFileProgress, false);
        xhr.onreadystatechange = function (e) {};
        xhr.open("POST", fileUploadForm.current.action, true);
        xhr.setRequestHeader("X-File-Name", file.name);
        xhr.setRequestHeader("X-File-Size", file.size);
        xhr.setRequestHeader("Content-Type", "multipart/form-data");
        xhr.send(file);
      } else {
        output("Please upload a smaller file (< " + fileSizeLimit + " MB).");
      }
    }
  };
  var attributes = _extends({}, props);
  var fileUploadComponent = React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$v, Object.assign({}, attributes), React.createElement("form", {
    className: "uploader",
    ref: fileUploadForm
  }, React.createElement("input", {
    id: "file-upload",
    ref: fileSelect,
    type: "file",
    name: "fileUpload",
    accept: "image/*"
  }), React.createElement("label", {
    htmlFor: "file-upload",
    id: "file-drag",
    ref: fileDrag
  }, React.createElement("img", {
    id: "file-image",
    ref: fileImage,
    src: "#",
    alt: "Preview",
    className: "hidden"
  }), React.createElement("div", {
    id: "start",
    ref: start
  }, React.createElement("i", {
    className: "fa fa-download",
    "aria-hidden": "true"
  }), React.createElement("div", null, "Select a file or drag here"), React.createElement("div", {
    id: "notimage",
    ref: notImage,
    className: "hidden"
  }, "Please select an image"), React.createElement("span", {
    id: "file-upload-btn",
    ref: submitButton,
    className: "btn btn-primary"
  }, "Select a file")), React.createElement("div", {
    id: "response",
    ref: response,
    className: "hidden"
  }, React.createElement("div", {
    id: "messages",
    ref: messageBar
  }), React.createElement(Progress, {
    id: "file-progress",
    text: value + "%",
    value: value
  }))))));
  return fileUploadComponent;
};
function _templateObject$z() {
  var data = _taggedTemplateLiteralLoose([""]);
  _templateObject$z = function _templateObject() {
    return data;
  };
  return data;
}
var Component$x = styled.footer(_templateObject$z());
var Footer = function Footer(props) {
  var color = props.color,
    children = props.children,
    className = props.className,
    tag = props.tag,
    attributes = _objectWithoutPropertiesLoose(props, ["color", "children", "className", "tag"]);
  var classes = classNames("page-footer", color && color, className);
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$x, Object.assign({
    as: tag,
    "data-test": "footer"
  }, attributes, {
    className: classes
  }), children));
};
Footer.defaultProps = {
  tag: "footer"
};
function _templateObject$A() {
  var data = _taggedTemplateLiteralLoose(["\n\tdisplay:block;\n\ta {\n\t\tcolor:black;\n\t}\n\n"]);
  _templateObject$A = function _templateObject() {
    return data;
  };
  return data;
}
var Component$y = styled.li(_templateObject$A());
var FooterLink = function FooterLink(props) {
  var children = props.children,
    className = props.className,
    href = props.href,
    tag = props.tag,
    attributes = _objectWithoutPropertiesLoose(props, ["children", "className", "href", "tag"]);
  var classes = classNames(className);
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$y, Object.assign({
    as: tag,
    "data-test": "footer-link"
  }, attributes, {
    className: classes
  }), React.createElement("a", {
    href: href
  }, children)));
};
FooterLink.defaultProps = {
  tag: "footerLink"
};
var HamburgerToggler = function HamburgerToggler(props) {
  var id = props.id,
    color = props.color,
    className = props.className,
    isOpen = props.isOpen,
    onClick = props.onClick,
    attributes = _objectWithoutPropertiesLoose(props, ["id", "color", "className", "isOpen", "onClick"]);
  var classes = classNames("hamburger-button__button", className);
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement("div", Object.assign({}, attributes), React.createElement("input", {
    "data-test": "hamburger-toggler",
    type: "checkbox",
    defaultChecked: isOpen || false,
    onChange: function onChange() {
      return onClick();
    },
    className: "hamburger-button__checkbox",
    id: id
  }), React.createElement("label", {
    id: "nav-icon1",
    className: classes,
    htmlFor: id
  }, React.createElement("span", {
    style: {
      background: color
    }
  }), React.createElement("span", {
    style: {
      background: color
    }
  }), React.createElement("span", {
    style: {
      background: color
    }
  }))));
};
HamburgerToggler.defaultProps = {
  className: "",
  color: "",
  id: "",
  onClick: function onClick() {},
  isOpen: false
};
function _templateObject$B() {
  var data = _taggedTemplateLiteralLoose(["\n  position: relative;\n  iframe,\n  embed,\n  object,\n  video {\n    margin: 0 auto;\n    top: 0;\n    bottom: 0;\n    left: 0;\n    border: 0;\n  }\n"]);
  _templateObject$B = function _templateObject() {
    return data;
  };
  return data;
}
var Component$z = styled.div(_templateObject$B());
var Iframe = function Iframe(props) {
  var _useState = useState(""),
    stateWidth = _useState[0],
    setStateWidth = _useState[1];
  var _useState2 = useState(""),
    stateHeight = _useState2[0],
    setStateHeight = _useState2[1];
  var _useState3 = useState(""),
    stateRatio = _useState3[0],
    setRatio = _useState3[1];
  useEffect(function () {
    var ratio = props.ratio;
    var width = props.width,
      height = props.height;
    var ratioNumber = 9 / 16;
    if (ratio) {
      var newRatio = ratio.split("by")[0] / ratio.split("by")[1];
      if (typeof ratioNumber === "number") {
        ratioNumber = newRatio;
      }
    }
    if (width && height) {
      return;
    }
    if (width) {
      height = width * ratioNumber;
    } else if (height) {
      width = height * (1 / ratioNumber);
    }
    setStateHeight(height);
    setStateWidth(width);
    setRatio(ratio);
  }, [setStateHeight, setStateWidth, setRatio, props]);
  var allowFullScreen = props.allowFullScreen,
    className = props.className,
    id = props.id,
    name = props.name,
    onMouseOver = props.onMouseOver,
    onMouseOut = props.onMouseOut,
    onLoad = props.onLoad,
    sandbox = props.sandbox,
    src = props.src,
    style = props.style,
    _props$title = props.title,
    title = _props$title === void 0 ? "" : _props$title,
    ratio = props.ratio,
    height = props.height,
    width = props.width,
    attributes = _objectWithoutPropertiesLoose(props, ["allowFullScreen", "className", "id", "name", "iframe", "onMouseOver", "onMouseOut", "onLoad", "sandbox", "src", "style", "title", "ratio", "height", "width"]);
  var classes = classNames("embed-responsive-item", className);
  var wrapperClasses = classNames(!(height || width) && "embed-responsive", ratio ? "embed-responsive-" + ratio : "embed-responsive-16by9");
  var iframeAttributes = {
    src: src,
    id: id || false,
    frameBorder: "0",
    target: "_parent",
    ratio: stateRatio || "4by3",
    allowFullScreen: allowFullScreen || true,
    height: stateHeight || "100%",
    name: name || undefined,
    width: stateWidth || "100%",
    onLoad: onLoad || undefined,
    onMouseOver: onMouseOver || undefined,
    onMouseOut: onMouseOut || undefined,
    sandbox: sandbox || undefined,
    style: style || undefined
  };
  var returnAttributes = function returnAttributes(attributes) {
    var newAttributesObject = Object.keys(attributes).reduce(function (previousValue, currentElement) {
      if (attributes[currentElement]) {
        previousValue[currentElement] = attributes[currentElement];
      }
      return previousValue;
    }, {});
    return newAttributesObject;
  };
  iframeAttributes = returnAttributes(iframeAttributes);
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$z, Object.assign({
    "data-test": "iframe",
    className: wrapperClasses
  }, attributes), React.createElement("iframe", Object.assign({}, iframeAttributes, props, {
    title: title,
    className: classes,
    ratio: ratio
  }))));
};
function _templateObject$C() {
  var data = _taggedTemplateLiteralLoose(["\n      .input-flex-fill {\n        flex-grow: 1\n      }\n      .input-background {\n        background-color: #f4f4f4;\n      }\n      .md-form.input-group label {\n        top: 0;\n        margin-bottom: 0;\n      }\n      .md-form.input-group .input-group-text {\n        background-color: #e0e0e0;\n      }\n      .md-form.input-group .input-group-text.md-addon {\n        font-weight: 500;\n        background-color: transparent;\n        border: none;\n      }\n      .md-form.input-group .form-control {\n        padding: 0.375rem 0.75rem;\n        margin: 0;\n      }\n      .md-form {\n        position: relative;\n        margin-top: 1.5rem;\n        margin-bottom: 1.5rem;\n      }\n      .md-form input:not([type]),\n      .md-form input[type=\"text\"]:not(.browser-default),\n      .md-form input[type=\"password\"]:not(.browser-default),\n      .md-form input[type=\"email\"]:not(.browser-default),\n      .md-form input[type=\"url\"]:not(.browser-default),\n      .md-form input[type=\"time\"]:not(.browser-default),\n      .md-form input[type=\"date\"]:not(.browser-default),\n      .md-form input[type=\"datetime\"]:not(.browser-default),\n      .md-form input[type=\"datetime-local\"]:not(.browser-default),\n      .md-form input[type=\"tel\"]:not(.browser-default),\n      .md-form input[type=\"number\"]:not(.browser-default),\n      .md-form input[type=\"search\"]:not(.browser-default),\n      .md-form input[type=\"phone\"]:not(.browser-default),\n      .md-form input[type=\"search-md\"],\n      .md-form textarea.md-textarea {\n        box-sizing: content-box;\n        background-color: transparent;\n        border: none;\n        border-bottom: 1px solid #ced4da;\n        border-radius: 0;\n        outline: none;\n        box-shadow: none;\n        transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;\n      }\n      .md-form input:not([type]):focus:not([readonly]),\n      .md-form input[type=\"text\"]:not(.browser-default):focus:not([readonly]),\n      .md-form\n        input[type=\"password\"]:not(.browser-default):focus:not([readonly]),\n      .md-form input[type=\"email\"]:not(.browser-default):focus:not([readonly]),\n      .md-form input[type=\"url\"]:not(.browser-default):focus:not([readonly]),\n      .md-form input[type=\"time\"]:not(.browser-default):focus:not([readonly]),\n      .md-form input[type=\"date\"]:not(.browser-default):focus:not([readonly]),\n      .md-form\n        input[type=\"datetime\"]:not(.browser-default):focus:not([readonly]),\n      .md-form\n        input[type=\"datetime-local\"]:not(.browser-default):focus:not([readonly]),\n      .md-form input[type=\"tel\"]:not(.browser-default):focus:not([readonly]),\n      .md-form input[type=\"number\"]:not(.browser-default):focus:not([readonly]),\n      .md-form input[type=\"search\"]:not(.browser-default):focus:not([readonly]),\n      .md-form input[type=\"phone\"]:not(.browser-default):focus:not([readonly]),\n      .md-form input[type=\"search-md\"]:focus:not([readonly]),\n      .md-form textarea.md-textarea:focus:not([readonly]) {\n        border-bottom: 1px solid #4285f4;\n        box-shadow: 0 1px 0 0 #4285f4;\n      }\n      .md-form input:not([type]):focus:not([readonly]) + label,\n      .md-form\n        input[type=\"text\"]:not(.browser-default):focus:not([readonly])\n        + label,\n      .md-form\n        input[type=\"password\"]:not(.browser-default):focus:not([readonly])\n        + label,\n      .md-form\n        input[type=\"email\"]:not(.browser-default):focus:not([readonly])\n        + label,\n      .md-form\n        input[type=\"url\"]:not(.browser-default):focus:not([readonly])\n        + label,\n      .md-form\n        input[type=\"time\"]:not(.browser-default):focus:not([readonly])\n        + label,\n      .md-form\n        input[type=\"date\"]:not(.browser-default):focus:not([readonly])\n        + label,\n      .md-form\n        input[type=\"datetime\"]:not(.browser-default):focus:not([readonly])\n        + label,\n      .md-form\n        input[type=\"datetime-local\"]:not(.browser-default):focus:not([readonly])\n        + label,\n      .md-form\n        input[type=\"tel\"]:not(.browser-default):focus:not([readonly])\n        + label,\n      .md-form\n        input[type=\"number\"]:not(.browser-default):focus:not([readonly])\n        + label,\n      .md-form\n        input[type=\"search\"]:not(.browser-default):focus:not([readonly])\n        + label,\n      .md-form\n        input[type=\"phone\"]:not(.browser-default):focus:not([readonly])\n        + label,\n      .md-form input[type=\"search-md\"]:focus:not([readonly]) + label,\n      .md-form textarea.md-textarea:focus:not([readonly]) + label {\n        color: #4285f4;\n      }\n      .md-form input:not([type]) + label::after,\n      .md-form input[type=\"text\"]:not(.browser-default) + label::after,\n      .md-form input[type=\"password\"]:not(.browser-default) + label::after,\n      .md-form input[type=\"email\"]:not(.browser-default) + label::after,\n      .md-form input[type=\"url\"]:not(.browser-default) + label::after,\n      .md-form input[type=\"time\"]:not(.browser-default) + label::after,\n      .md-form input[type=\"date\"]:not(.browser-default) + label::after,\n      .md-form input[type=\"datetime\"]:not(.browser-default) + label::after,\n      .md-form\n        input[type=\"datetime-local\"]:not(.browser-default)\n        + label::after,\n      .md-form input[type=\"tel\"]:not(.browser-default) + label::after,\n      .md-form input[type=\"number\"]:not(.browser-default) + label::after,\n      .md-form input[type=\"search\"]:not(.browser-default) + label::after,\n      .md-form input[type=\"phone\"]:not(.browser-default) + label::after,\n      .md-form input[type=\"search-md\"] + label::after,\n      .md-form textarea.md-textarea + label::after {\n        position: absolute;\n        top: 65px;\n        display: block;\n        content: \"\";\n        opacity: 0;\n        transition: 0.2s opacity ease-out, 0.2s color ease-out;\n      }\n      .md-form input:not([type]).valid,\n      .md-form input:not([type]):focus.valid,\n      .md-form input[type=\"text\"]:not(.browser-default).valid,\n      .md-form input[type=\"text\"]:not(.browser-default):focus.valid,\n      .md-form input[type=\"password\"]:not(.browser-default).valid,\n      .md-form input[type=\"password\"]:not(.browser-default):focus.valid,\n      .md-form input[type=\"email\"]:not(.browser-default).valid,\n      .md-form input[type=\"email\"]:not(.browser-default):focus.valid,\n      .md-form input[type=\"url\"]:not(.browser-default).valid,\n      .md-form input[type=\"url\"]:not(.browser-default):focus.valid,\n      .md-form input[type=\"time\"]:not(.browser-default).valid,\n      .md-form input[type=\"time\"]:not(.browser-default):focus.valid,\n      .md-form input[type=\"date\"]:not(.browser-default).valid,\n      .md-form input[type=\"date\"]:not(.browser-default):focus.valid,\n      .md-form input[type=\"datetime\"]:not(.browser-default).valid,\n      .md-form input[type=\"datetime\"]:not(.browser-default):focus.valid,\n      .md-form input[type=\"datetime-local\"]:not(.browser-default).valid,\n      .md-form input[type=\"datetime-local\"]:not(.browser-default):focus.valid,\n      .md-form input[type=\"tel\"]:not(.browser-default).valid,\n      .md-form input[type=\"tel\"]:not(.browser-default):focus.valid,\n      .md-form input[type=\"number\"]:not(.browser-default).valid,\n      .md-form input[type=\"number\"]:not(.browser-default):focus.valid,\n      .md-form input[type=\"search\"]:not(.browser-default).valid,\n      .md-form input[type=\"search\"]:not(.browser-default):focus.valid,\n      .md-form input[type=\"phone\"]:not(.browser-default).valid,\n      .md-form input[type=\"phone\"]:not(.browser-default):focus.valid,\n      .md-form input[type=\"search-md\"].valid,\n      .md-form input[type=\"search-md\"]:focus.valid,\n      .md-form textarea.md-textarea.valid,\n      .md-form textarea.md-textarea:focus.valid {\n        border-bottom: 1px solid #00c851;\n        box-shadow: 0 1px 0 0 #00c851;\n      }\n      .md-form input:not([type]).valid + label:after,\n      .md-form input:not([type]):focus.valid + label:after,\n      .md-form input[type=\"text\"]:not(.browser-default).valid + label:after,\n      .md-form\n        input[type=\"text\"]:not(.browser-default):focus.valid\n        + label:after,\n      .md-form input[type=\"password\"]:not(.browser-default).valid + label:after,\n      .md-form\n        input[type=\"password\"]:not(.browser-default):focus.valid\n        + label:after,\n      .md-form input[type=\"email\"]:not(.browser-default).valid + label:after,\n      .md-form\n        input[type=\"email\"]:not(.browser-default):focus.valid\n        + label:after,\n      .md-form input[type=\"url\"]:not(.browser-default).valid + label:after,\n      .md-form\n        input[type=\"url\"]:not(.browser-default):focus.valid\n        + label:after,\n      .md-form input[type=\"time\"]:not(.browser-default).valid + label:after,\n      .md-form\n        input[type=\"time\"]:not(.browser-default):focus.valid\n        + label:after,\n      .md-form input[type=\"date\"]:not(.browser-default).valid + label:after,\n      .md-form\n        input[type=\"date\"]:not(.browser-default):focus.valid\n        + label:after,\n      .md-form input[type=\"datetime\"]:not(.browser-default).valid + label:after,\n      .md-form\n        input[type=\"datetime\"]:not(.browser-default):focus.valid\n        + label:after,\n      .md-form\n        input[type=\"datetime-local\"]:not(.browser-default).valid\n        + label:after,\n      .md-form\n        input[type=\"datetime-local\"]:not(.browser-default):focus.valid\n        + label:after,\n      .md-form input[type=\"tel\"]:not(.browser-default).valid + label:after,\n      .md-form\n        input[type=\"tel\"]:not(.browser-default):focus.valid\n        + label:after,\n      .md-form input[type=\"number\"]:not(.browser-default).valid + label:after,\n      .md-form\n        input[type=\"number\"]:not(.browser-default):focus.valid\n        + label:after,\n      .md-form input[type=\"search\"]:not(.browser-default).valid + label:after,\n      .md-form\n        input[type=\"search\"]:not(.browser-default):focus.valid\n        + label:after,\n      .md-form input[type=\"phone\"]:not(.browser-default).valid + label:after,\n      .md-form\n        input[type=\"phone\"]:not(.browser-default):focus.valid\n        + label:after,\n      .md-form input[type=\"search-md\"].valid + label:after,\n      .md-form input[type=\"search-md\"]:focus.valid + label:after,\n      .md-form textarea.md-textarea.valid + label:after,\n      .md-form textarea.md-textarea:focus.valid + label:after {\n        color: #00c851;\n        content: attr(data-success);\n        opacity: 1;\n      }\n      .md-form input:not([type]).invalid,\n      .md-form input:not([type]):focus.invalid,\n      .md-form input[type=\"text\"]:not(.browser-default).invalid,\n      .md-form input[type=\"text\"]:not(.browser-default):focus.invalid,\n      .md-form input[type=\"password\"]:not(.browser-default).invalid,\n      .md-form input[type=\"password\"]:not(.browser-default):focus.invalid,\n      .md-form input[type=\"email\"]:not(.browser-default).invalid,\n      .md-form input[type=\"email\"]:not(.browser-default):focus.invalid,\n      .md-form input[type=\"url\"]:not(.browser-default).invalid,\n      .md-form input[type=\"url\"]:not(.browser-default):focus.invalid,\n      .md-form input[type=\"time\"]:not(.browser-default).invalid,\n      .md-form input[type=\"time\"]:not(.browser-default):focus.invalid,\n      .md-form input[type=\"date\"]:not(.browser-default).invalid,\n      .md-form input[type=\"date\"]:not(.browser-default):focus.invalid,\n      .md-form input[type=\"datetime\"]:not(.browser-default).invalid,\n      .md-form input[type=\"datetime\"]:not(.browser-default):focus.invalid,\n      .md-form input[type=\"datetime-local\"]:not(.browser-default).invalid,\n      .md-form input[type=\"datetime-local\"]:not(.browser-default):focus.invalid,\n      .md-form input[type=\"tel\"]:not(.browser-default).invalid,\n      .md-form input[type=\"tel\"]:not(.browser-default):focus.invalid,\n      .md-form input[type=\"number\"]:not(.browser-default).invalid,\n      .md-form input[type=\"number\"]:not(.browser-default):focus.invalid,\n      .md-form input[type=\"search\"]:not(.browser-default).invalid,\n      .md-form input[type=\"search\"]:not(.browser-default):focus.invalid,\n      .md-form input[type=\"phone\"]:not(.browser-default).invalid,\n      .md-form input[type=\"phone\"]:not(.browser-default):focus.invalid,\n      .md-form input[type=\"search-md\"].invalid,\n      .md-form input[type=\"search-md\"]:focus.invalid,\n      .md-form textarea.md-textarea.invalid,\n      .md-form textarea.md-textarea:focus.invalid {\n        border-bottom: 1px solid #f44336;\n        box-shadow: 0 1px 0 0 #f44336;\n      }\n      .md-form input:not([type]).invalid + label:after,\n      .md-form input:not([type]):focus.invalid + label:after,\n      .md-form input[type=\"text\"]:not(.browser-default).invalid + label:after,\n      .md-form\n        input[type=\"text\"]:not(.browser-default):focus.invalid\n        + label:after,\n      .md-form\n        input[type=\"password\"]:not(.browser-default).invalid\n        + label:after,\n      .md-form\n        input[type=\"password\"]:not(.browser-default):focus.invalid\n        + label:after,\n      .md-form input[type=\"email\"]:not(.browser-default).invalid + label:after,\n      .md-form\n        input[type=\"email\"]:not(.browser-default):focus.invalid\n        + label:after,\n      .md-form input[type=\"url\"]:not(.browser-default).invalid + label:after,\n      .md-form\n        input[type=\"url\"]:not(.browser-default):focus.invalid\n        + label:after,\n      .md-form input[type=\"time\"]:not(.browser-default).invalid + label:after,\n      .md-form\n        input[type=\"time\"]:not(.browser-default):focus.invalid\n        + label:after,\n      .md-form input[type=\"date\"]:not(.browser-default).invalid + label:after,\n      .md-form\n        input[type=\"date\"]:not(.browser-default):focus.invalid\n        + label:after,\n      .md-form\n        input[type=\"datetime\"]:not(.browser-default).invalid\n        + label:after,\n      .md-form\n        input[type=\"datetime\"]:not(.browser-default):focus.invalid\n        + label:after,\n      .md-form\n        input[type=\"datetime-local\"]:not(.browser-default).invalid\n        + label:after,\n      .md-form\n        input[type=\"datetime-local\"]:not(.browser-default):focus.invalid\n        + label:after,\n      .md-form input[type=\"tel\"]:not(.browser-default).invalid + label:after,\n      .md-form\n        input[type=\"tel\"]:not(.browser-default):focus.invalid\n        + label:after,\n      .md-form input[type=\"number\"]:not(.browser-default).invalid + label:after,\n      .md-form\n        input[type=\"number\"]:not(.browser-default):focus.invalid\n        + label:after,\n      .md-form input[type=\"search\"]:not(.browser-default).invalid + label:after,\n      .md-form\n        input[type=\"search\"]:not(.browser-default):focus.invalid\n        + label:after,\n      .md-form input[type=\"phone\"]:not(.browser-default).invalid + label:after,\n      .md-form\n        input[type=\"phone\"]:not(.browser-default):focus.invalid\n        + label:after,\n      .md-form input[type=\"search-md\"].invalid + label:after,\n      .md-form input[type=\"search-md\"]:focus.invalid + label:after,\n      .md-form textarea.md-textarea.invalid + label:after,\n      .md-form textarea.md-textarea:focus.invalid + label:after {\n        color: #f44336;\n        content: attr(data-error);\n        opacity: 1;\n      }\n      .md-form input:not([type]).form-control.valid + label:after,\n      .md-form input:not([type]).form-control:focus.valid + label:after,\n      .md-form\n        input[type=\"text\"]:not(.browser-default).form-control.valid\n        + label:after,\n      .md-form\n        input[type=\"text\"]:not(.browser-default).form-control:focus.valid\n        + label:after,\n      .md-form\n        input[type=\"password\"]:not(.browser-default).form-control.valid\n        + label:after,\n      .md-form\n        input[type=\"password\"]:not(.browser-default).form-control:focus.valid\n        + label:after,\n      .md-form\n        input[type=\"email\"]:not(.browser-default).form-control.valid\n        + label:after,\n      .md-form\n        input[type=\"email\"]:not(.browser-default).form-control:focus.valid\n        + label:after,\n      .md-form\n        input[type=\"url\"]:not(.browser-default).form-control.valid\n        + label:after,\n      .md-form\n        input[type=\"url\"]:not(.browser-default).form-control:focus.valid\n        + label:after,\n      .md-form\n        input[type=\"time\"]:not(.browser-default).form-control.valid\n        + label:after,\n      .md-form\n        input[type=\"time\"]:not(.browser-default).form-control:focus.valid\n        + label:after,\n      .md-form\n        input[type=\"date\"]:not(.browser-default).form-control.valid\n        + label:after,\n      .md-form\n        input[type=\"date\"]:not(.browser-default).form-control:focus.valid\n        + label:after,\n      .md-form\n        input[type=\"datetime\"]:not(.browser-default).form-control.valid\n        + label:after,\n      .md-form\n        input[type=\"datetime\"]:not(.browser-default).form-control:focus.valid\n        + label:after,\n      .md-form\n        input[type=\"datetime-local\"]:not(.browser-default).form-control.valid\n        + label:after,\n      .md-form\n        input[type=\"datetime-local\"]:not(.browser-default).form-control:focus.valid\n        + label:after,\n      .md-form\n        input[type=\"tel\"]:not(.browser-default).form-control.valid\n        + label:after,\n      .md-form\n        input[type=\"tel\"]:not(.browser-default).form-control:focus.valid\n        + label:after,\n      .md-form\n        input[type=\"number\"]:not(.browser-default).form-control.valid\n        + label:after,\n      .md-form\n        input[type=\"number\"]:not(.browser-default).form-control:focus.valid\n        + label:after,\n      .md-form\n        input[type=\"search\"]:not(.browser-default).form-control.valid\n        + label:after,\n      .md-form\n        input[type=\"search\"]:not(.browser-default).form-control:focus.valid\n        + label:after,\n      .md-form\n        input[type=\"phone\"]:not(.browser-default).form-control.valid\n        + label:after,\n      .md-form\n        input[type=\"phone\"]:not(.browser-default).form-control:focus.valid\n        + label:after,\n      .md-form input[type=\"search-md\"].form-control.valid + label:after,\n      .md-form input[type=\"search-md\"].form-control:focus.valid + label:after,\n      .md-form textarea.md-textarea.form-control.valid + label:after,\n      .md-form textarea.md-textarea.form-control:focus.valid + label:after {\n        top: 4.1rem;\n      }\n      .md-form input:not([type]).form-control.invalid + label:after,\n      .md-form input:not([type]).form-control:focus.invalid + label:after,\n      .md-form\n        input[type=\"text\"]:not(.browser-default).form-control.invalid\n        + label:after,\n      .md-form\n        input[type=\"text\"]:not(.browser-default).form-control:focus.invalid\n        + label:after,\n      .md-form\n        input[type=\"password\"]:not(.browser-default).form-control.invalid\n        + label:after,\n      .md-form\n        input[type=\"password\"]:not(.browser-default).form-control:focus.invalid\n        + label:after,\n      .md-form\n        input[type=\"email\"]:not(.browser-default).form-control.invalid\n        + label:after,\n      .md-form\n        input[type=\"email\"]:not(.browser-default).form-control:focus.invalid\n        + label:after,\n      .md-form\n        input[type=\"url\"]:not(.browser-default).form-control.invalid\n        + label:after,\n      .md-form\n        input[type=\"url\"]:not(.browser-default).form-control:focus.invalid\n        + label:after,\n      .md-form\n        input[type=\"time\"]:not(.browser-default).form-control.invalid\n        + label:after,\n      .md-form\n        input[type=\"time\"]:not(.browser-default).form-control:focus.invalid\n        + label:after,\n      .md-form\n        input[type=\"date\"]:not(.browser-default).form-control.invalid\n        + label:after,\n      .md-form\n        input[type=\"date\"]:not(.browser-default).form-control:focus.invalid\n        + label:after,\n      .md-form\n        input[type=\"datetime\"]:not(.browser-default).form-control.invalid\n        + label:after,\n      .md-form\n        input[type=\"datetime\"]:not(.browser-default).form-control:focus.invalid\n        + label:after,\n      .md-form\n        input[type=\"datetime-local\"]:not(.browser-default).form-control.invalid\n        + label:after,\n      .md-form\n        input[type=\"datetime-local\"]:not(.browser-default).form-control:focus.invalid\n        + label:after,\n      .md-form\n        input[type=\"tel\"]:not(.browser-default).form-control.invalid\n        + label:after,\n      .md-form\n        input[type=\"tel\"]:not(.browser-default).form-control:focus.invalid\n        + label:after,\n      .md-form\n        input[type=\"number\"]:not(.browser-default).form-control.invalid\n        + label:after,\n      .md-form\n        input[type=\"number\"]:not(.browser-default).form-control:focus.invalid\n        + label:after,\n      .md-form\n        input[type=\"search\"]:not(.browser-default).form-control.invalid\n        + label:after,\n      .md-form\n        input[type=\"search\"]:not(.browser-default).form-control:focus.invalid\n        + label:after,\n      .md-form\n        input[type=\"phone\"]:not(.browser-default).form-control.invalid\n        + label:after,\n      .md-form\n        input[type=\"phone\"]:not(.browser-default).form-control:focus.invalid\n        + label:after,\n      .md-form input[type=\"search-md\"].form-control.invalid + label:after,\n      .md-form input[type=\"search-md\"].form-control:focus.invalid + label:after,\n      .md-form textarea.md-textarea.form-control.invalid + label:after,\n      .md-form textarea.md-textarea.form-control:focus.invalid + label:after {\n        top: 4rem;\n      }\n      .md-form input:not([type]).form-control-lg.valid + label:after,\n      .md-form input:not([type]).form-control-lg:focus.valid + label:after,\n      .md-form\n        input[type=\"text\"]:not(.browser-default).form-control-lg.valid\n        + label:after,\n      .md-form\n        input[type=\"text\"]:not(.browser-default).form-control-lg:focus.valid\n        + label:after,\n      .md-form\n        input[type=\"password\"]:not(.browser-default).form-control-lg.valid\n        + label:after,\n      .md-form\n        input[type=\"password\"]:not(.browser-default).form-control-lg:focus.valid\n        + label:after,\n      .md-form\n        input[type=\"email\"]:not(.browser-default).form-control-lg.valid\n        + label:after,\n      .md-form\n        input[type=\"email\"]:not(.browser-default).form-control-lg:focus.valid\n        + label:after,\n      .md-form\n        input[type=\"url\"]:not(.browser-default).form-control-lg.valid\n        + label:after,\n      .md-form\n        input[type=\"url\"]:not(.browser-default).form-control-lg:focus.valid\n        + label:after,\n      .md-form\n        input[type=\"time\"]:not(.browser-default).form-control-lg.valid\n        + label:after,\n      .md-form\n        input[type=\"time\"]:not(.browser-default).form-control-lg:focus.valid\n        + label:after,\n      .md-form\n        input[type=\"date\"]:not(.browser-default).form-control-lg.valid\n        + label:after,\n      .md-form\n        input[type=\"date\"]:not(.browser-default).form-control-lg:focus.valid\n        + label:after,\n      .md-form\n        input[type=\"datetime\"]:not(.browser-default).form-control-lg.valid\n        + label:after,\n      .md-form\n        input[type=\"datetime\"]:not(.browser-default).form-control-lg:focus.valid\n        + label:after,\n      .md-form\n        input[type=\"datetime-local\"]:not(.browser-default).form-control-lg.valid\n        + label:after,\n      .md-form\n        input[type=\"datetime-local\"]:not(.browser-default).form-control-lg:focus.valid\n        + label:after,\n      .md-form\n        input[type=\"tel\"]:not(.browser-default).form-control-lg.valid\n        + label:after,\n      .md-form\n        input[type=\"tel\"]:not(.browser-default).form-control-lg:focus.valid\n        + label:after,\n      .md-form\n        input[type=\"number\"]:not(.browser-default).form-control-lg.valid\n        + label:after,\n      .md-form\n        input[type=\"number\"]:not(.browser-default).form-control-lg:focus.valid\n        + label:after,\n      .md-form\n        input[type=\"search\"]:not(.browser-default).form-control-lg.valid\n        + label:after,\n      .md-form\n        input[type=\"search\"]:not(.browser-default).form-control-lg:focus.valid\n        + label:after,\n      .md-form\n        input[type=\"phone\"]:not(.browser-default).form-control-lg.valid\n        + label:after,\n      .md-form\n        input[type=\"phone\"]:not(.browser-default).form-control-lg:focus.valid\n        + label:after,\n      .md-form input[type=\"search-md\"].form-control-lg.valid + label:after,\n      .md-form\n        input[type=\"search-md\"].form-control-lg:focus.valid\n        + label:after,\n      .md-form textarea.md-textarea.form-control-lg.valid + label:after,\n      .md-form textarea.md-textarea.form-control-lg:focus.valid + label:after {\n        top: 4.6rem;\n      }\n      .md-form input:not([type]).form-control-lg.invalid + label:after,\n      .md-form input:not([type]).form-control-lg:focus.invalid + label:after,\n      .md-form\n        input[type=\"text\"]:not(.browser-default).form-control-lg.invalid\n        + label:after,\n      .md-form\n        input[type=\"text\"]:not(.browser-default).form-control-lg:focus.invalid\n        + label:after,\n      .md-form\n        input[type=\"password\"]:not(.browser-default).form-control-lg.invalid\n        + label:after,\n      .md-form\n        input[type=\"password\"]:not(.browser-default).form-control-lg:focus.invalid\n        + label:after,\n      .md-form\n        input[type=\"email\"]:not(.browser-default).form-control-lg.invalid\n        + label:after,\n      .md-form\n        input[type=\"email\"]:not(.browser-default).form-control-lg:focus.invalid\n        + label:after,\n      .md-form\n        input[type=\"url\"]:not(.browser-default).form-control-lg.invalid\n        + label:after,\n      .md-form\n        input[type=\"url\"]:not(.browser-default).form-control-lg:focus.invalid\n        + label:after,\n      .md-form\n        input[type=\"time\"]:not(.browser-default).form-control-lg.invalid\n        + label:after,\n      .md-form\n        input[type=\"time\"]:not(.browser-default).form-control-lg:focus.invalid\n        + label:after,\n      .md-form\n        input[type=\"date\"]:not(.browser-default).form-control-lg.invalid\n        + label:after,\n      .md-form\n        input[type=\"date\"]:not(.browser-default).form-control-lg:focus.invalid\n        + label:after,\n      .md-form\n        input[type=\"datetime\"]:not(.browser-default).form-control-lg.invalid\n        + label:after,\n      .md-form\n        input[type=\"datetime\"]:not(.browser-default).form-control-lg:focus.invalid\n        + label:after,\n      .md-form\n        input[type=\"datetime-local\"]:not(.browser-default).form-control-lg.invalid\n        + label:after,\n      .md-form\n        input[type=\"datetime-local\"]:not(.browser-default).form-control-lg:focus.invalid\n        + label:after,\n      .md-form\n        input[type=\"tel\"]:not(.browser-default).form-control-lg.invalid\n        + label:after,\n      .md-form\n        input[type=\"tel\"]:not(.browser-default).form-control-lg:focus.invalid\n        + label:after,\n      .md-form\n        input[type=\"number\"]:not(.browser-default).form-control-lg.invalid\n        + label:after,\n      .md-form\n        input[type=\"number\"]:not(.browser-default).form-control-lg:focus.invalid\n        + label:after,\n      .md-form\n        input[type=\"search\"]:not(.browser-default).form-control-lg.invalid\n        + label:after,\n      .md-form\n        input[type=\"search\"]:not(.browser-default).form-control-lg:focus.invalid\n        + label:after,\n      .md-form\n        input[type=\"phone\"]:not(.browser-default).form-control-lg.invalid\n        + label:after,\n      .md-form\n        input[type=\"phone\"]:not(.browser-default).form-control-lg:focus.invalid\n        + label:after,\n      .md-form input[type=\"search-md\"].form-control-lg.invalid + label:after,\n      .md-form\n        input[type=\"search-md\"].form-control-lg:focus.invalid\n        + label:after,\n      .md-form textarea.md-textarea.form-control-lg.invalid + label:after,\n      .md-form\n        textarea.md-textarea.form-control-lg:focus.invalid\n        + label:after {\n        top: 4.6rem;\n      }\n      .md-form input:not([type]).form-control-sm.valid + label:after,\n      .md-form input:not([type]).form-control-sm:focus.valid + label:after,\n      .md-form\n        input[type=\"text\"]:not(.browser-default).form-control-sm.valid\n        + label:after,\n      .md-form\n        input[type=\"text\"]:not(.browser-default).form-control-sm:focus.valid\n        + label:after,\n      .md-form\n        input[type=\"password\"]:not(.browser-default).form-control-sm.valid\n        + label:after,\n      .md-form\n        input[type=\"password\"]:not(.browser-default).form-control-sm:focus.valid\n        + label:after,\n      .md-form\n        input[type=\"email\"]:not(.browser-default).form-control-sm.valid\n        + label:after,\n      .md-form\n        input[type=\"email\"]:not(.browser-default).form-control-sm:focus.valid\n        + label:after,\n      .md-form\n        input[type=\"url\"]:not(.browser-default).form-control-sm.valid\n        + label:after,\n      .md-form\n        input[type=\"url\"]:not(.browser-default).form-control-sm:focus.valid\n        + label:after,\n      .md-form\n        input[type=\"time\"]:not(.browser-default).form-control-sm.valid\n        + label:after,\n      .md-form\n        input[type=\"time\"]:not(.browser-default).form-control-sm:focus.valid\n        + label:after,\n      .md-form\n        input[type=\"date\"]:not(.browser-default).form-control-sm.valid\n        + label:after,\n      .md-form\n        input[type=\"date\"]:not(.browser-default).form-control-sm:focus.valid\n        + label:after,\n      .md-form\n        input[type=\"datetime\"]:not(.browser-default).form-control-sm.valid\n        + label:after,\n      .md-form\n        input[type=\"datetime\"]:not(.browser-default).form-control-sm:focus.valid\n        + label:after,\n      .md-form\n        input[type=\"datetime-local\"]:not(.browser-default).form-control-sm.valid\n        + label:after,\n      .md-form\n        input[type=\"datetime-local\"]:not(.browser-default).form-control-sm:focus.valid\n        + label:after,\n      .md-form\n        input[type=\"tel\"]:not(.browser-default).form-control-sm.valid\n        + label:after,\n      .md-form\n        input[type=\"tel\"]:not(.browser-default).form-control-sm:focus.valid\n        + label:after,\n      .md-form\n        input[type=\"number\"]:not(.browser-default).form-control-sm.valid\n        + label:after,\n      .md-form\n        input[type=\"number\"]:not(.browser-default).form-control-sm:focus.valid\n        + label:after,\n      .md-form\n        input[type=\"search\"]:not(.browser-default).form-control-sm.valid\n        + label:after,\n      .md-form\n        input[type=\"search\"]:not(.browser-default).form-control-sm:focus.valid\n        + label:after,\n      .md-form\n        input[type=\"phone\"]:not(.browser-default).form-control-sm.valid\n        + label:after,\n      .md-form\n        input[type=\"phone\"]:not(.browser-default).form-control-sm:focus.valid\n        + label:after,\n      .md-form input[type=\"search-md\"].form-control-sm.valid + label:after,\n      .md-form\n        input[type=\"search-md\"].form-control-sm:focus.valid\n        + label:after,\n      .md-form textarea.md-textarea.form-control-sm.valid + label:after,\n      .md-form textarea.md-textarea.form-control-sm:focus.valid + label:after {\n        top: 3.7rem;\n      }\n      .md-form input:not([type]).form-control-sm.invalid + label:after,\n      .md-form input:not([type]).form-control-sm:focus.invalid + label:after,\n      .md-form\n        input[type=\"text\"]:not(.browser-default).form-control-sm.invalid\n        + label:after,\n      .md-form\n        input[type=\"text\"]:not(.browser-default).form-control-sm:focus.invalid\n        + label:after,\n      .md-form\n        input[type=\"password\"]:not(.browser-default).form-control-sm.invalid\n        + label:after,\n      .md-form\n        input[type=\"password\"]:not(.browser-default).form-control-sm:focus.invalid\n        + label:after,\n      .md-form\n        input[type=\"email\"]:not(.browser-default).form-control-sm.invalid\n        + label:after,\n      .md-form\n        input[type=\"email\"]:not(.browser-default).form-control-sm:focus.invalid\n        + label:after,\n      .md-form\n        input[type=\"url\"]:not(.browser-default).form-control-sm.invalid\n        + label:after,\n      .md-form\n        input[type=\"url\"]:not(.browser-default).form-control-sm:focus.invalid\n        + label:after,\n      .md-form\n        input[type=\"time\"]:not(.browser-default).form-control-sm.invalid\n        + label:after,\n      .md-form\n        input[type=\"time\"]:not(.browser-default).form-control-sm:focus.invalid\n        + label:after,\n      .md-form\n        input[type=\"date\"]:not(.browser-default).form-control-sm.invalid\n        + label:after,\n      .md-form\n        input[type=\"date\"]:not(.browser-default).form-control-sm:focus.invalid\n        + label:after,\n      .md-form\n        input[type=\"datetime\"]:not(.browser-default).form-control-sm.invalid\n        + label:after,\n      .md-form\n        input[type=\"datetime\"]:not(.browser-default).form-control-sm:focus.invalid\n        + label:after,\n      .md-form\n        input[type=\"datetime-local\"]:not(.browser-default).form-control-sm.invalid\n        + label:after,\n      .md-form\n        input[type=\"datetime-local\"]:not(.browser-default).form-control-sm:focus.invalid\n        + label:after,\n      .md-form\n        input[type=\"tel\"]:not(.browser-default).form-control-sm.invalid\n        + label:after,\n      .md-form\n        input[type=\"tel\"]:not(.browser-default).form-control-sm:focus.invalid\n        + label:after,\n      .md-form\n        input[type=\"number\"]:not(.browser-default).form-control-sm.invalid\n        + label:after,\n      .md-form\n        input[type=\"number\"]:not(.browser-default).form-control-sm:focus.invalid\n        + label:after,\n      .md-form\n        input[type=\"search\"]:not(.browser-default).form-control-sm.invalid\n        + label:after,\n      .md-form\n        input[type=\"search\"]:not(.browser-default).form-control-sm:focus.invalid\n        + label:after,\n      .md-form\n        input[type=\"phone\"]:not(.browser-default).form-control-sm.invalid\n        + label:after,\n      .md-form\n        input[type=\"phone\"]:not(.browser-default).form-control-sm:focus.invalid\n        + label:after,\n      .md-form input[type=\"search-md\"].form-control-sm.invalid + label:after,\n      .md-form\n        input[type=\"search-md\"].form-control-sm:focus.invalid\n        + label:after,\n      .md-form textarea.md-textarea.form-control-sm.invalid + label:after,\n      .md-form\n        textarea.md-textarea.form-control-sm:focus.invalid\n        + label:after {\n        top: 3.6rem;\n      }\n      .md-form > input[type=\"date\"]:not(.browser-default) + label {\n        transform: translateY(-27px) scale(0.8);\n        transform-origin: 0 0;\n      }\n      .md-form\n        > input[type]:-webkit-autofill:not(.browser-default):not([type=\"search\"])\n        + label,\n      .md-form > input[type=\"time\"]:not(.browser-default) + label {\n        font-size: 0.8rem;\n        transform: translateY(-25px);\n        transform-origin: 0 0;\n      }\n      .md-form .was-validated input[type=\"text\"]:valid + label {\n        color: #00c851 !important;\n      }\n      .md-form .was-validated input[type=\"text\"]:invalid + label {\n        color: #f44336 !important;\n      }\n      .md-form .was-validated .form-control:valid:focus {\n        box-shadow: 0 1px 0 0 #00c851 !important;\n      }\n      .md-form .was-validated .form-control:valid {\n        border-color: #00c851 !important;\n      }\n      .md-form .was-validated .form-control:invalid:focus {\n        box-shadow: 0 1px 0 0 #f44336 !important;\n      }\n      .md-form .was-validated .form-control:invalid {\n        border-color: #f44336 !important;\n      }\n      .md-form .form-control {\n        height: auto;\n        padding: 0.6rem 0 0.4rem 0;\n        margin: 0 0 0.5rem 0;\n        background-color: transparent;\n        border-radius: 0;\n      }\n      .md-form .form-control:focus {\n        box-shadow: none;\n      }\n      .md-form .form-control:disabled,\n      .md-form .form-control[readonly] {\n        background-color: transparent;\n        border-bottom: 1px solid #bdbdbd;\n      }\n      .md-form .form-control.is-valid {\n        border-color: #00c851;\n      }\n      .md-form .form-control.is-valid:focus {\n        border-color: #00c851 !important;\n        box-shadow: 0 1px 0 0 #00c851 !important;\n      }\n      .md-form .form-control.is-invalid {\n        border-color: #f44336;\n      }\n      .md-form .form-control.is-invalid:focus {\n        border-color: #f44336 !important;\n        box-shadow: 0 1px 0 0 #f44336 !important;\n      }\n      .md-form .form-control.is-valid,\n      .md-form .form-control.is-invalid {\n        background-position: center right !important;\n      }\n      .md-form .validate {\n        margin-bottom: 2.5rem;\n      }\n      .md-form label {\n        font-size: 1rem;\n      }\n      .md-form label.active {\n        font-size: 1rem;\n      }\n      .md-form .prefix {\n        top: 0.25rem;\n        font-size: 1.75rem;\n      }\n      .md-form .prefix ~ input,\n      .md-form .prefix ~ textarea {\n        width: calc(100% - 2.5rem);\n        margin-left: 2.5rem;\n      }\n      .md-form .prefix ~ label {\n        margin-left: 2.5rem;\n      }\n      .md-form .prefix ~ .form-text {\n        margin-left: 2.6rem;\n      }\n      .md-form label {\n        position: absolute;\n        top: 0;\n        left: 0;\n        font-size: 1rem;\n        color: #757575;\n        cursor: text;\n        transition: transform 0.2s ease-out, color 0.2s ease-out;\n        transform: translateY(12px);\n        transform-origin: 0% 100%;\n      }\n      .md-form label.active {\n        transform: translateY(-14px) scale(0.8);\n      }\n      .md-form .prefix {\n        position: absolute;\n        transition: color 0.2s;\n      }\n      .md-form .prefix.active {\n        color: #4285f4;\n      }\n      .md-form.form-lg .validate {\n        margin-bottom: 2.8rem;\n      }\n      .md-form.form-lg label {\n        font-size: 1.25rem;\n      }\n      .md-form.form-lg label.active {\n        font-size: 1.15rem;\n      }\n      .md-form.form-lg .prefix {\n        top: 0.4rem;\n        font-size: 2rem;\n      }\n      .md-form.form-lg .prefix ~ input,\n      .md-form.form-lg .prefix ~ textarea {\n        width: calc(100% - 3rem);\n        margin-left: 3rem;\n      }\n      .md-form.form-lg .prefix ~ label {\n        margin-left: 3rem;\n      }\n      .md-form.form-lg .prefix ~ .form-text {\n        margin-left: 3.1rem;\n      }\n      .md-form.form-sm .validate {\n        margin-bottom: 2.3rem;\n      }\n      .md-form.form-sm label {\n        font-size: 0.875rem;\n      }\n      .md-form.form-sm label.active {\n        font-size: 0.95rem;\n      }\n      .md-form.form-sm .prefix {\n        top: 0.35rem;\n        font-size: 1.5rem;\n      }\n      .md-form.form-sm .prefix ~ input,\n      .md-form.form-sm .prefix ~ textarea {\n        width: calc(100% - 2rem);\n        margin-left: 2rem;\n      }\n      .md-form.form-sm .prefix ~ label {\n        margin-left: 2rem;\n      }\n      .md-form.form-sm .prefix ~ .form-text {\n        margin-left: 2rem;\n      }\n      .md-form textarea.md-textarea {\n        padding: 0;\n        overflow-y: hidden;\n      }\n      .md-form textarea.md-textarea + label {\n        top: -0.6rem;\n      }\n      .md-form textarea.md-textarea-auto {\n        padding: 0;\n        padding-top: 1.5rem;\n      }\n      .md-form textarea.md-textarea-auto + label {\n        top: 0;\n      }\n      .md-form.md-outline {\n        position: relative;\n        margin-top: 1.5rem;\n        margin-bottom: 1.5rem;\n      }\n      .md-form.md-outline input[type=\"text\"],\n      .md-form.md-outline input[type=\"password\"],\n      .md-form.md-outline input[type=\"email\"],\n      .md-form.md-outline input[type=\"url\"],\n      .md-form.md-outline input[type=\"time\"],\n      .md-form.md-outline input[type=\"date\"],\n      .md-form.md-outline input[type=\"datetime-local\"],\n      .md-form.md-outline input[type=\"tel\"],\n      .md-form.md-outline input[type=\"number\"],\n      .md-form.md-outline input[type=\"search-md\"],\n      .md-form.md-outline input[type=\"search\"],\n      .md-form.md-outline textarea.md-textarea {\n        box-sizing: border-box;\n        background-color: transparent;\n        border: 1px solid #dadce0;\n        border-radius: 4px;\n        outline: none;\n        box-shadow: none;\n        transition: all 0.3s;\n      }\n      .md-form.md-outline input[type=\"text\"]:focus:not([readonly]),\n      .md-form.md-outline input[type=\"password\"]:focus:not([readonly]),\n      .md-form.md-outline input[type=\"email\"]:focus:not([readonly]),\n      .md-form.md-outline input[type=\"url\"]:focus:not([readonly]),\n      .md-form.md-outline input[type=\"time\"]:focus:not([readonly]),\n      .md-form.md-outline input[type=\"date\"]:focus:not([readonly]),\n      .md-form.md-outline input[type=\"datetime-local\"]:focus:not([readonly]),\n      .md-form.md-outline input[type=\"tel\"]:focus:not([readonly]),\n      .md-form.md-outline input[type=\"number\"]:focus:not([readonly]),\n      .md-form.md-outline input[type=\"search-md\"]:focus:not([readonly]),\n      .md-form.md-outline input[type=\"search\"]:focus:not([readonly]),\n      .md-form.md-outline textarea.md-textarea:focus:not([readonly]) {\n        border-color: #4285f4;\n        box-shadow: inset 0 0 0 1px #4285f4;\n      }\n      .md-form.md-outline input[type=\"text\"]:focus:not([readonly]) + label,\n      .md-form.md-outline input[type=\"password\"]:focus:not([readonly]) + label,\n      .md-form.md-outline input[type=\"email\"]:focus:not([readonly]) + label,\n      .md-form.md-outline input[type=\"url\"]:focus:not([readonly]) + label,\n      .md-form.md-outline input[type=\"time\"]:focus:not([readonly]) + label,\n      .md-form.md-outline input[type=\"date\"]:focus:not([readonly]) + label,\n      .md-form.md-outline\n        input[type=\"datetime-local\"]:focus:not([readonly])\n        + label,\n      .md-form.md-outline input[type=\"tel\"]:focus:not([readonly]) + label,\n      .md-form.md-outline input[type=\"number\"]:focus:not([readonly]) + label,\n      .md-form.md-outline input[type=\"search-md\"]:focus:not([readonly]) + label,\n      .md-form.md-outline input[type=\"search\"]:focus:not([readonly]) + label,\n      .md-form.md-outline textarea.md-textarea:focus:not([readonly]) + label {\n        color: #4285f4;\n      }\n      .md-form.md-outline input[type=\"text\"].valid,\n      .md-form.md-outline input[type=\"text\"]:focus.valid,\n      .md-form.md-outline input[type=\"password\"].valid,\n      .md-form.md-outline input[type=\"password\"]:focus.valid,\n      .md-form.md-outline input[type=\"email\"].valid,\n      .md-form.md-outline input[type=\"email\"]:focus.valid,\n      .md-form.md-outline input[type=\"url\"].valid,\n      .md-form.md-outline input[type=\"url\"]:focus.valid,\n      .md-form.md-outline input[type=\"time\"].valid,\n      .md-form.md-outline input[type=\"time\"]:focus.valid,\n      .md-form.md-outline input[type=\"date\"].valid,\n      .md-form.md-outline input[type=\"date\"]:focus.valid,\n      .md-form.md-outline input[type=\"datetime-local\"].valid,\n      .md-form.md-outline input[type=\"datetime-local\"]:focus.valid,\n      .md-form.md-outline input[type=\"tel\"].valid,\n      .md-form.md-outline input[type=\"tel\"]:focus.valid,\n      .md-form.md-outline input[type=\"number\"].valid,\n      .md-form.md-outline input[type=\"number\"]:focus.valid,\n      .md-form.md-outline input[type=\"search-md\"].valid,\n      .md-form.md-outline input[type=\"search-md\"]:focus.valid,\n      .md-form.md-outline input[type=\"search\"].valid,\n      .md-form.md-outline input[type=\"search\"]:focus.valid,\n      .md-form.md-outline textarea.md-textarea.valid,\n      .md-form.md-outline textarea.md-textarea:focus.valid {\n        border-color: #00c851;\n        box-shadow: inset 0 0 0 1px #00c851;\n      }\n      .md-form.md-outline\n        input[type=\"text\"]:focus:not([readonly]).valid\n        + label,\n      .md-form.md-outline input[type=\"text\"].valid + label:after,\n      .md-form.md-outline input[type=\"text\"]:focus.valid + label:after,\n      .md-form.md-outline\n        input[type=\"password\"]:focus:not([readonly]).valid\n        + label,\n      .md-form.md-outline input[type=\"password\"].valid + label:after,\n      .md-form.md-outline input[type=\"password\"]:focus.valid + label:after,\n      .md-form.md-outline\n        input[type=\"email\"]:focus:not([readonly]).valid\n        + label,\n      .md-form.md-outline input[type=\"email\"].valid + label:after,\n      .md-form.md-outline input[type=\"email\"]:focus.valid + label:after,\n      .md-form.md-outline input[type=\"url\"]:focus:not([readonly]).valid + label,\n      .md-form.md-outline input[type=\"url\"].valid + label:after,\n      .md-form.md-outline input[type=\"url\"]:focus.valid + label:after,\n      .md-form.md-outline\n        input[type=\"time\"]:focus:not([readonly]).valid\n        + label,\n      .md-form.md-outline input[type=\"time\"].valid + label:after,\n      .md-form.md-outline input[type=\"time\"]:focus.valid + label:after,\n      .md-form.md-outline\n        input[type=\"date\"]:focus:not([readonly]).valid\n        + label,\n      .md-form.md-outline input[type=\"date\"].valid + label:after,\n      .md-form.md-outline input[type=\"date\"]:focus.valid + label:after,\n      .md-form.md-outline\n        input[type=\"datetime-local\"]:focus:not([readonly]).valid\n        + label,\n      .md-form.md-outline input[type=\"datetime-local\"].valid + label:after,\n      .md-form.md-outline\n        input[type=\"datetime-local\"]:focus.valid\n        + label:after,\n      .md-form.md-outline input[type=\"tel\"]:focus:not([readonly]).valid + label,\n      .md-form.md-outline input[type=\"tel\"].valid + label:after,\n      .md-form.md-outline input[type=\"tel\"]:focus.valid + label:after,\n      .md-form.md-outline\n        input[type=\"number\"]:focus:not([readonly]).valid\n        + label,\n      .md-form.md-outline input[type=\"number\"].valid + label:after,\n      .md-form.md-outline input[type=\"number\"]:focus.valid + label:after,\n      .md-form.md-outline\n        input[type=\"search-md\"]:focus:not([readonly]).valid\n        + label,\n      .md-form.md-outline input[type=\"search-md\"].valid + label:after,\n      .md-form.md-outline input[type=\"search-md\"]:focus.valid + label:after,\n      .md-form.md-outline\n        input[type=\"search\"]:focus:not([readonly]).valid\n        + label,\n      .md-form.md-outline input[type=\"search\"].valid + label:after,\n      .md-form.md-outline input[type=\"search\"]:focus.valid + label:after,\n      .md-form.md-outline\n        textarea.md-textarea:focus:not([readonly]).valid\n        + label,\n      .md-form.md-outline textarea.md-textarea.valid + label:after,\n      .md-form.md-outline textarea.md-textarea:focus.valid + label:after {\n        color: #00c851;\n        content: attr(data-success);\n        opacity: 1;\n      }\n      .md-form.md-outline input[type=\"text\"].invalid,\n      .md-form.md-outline input[type=\"text\"]:focus.invalid,\n      .md-form.md-outline input[type=\"password\"].invalid,\n      .md-form.md-outline input[type=\"password\"]:focus.invalid,\n      .md-form.md-outline input[type=\"email\"].invalid,\n      .md-form.md-outline input[type=\"email\"]:focus.invalid,\n      .md-form.md-outline input[type=\"url\"].invalid,\n      .md-form.md-outline input[type=\"url\"]:focus.invalid,\n      .md-form.md-outline input[type=\"time\"].invalid,\n      .md-form.md-outline input[type=\"time\"]:focus.invalid,\n      .md-form.md-outline input[type=\"date\"].invalid,\n      .md-form.md-outline input[type=\"date\"]:focus.invalid,\n      .md-form.md-outline input[type=\"datetime-local\"].invalid,\n      .md-form.md-outline input[type=\"datetime-local\"]:focus.invalid,\n      .md-form.md-outline input[type=\"tel\"].invalid,\n      .md-form.md-outline input[type=\"tel\"]:focus.invalid,\n      .md-form.md-outline input[type=\"number\"].invalid,\n      .md-form.md-outline input[type=\"number\"]:focus.invalid,\n      .md-form.md-outline input[type=\"search-md\"].invalid,\n      .md-form.md-outline input[type=\"search-md\"]:focus.invalid,\n      .md-form.md-outline input[type=\"search\"].invalid,\n      .md-form.md-outline input[type=\"search\"]:focus.invalid,\n      .md-form.md-outline textarea.md-textarea.invalid,\n      .md-form.md-outline textarea.md-textarea:focus.invalid {\n        border-color: #f44336;\n        box-shadow: inset 0 0 0 1px #f44336;\n      }\n      .md-form.md-outline\n        input[type=\"text\"]:focus:not([readonly]).invalid\n        + label,\n      .md-form.md-outline input[type=\"text\"].invalid + label:after,\n      .md-form.md-outline input[type=\"text\"]:focus.invalid + label:after,\n      .md-form.md-outline\n        input[type=\"password\"]:focus:not([readonly]).invalid\n        + label,\n      .md-form.md-outline input[type=\"password\"].invalid + label:after,\n      .md-form.md-outline input[type=\"password\"]:focus.invalid + label:after,\n      .md-form.md-outline\n        input[type=\"email\"]:focus:not([readonly]).invalid\n        + label,\n      .md-form.md-outline input[type=\"email\"].invalid + label:after,\n      .md-form.md-outline input[type=\"email\"]:focus.invalid + label:after,\n      .md-form.md-outline\n        input[type=\"url\"]:focus:not([readonly]).invalid\n        + label,\n      .md-form.md-outline input[type=\"url\"].invalid + label:after,\n      .md-form.md-outline input[type=\"url\"]:focus.invalid + label:after,\n      .md-form.md-outline\n        input[type=\"time\"]:focus:not([readonly]).invalid\n        + label,\n      .md-form.md-outline input[type=\"time\"].invalid + label:after,\n      .md-form.md-outline input[type=\"time\"]:focus.invalid + label:after,\n      .md-form.md-outline\n        input[type=\"date\"]:focus:not([readonly]).invalid\n        + label,\n      .md-form.md-outline input[type=\"date\"].invalid + label:after,\n      .md-form.md-outline input[type=\"date\"]:focus.invalid + label:after,\n      .md-form.md-outline\n        input[type=\"datetime-local\"]:focus:not([readonly]).invalid\n        + label,\n      .md-form.md-outline input[type=\"datetime-local\"].invalid + label:after,\n      .md-form.md-outline\n        input[type=\"datetime-local\"]:focus.invalid\n        + label:after,\n      .md-form.md-outline\n        input[type=\"tel\"]:focus:not([readonly]).invalid\n        + label,\n      .md-form.md-outline input[type=\"tel\"].invalid + label:after,\n      .md-form.md-outline input[type=\"tel\"]:focus.invalid + label:after,\n      .md-form.md-outline\n        input[type=\"number\"]:focus:not([readonly]).invalid\n        + label,\n      .md-form.md-outline input[type=\"number\"].invalid + label:after,\n      .md-form.md-outline input[type=\"number\"]:focus.invalid + label:after,\n      .md-form.md-outline\n        input[type=\"search-md\"]:focus:not([readonly]).invalid\n        + label,\n      .md-form.md-outline input[type=\"search-md\"].invalid + label:after,\n      .md-form.md-outline input[type=\"search-md\"]:focus.invalid + label:after,\n      .md-form.md-outline\n        input[type=\"search\"]:focus:not([readonly]).invalid\n        + label,\n      .md-form.md-outline input[type=\"search\"].invalid + label:after,\n      .md-form.md-outline input[type=\"search\"]:focus.invalid + label:after,\n      .md-form.md-outline\n        textarea.md-textarea:focus:not([readonly]).invalid\n        + label,\n      .md-form.md-outline textarea.md-textarea.invalid + label:after,\n      .md-form.md-outline textarea.md-textarea:focus.invalid + label:after {\n        color: #f44336;\n        content: attr(data-error);\n        opacity: 1;\n      }\n      .md-form.md-outline input[type=\"text\"].form-control.valid + label:after,\n      .md-form.md-outline\n        input[type=\"text\"].form-control:focus.valid\n        + label:after,\n      .md-form.md-outline\n        input[type=\"password\"].form-control.valid\n        + label:after,\n      .md-form.md-outline\n        input[type=\"password\"].form-control:focus.valid\n        + label:after,\n      .md-form.md-outline input[type=\"email\"].form-control.valid + label:after,\n      .md-form.md-outline\n        input[type=\"email\"].form-control:focus.valid\n        + label:after,\n      .md-form.md-outline input[type=\"url\"].form-control.valid + label:after,\n      .md-form.md-outline\n        input[type=\"url\"].form-control:focus.valid\n        + label:after,\n      .md-form.md-outline input[type=\"time\"].form-control.valid + label:after,\n      .md-form.md-outline\n        input[type=\"time\"].form-control:focus.valid\n        + label:after,\n      .md-form.md-outline input[type=\"date\"].form-control.valid + label:after,\n      .md-form.md-outline\n        input[type=\"date\"].form-control:focus.valid\n        + label:after,\n      .md-form.md-outline\n        input[type=\"datetime-local\"].form-control.valid\n        + label:after,\n      .md-form.md-outline\n        input[type=\"datetime-local\"].form-control:focus.valid\n        + label:after,\n      .md-form.md-outline input[type=\"tel\"].form-control.valid + label:after,\n      .md-form.md-outline\n        input[type=\"tel\"].form-control:focus.valid\n        + label:after,\n      .md-form.md-outline input[type=\"number\"].form-control.valid + label:after,\n      .md-form.md-outline\n        input[type=\"number\"].form-control:focus.valid\n        + label:after,\n      .md-form.md-outline\n        input[type=\"search-md\"].form-control.valid\n        + label:after,\n      .md-form.md-outline\n        input[type=\"search-md\"].form-control:focus.valid\n        + label:after,\n      .md-form.md-outline input[type=\"search\"].form-control.valid + label:after,\n      .md-form.md-outline\n        input[type=\"search\"].form-control:focus.valid\n        + label:after,\n      .md-form.md-outline textarea.md-textarea.form-control.valid + label:after,\n      .md-form.md-outline\n        textarea.md-textarea.form-control:focus.valid\n        + label:after {\n        position: absolute;\n        top: 4rem;\n        left: 0;\n      }\n      .md-form.md-outline input[type=\"text\"].form-control.invalid + label:after,\n      .md-form.md-outline\n        input[type=\"text\"].form-control:focus.invalid\n        + label:after,\n      .md-form.md-outline\n        input[type=\"password\"].form-control.invalid\n        + label:after,\n      .md-form.md-outline\n        input[type=\"password\"].form-control:focus.invalid\n        + label:after,\n      .md-form.md-outline\n        input[type=\"email\"].form-control.invalid\n        + label:after,\n      .md-form.md-outline\n        input[type=\"email\"].form-control:focus.invalid\n        + label:after,\n      .md-form.md-outline input[type=\"url\"].form-control.invalid + label:after,\n      .md-form.md-outline\n        input[type=\"url\"].form-control:focus.invalid\n        + label:after,\n      .md-form.md-outline input[type=\"time\"].form-control.invalid + label:after,\n      .md-form.md-outline\n        input[type=\"time\"].form-control:focus.invalid\n        + label:after,\n      .md-form.md-outline input[type=\"date\"].form-control.invalid + label:after,\n      .md-form.md-outline\n        input[type=\"date\"].form-control:focus.invalid\n        + label:after,\n      .md-form.md-outline\n        input[type=\"datetime-local\"].form-control.invalid\n        + label:after,\n      .md-form.md-outline\n        input[type=\"datetime-local\"].form-control:focus.invalid\n        + label:after,\n      .md-form.md-outline input[type=\"tel\"].form-control.invalid + label:after,\n      .md-form.md-outline\n        input[type=\"tel\"].form-control:focus.invalid\n        + label:after,\n      .md-form.md-outline\n        input[type=\"number\"].form-control.invalid\n        + label:after,\n      .md-form.md-outline\n        input[type=\"number\"].form-control:focus.invalid\n        + label:after,\n      .md-form.md-outline\n        input[type=\"search-md\"].form-control.invalid\n        + label:after,\n      .md-form.md-outline\n        input[type=\"search-md\"].form-control:focus.invalid\n        + label:after,\n      .md-form.md-outline\n        input[type=\"search\"].form-control.invalid\n        + label:after,\n      .md-form.md-outline\n        input[type=\"search\"].form-control:focus.invalid\n        + label:after,\n      .md-form.md-outline\n        textarea.md-textarea.form-control.invalid\n        + label:after,\n      .md-form.md-outline\n        textarea.md-textarea.form-control:focus.invalid\n        + label:after {\n        position: absolute;\n        top: 4rem;\n        left: 0;\n      }\n      .md-form.md-outline\n        > input[type]:-webkit-autofill:not(.browser-default):not([type=\"search\"])\n        + label,\n      .md-form.md-outline > input[type=\"time\"]:not(.browser-default) + label {\n        left: 8px;\n        padding-right: 5px;\n        padding-left: 5px;\n        font-size: 1rem;\n        font-weight: 500;\n        background: #fff;\n        transform: translateY(-9px) scale(0.8);\n        transform-origin: 0 0;\n      }\n      .md-form.md-outline\n        > input[type]:-webkit-autofill:not(.browser-default):not([type=\"search\"])\n        + label.active,\n      .md-form.md-outline\n        > input[type=\"time\"]:not(.browser-default)\n        + label.active {\n        transform: translateY(-9px) scale(0.8);\n        transform-origin: 0 0;\n      }\n      @-webkit-keyframes autofill {\n        to {\n          color: #495057;\n          background: transparent;\n        }\n      }\n      @keyframes autofill {\n        to {\n          color: #495057;\n          background: transparent;\n        }\n      }\n      .md-form.md-outline input:-webkit-autofill {\n        -webkit-animation-name: autofill;\n        animation-name: autofill;\n        -webkit-animation-fill-mode: both;\n        animation-fill-mode: both;\n      }\n      .md-form.md-outline .form-control {\n        padding: 0.375rem 0.75rem;\n      }\n      .md-form.md-outline label {\n        position: absolute;\n        top: 0;\n        left: 0;\n        padding-left: 10px;\n        font-size: 1rem;\n        color: #757575;\n        cursor: text;\n        transition: transform 0.2s ease-out, color 0.2s ease-out;\n        transform: translateY(9px);\n        transform-origin: 0% 100%;\n      }\n      .md-form.md-outline label.active {\n        left: 8px;\n        padding-right: 5px;\n        padding-left: 5px;\n        font-weight: 500;\n        background: #fff;\n        transform: translateY(-13px) scale(0.8);\n      }\n      .md-form.md-outline.form-lg .form-control.form-control-lg {\n        padding: 0.5rem 0.725rem;\n      }\n      .md-form.md-outline.form-lg label {\n        font-size: 1.25rem;\n        transform: translateY(10px);\n      }\n      .md-form.md-outline.form-lg label.active {\n        font-size: 1.1rem;\n        transform: translateY(-14px) scale(0.8);\n      }\n      .md-form.md-outline.form-lg .prefix {\n        top: 0.65rem;\n        font-size: 25px;\n      }\n      .md-form.md-outline.form-lg .prefix ~ input,\n      .md-form.md-outline.form-lg .prefix ~ textarea {\n        width: calc(100% - 2.2rem);\n        margin-left: 2.2rem;\n      }\n      .md-form.md-outline.form-lg .prefix ~ label {\n        margin-left: 2.2rem;\n      }\n      .md-form.md-outline.form-lg .prefix ~ .form-text {\n        margin-left: 2.3rem;\n      }\n      .md-form.md-outline.form-sm .form-control.form-control-sm {\n        padding: 0.25rem 0.625rem;\n      }\n      .md-form.md-outline.form-sm label {\n        font-size: 0.8rem;\n        transform: translateY(8px);\n      }\n      .md-form.md-outline.form-sm label.active {\n        font-size: 0.85rem;\n        transform: translateY(-12px) scale(0.8);\n      }\n      .md-form.md-outline.form-sm .prefix {\n        top: 0.5rem;\n        font-size: 15px;\n      }\n      .md-form.md-outline.form-sm .prefix ~ input,\n      .md-form.md-outline.form-sm .prefix ~ textarea {\n        width: calc(100% - 1.6rem);\n        margin-left: 1.6rem;\n      }\n      .md-form.md-outline.form-sm .prefix ~ label {\n        margin-left: 1.6rem;\n      }\n      .md-form.md-outline.form-sm .prefix ~ .form-text {\n        margin-left: 1.7rem;\n      }\n      .md-form.md-outline .prefix {\n        position: absolute;\n        top: 0.6rem;\n        font-size: 20px;\n        transition: color 0.2s;\n      }\n      .md-form.md-outline .prefix:focus {\n        color: #4285f4;\n      }\n      .md-form.md-outline .prefix ~ input,\n      .md-form.md-outline .prefix ~ textarea {\n        width: calc(100% - 2rem);\n        margin-left: 2rem;\n      }\n      .md-form.md-outline .prefix ~ label {\n        margin-left: 2rem;\n      }\n      .md-form.md-outline .prefix ~ .form-text {\n        margin-left: 2.1rem;\n      }\n      .md-form.md-outline .character-counter {\n        margin-top: -0.5rem;\n      }\n      .md-form.md-bg input[type=\"text\"],\n      .md-form.md-bg input[type=\"password\"],\n      .md-form.md-bg input[type=\"email\"],\n      .md-form.md-bg input[type=\"url\"],\n      .md-form.md-bg input[type=\"time\"],\n      .md-form.md-bg input[type=\"date\"],\n      .md-form.md-bg input[type=\"datetime-local\"],\n      .md-form.md-bg input[type=\"tel\"],\n      .md-form.md-bg input[type=\"number\"],\n      .md-form.md-bg input[type=\"search-md\"],\n      .md-form.md-bg input[type=\"search\"],\n      .md-form.md-bg textarea.md-textarea {\n        box-sizing: border-box;\n        padding: 10px 5px;\n        background: #f5f5f5 no-repeat;\n        background-image: linear-gradient(to bottom, #4285f4, #4285f4),\n          linear-gradient(to bottom, #ced4da, #ced4da);\n        background-position: 50% 100%, 50% 100%;\n        background-size: 0 2px, 100% 1px;\n        border: 0;\n        border-top-left-radius: 0.3rem;\n        border-top-right-radius: 0.3rem;\n        transition: background-size 0.3s cubic-bezier(0.64, 0.09, 0.08, 1);\n      }\n      .md-form.md-bg input[type=\"text\"]:focus:not([readonly]),\n      .md-form.md-bg input[type=\"password\"]:focus:not([readonly]),\n      .md-form.md-bg input[type=\"email\"]:focus:not([readonly]),\n      .md-form.md-bg input[type=\"url\"]:focus:not([readonly]),\n      .md-form.md-bg input[type=\"time\"]:focus:not([readonly]),\n      .md-form.md-bg input[type=\"date\"]:focus:not([readonly]),\n      .md-form.md-bg input[type=\"datetime-local\"]:focus:not([readonly]),\n      .md-form.md-bg input[type=\"tel\"]:focus:not([readonly]),\n      .md-form.md-bg input[type=\"number\"]:focus:not([readonly]),\n      .md-form.md-bg input[type=\"search-md\"]:focus:not([readonly]),\n      .md-form.md-bg input[type=\"search\"]:focus:not([readonly]),\n      .md-form.md-bg textarea.md-textarea:focus:not([readonly]) {\n        border-bottom: none;\n        box-shadow: none;\n      }\n      .md-form.md-bg input[type=\"text\"]:focus,\n      .md-form.md-bg input[type=\"password\"]:focus,\n      .md-form.md-bg input[type=\"email\"]:focus,\n      .md-form.md-bg input[type=\"url\"]:focus,\n      .md-form.md-bg input[type=\"time\"]:focus,\n      .md-form.md-bg input[type=\"date\"]:focus,\n      .md-form.md-bg input[type=\"datetime-local\"]:focus,\n      .md-form.md-bg input[type=\"tel\"]:focus,\n      .md-form.md-bg input[type=\"number\"]:focus,\n      .md-form.md-bg input[type=\"search-md\"]:focus,\n      .md-form.md-bg input[type=\"search\"]:focus,\n      .md-form.md-bg textarea.md-textarea:focus {\n        background-color: #dcdcdc;\n        background-size: 100% 2px, 100% 1px;\n        outline: none;\n      }\n      .md-form.md-bg > input[type=\"date\"]:not(.browser-default) + label {\n        transform: translateY(-12px) scale(0.8);\n        transform-origin: 0 0;\n      }\n      .md-form.md-bg\n        > input[type]:-webkit-autofill:not(.browser-default):not([type=\"search\"])\n        + label,\n      .md-form.md-bg > input[type=\"time\"]:not(.browser-default) + label {\n        font-size: 0.8rem;\n        transform: translateY(-12px);\n        transform-origin: 0 0;\n      }\n      .md-form.md-bg .form-control {\n        padding: 1.1rem 0.7rem 0.4rem !important;\n      }\n      .md-form.md-bg label {\n        top: 0;\n        padding-left: 0.7rem;\n        font-size: 1rem;\n        transition: transform 0.2s ease-out, color 0.2s ease-out;\n        transform: translateY(13px);\n        transform-origin: 0% 100%;\n      }\n      .md-form.md-bg label.active {\n        padding-left: 0.75rem;\n        font-weight: 500;\n        transform: translateY(-3px) scale(0.8);\n      }\n      .md-form.md-bg.form-lg label {\n        transform: translateY(16px);\n      }\n      .md-form.md-bg.form-lg label.active {\n        transform: translateY(-4px) scale(0.8);\n      }\n      .md-form.md-bg.form-sm label {\n        transform: translateY(11px);\n      }\n      .md-form.md-bg.form-sm label.active {\n        transform: translateY(-2px) scale(0.8);\n      }\n      .md-form .form-control.is-invalid,\n      .was-validated .md-form .form-control:invalid {\n        padding-right: 0;\n      }\n      .md-form .form-control.is-valid,\n      .was-validated .md-form .form-control:valid {\n        padding-right: 0;\n      }\n      .needs-validation .md-form label {\n        left: 0.3rem;\n      }\n      .custom-file-input:lang(es) ~ .custom-file-label::after {\n        content: \"Elegir\";\n      }\n      .custom-file-input:lang(pl-pl) ~ .custom-file-label::after {\n        content: \"Wybierz\";\n      }\n      .custom-file-input:lang(fr) ~ .custom-file-label::after {\n        content: \"Choisir\";\n      }\n      .custom-file-input:lang(in) ~ .custom-file-label::after {\n        content: \"Pilih\";\n      }\n      .custom-file-input:lang(zh) ~ .custom-file-label::after {\n        content: \"\u9078\u64C7\";\n      }\n      .custom-file-input:lang(de) ~ .custom-file-label::after {\n        content: \"W\xE4hlen\";\n      }\n      .custom-file-input:lang(ru) ~ .custom-file-label::after {\n        content: \"\u0412\u044B\u0431\u0440\u0430\u0442\u044C\";\n      }\n      .md-form > label {\n        max-width: 100%;\n        white-space: nowrap;\n        overflow: hidden;\n        text-overflow: ellipsis;\n      }\n      .md-form .form-control {\n        box-sizing: border-box !important;\n      }\n      .md-form .input-prefix {\n        position: absolute;\n        top: 50%;\n        transform: translateY(-50%);\n        transition: color 0.2s;\n        color: rgba(0, 0, 0, 0.87);\n        pointer-events: none;\n      }\n      .md-form .input-prefix.active {\n        color: #4285f4;\n      }\n      .md-form.input-with-pre-icon label {\n        left: 36px;\n        right: initial;\n      }\n      .md-form.input-with-pre-icon .input-prefix {\n        left: 16px;\n        right: initial;\n      }\n      .md-form.input-with-pre-icon .form-control {\n        padding-left: 2.7rem !important;\n      }\n      .md-form.input-with-post-icon .input-prefix {\n        right: 16px;\n        left: initial;\n      }\n      .md-form.input-with-post-icon .form-control {\n        padding-right: 2.7rem !important;\n      }\n      .md-form.input-with-post-icon.input-with-pre-icon .input-prefix {\n        right: 16px;\n        left: initial;\n      }\n      .md-form.input-with-post-icon.input-with-pre-icon\n        .input-prefix:first-of-type {\n        left: 16px;\n        right: initial;\n      }\n      .md-form.input-with-post-icon.input-with-pre-icon .form-control {\n        padding-left: 2.5rem !important;\n        padding-right: 2.5rem !important;\n      }\n"]);
  _templateObject$C = function _templateObject() {
    return data;
  };
  return data;
}
var Component$A = styled.div(_templateObject$C());
var InputGroup = function InputGroup(props) {
  var append = props.append,
    appendClassName = props.appendClassName,
    ariaLabel = props.ariaLabel,
    background = props.background,
    children = props.children,
    className = props.className,
    containerClassName = props.containerClassName,
    containerId = props.containerId,
    icon = props.icon,
    id = props.id,
    inputs = props.inputs,
    label = props.label,
    labelClassName = props.labelClassName,
    material = props.material,
    prepend = props.prepend,
    prependClassName = props.prependClassName,
    size = props.size,
    textClassName = props.textClassName,
    type = props.type,
    value = props.value,
    valueDefault = props.valueDefault,
    getValue = props.getValue,
    onChange = props.onChange,
    attributes = _objectWithoutPropertiesLoose(props, ["append", "appendClassName", "ariaLabel", "as", "notag", "background", "children", "className", "containerClassName", "containerId", "hint", "icon", "id", "div", "inputs", "label", "labelClassName", "material", "prepend", "prependClassName", "size", "tag", "textClassName", "type", "value", "valueDefault", "getValue", "onChange", "onIconClick"]);
  var containerClassNames = classNames(material && 'md-form', 'input-group', size && "input-group-" + size, containerClassName);
  var inputClassNames = classNames(className, background && 'input-background', 'input-flex-fill');
  var prependClassNames = classNames('input-group-prepend', prependClassName);
  var appendClassNames = classNames('input-group-append', appendClassName);
  var textClassNames = classNames('input-group-text', material && 'md-addon', textClassName);
  var handleChange = function handleChange(event) {
    event.persist();
    onChange && onChange(event);
    getValue && getValue(event.target.value);
  };
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(React.Fragment, null, label && React.createElement("label", {
    htmlFor: id,
    className: labelClassName
  }, label), React.createElement(Component$A, null, React.createElement("div", Object.assign({
    "data-test": 'input-group'
  }, attributes, {
    className: containerClassNames,
    id: containerId
  }), prepend && React.createElement("div", {
    className: prependClassNames
  }, typeof prepend === 'string' ? React.createElement("span", {
    className: textClassNames
  }, prepend) : prepend), inputs || React.createElement(Input, {
    type: type,
    className: inputClassNames,
    icon: icon,
    id: id,
    value: value,
    valueDefault: valueDefault,
    "aria-label": ariaLabel,
    onChange: handleChange
  }), append && React.createElement("div", {
    className: appendClassNames
  }, typeof append === 'string' ? React.createElement("span", {
    className: textClassNames
  }, append) : append), children))));
};
InputGroup.defaultProps = {
  tag: 'div',
  type: 'text'
};
function _templateObject$D() {
  var data = _taggedTemplateLiteralLoose([""]);
  _templateObject$D = function _templateObject() {
    return data;
  };
  return data;
}
var Component$B = styled.div(_templateObject$D());
var Jumbotron = function Jumbotron(props) {
  var className = props.className,
    children = props.children,
    fluid = props.fluid,
    attributes = _objectWithoutPropertiesLoose(props, ["className", "children", "fluid"]);
  var jumbotronClasses = classNames("jumbotron", fluid ? "jumbotron-fluid" : false, className);
  var jumbotronComponent = React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$B, Object.assign({
    "data-test": "jumbotron"
  }, attributes, {
    className: jumbotronClasses
  }), children));
  return jumbotronComponent;
};
var Link = function Link(props) {
  var active = props.active,
    children = props.children,
    className = props.className,
    disabled = props.disabled,
    onMouseUp = props.onMouseUp,
    onTouchStart = props.onTouchStart,
    to = props.to,
    attributes = _objectWithoutPropertiesLoose(props, ["active", "children", "className", "disabled", "onMouseUp", "onTouchStart", "to"]);
  var classes = classNames("nav-link", disabled && "disabled", active && "active", className);
  var linkComponent = React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Link$1, Object.assign({
    "data-test": "link-router",
    className: classes,
    onMouseUp: onMouseUp,
    onTouchStart: onTouchStart,
    to: to
  }, attributes), children));
  return linkComponent;
};
Link.defaultProps = {
  active: false,
  className: "",
  disabled: false
};
function _templateObject$E() {
  var data = _taggedTemplateLiteralLoose(["\n  border: none;\n"]);
  _templateObject$E = function _templateObject() {
    return data;
  };
  return data;
}
var Component$C = styled.ul(_templateObject$E());
var ListGroup = function ListGroup(props) {
  var children = props.children,
    className = props.className,
    attributes = _objectWithoutPropertiesLoose(props, ["children", "className", "tag"]);
  var classes = classNames("list-group", className);
  var listGroupComponent = React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$C, Object.assign({
    "data-test": "list-group"
  }, attributes, {
    className: classes
  }), children));
  return listGroupComponent;
};
ListGroup.defaultProps = {
  tag: "ul"
};
function _templateObject2$e() {
  var data = _taggedTemplateLiteralLoose(["\n  ", "\n"]);
  _templateObject2$e = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$F() {
  var data = _taggedTemplateLiteralLoose(["\n  ", "\n"]);
  _templateObject$F = function _templateObject() {
    return data;
  };
  return data;
}
var Component$D = styled.li(_templateObject$F(), colorStyle);
var LinkComponent = styled(Link$1)(_templateObject2$e(), colorStyle);
var ListGroupItem = function ListGroupItem(props) {
  var active = props.active,
    children = props.children,
    className = props.className,
    color = props.color,
    disabled = props.disabled,
    hover = props.hover,
    href = props.href,
    attributes = _objectWithoutPropertiesLoose(props, ["active", "children", "className", "color", "disabled", "hover", "info", "to", "tag", "href"]);
  var classes = classNames("list-group-item", className, active && "active", disabled, color && "list-group-item-" + color, hover && "list-group-item-action");
  var listGroupItemComponent = href ? React.createElement(LinkComponent, Object.assign({
    "data-test": "list-group-item"
  }, attributes, {
    className: classes,
    colors: color,
    to: href,
    style: {
      textDecoration: "none"
    }
  }), children) : React.createElement(Component$D, Object.assign({
    "data-test": "list-group-item"
  }, attributes, {
    className: classes,
    colors: color
  }), children);
  return React.createElement(ThemeProvider, {
    theme: theme
  }, listGroupItemComponent);
};
ListGroupItem.defaultProps = {
  tag: "li"
};
var patternGroup = {
  pattern1: "img/overlays/01.png",
  pattern2: "img/overlays/02.png",
  pattern3: "img/overlays/03.png",
  pattern4: "img/overlays/04.png",
  pattern5: "img/overlays/05.png",
  pattern6: "img/overlays/06.png",
  pattern7: "img/overlays/07.png",
  pattern8: "img/overlays/08.png",
  pattern9: "img/overlays/09.png"
};
var pattern1 = patternGroup.pattern1,
  pattern2 = patternGroup.pattern2,
  pattern3 = patternGroup.pattern3,
  pattern4 = patternGroup.pattern4,
  pattern5 = patternGroup.pattern5,
  pattern6 = patternGroup.pattern6,
  pattern7 = patternGroup.pattern7,
  pattern8 = patternGroup.pattern8,
  pattern9 = patternGroup.pattern9;
var Component$E = styled("span")(space, colorStyle, {
  position: "absolute",
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  width: "100 %",
  height: "100 %",
  overflow: " hidden",
  backgroundAttachment: "fixed"
}, variant({
  prop: "pattern",
  variants: {
    pattern1: {
      backgroundImage: "url(" + pattern1 + ")",
      backgroundAttachment: "fixed"
    },
    pattern2: {
      backgroundImage: "url(" + pattern2 + ")",
      backgroundAttachment: "fixed"
    },
    pattern3: {
      backgroundImage: "url(" + pattern3 + ")",
      backgroundAttachment: "fixed"
    },
    pattern4: {
      backgroundImage: "url(" + pattern4 + ")",
      backgroundAttachment: "fixed"
    },
    pattern5: {
      backgroundImage: "url(" + pattern5 + ")",
      backgroundAttachment: "fixed"
    },
    pattern6: {
      backgroundImage: "url(" + pattern6 + ")",
      backgroundAttachment: "fixed"
    },
    pattern7: {
      backgroundImage: "url(" + pattern7 + ")",
      backgroundAttachment: "fixed"
    },
    pattern8: {
      backgroundImage: "url(" + pattern8 + ")",
      backgroundAttachment: "fixed"
    },
    pattern9: {
      backgroundImage: "url(" + pattern9 + ")",
      backgroundAttachment: "fixed"
    }
  }
}), variant({
  prop: "overlay",
  variants: {
    blueLight: {
      backgroundColor: "rgba(33,150,243,0.3)",
      color: "#222222"
    },
    redLight: {
      backgroundColor: "rgba(244,67,54,0.3);",
      color: "#222222"
    },
    pinkLight: {
      backgroundColor: "rgba(233,30,99,0.3",
      color: "#222222"
    },
    yellowLight: {
      backgroundColor: "rgba(255,235,59,0.3)",
      color: "#222222"
    },
    orangeLight: {
      backgroundColor: "rgba(255,152,0,0.3)",
      color: "#222222"
    },
    purpleLight: {
      backgroundColor: "rgba(156,39,176,0.3)",
      color: "#222222"
    },
    brownLight: {
      backgroundColor: "rgba(33,150,243,0.3)",
      color: "#222222"
    },
    tealLight: {
      backgroundColor: "rgba(0,150,136,0.3)",
      color: "#222222"
    },
    cyanLight: {
      backgroundColor: "rgba(0,188,212,0.3)",
      color: "#222222"
    },
    greenLight: {
      backgroundColor: "rgba(76,175,80,0.3);",
      color: "#222222"
    },
    indigoLight: {
      backgroundColor: "rgba(63,81,181,0.3)",
      color: "#222222"
    },
    darkLight: {
      backgroundColor: "rgba(0,0,0,0.3)",
      color: "white"
    },
    blueStrong: {
      backgroundColor: "rgba(33,150,243,0.7)",
      color: "white"
    },
    redStrong: {
      backgroundColor: "rgba(244,67,54,0.7);",
      color: "white"
    },
    pinkStrong: {
      backgroundColor: "rgba(233,30,99,0.7",
      color: "white"
    },
    yellowStrong: {
      backgroundColor: "rgba(255,235,59,0.7)",
      color: "white"
    },
    orangeStrong: {
      backgroundColor: "rgba(255,152,0,0.7)",
      color: "white"
    },
    purpleStrong: {
      backgroundColor: "rgba(156,39,176,0.7)",
      color: "white"
    },
    brownStrong: {
      backgroundColor: "rgba(153,102,51,0.7)",
      color: "white"
    },
    tealStrong: {
      backgroundColor: "rgba(0,150,136,0.7)",
      color: "white"
    },
    cyanStrong: {
      backgroundColor: "rgba(0,188,212,0.7)",
      color: "white"
    },
    greenStrong: {
      backgroundColor: "rgba(76,175,80,0.7);",
      color: "white"
    },
    indigoStrong: {
      backgroundColor: "rgba(63,81,181,0.7)",
      color: "white"
    },
    darkStrong: {
      backgroundColor: "rgba(0,0,0,0.7)",
      color: "white"
    }
  }
}));
var Mask = function Mask(props) {
  var children = props.children,
    className = props.className,
    overlay = props.overlay,
    pattern = props.pattern,
    tag = props.tag,
    attributes = _objectWithoutPropertiesLoose(props, ["children", "className", "overlay", "pattern", "tag"]);
  var maskClasses = classNames(className);
  var maskComponent = React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$E, Object.assign({
    as: tag,
    "data-test": "mask"
  }, attributes, {
    pattern: pattern,
    overlay: overlay,
    className: maskClasses
  }), children));
  return maskComponent;
};
Mask.defaultProps = {
  className: "",
  overlay: "",
  pattern: "",
  tag: "div"
};
function _templateObject$G() {
  var data = _taggedTemplateLiteralLoose(["\n  padding: 0;\n\n  .modal-dialog .modal-content {\n    border: 0;\n    border-radius: 0.125rem;\n    box-shadow: 0 5px 11px 0 rgba(0, 0, 0, 0.18),\n      0 4px 15px 0 rgba(0, 0, 0, 0.15);\n  }\n  .modal-dialog .modal-content .modal-header {\n    border-top-left-radius: 0.125rem;\n    border-top-right-radius: 0.125rem;\n  }\n  .modal-dialog.cascading-modal {\n    margin-top: 10%;\n  }\n  .modal-dialog.cascading-modal .close {\n    color: #fff;\n    text-shadow: none;\n    outline: 0;\n    opacity: 1;\n  }\n  .modal-dialog.cascading-modal .modal-header {\n    padding: 1.5rem;\n    margin: -2rem 1rem 1rem 1rem;\n    text-align: center;\n    border: none;\n    border-radius: 0.125rem;\n    box-shadow: 0 5px 11px 0 rgba(0, 0, 0, 0.18),\n      0 4px 15px 0 rgba(0, 0, 0, 0.15);\n  }\n  .modal-dialog.cascading-modal .modal-header .close {\n    margin-right: 1rem;\n  }\n  .modal-dialog.cascading-modal .modal-header .title {\n    width: 100%;\n    margin-bottom: 0;\n    font-size: 1.25rem;\n  }\n  .modal-dialog.cascading-modal .modal-header .title .fas,\n  .modal-dialog.cascading-modal .modal-header .title .fab,\n  .modal-dialog.cascading-modal .modal-header .title .far {\n    margin-right: 9px;\n  }\n  .modal-dialog.cascading-modal .modal-header .social-buttons {\n    margin-top: 1.5rem;\n  }\n  .modal-dialog.cascading-modal .modal-header .social-buttons a {\n    font-size: 1rem;\n  }\n  .modal-dialog.cascading-modal .modal-c-tabs .md-tabs {\n    display: flex;\n    margin: -1.5rem 1rem 0 1rem;\n    box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.16),\n      0 2px 10px 0 rgba(0, 0, 0, 0.12);\n  }\n  .modal-dialog.cascading-modal .modal-c-tabs .md-tabs li {\n    flex: 1;\n  }\n  .modal-dialog.cascading-modal .modal-c-tabs .md-tabs li a {\n    text-align: center;\n  }\n  .modal-dialog.cascading-modal .modal-c-tabs .tab-content {\n    padding: 1.7rem 0 0 0;\n  }\n  .modal-dialog.cascading-modal .modal-body,\n  .modal-dialog.cascading-modal .modal-footer {\n    padding-right: 2rem;\n    padding-left: 2rem;\n    color: #616161;\n  }\n  .modal-dialog.cascading-modal .modal-body .additional-option,\n  .modal-dialog.cascading-modal .modal-footer .additional-option {\n    margin-top: 1rem;\n    text-align: center;\n  }\n  .modal-dialog.cascading-modal.modal-avatar {\n    margin-top: 6rem;\n  }\n  .modal-dialog.cascading-modal.modal-avatar .modal-header {\n    margin: -6rem 0 -1rem;\n    box-shadow: none;\n  }\n  .modal-dialog.cascading-modal.modal-avatar .modal-header img {\n    width: 130px;\n    margin-right: auto;\n    margin-left: auto;\n    box-shadow: 0 8px 17px 0 rgba(0, 0, 0, 0.2),\n      0 6px 20px 0 rgba(0, 0, 0, 0.19);\n  }\n  .modal-dialog.modal-notify .heading {\n    padding: 0.3rem;\n    margin: 0;\n    font-size: 1.15rem;\n    color: #fff;\n  }\n  .modal-dialog.modal-notify .modal-header {\n    border: 0;\n    box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.16),\n      0 2px 10px 0 rgba(0, 0, 0, 0.12);\n  }\n  .modal-dialog.modal-notify .close {\n    opacity: 1;\n  }\n  .modal-dialog.modal-notify .modal-body {\n    padding: 1.5rem;\n    color: #616161;\n  }\n  .modal-dialog.modal-notify.modal-primary .modal-header {\n    background-color: #4285f4;\n  }\n  .modal-dialog.modal-notify.modal-primary .fas,\n  .modal-dialog.modal-notify.modal-primary .fab,\n  .modal-dialog.modal-notify.modal-primary .far {\n    color: #4285f4;\n  }\n  .modal-dialog.modal-notify.modal-primary .badge {\n    background-color: #4285f4;\n  }\n  .modal-dialog.modal-notify.modal-primary .btn .fas,\n  .modal-dialog.modal-notify.modal-primary .btn .fab,\n  .modal-dialog.modal-notify.modal-primary .btn .far {\n    color: #fff;\n  }\n  .modal-dialog.modal-notify.modal-primary .btn.btn-outline-primary .fas,\n  .modal-dialog.modal-notify.modal-primary .btn.btn-outline-primary .fab,\n  .modal-dialog.modal-notify.modal-primary .btn.btn-outline-primary .far {\n    color: #4285f4;\n  }\n  .modal-dialog.modal-notify.modal-danger .modal-header {\n    background-color: #ff3547;\n  }\n  .modal-dialog.modal-notify.modal-danger .fas,\n  .modal-dialog.modal-notify.modal-danger .fab,\n  .modal-dialog.modal-notify.modal-danger .far {\n    color: #ff3547;\n  }\n  .modal-dialog.modal-notify.modal-danger .badge {\n    background-color: #ff3547;\n  }\n  .modal-dialog.modal-notify.modal-danger .btn .fas,\n  .modal-dialog.modal-notify.modal-danger .btn .fab,\n  .modal-dialog.modal-notify.modal-danger .btn .far {\n    color: #fff;\n  }\n  .modal-dialog.modal-notify.modal-danger .btn.btn-outline-danger .fas,\n  .modal-dialog.modal-notify.modal-danger .btn.btn-outline-danger .fab,\n  .modal-dialog.modal-notify.modal-danger .btn.btn-outline-danger .far {\n    color: #ff3547;\n  }\n  .modal-dialog.modal-notify.modal-warning .modal-header {\n    background-color: #fb3;\n  }\n  .modal-dialog.modal-notify.modal-warning .fas,\n  .modal-dialog.modal-notify.modal-warning .fab,\n  .modal-dialog.modal-notify.modal-warning .far {\n    color: #fb3;\n  }\n  .modal-dialog.modal-notify.modal-warning .badge {\n    background-color: #fb3;\n  }\n  .modal-dialog.modal-notify.modal-warning .btn .fas,\n  .modal-dialog.modal-notify.modal-warning .btn .fab,\n  .modal-dialog.modal-notify.modal-warning .btn .far {\n    color: #fff;\n  }\n  .modal-dialog.modal-notify.modal-warning .btn.btn-outline-warning .fas,\n  .modal-dialog.modal-notify.modal-warning .btn.btn-outline-warning .fab,\n  .modal-dialog.modal-notify.modal-warning .btn.btn-outline-warning .far {\n    color: #fb3;\n  }\n  .modal-dialog.modal-notify.modal-success .modal-header {\n    background-color: #00c851;\n  }\n  .modal-dialog.modal-notify.modal-success .fas,\n  .modal-dialog.modal-notify.modal-success .fab,\n  .modal-dialog.modal-notify.modal-success .far {\n    color: #00c851;\n  }\n  .modal-dialog.modal-notify.modal-success .badge {\n    background-color: #00c851;\n  }\n  .modal-dialog.modal-notify.modal-success .btn .fas,\n  .modal-dialog.modal-notify.modal-success .btn .fab,\n  .modal-dialog.modal-notify.modal-success .btn .far {\n    color: #fff;\n  }\n  .modal-dialog.modal-notify.modal-success .btn.btn-outline-success .fas,\n  .modal-dialog.modal-notify.modal-success .btn.btn-outline-success .fab,\n  .modal-dialog.modal-notify.modal-success .btn.btn-outline-success .far {\n    color: #00c851;\n  }\n  .modal-dialog.modal-notify.modal-info .modal-header {\n    background-color: #33b5e5;\n  }\n  .modal-dialog.modal-notify.modal-info .fas,\n  .modal-dialog.modal-notify.modal-info .fab,\n  .modal-dialog.modal-notify.modal-info .far {\n    color: #33b5e5;\n  }\n  .modal-dialog.modal-notify.modal-info .badge {\n    background-color: #33b5e5;\n  }\n  .modal-dialog.modal-notify.modal-info .btn .fas,\n  .modal-dialog.modal-notify.modal-info .btn .fab,\n  .modal-dialog.modal-notify.modal-info .btn .far {\n    color: #fff;\n  }\n  .modal-dialog.modal-notify.modal-info .btn.btn-outline-info .fas,\n  .modal-dialog.modal-notify.modal-info .btn.btn-outline-info .fab,\n  .modal-dialog.modal-notify.modal-info .btn.btn-outline-info .far {\n    color: #33b5e5;\n  }\n  @media (min-width: 768px) {\n    .modal-dialog.modal-top {\n      top: 0;\n    }\n    .modal-dialog.modal-left {\n      left: 0;\n    }\n    .modal-dialog.modal-right {\n      right: 0;\n    }\n    .modal-dialog.modal-bottom {\n      bottom: 0;\n    }\n    .modal-dialog.modal-top-left {\n      top: 10px;\n      left: 10px;\n    }\n    .modal-dialog.modal-top-right {\n      top: 10px;\n      right: 10px;\n    }\n    .modal-dialog.modal-bottom-left {\n      bottom: 10px;\n      left: 10px;\n    }\n    .modal-dialog.modal-bottom-right {\n      right: 10px;\n      bottom: 10px;\n    }\n  }\n  .modal.fade.top:not(.show) .modal-dialog {\n    transform: translate3d(0, -25%, 0);\n  }\n  .modal.fade.left:not(.show) .modal-dialog {\n    transform: translate3d(-25%, 0, 0);\n  }\n  .modal.fade.right:not(.show) .modal-dialog {\n    transform: translate3d(25%, 0, 0);\n  }\n  .modal.fade.bottom:not(.show) .modal-dialog {\n    transform: translate3d(0, 25%, 0);\n  }\n  @media (min-width: 992px) {\n    .modal.modal-scrolling {\n      position: relative;\n    }\n    .modal.modal-scrolling .modal-dialog {\n      position: fixed;\n      z-index: 1050;\n    }\n    .modal.modal-content-clickable {\n      top: auto;\n      bottom: auto;\n    }\n    .modal.modal-content-clickable .modal-dialog {\n      position: fixed;\n    }\n    .modal-fluid {\n      width: 100%;\n      max-width: 100%;\n    }\n    .modal-fluid .modal-content {\n      width: 100%;\n    }\n    .modal-frame {\n      position: absolute;\n      width: 100%;\n      max-width: 100% !important;\n      margin: 0 !important;\n    }\n    .modal-frame.modal-bottom {\n      bottom: 0;\n    }\n    .modal-full-height {\n      position: absolute;\n      top: 0;\n      right: 0;\n      display: flex;\n      width: 400px;\n      height: auto;\n      min-height: 100%;\n      margin: 0;\n    }\n    .modal-full-height.modal-top,\n    .modal-full-height.modal-bottom {\n      display: block;\n      width: 100%;\n      max-width: 100%;\n      height: auto;\n    }\n    .modal-full-height.modal-top {\n      bottom: auto;\n    }\n    .modal-full-height.modal-bottom {\n      top: auto;\n      min-height: 0;\n    }\n    .modal-full-height .modal-content {\n      width: 100%;\n    }\n    .modal-full-height.modal-lg {\n      width: 90%;\n      max-width: 90%;\n    }\n  }\n  @media (min-width: 992px) and (min-width: 992px) {\n    .modal-full-height.modal-lg {\n      width: 800px;\n      max-width: 800px;\n    }\n  }\n  @media (min-width: 992px) and (min-width: 1200px) {\n    .modal-full-height.modal-lg {\n      width: 1000px;\n      max-width: 1000px;\n    }\n  }\n  @media (min-width: 992px) {\n    .modal-side {\n      position: absolute;\n      right: 10px;\n      bottom: 10px;\n      width: 400px;\n      margin: 0;\n    }\n  }\n"]);
  _templateObject$G = function _templateObject() {
    return data;
  };
  return data;
}
var Component$F = styled.div(_templateObject$G());
var Modal = function Modal(props) {
  var _classNames;
  var animation = props.animation,
    backdrop = props.backdrop,
    backdropClassName = props.backdropClassName,
    backdropTransitionTimeout = props.backdropTransitionTimeout,
    cascading = props.cascading,
    centered = props.centered,
    children = props.children,
    className = props.className,
    contentClassName = props.contentClassName,
    disableFocusTrap = props.disableFocusTrap,
    fade = props.fade,
    frame = props.frame,
    fullHeight = props.fullHeight,
    id = props.id,
    inline = props.inline,
    isOpen = props.isOpen,
    modalStyle = props.modalStyle,
    modalStylesWithoutBackdrop = props.modalStylesWithoutBackdrop,
    modalTransitionTimeout = props.modalTransitionTimeout,
    noClickableBodyWithoutBackdrop = props.noClickableBodyWithoutBackdrop,
    position = props.position,
    role = props.role,
    side = props.side,
    size = props.size,
    tabIndex = props.tabIndex,
    wrapClassName = props.wrapClassName,
    wrapperStyles = props.wrapperStyles,
    attributes = _objectWithoutPropertiesLoose(props, ["animation", "backdrop", "backdropClassName", "backdropTransitionTimeout", "cascading", "centered", "children", "className", "contentClassName", "disableFocusTrap", "fade", "frame", "fullHeight", "id", "inline", "isOpen", "keyboard", "modalStyle", "modalStylesWithoutBackdrop", "modalTransitionTimeout", "noClickableBodyWithoutBackdrop", "overflowScroll", "position", "role", "side", "size", "tabIndex", "toggle", "wrapClassName", "wrapperStyles", "zIndex"]);
  var _useState = useState(props.isOpen || false),
    isModalOpen = _useState[0],
    setModalOpen = _useState[1];
  var overflowStatement = "overflow-hidden";
  var modalContent = useRef(null);
  var mounted = useRef();
  useEffect(function () {
    var setComponentOpen = function setComponentOpen() {
      var isOpen = props.isOpen;
      if (isModalOpen !== isOpen) {
        Promise.resolve().then(function () {
          return setModalOpen(isOpen);
        }).then(function () {
          if (isModalOpen) {
            document.body.classList.add(overflowStatement);
          } else {
            document.body.classList.remove(overflowStatement);
          }
        });
      }
    };
    if (!mounted.current) {
      mounted.current = true;
    } else {
      setComponentOpen();
    }
  }, [isOpen]);
  var handleOnEntered = function handleOnEntered(type, node) {
    if (type === "backdrop" && props.fade === false) {
      return;
    }
    node.classList.add("show");
    props.autoFocus && node.focus();
    if (type === "modal") {
      props.showModal && props.showModal();
    }
  };
  var handleOnExit = function handleOnExit(type, node) {
    if (type === "backdrop" && props.fade === false) {
      return;
    }
    node.classList.remove("show");
    if (type === "modal") {
      props.hideModal && props.hideModal();
    }
  };
  var handleOnExited = function handleOnExited() {
    props.hiddenModal && props.hiddenModal();
  };
  var handleBackdropClick = function handleBackdropClick(e) {
    if (!props.backdrop || e.target.closest('[role="dialog"]') && !e.target.classList.contains("modal")) {
      return;
    }
    if (!(e.clientX > e.target.clientWidth || e.clientY > e.target.clientHeight)) {
      if (!modalContent.current.contains(e.target)) {
        if (!props.disableBackdrop) {
          props.toggle();
        }
      }
    }
  };
  var handleEscape = function handleEscape(e) {
    if (props.keyboard && e.keyCode === 27) {
      e.preventDefault();
      props.toggle();
    }
  };
  var whichPosition = function whichPosition() {
    if (position === "bottom-right") {
      return {
        top: "auto",
        bottom: 10,
        right: 10,
        left: "auto"
      };
    } else if (position === "bottom-left") {
      return {
        top: "auto",
        bottom: 10,
        right: "auto",
        left: 10
      };
    } else if (position === "top-right") {
      return {
        top: 10,
        bottom: "auto",
        right: 10,
        left: "auto"
      };
    } else if (position === "top-left") {
      return {
        top: 10,
        bottom: "auto",
        right: "auto",
        left: 10
      };
    } else if (position === "right") {
      return {
        right: 0
      };
    } else if (position === "left") {
      return {
        left: 0
      };
    } else if (position === "bottom") {
      return {
        bottom: 0
      };
    } else if (position === "top") {
      return {
        top: 0
      };
    } else if (position === "center") {
      return {
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)"
      };
    } else {
      return {
        top: 0,
        left: 0,
        bottom: 0,
        right: 0
      };
    }
  };
  var timeout = fade ? modalTransitionTimeout : 0;
  var backdropTimeout = fade ? backdropTransitionTimeout : 0;
  var removeBackdropClass = _extends({
    position: "fixed"
  }, whichPosition(), modalStylesWithoutBackdrop);
  var removeBackdropConditions = !backdrop && isModalOpen && !noClickableBodyWithoutBackdrop;
  var modalDialogClasses = classNames((_classNames = {
    "cascading-modal": cascading,
    "modal-side": side,
    "modal-full-height": fullHeight,
    "modal-frame": frame,
    "modal-dialog-centered": centered
  }, _classNames["modal-" + size] = size, _classNames["modal-" + position] = position, _classNames["modal-notify white-text modal-" + modalStyle] = modalStyle, _classNames), "modal-dialog", className);
  var splitPosition = position.split("-");
  var wrapperClasses = classNames({
    modal: !inline,
    fade: fade,
    top: fade && !animation && !position,
    animation: fade && animation
  }, fade && position && position && splitPosition.length > 1 ? splitPosition[1] : splitPosition[0], wrapClassName);
  var backdropClasses = classNames("modal-backdrop", fade ? "fade" : "show", backdropClassName);
  var contentClasses = classNames("modal-content", contentClassName);
  var modalAttributes = returnAttributes(_extends({
    style: {
      display: "block",
      position: removeBackdropConditions && "fixed",
      width: removeBackdropConditions && 0
    },
    id: id,
    tabIndex: tabIndex,
    role: role,
    "aria-hidden": "true"
  }, attributes));
  var styles = removeBackdropConditions ? removeBackdropClass : {};
  var modal = React.createElement(Component$F, Object.assign({
    "data-test": "modal",
    onKeyUp: handleEscape,
    className: wrapperClasses,
    style: wrapperStyles
  }, modalAttributes), React.createElement("div", {
    style: styles,
    className: modalDialogClasses,
    role: "document"
  }, React.createElement("div", {
    ref: function ref(elem) {
      return modalContent.current = elem;
    },
    className: contentClasses
  }, children)));
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(React.Fragment, null, backdrop && React.createElement(Transition$1, {
    timeout: backdropTimeout,
    in: isModalOpen,
    appear: isModalOpen,
    mountOnEnter: true,
    unmountOnExit: true,
    onEntered: function onEntered(node) {
      return handleOnEntered("backdrop", node);
    },
    onExit: function onExit(node) {
      return handleOnExit("backdrop", node);
    },
    onExited: handleOnExited
  }, React.createElement("div", {
    className: backdropClasses
  })), React.createElement(Transition$1, {
    timeout: timeout,
    in: isModalOpen,
    appear: isModalOpen,
    mountOnEnter: true,
    unmountOnExit: true,
    onMouseDown: function onMouseDown(e) {
      return handleBackdropClick(e);
    },
    onEntered: function onEntered(node) {
      return handleOnEntered("modal", node);
    },
    onExit: function onExit(node) {
      return handleOnExit("modal", node);
    }
  }, !disableFocusTrap ? React.createElement(FocusTrap, null, modal) : modal)));
};
Modal.defaultProps = {
  autoFocus: true,
  backdrop: true,
  backdropTransitionTimeout: 150,
  disableBackdrop: false,
  disableFocusTrap: true,
  fade: true,
  isOpen: false,
  keyboard: true,
  modalTransitionTimeout: 300,
  overflowScroll: true,
  position: "center",
  role: "dialog",
  tabIndex: "-1",
  zIndex: 1050,
  noClickableBodyWithoutBackdrop: false
};
function _templateObject$H() {
  var data = _taggedTemplateLiteralLoose(["\n  .close {\n    height: 10px;\n    width: 10px;\n    margin-right: 10px;\n    &:focus,\n    &:hover {\n      outline: none;\n      border: none;\n    }\n  }\n"]);
  _templateObject$H = function _templateObject() {
    return data;
  };
  return data;
}
var Component$G = styled.h4(_templateObject$H());
var ModalHeader = function ModalHeader(props) {
  var closeButton;
  var className = props.className,
    children = props.children,
    toggle = props.toggle,
    Tag = props.tag,
    closeAriaLabel = props.closeAriaLabel,
    titleClass = props.titleClass,
    attributes = _objectWithoutPropertiesLoose(props, ["className", "children", "toggle", "tag", "closeAriaLabel", "titleClass"]);
  var classes = classNames("modal-header", className);
  var titleClasses = classNames("modal-title", titleClass);
  if (toggle) {
    closeButton = React.createElement("button", {
      type: "button",
      onClick: function onClick() {
        return toggle;
      },
      className: "close",
      "aria-label": closeAriaLabel
    }, React.createElement("span", {
      "aria-hidden": "true",
      style: {
        height: "100px !important"
      }
    }, React.createElement("svg", {
      height: "10",
      viewBox: "0 0 329.26933 329",
      width: "10",
      xmlns: "http://www.w3.org/2000/svg"
    }, React.createElement("path", {
      d: "m194.800781 164.769531 128.210938-128.214843c8.34375-8.339844 8.34375-21.824219 0-30.164063-8.339844-8.339844-21.824219-8.339844-30.164063 0l-128.214844 128.214844-128.210937-128.214844c-8.34375-8.339844-21.824219-8.339844-30.164063 0-8.34375 8.339844-8.34375 21.824219 0 30.164063l128.210938 128.214843-128.210938 128.214844c-8.34375 8.339844-8.34375 21.824219 0 30.164063 4.15625 4.160156 9.621094 6.25 15.082032 6.25 5.460937 0 10.921875-2.089844 15.082031-6.25l128.210937-128.214844 128.214844 128.214844c4.160156 4.160156 9.621094 6.25 15.082032 6.25 5.460937 0 10.921874-2.089844 15.082031-6.25 8.34375-8.339844 8.34375-21.824219 0-30.164063zm0 0"
    }))));
  }
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$G, Object.assign({
    "data-test": "modal-header"
  }, attributes, {
    className: classes
  }), React.createElement(Tag, {
    className: titleClasses
  }, children), closeButton));
};
ModalHeader.defaultProps = {
  tag: "h4",
  closeAriaLabel: "Close"
};
var ModalFooter = function ModalFooter(props) {
  var className = props.className,
    children = props.children,
    center = props.center,
    start = props.start,
    end = props.end,
    around = props.around,
    between = props.between,
    attributes = _objectWithoutPropertiesLoose(props, ["className", "children", "center", "start", "end", "around", "between"]);
  var classes = classNames('modal-footer', className, {
    'justify-content-start': start,
    'justify-content-end': end,
    'justify-content-center': center,
    'justify-content-between': between,
    'justify-content-around': around
  });
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement("div", Object.assign({
    "data-test": 'modal-footer'
  }, attributes, {
    className: classes
  }), children));
};
var ModalBody = function ModalBody(props) {
  var className = props.className,
    children = props.children,
    attributes = _objectWithoutPropertiesLoose(props, ["className", "children"]);
  var classes = classNames('modal-body', className);
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement("div", Object.assign({
    "data-test": 'modal-body'
  }, attributes, {
    className: classes
  }), children));
};
function _templateObject3$a() {
  var data = _taggedTemplateLiteralLoose(["\n\tcolor:#000;\n\tbackground:#fff;\n\tmargin-top:10px;\n\tmax-height:0px;\n\toverflow-y:scroll;\n\ttransition: all 0.3s ease-in-out;\n\n\t&.display {\n\t\tmax-height:150px;\n\t\tbox-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2);\n\t}\n\n\t::-webkit-scrollbar {\n\t\twidth:3px;\n\t}\n\n\t::-webkit-scrollbar-thumb {\n\t\tborder-radius:5px;\n\t\tbackground-color: #ccc;\n\t}\n\n\tspan {\n\t\tfont-size:0.9em;\n\t}\n\n\tdiv {\n\t\tpadding:10px;\n\t\tcursor:pointer;\n\t\ttransition: all 0.1s ease-in-out;\n\n\t\t:hover{\n\t\t\tbackground-color:#ddd;\n\t\t}\n\t}\n"]);
  _templateObject3$a = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$f() {
  var data = _taggedTemplateLiteralLoose(["\n\tpadding:5px;\n\tdisplay:flex;\n\tflex-wrap:wrap;\n\tcursor:pointer;\n\tmin-height:35px;\n\tborder:1px solid;\n\talign-items:center;\n\tbox-sizing:border-box;\n\n\tspan {\n\t\tfont-size:0.8em;\n\t\tmargin:2px;\n\n\t\ti {\n\t\t\tfont-size:0.9em;\n\t\t\tmargin:0px;\n\t\t}\n\t}\n\n\tform {\n\t\tflex:1;\n\n\t\tinput{\n\t\t\twidth:100%;\n\t\t\tmin-width:10px;\n\t\t\tborder:none;\n\t\t\toutline:none;\n\t\t\tfont-size:0.9em;\n\t\t}\n\t}\n"]);
  _templateObject2$f = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$I() {
  var data = _taggedTemplateLiteralLoose(["\n\twidth:100%;\n"]);
  _templateObject$I = function _templateObject() {
    return data;
  };
  return data;
}
var Component$H = styled.div(_templateObject$I());
var Div = styled.div(_templateObject2$f());
var Options = styled.div(_templateObject3$a());
var Multiselect = function Multiselect(props) {
  var tag = props.tag,
    color = props.color,
    submit = props.submit,
    options = props.options,
    search = props.search,
    selected = props.selected,
    className = props.className,
    limitedOptions = props.limitedOptions,
    optionClassName = props.optionClassName,
    CDBBadge = props.CDBBadge,
    attributes = _objectWithoutPropertiesLoose(props, ["tag", "text", "showing", "color", "submit", "options", "search", "selected", "className", "limitedOptions", "optionClassName", "CDBBadge"]);
  var _useState = useState(false),
    show = _useState[0],
    setShow = _useState[1];
  var _useState2 = useState([selected]),
    active = _useState2[0];
  var _useState3 = useState(''),
    input = _useState3[0],
    setInput = _useState3[1];
  var wrapperRef = useRef(null);
  var inputRef = useRef(null);
  var multiSelectClasses = classNames(className);
  var optionClasses = classNames(show ? 'display' : '', optionClassName);
  var display = function display() {
    (search || submit) && inputRef.current.focus();
    setShow(!show);
    console.log('tampered');
  };
  var addActive = function addActive(text) {
    var condition = true;
    for (var i = 0; i < active.length; i++) {
      text === active[i] && (condition = false);
    }
    condition && active.push(text);
  };
  var setShowing = function setShowing(option) {
    limitedOptions && (option.showing = false);
    addActive(option.text);
    setShow(!show);
    setInput('');
  };
  var close = function close(item, index) {
    active.splice(index, 1);
    for (var i = 0; i < updatedOptions.length; i++) {
      item === updatedOptions[i].text && (updatedOptions[i].showing = true);
    }
  };
  var inptUpdate = function inptUpdate(e) {
    setInput(e.target.value);
    setShow(true);
  };
  var handleSubmit = function handleSubmit(e) {
    e.preventDefault();
    submit && addActive(input);
    setInput('');
  };
  var updatedOptions = options.filter(function (item) {
    return item.text.toLowerCase().includes(input.toLowerCase());
  });
  var useOutsideClose = function useOutsideClose(ref) {
    useEffect(function () {
      function handleClickOutside(event) {
        if (ref.current && !ref.current.contains(event.target)) {
          setShow(false);
        }
      }
      document.addEventListener('mousedown', handleClickOutside);
      return function () {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [ref]);
  };
  var multiselectComponent = React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$H, Object.assign({
    "data-test": 'select',
    className: multiSelectClasses,
    ref: wrapperRef,
    onClick: useOutsideClose(wrapperRef),
    as: tag
  }, attributes), React.createElement(Div, {
    onClick: display
  }, active.map(function (item, index) {
    return React.createElement(CDBBadge, {
      key: index,
      flat: true,
      colors: color
    }, item, ' ', React.createElement("div", {
      onClick: function onClick() {
        return close(item, index);
      }
    }, ' ', React.createElement(Icon, {
      fas: true,
      icon: 'times'
    })));
  }), (search || submit) && React.createElement("form", {
    onSubmit: handleSubmit
  }, React.createElement("input", {
    placeholder: 'Tag',
    ref: inputRef,
    value: input,
    onChange: inptUpdate,
    type: 'text'
  }))), React.createElement(Options, {
    className: optionClasses
  }, options && (search ? updatedOptions : options).map(function (optionType, index) {
    var text = optionType.text,
      showing = optionType.showing;
    return showing !== false && React.createElement("div", {
      key: index,
      onClick: function onClick() {
        return setShowing(optionType);
      }
    }, React.createElement("span", null, text));
  }))));
  return multiselectComponent;
};
Multiselect.defaultProps = {
  tag: 'div',
  color: 'primary'
};
function _templateObject$J() {
  var data = _taggedTemplateLiteralLoose(["\n  &.nav-tabs {\n    border-bottom: 5px solid #e2e2e2;\n    .nav-item {\n      margin-bottom: -5px;\n    }\n  }\n"]);
  _templateObject$J = function _templateObject() {
    return data;
  };
  return data;
}
var Component$I = styled.div(_templateObject$J());
var Nav = function Nav(props) {
  var children = props.children,
    className = props.className,
    tag = props.tag,
    tabs = props.tabs,
    color = props.color,
    classicTabs = props.classicTabs,
    pills = props.pills,
    header = props.header,
    attributes = _objectWithoutPropertiesLoose(props, ["children", "className", "tag", "tabs", "color", "classicTabs", "pills", "header"]);
  var classes = classNames("nav", tabs && "md-tabs", pills && "md-pills", header && "nav-pills card-header-pills", color && !tabs && !classicTabs && !pills ? color : false, pills && color ? "pills-" + color : false, (tabs || classicTabs) && color ? "tabs-" + color : false, className);
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$I, Object.assign({
    as: tag,
    "data-test": "nav"
  }, attributes, {
    className: classes
  }), children));
};
Nav.defaultProps = {
  tag: "ul",
  classicTabs: false,
  pills: false,
  tabs: false,
  header: false
};
function _templateObject$K() {
  var data = _taggedTemplateLiteralLoose([""]);
  _templateObject$K = function _templateObject() {
    return data;
  };
  return data;
}
var Component$J = styled.nav(_templateObject$K());
var Navbar = function Navbar(props) {
  var _classNames;
  var _useState = useState(false),
    isCollapsed = _useState[0],
    setIsCollapsed = _useState[1];
  var handleScroll = function handleScroll() {
    var scrollingNavbarOffset = props.scrollingNavbarOffset || 50;
    if (window.pageYOffset > scrollingNavbarOffset) {
      setIsCollapsed(true);
    } else {
      setIsCollapsed(false);
    }
  };
  useEffect(function () {
    var scrolling = props.scrolling,
      scrollingNavbarOffset = props.scrollingNavbarOffset;
    if (scrolling || scrollingNavbarOffset) {
      window.addEventListener('scroll', handleScroll);
    }
    return function () {
      if (scrolling || scrollingNavbarOffset) {
        window.removeEventListener('scroll', handleScroll);
      }
    };
  });
  var getExpandClass = function getExpandClass(expand) {
    if (expand === false) {
      return false;
    }
    if (expand === true || expand === 'xs') {
      return 'navbar-expand';
    }
    return "navbar-expand-" + expand;
  };
  var expand = props.expand,
    light = props.light,
    dark = props.dark,
    sticky = props.sticky,
    fixed = props.fixed,
    scrolling = props.scrolling,
    color = props.color,
    className = props.className,
    scrollingNavbarOffset = props.scrollingNavbarOffset,
    tag = props.tag,
    double = props.double,
    transparent = props.transparent,
    attributes = _objectWithoutPropertiesLoose(props, ["expand", "light", "dark", "sticky", "fixed", "scrolling", "color", "className", "scrollingNavbarOffset", "tag", "double", "transparent"]);
  var classes = classNames((_classNames = {
    'navbar-light': light,
    'navbar-dark': dark
  }, _classNames["sticky-" + sticky] = sticky, _classNames["fixed-" + fixed] = fixed, _classNames['scrolling-navbar'] = scrolling || scrollingNavbarOffset, _classNames['double-nav'] = double, _classNames['top-nav-collapse'] = isCollapsed, _classNames["" + color] = color && transparent ? isCollapsed : color, _classNames), 'navbar', getExpandClass(expand), className);
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$J, Object.assign({
    as: tag,
    "data-test": 'navbar'
  }, attributes, {
    className: classes,
    role: 'navigation'
  })));
};
Navbar.defaultProps = {
  tag: 'nav',
  expand: false,
  scrolling: false
};
function _templateObject$L() {
  var data = _taggedTemplateLiteralLoose([""]);
  _templateObject$L = function _templateObject() {
    return data;
  };
  return data;
}
var Component$K = styled.ul(_templateObject$L());
var NavbarNav = function NavbarNav(props) {
  var children = props.children,
    className = props.className,
    right = props.right,
    left = props.left,
    tag = props.tag,
    attributes = _objectWithoutPropertiesLoose(props, ["children", "className", "right", "left", "tag"]);
  var classes = classNames('navbar-nav', right ? 'ml-auto' : left ? 'mr-auto' : 'justify-content-around w-100', className);
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$K, Object.assign({
    as: tag,
    "data-test": 'navbar-nav'
  }, attributes, {
    className: classes
  }), children));
};
NavbarNav.defaultProps = {
  tag: 'ul'
};
function _templateObject$M() {
  var data = _taggedTemplateLiteralLoose(["\n"]);
  _templateObject$M = function _templateObject() {
    return data;
  };
  return data;
}
var Component$L = styled(NavLink$1)(_templateObject$M());
var NavBrand = function NavBrand(props) {
  var className = props.className,
    href = props.href,
    children = props.children,
    attributes = _objectWithoutPropertiesLoose(props, ["className", "href", "children"]);
  var classes = classNames('navbar-brand', className);
  var navBrand = function navBrand() {
    if (href) {
      return React.createElement(Component$L, Object.assign({
        "data-test": 'navbar-brand',
        to: href
      }, attributes, {
        className: classes
      }), " ", children, " ");
    }
    return React.createElement("div", Object.assign({
      "data-test": 'navbar-brand'
    }, attributes, {
      className: classes
    }));
  };
  return React.createElement(ThemeProvider, {
    theme: theme
  }, navBrand());
};
function _templateObject$N() {
  var data = _taggedTemplateLiteralLoose([""]);
  _templateObject$N = function _templateObject() {
    return data;
  };
  return data;
}
var Component$M = styled.li(_templateObject$N());
var NavItem = function NavItem(props) {
  var children = props.children,
    className = props.className,
    active = props.active,
    text = props.text,
    tag = props.tag,
    attributes = _objectWithoutPropertiesLoose(props, ["children", "className", "active", "text", "tag"]);
  var classes = classNames('nav-item', active && 'active', text && 'navbar-text', className);
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$M, Object.assign({
    as: tag,
    "data-test": 'nav-item'
  }, attributes, {
    className: classes
  }), children));
};
NavItem.defaultProps = {
  tag: 'li'
};
var NavLink = function NavLink(props) {
  var children = props.children,
    className = props.className,
    active = props.active,
    to = props.to,
    link = props.link,
    attributes = _objectWithoutPropertiesLoose(props, ["children", "className", "disabled", "active", "to", "link"]);
  var classes = classNames('nav-link', active && 'active', className);
  var Tag = link ? Link : NavLink$1;
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Tag, Object.assign({
    "data-test": 'nav-link',
    className: classes,
    to: to
  }, attributes), children));
};
NavLink.defaultProps = {
  active: false,
  className: '',
  disabled: false,
  link: false
};
function _templateObject$O() {
  var data = _taggedTemplateLiteralLoose([""]);
  _templateObject$O = function _templateObject() {
    return data;
  };
  return data;
}
var Component$N = styled.button(_templateObject$O());
var NavToggle = function NavToggle(props) {
  var right = props.right,
    left = props.left,
    children = props.children,
    className = props.className,
    tag = props.tag,
    image = props.image,
    attributes = _objectWithoutPropertiesLoose(props, ["right", "left", "children", "className", "tag", "image"]);
  var classes = classNames({
    'navbar-toggler-right': right,
    'navbar-toggler-left': left
  }, 'navbar-toggler', className);
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$N, Object.assign({
    "data-test": 'navbar-toggler',
    as: tag
  }, attributes, {
    className: classes
  }), children || (image ? React.createElement("span", {
    className: 'navbar-toggler-icon',
    style: {
      backgroundImage: "url(\"" + image + "\")"
    }
  }) : React.createElement("span", {
    className: 'navbar-toggler-icon'
  }))));
};
NavToggle.defaultProps = {
  tag: 'button',
  type: 'button'
};
function _templateObject3$b() {
  var data = _taggedTemplateLiteralLoose([""]);
  _templateObject3$b = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$g() {
  var data = _taggedTemplateLiteralLoose(["\n          position: absolute;\n          z-ndex: 9999;\n          min-width: 350px;\n        "]);
  _templateObject2$g = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$P() {
  var data = _taggedTemplateLiteralLoose(["\n  flex-basis: none !important;\n  border: none;\n  margin: 0 10px;\n  position: relative;\n  transition: 1s;\n  .toast-message {\n    transition: 1s;\n    padding: 10px;\n  }\n  .toast-container {\n    display: flex;\n    flex-direction: row;\n    padding: 10px;\n    position: relative;\n    width: 100%;\n    gap: 6px;\n    .close-icon {\n      position: absolute;\n      right: 10px;\n      top: 5px;\n      background: none;\n      border: none;\n    }\n    .body {\n      display: flex;\n      flex-direction: column;\n      .small-text {\n        color: #e5e5e5;\n      }\n    }\n    .toggle {\n      display: flex;\n      justify-content: center;\n      position: absolute;\n      bottom: 0;\n      width: 100%;\n      left: 0;\n      right: 0;\n      span {\n        width: 50px;\n        height: 4px;\n        background: #e5e5e5;\n        border-radius: 2px;\n        margin-bottom: 3px;\n        cursor: pointer;\n        pointer-events: auto;\n      }\n    }\n\n    .icon-box {\n      width: 50px;\n      height: 50px;\n      display: flex;\n      justify-content: center;\n      align-items: center;\n      border-radius: 10px;\n      background: ", ";\n      & > * {\n        color: white;\n      }\n\n    }\n  }\n\n  ", ";\n\n  ", ";\n  \n"]);
  _templateObject$P = function _templateObject() {
    return data;
  };
  return data;
}
var Component$O = styled.div(_templateObject$P(), function (props) {
  return props.color === "primary" ? "#3e98c7" : props.color === "secondary" ? "#7356BF" : props.color === "danger" ? "#E11900" : props.color === "warning" ? "#66512C" : props.color === "info" ? "#17A2B8" : props.color === "success" ? "#05944F" : "#000000";
}, function (props) {
  return props.position ? css(_templateObject2$g()) : css(_templateObject3$b());
}, variant({
  prop: "position",
  variants: {
    topLeft: {
      top: "10px",
      left: "10px"
    },
    topRight: {
      top: "10px",
      right: "10px"
    },
    bottomLeft: {
      bottom: "10px",
      left: "10px"
    },
    bottomRight: {
      bottom: "10px",
      right: "10px"
    }
  }
}));
var Notification = function Notification(props) {
  var tag = props.tag,
    autohide = props.autohide,
    className = props.className,
    show = props.show,
    fade = props.fade,
    hideMessage = props.hideMessage,
    message = props.message,
    bodyClassName = props.bodyClassName,
    icon = props.icon,
    iconClassName = props.iconClassName,
    position = props.position,
    title = props.title,
    text = props.text,
    closeClassName = props.closeClassName,
    iconBoxClassName = props.iconBoxClassName,
    color = props.color,
    attributes = _objectWithoutPropertiesLoose(props, ["tag", "autohide", "className", "show", "fade", "hideMessage", "message", "bodyClassName", "icon", "iconClassName", "position", "title", "titleClassName", "text", "closeClassName", "iconBoxClassName", "color"]);
  var _useState = useState(show ? "show" : "hide"),
    componentState = _useState[0],
    setComponentState = _useState[1];
  var _useState2 = useState(!hideMessage),
    componentMessageState = _useState2[0],
    setComponentMessageState = _useState2[1];
  useEffect(function () {
    if (autohide && autohide > 0) {
      hide(autohide * 1000);
    }
  }, [autohide]);
  var hideMsg = function hideMsg(e) {
    setComponentMessageState(!componentMessageState);
  };
  var hide = function hide(time) {
    if (typeof time === "object") {
      time = 0;
    }
    setTimeout(function () {
      setTimeout(function () {
        setTimeout(function () {
          setComponentState("hide");
        }, 150);
      }, 1000);
      setComponentState("fade");
    }, time);
  };
  var classes = classNames("toast", fade && "fade", componentState, className);
  var iconClassNames = classNames(iconClassName);
  var iconBoxClassNames = classNames("icon-box", iconBoxClassName);
  var bodyClasses = classNames("body", bodyClassName);
  var closeClasses = classNames("close-icon", closeClassName);
  var notificationComp = React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$O, Object.assign({
    as: tag,
    "data-test": "notification",
    color: color,
    position: position === "top-right" ? "topRight" : position === "top-left" ? "topLeft" : position === "bottom-right" ? "bottomRight" : position === "bottom-left" ? "bottomLeft" : position
  }, attributes, {
    className: classes
  }), React.createElement("div", {
    className: "toast-container"
  }, React.createElement("div", {
    className: iconBoxClassNames
  }, React.createElement(Icon, {
    icon: icon,
    size: "lg",
    className: iconClassNames
  })), React.createElement("div", {
    className: bodyClasses
  }, React.createElement("strong", {
    className: "ml-3"
  }, title), React.createElement("small", {
    className: "ml-3 small-text"
  }, text)), React.createElement(CloseIcon, {
    className: closeClasses,
    onClick: hide
  }), React.createElement("div", {
    className: "toggle"
  }, React.createElement("span", {
    onClick: hideMsg
  }))), componentMessageState && React.createElement("div", {
    className: "toast-message"
  }, message)));
  return notificationComp;
};
Notification.defaultProps = {
  icon: "square",
  tag: "div",
  closeClassName: "text-dark",
  hideMessage: false
};
function _templateObject$Q() {
  var data = _taggedTemplateLiteralLoose(["\n\tdisplay:flex;\n\talign-items:center;\n\tbox-sizing:border-box;\n"]);
  _templateObject$Q = function _templateObject() {
    return data;
  };
  return data;
}
var Component$P = styled.div(_templateObject$Q());
var Pane = function Pane(props) {
  var className = props.className,
    tag = props.tag,
    attributes = _objectWithoutPropertiesLoose(props, ["className", "tag"]);
  var paneClasses = classNames(className);
  var paneComponent = React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$P, Object.assign({
    as: tag
  }, attributes, {
    className: paneClasses
  })));
  return paneComponent;
};
Pane.defaultProps = {
  tag: "div"
};
function _templateObject3$c() {
  var data = _taggedTemplateLiteralLoose([""]);
  _templateObject3$c = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$h() {
  var data = _taggedTemplateLiteralLoose(["\n          width:", ";\n          height:", ";\n        "]);
  _templateObject2$h = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$R() {
  var data = _taggedTemplateLiteralLoose(["\n\n\tborder-radius:50%;\n\toverflow:hidden;\n\tposition:relative;\n\n\t", ";\n\n\t", ";\n"]);
  _templateObject$R = function _templateObject() {
    return data;
  };
  return data;
}
var Component$Q = styled.img(_templateObject$R(), variant({
  prop: "size",
  variants: {
    lg: {
      width: "4.5rem",
      height: "4.5rem"
    },
    md: {
      width: "3rem",
      height: "3rem"
    },
    sm: {
      width: "2.5rem",
      height: "2.5rem"
    },
    xs: {
      width: "2rem",
      height: "2rem"
    }
  }
}), function (props) {
  return props.width ? css(_templateObject2$h(), props.width, props.height ? props.height : props.width) : css(_templateObject3$c());
});
var PaneImage = function PaneImage(props) {
  var className = props.className,
    tag = props.tag,
    size = props.size,
    src = props.src,
    width = props.width,
    height = props.height,
    attributes = _objectWithoutPropertiesLoose(props, ["className", "tag", "size", "src", "width", "height"]);
  var paneImageClasses = classNames(className);
  var paneImageComponent = React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$Q, Object.assign({
    as: tag,
    src: src,
    width: width,
    height: height,
    size: size
  }, attributes, {
    className: paneImageClasses
  })));
  return paneImageComponent;
};
PaneImage.defaultProps = {
  tag: "img",
  size: "md"
};
function _templateObject$S() {
  var data = _taggedTemplateLiteralLoose(["\n\t", "\n\tpadding:20px;\n\twidth:300px;\n\tborder:1px #ccc solid;\n\tbackground-color: #ffffff;\n\tbackground-clip: border-box;\n"]);
  _templateObject$S = function _templateObject() {
    return data;
  };
  return data;
}
var Component$R = styled.div(_templateObject$S(), colorStyle);
var Panel = function Panel(props) {
  var className = props.className,
    tag = props.tag,
    color = props.color,
    children = props.children,
    attributes = _objectWithoutPropertiesLoose(props, ["className", "tag", "color", "children"]);
  var panelClasses = classNames(className);
  var panelComponent = React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$R, Object.assign({
    colors: color,
    as: tag
  }, attributes, {
    className: panelClasses
  }), children));
  return panelComponent;
};
Panel.defaultProps = {
  tag: "div"
};
function _templateObject5$4() {
  var data = _taggedTemplateLiteralLoose([""]);
  _templateObject5$4 = function _templateObject5() {
    return data;
  };
  return data;
}
function _templateObject4$6() {
  var data = _taggedTemplateLiteralLoose(["\n          opacity:0.6;\n        "]);
  _templateObject4$6 = function _templateObject4() {
    return data;
  };
  return data;
}
function _templateObject3$d() {
  var data = _taggedTemplateLiteralLoose([""]);
  _templateObject3$d = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$i() {
  var data = _taggedTemplateLiteralLoose(["\n          font-size:0.8em;\n        "]);
  _templateObject2$i = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$T() {
  var data = _taggedTemplateLiteralLoose(["\n\tmargin:0px;\n\tfont-size:0.9em;\n\tletter-spacing:0.05em;\n\n\t", ";\n\t", ";\n"]);
  _templateObject$T = function _templateObject() {
    return data;
  };
  return data;
}
var Component$S = styled.p(_templateObject$T(), function (props) {
  return props.small ? css(_templateObject2$i()) : css(_templateObject3$d());
}, function (props) {
  return props.muted ? css(_templateObject4$6()) : css(_templateObject5$4());
});
var PanelText = function PanelText(props) {
  var className = props.className,
    tag = props.tag,
    small = props.small,
    muted = props.muted,
    children = props.children,
    attributes = _objectWithoutPropertiesLoose(props, ["className", "tag", "small", "muted", "children"]);
  var panelTextClasses = classNames(className);
  var panelTextComponent = React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$S, Object.assign({}, attributes, {
    as: tag,
    small: small,
    muted: muted,
    className: panelTextClasses
  }), children));
  return panelTextComponent;
};
PanelText.defaultProps = {
  tag: "p"
};
function _templateObject$U() {
  var data = _taggedTemplateLiteralLoose(["\n\tmargin:0px;\n"]);
  _templateObject$U = function _templateObject() {
    return data;
  };
  return data;
}
var Component$T = styled.h3(_templateObject$U());
var PanelTitle = function PanelTitle(props) {
  var className = props.className,
    tag = props.tag,
    children = props.children,
    attributes = _objectWithoutPropertiesLoose(props, ["className", "tag", "children"]);
  var panelTitleClasses = classNames(className);
  var panelTitleComponent = React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$T, Object.assign({}, attributes, {
    as: tag,
    className: panelTitleClasses
  }), children));
  return panelTitleComponent;
};
PanelTitle.defaultProps = {
  tag: "h6"
};
function _templateObject$V() {
  var data = _taggedTemplateLiteralLoose(["\n  &.popover {\n    width: auto;\n    background-color: white;\n    color: #97999b;\n    text-align: center;\n    display: inline-block;\n    border-radius: 3px;\n    position: absolute;\n    font-size: 0.83em;\n    font-weight: normal;\n    border: 1px rgb(0, 0, 0) solid;\n    z-index: 10;\n    max-width: 274px;\n    text-align: start;\n    background-color: #fff;\n    border: 1px solid rgba(0, 0, 0, 0.2);\n    border-radius: 0.3rem;\n    opacity: 0;\n    transition: opacity 0.3s, visibility 0.3s;\n    visibility: hidden;\n  }\n\n  &.show.popover {\n    z-index: 999;\n    opacity: 1;\n    visibility: visible;\n  }\n\n  .popover-body {\n    color: #6c6e71;\n  }\n\n  &.popover .popover_arrow {\n    width: 0;\n    height: 0;\n    border-style: solid;\n    position: absolute;\n    margin: 6px;\n    color: transparent;\n  }\n\n  .popover[x-placement^='top'] {\n    margin-bottom: 15px;\n  }\n\n  .popover[x-placement^='top'] .popover_arrow {\n    border-width: 8px 8px 0 8px;\n    border-color: #d6d6d6 transparent transparent transparent;\n    bottom: -8px;\n    margin-bottom: 0;\n  }\n\n  .popover[x-placement^='top'] .popover_arrow::before {\n    content: '';\n    display: inline-block;\n    position: absolute;\n    left: -8px;\n    bottom: 1.5px;\n    border: solid;\n    border-width: 8px 8px 0 8px;\n    border-color: white transparent transparent transparent;\n  }\n\n  .popover[x-placement^='bottom'] {\n    margin-top: 15px;\n  }\n\n  .popover[x-placement^='bottom'] .popover_arrow {\n    border-width: 0 8px 8px 8px;\n    border-color: transparent transparent #d6d6d6 transparent;\n    top: -8px;\n    margin-top: 0;\n  }\n\n  .popover[x-placement^='bottom'] .popover_arrow::before {\n    content: '';\n    display: inline-block;\n    position: absolute;\n    left: -8px;\n    top: 1.45px;\n    border: solid;\n    border-width: 0 8px 8px 8px;\n    border-color: transparent transparent white transparent;\n  }\n\n  .popover[x-placement^='right'] {\n    margin-left: 15px;\n  }\n\n  .popover[x-placement^='right'] .popover_arrow {\n    border-width: 8px 8px 8px 0;\n    border-color: transparent #d6d6d6 transparent transparent;\n    left: -8px;\n    margin-left: 0;\n  }\n\n  .popover[x-placement^='right'] .popover_arrow::before {\n    content: '';\n    display: inline-block;\n    position: absolute;\n    top: -8px;\n    left: 1.45px;\n    border: solid;\n    border-width: 8px 8px 8px 0;\n    border-color: transparent white transparent transparent;\n  }\n\n  .popover[x-placement^='left'] {\n    margin-right: 15px;\n  }\n\n  .popover[x-placement^='left'] .popover_arrow {\n    border-width: 8px 0 8px 8px;\n    border-color: transparent transparent transparent #d6d6d6;\n    right: -8px;\n    margin-right: 0;\n  }\n\n  .popover[x-placement^='left'] .popover_arrow::before {\n    content: '';\n    display: inline-block;\n    position: absolute;\n    top: -8px;\n    right: 1.45px;\n    border: solid;\n    border-width: 8px 0 8px 8px;\n    border-color: transparent transparent transparent white;\n  }\n\n  &.tooltip {\n    width: auto;\n    background-color: black;\n    color: white;\n    text-align: center;\n    display: inline-block;\n    border-radius: 3px;\n    position: absolute;\n    /* font-size: 0.83em; */\n    font-weight: normal;\n    border: 1px rgb(0, 0, 0) solid;\n    /* z-index: 200000; */\n    z-index: 15;\n    /* max-width: initial; */\n    max-width: 274px;\n    text-align: start;\n    border: 1px solid rgba(0, 0, 0, 0.2);\n    border-radius: 0.3rem;\n    opacity: 0;\n    transition: opacity 0.3s, visibility 0.3s;\n    visibility: hidden;\n  }\n\n  .tooltip-inner {\n    display: block;\n  }\n\n  &.show.tooltip {\n    z-index: 999;\n\n    opacity: 1;\n    visibility: visible;\n  }\n\n  &.tooltip .popover_arrow {\n    width: 0;\n    height: 0;\n    border-style: solid;\n    position: absolute;\n    margin: 6px;\n    color: transparent;\n  }\n\n  &.tooltip[x-placement^='top'],\n  &.show[x-placement^='top']:not(.tooltip) {\n    margin-bottom: 5px;\n  }\n\n  &.tooltip[x-placement^='top'] .popover_arrow {\n    border-width: 6px 6px 0 6px;\n    border-color: #131313 transparent transparent transparent;\n    bottom: -6px;\n    margin-bottom: 0;\n  }\n\n  &.tooltip[x-placement^='top'] .popover_arrow::before {\n    content: '';\n    display: inline-block;\n    position: absolute;\n    left: -6px;\n    bottom: 1.5px;\n    border: solid;\n    border-width: 6px 6px 0 6px;\n    border-color: black transparent transparent transparent;\n  }\n\n  &.tooltip[x-placement^='bottom'],\n  &.show[x-placement^='bottom']:not(.tooltip) {\n    margin-top: 5px;\n  }\n\n  &.tooltip[x-placement^='bottom'] .popover_arrow {\n    border-width: 0 6px 6px 6px;\n    border-color: transparent transparent #131313 transparent;\n    top: -6px;\n    margin-top: 0;\n  }\n\n  &.tooltip[x-placement^='bottom'] .popover_arrow::before {\n    content: '';\n    display: inline-block;\n    position: absolute;\n    left: -6px;\n    top: 1.45px;\n    border: solid;\n    border-width: 0 6px 6px 6px;\n    border-color: transparent transparent black transparent;\n  }\n\n  &.tooltip[x-placement^='right'],\n  &.show[x-placement^='right']:not(.tooltip) {\n    margin-left: 5px;\n  }\n\n  &.tooltip[x-placement^='right'] .popover_arrow {\n    border-width: 6px 6px 6px 0;\n    border-color: transparent #131313 transparent transparent;\n    left: -6px;\n    margin-left: 0;\n  }\n\n  &.tooltip[x-placement^='right'] .popover_arrow::before {\n    content: '';\n    display: inline-block;\n    position: absolute;\n    top: -6px;\n    left: 1.45px;\n    border: solid;\n    border-width: 6px 6px 6px 0;\n    border-color: transparent black transparent transparent;\n  }\n\n  &.tooltip[x-placement^='left'],\n  &.show[x-placement^='left']:not(.tooltip) {\n    margin-right: 5px;\n  }\n\n  &.tooltip[x-placement^='left'] .popover_arrow {\n    border-width: 6px 0 6px 6px;\n    border-color: transparent transparent transparent #131313;\n    right: -6px;\n    margin-right: 0;\n  }\n\n  &.tooltip[x-placement^='left'] .popover_arrow::before {\n    content: '';\n    display: inline-block;\n    position: absolute;\n    top: -6px;\n    right: 1.45px;\n    border: solid;\n    border-width: 6px 0 6px 6px;\n    border-color: transparent transparent transparent black;\n  }\n"]);
  _templateObject$V = function _templateObject() {
    return data;
  };
  return data;
}
var Tag$1 = styled.div(_templateObject$V());
var Popover = function Popover(props) {
  var children = props.children,
    className = props.className,
    clickable = props.clickable,
    domElement = props.domElement,
    email = props.email,
    id = props.id,
    material = props.material,
    popover = props.popover,
    sm = props.sm,
    tag = props.tag,
    attributes = _objectWithoutPropertiesLoose(props, ["children", "className", "clickable", "domElement", "email", "id", "isVisible", "material", "modifiers", "placement", "popover", "sm", "style", "onChange", "tag"]);
  var _useState = useState({
      popperJS: null,
      visible: props.isVisible,
      showPopper: props.isVisible
    }),
    state = _useState[0],
    setState = _useState[1];
  var _useState2 = useState(),
    timer = _useState2[0],
    setTimer = _useState2[1];
  var popoverWrapperRef = useRef();
  var referenceElm = useRef();
  useEffect(function () {
    var showPopper = state.showPopper;
    var isVisible = props.isVisible,
      onChange = props.onChange;
    setPopperJS();
    if (props.isVisible !== isVisible && isVisible !== showPopper && showPopper !== props.showPopper) {
      setState(_extends({}, state, {
        showPopper: isVisible
      }));
    }
    if (onChange && showPopper !== state.showPopper) {
      onChange(showPopper);
    }
    if (showPopper && state.showPopper !== showPopper) {
      createPopper();
    }
  }, [state]);
  useEffect(function () {
    var time = setInterval(function () {
      return setPopperJS();
    }, 3);
    setTimer(time);
    document.addEventListener("click", handleClick);
  }, []);
  var setPopperJS = function setPopperJS() {
    var showPopper = state.showPopper,
      popperJS = state.popperJS;
    if (showPopper) {
      popperJS ? popperJS.scheduleUpdate() : createPopper();
      setTimeout(function () {
        return clearInterval(timer);
      }, 1000);
    }
  };
  var createPopper = function createPopper() {
    var placement = props.placement,
      modifiers = props.modifiers;
    if (referenceElm.current && popoverWrapperRef.current) {
      setState(function (prevState) {
        return _extends({}, prevState, {
          popperJS: new Popper$1(referenceElm.current, popoverWrapperRef.current, _extends({
            placement: placement
          }, modifiers))
        });
      });
    }
  };
  useEffect(function () {
    var popperJS = state.popperJS;
    if (popperJS !== null) {
      setTimeout(function () {
        popperJS.scheduleUpdate();
      }, 10);
    }
  }, []);
  var doToggle = function doToggle(toggler) {
    setState(_extends({}, state, {
      showPopper: toggler && true
    }));
    var showPopper = state.showPopper,
      visible = state.visible;
    if (showPopper) {
      setState(_extends({}, state, {
        visible: typeof toggler !== "undefined" ? toggler : !visible
      }));
      createPopper();
      state.popperJS.scheduleUpdate();
    }
  };
  var handleClick = function handleClick(e) {
    var target = e.target;
    var showPopper = state.showPopper;
    if (popoverWrapperRef.current && showPopper) {
      if (popoverWrapperRef.current.contains(target) || referenceElm.current.contains(target) || target === referenceElm.current) {
        return;
      }
      doToggle(false);
    }
  };
  var visible = state.visible,
    showPopper = state.showPopper;
  var _Popper = children[1];
  var Wrapper = children[0];
  var classes = classNames(visible && "show", popover ? "popover" : !material && !email && "tooltip px-2", className);
  var popperClasses = classNames((material || email) && "tooltip-inner", material && (sm ? "md-inner" : "md-inner-main"), email && (sm ? "md-inner" : "md-inner-email"));
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Fragment, null, !domElement ? React.createElement(Wrapper.type, Object.assign({}, Wrapper.props, {
    onMouseEnter: function onMouseEnter() {
      return !clickable && doToggle(true);
    },
    onMouseLeave: function onMouseLeave() {
      return !clickable && !popover && setTimeout(function () {
        return doToggle(false);
      }, 0);
    },
    onTouchStart: function onTouchStart() {
      return !clickable && doToggle(true);
    },
    onTouchEnd: function onTouchEnd() {
      return !clickable && !popover && doToggle(false);
    },
    onMouseDown: function onMouseDown() {
      clickable && doToggle(!showPopper);
      setTimeout(function () {
        return setPopperJS();
      }, 100);
    },
    onMouseUp: function onMouseUp() {
      return setTimeout(function () {
        return setPopperJS();
      }, 0);
    },
    innerRef: function innerRef(ref) {
      return referenceElm.current = ref;
    },
    "data-popper": id
  })) : React.createElement(Wrapper.type, Object.assign({}, Wrapper.props, {
    onMouseEnter: function onMouseEnter() {
      return !clickable && doToggle(true);
    },
    onMouseLeave: function onMouseLeave() {
      return !clickable && !popover && setTimeout(function () {
        return doToggle(false);
      }, 0);
    },
    onTouchStart: function onTouchStart() {
      return !clickable && doToggle(true);
    },
    onTouchEnd: function onTouchEnd() {
      return !clickable && !popover && doToggle(false);
    },
    onMouseDown: function onMouseDown() {
      return clickable && doToggle(!showPopper);
    },
    onMouseUp: function onMouseUp() {
      return setTimeout(function () {
        return setPopperJS();
      }, 0);
    },
    ref: function ref(_ref) {
      return referenceElm.current = _ref;
    },
    "data-popper": id
  })), showPopper && React.createElement(Tag$1, Object.assign({
    ref: function ref(_ref2) {
      return popoverWrapperRef.current = _ref2;
    },
    className: classes,
    "data-popper": id
  }, attributes, {
    as: tag
  }), React.createElement(_Popper.type, {
    className: classNames(popperClasses, _Popper.props.className)
  }, _Popper.props.children), React.createElement("span", {
    "x-arrow": "",
    className: classNames("popover_arrow")
  }))));
};
Popover.defaultProps = {
  clickable: false,
  domElement: false,
  id: "popper",
  isVisible: false,
  placement: "top",
  popover: false,
  style: {
    display: "inline-block"
  },
  tag: "div"
};
var PopoverBody = function PopoverBody(props) {
  var children = props.children,
    className = props.className,
    Tag = props.tag,
    attributes = _objectWithoutPropertiesLoose(props, ["children", "className", "tag"]);
  var classes = classNames('popover-body', className);
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Tag, Object.assign({
    "data-test": 'popover-body'
  }, attributes, {
    className: classes
  }), children));
};
PopoverBody.defaultProps = {
  tag: 'div'
};
var PopoverHeader = function PopoverHeader(props) {
  var children = props.children,
    className = props.className,
    Tag = props.tag,
    attributes = _objectWithoutPropertiesLoose(props, ["children", "className", "tag"]);
  var classes = classNames('popover-header', className);
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Tag, Object.assign({
    "data-test": 'popover-header'
  }, attributes, {
    className: classes
  }), children));
};
PopoverHeader.defaultProps = {
  className: '',
  tag: 'h3'
};
var Radio = function Radio(props) {
  var fill = props.fill,
    colorfill = props.colorfill,
    name = props.name,
    value = props.value,
    attributes = _objectWithoutPropertiesLoose(props, ["fill", "colorfill", "name", "value"]);
  var _useState = useState({
      chosen: false,
      hovered: false,
      moused: false
    }),
    state = _useState[0],
    setState = _useState[1];
  var handleClick = function handleClick(e) {
    e.preventDefault();
    return setState(function (state) {
      return _extends({}, state, {
        moused: false,
        chosen: !state.chosen
      });
    });
  };
  var handleMouseDown = function handleMouseDown(e) {
    e.preventDefault();
    return setState(function (state) {
      return _extends({}, state, {
        moused: true
      });
    });
  };
  var handleMouseEnter = function handleMouseEnter(e) {
    e.preventDefault();
    return setState(function (state) {
      return _extends({}, state, {
        hovered: true
      });
    });
  };
  var handleMouseLeave = function handleMouseLeave(e) {
    e.preventDefault();
    return setState(function (state) {
      return _extends({}, state, {
        hovered: false
      });
    });
  };
  var radioGroupStyle = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
  };
  var radioBtnContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    fontSize: '1rem',
    padding: '1rem'
  };
  var radioBtnInputStyle = {
    position: 'absolute',
    opacity: 0,
    cursor: 'pointer',
    height: 0,
    width: 0
  };
  var radioBtnUncheckedStyle = {
    border: ".2rem solid " + fill
  };
  var radioBtnCheckedStyle = {
    backgroundColor: fill,
    border: ".2rem solid " + colorfill,
    boxShadow: "0 0 0 .2rem " + fill
  };
  var radioBtnHoveredStyle = {
    boxShadow: "0 0 0 .4rem " + fill + "75",
    border: ".2rem solid " + fill
  };
  var radioBtnMousedStyle = {
    border: ".2rem solid " + fill + "75"
  };
  var radioBtnStyle = {
    display: 'inline-block',
    height: '.9rem',
    width: '.9rem',
    borderRadius: '50%',
    marginRight: '1rem',
    transition: '.15s ease-in-out'
  };
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement("div", Object.assign({
    id: 'radio-group',
    style: radioGroupStyle
  }, attributes), React.createElement("label", {
    onClick: function onClick(e) {
      return handleClick(e);
    },
    onMouseEnter: function onMouseEnter(e) {
      return handleMouseEnter(e);
    },
    onMouseLeave: function onMouseLeave(e) {
      return handleMouseLeave(e);
    },
    onMouseDown: function onMouseDown(e) {
      return handleMouseDown(e);
    },
    style: radioBtnContainerStyle
  }, React.createElement("input", {
    type: 'radio',
    value: value,
    name: name,
    checked: state.chosen,
    onChange: handleClick,
    style: radioBtnInputStyle
  }), React.createElement("div", {
    style: _extends({}, radioBtnStyle, state.chosen ? radioBtnCheckedStyle : state.moused ? radioBtnMousedStyle : state.hovered ? radioBtnHoveredStyle : radioBtnUncheckedStyle)
  }), value)));
};
Radio.defaultProps = {
  fill: '#455ff5'
};
var RadioGroup = function RadioGroup(props) {
  var _useState = useState({
      answers: props.answers.map(function (answer) {
        return {
          answer: answer,
          chosen: false,
          hovered: false,
          moused: false
        };
      })
    }),
    state = _useState[0],
    setState = _useState[1];
  var fill = props.fill,
    chosenFill = props.chosenFill,
    attributes = _objectWithoutPropertiesLoose(props, ["fill", "chosenFill"]);
  var handleClick = function handleClick(e, index) {
    e.preventDefault();
    return setState(function (state) {
      return {
        answers: state.answers.map(function (_ref, sIndex) {
          var rest = _extends({}, _ref);
          return _extends({}, rest, {
            moused: false,
            chosen: index === sIndex ? !state.answers[index].chosen : false
          });
        })
      };
    });
  };
  var handleMouseDown = function handleMouseDown(e, index) {
    e.preventDefault();
    return setState(function (state) {
      return {
        answers: state.answers.map(function (_ref2, sIndex) {
          var rest = _extends({}, _ref2);
          return _extends({}, rest, {
            moused: index === sIndex ? true : false
          });
        })
      };
    });
  };
  var handleMouseEnter = function handleMouseEnter(e, index) {
    e.preventDefault();
    return setState(function (state) {
      return {
        answers: state.answers.map(function (_ref3, sIndex) {
          var rest = _extends({}, _ref3);
          return _extends({}, rest, {
            hovered: index === sIndex ? true : false
          });
        })
      };
    });
  };
  var handleMouseLeave = function handleMouseLeave(e, index) {
    e.preventDefault();
    return setState(function (state) {
      return {
        answers: state.answers.map(function (_ref4, sIndex) {
          var hovered = _ref4.hovered,
            rest = _objectWithoutPropertiesLoose(_ref4, ["hovered"]);
          return _extends({}, rest, {
            hovered: index === sIndex ? false : hovered
          });
        })
      };
    });
  };
  var radioGroupStyle = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
  };
  var radioBtnContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    fontSize: '1rem',
    padding: '1rem'
  };
  var radioBtnInputStyle = {
    position: 'absolute',
    opacity: 0,
    cursor: 'pointer',
    height: 0,
    width: 0
  };
  var radioBtnUncheckedStyle = {
    border: ".2rem solid " + fill
  };
  var radioBtnCheckedStyle = {
    backgroundColor: fill,
    border: ".2rem solid " + chosenFill,
    boxShadow: "0 0 0 .2rem " + fill
  };
  var radioBtnHoveredStyle = {
    boxShadow: "0 0 0 .4rem " + fill + "75",
    border: ".2rem solid " + fill
  };
  var radioBtnMousedStyle = {
    border: ".2rem solid " + fill + "75"
  };
  var radioBtnStyle = {
    display: 'inline-block',
    height: '.9rem',
    width: '.9rem',
    borderRadius: '50%',
    marginRight: '1rem',
    transition: '.15s ease-in-out'
  };
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement("div", Object.assign({
    id: 'radio-group',
    style: radioGroupStyle
  }, attributes), state.answers.map(function (_ref5, index) {
    var answer = _ref5.answer,
      hovered = _ref5.hovered,
      chosen = _ref5.chosen,
      moused = _ref5.moused;
    return React.createElement("label", {
      key: index,
      onClick: function onClick(e) {
        return handleClick(e, index);
      },
      onMouseEnter: function onMouseEnter(e) {
        return handleMouseEnter(e, index);
      },
      onMouseLeave: function onMouseLeave(e) {
        return handleMouseLeave(e, index);
      },
      onMouseDown: function onMouseDown(e) {
        return handleMouseDown(e, index);
      },
      style: radioBtnContainerStyle
    }, React.createElement("input", {
      type: 'radio',
      value: answer,
      name: answer,
      checked: chosen,
      onChange: handleClick,
      style: radioBtnInputStyle
    }), React.createElement("div", {
      style: _extends({}, radioBtnStyle, chosen ? radioBtnCheckedStyle : moused ? radioBtnMousedStyle : hovered ? radioBtnHoveredStyle : radioBtnUncheckedStyle)
    }), answer);
  })));
};
var Rating = function Rating(props) {
  var Tag = props.tag,
    containerClassName = props.containerClassName,
    iconClassName = props.iconClassName,
    iconFaces = props.iconFaces,
    iconSize = props.iconSize,
    iconRegular = props.iconRegular,
    fillClassName = props.fillClassName,
    CDBBtn = props.CDBBtn,
    CDBPopoverBody = props.CDBPopoverBody,
    CDBPopoverHeader = props.CDBPopoverHeader,
    feedback = props.feedback,
    submitHandler = props.submitHandler,
    commonAttributes = _objectWithoutPropertiesLoose(props, ["tag", "containerClassName", "iconClassName", "iconFaces", "iconSize", "iconRegular", "fillClassName", "fillColors", "tooltip", "CDBBtn", "CDBPopoverBody", "CDBPopoverHeader", "type", "getValue", "feedback", "submitHandler"]);
  var _useState = useState([]),
    data = _useState[0],
    setData = _useState[1];
  var _useState2 = useState(null),
    hovered = _useState2[0],
    setHovered = _useState2[1];
  var _useState3 = useState({
      title: '',
      index: null
    }),
    choosed = _useState3[0],
    setChoosed = _useState3[1];
  var _useState4 = useState(''),
    feedbackValue = _useState4[0],
    setFeedbackValue = _useState4[1];
  var _useState5 = useState(null),
    openedForm = _useState5[0],
    setOpenedForm = _useState5[1];
  var onDocumentClick = function onDocumentClick(e) {
    if (!e.target.closest('.popover')) {
      setOpenedForm(null);
    }
  };
  useEffect(function () {
    window.addEventListener('click', onDocumentClick);
    return function () {
      return window.removeEventListener('click', onDocumentClick);
    };
  }, []);
  useEffect(function () {
    setData(props.data);
  }, [props.data]);
  useEffect(function () {
    var choosedIndex = data.findIndex(function (item) {
      return item.choosed;
    });
    if (choosedIndex !== -1) {
      setChoosed({
        title: data[choosedIndex].tooltip,
        index: choosedIndex
      });
    }
  }, [data]);
  useEffect(function () {
    if (props.getValue) {
      var title = choosed.title,
        index = choosed.index;
      index = index !== null ? index + 1 : index;
      props.getValue({
        title: title,
        value: index
      });
    }
  }, [choosed, props]);
  var handleMouseEnter = function handleMouseEnter(_, index) {
    setHovered(index);
  };
  var handleMouseLeave = function handleMouseLeave() {
    setHovered(null);
  };
  var handleClick = function handleClick(title, index, e) {
    e.stopPropagation();
    if (title === choosed.title && index === choosed.index) {
      setChoosed({
        title: '',
        index: null
      });
      feedback && setOpenedForm(null);
    } else {
      setChoosed({
        title: title,
        index: index
      });
      feedback && setTimeout(function () {
        setOpenedForm(index);
      }, 1);
    }
  };
  var onCloseHanlder = function onCloseHanlder() {
    setFeedbackValue('');
    setOpenedForm(null);
  };
  var feedbackValueHandler = function feedbackValueHandler(e) {
    setFeedbackValue(e.target.value);
  };
  var containerClasses = classNames('mdb-rating', 'd-flex', 'justify-content-start', 'align-items-center', containerClassName);
  var renderedIcons = [];
  if (data.length) {
    renderedIcons = data.map(function (_ref, index) {
      var _ref$icon = _ref.icon,
        icon = _ref$icon === void 0 ? 'star' : _ref$icon,
        tooltip = _ref.tooltip,
        size = _ref.size,
        itemAttributes = _objectWithoutPropertiesLoose(_ref, ["icon", "tooltip", "far", "size", "choosed"]);
      var isChoosed = choosed.index !== null;
      var isHovered = hovered !== null;
      var isFormOpened = openedForm !== null;
      var isFormActive = feedback && isFormOpened && openedForm === index;
      var toFill = false;
      if (isChoosed) {
        toFill = index <= choosed.index;
        if (isHovered && hovered > choosed.index) {
          toFill = index <= hovered;
        }
      } else if (isHovered) {
        toFill = index <= hovered;
      }
      var renderIcon = icon;
      if (iconFaces) {
        var faces = ['angry', 'frown', 'meh', 'smile', 'laugh'];
        renderIcon = 'meh-blank';
        if (isChoosed && index <= choosed.index) {
          renderIcon = faces[choosed.index];
          if (isHovered) {
            renderIcon = faces[hovered];
          }
        } else if (isHovered && index <= hovered) {
          renderIcon = faces[hovered];
        }
      }
      var iconClasses = classNames(iconRegular ? 'far' : false, icon ? "fa fa-" + renderIcon : false, iconSize ? "fa-" + size : false, 'py-2 px-1 rate-popover', toFill && fillClassName, iconClassName);
      var tooltipContent = tooltip;
      if (isFormActive) {
        tooltipContent = React.createElement("form", {
          onSubmit: function onSubmit(e) {
            submitHandler(e, tooltip, openedForm + 1, feedbackValue);
            onCloseHanlder();
          }
        }, React.createElement(CDBPopoverHeader, null, tooltip), React.createElement(CDBPopoverBody, null, React.createElement("textarea", {
          className: 'md-textarea form-control py-0',
          value: feedbackValue,
          onChange: feedbackValueHandler
        }), React.createElement("div", {
          className: 'd-flex align-items-center justify-content-around mt-2'
        }, React.createElement(CDBBtn, {
          type: 'submit',
          color: 'primary',
          size: 'sm'
        }, "Submit!"), React.createElement("button", {
          onMouseDown: onCloseHanlder,
          style: {
            backgroundColor: '#fff',
            border: 'none',
            padding: '0.5rem 1.6rem'
          }
        }, "Close"))));
      }
      return React.createElement(Popover, {
        key: tooltip,
        domElement: true,
        placement: 'top',
        tag: 'span',
        popover: isFormActive,
        isVisible: isFormActive,
        clickable: isFormActive
      }, React.createElement("span", null, React.createElement("i", Object.assign({
        style: {
          cursor: 'pointer'
        }
      }, commonAttributes, itemAttributes, {
        className: iconClasses,
        "data-index": index,
        "data-original-title": tooltip,
        onMouseEnter: function onMouseEnter() {
          return handleMouseEnter(tooltip, index);
        },
        onMouseLeave: handleMouseLeave,
        onClick: function onClick(e) {
          return handleClick(tooltip, index, e);
        }
      }))), React.createElement("div", {
        style: {
          userSelect: 'none'
        }
      }, tooltipContent));
    });
  }
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Tag, {
    "data-test": 'rating',
    className: containerClasses
  }, renderedIcons));
};
Rating.defaultProps = {
  containerClassName: '',
  data: [{
    tooltip: 'Very Bad'
  }, {
    tooltip: 'Poor'
  }, {
    tooltip: 'Ok'
  }, {
    tooltip: 'Good'
  }, {
    tooltip: 'Excellent'
  }],
  feedback: false,
  fillClassName: 'fiveStars',
  iconClassName: '',
  iconSize: '1x',
  iconRegular: false,
  tag: 'div',
  submitHandler: function submitHandler(e) {
    return e.preventDefault();
  }
};
function _templateObject$W() {
  var data = _taggedTemplateLiteralLoose([""]);
  _templateObject$W = function _templateObject() {
    return data;
  };
  return data;
}
var Component$U = styled.div(_templateObject$W());
var Row = function Row(props) {
  var around = props.around,
    between = props.between,
    bottom = props.bottom,
    center = props.center,
    className = props.className,
    end = props.end,
    middle = props.middle,
    start = props.start,
    top = props.top,
    children = props.children,
    attributes = _objectWithoutPropertiesLoose(props, ["around", "between", "bottom", "center", "className", "end", "middle", "start", "tag", "top", "children"]);
  var rowClasses = classNames("row", end && "justify-content-end", start && "justify-content-start", center && "justify-content-center", between && "justify-content-between", around && "justify-content-around", top && "align-self-start", middle && "align-self-center", bottom && "align-self-end", className);
  var rowComponent = React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$U, Object.assign({
    "data-test": "row"
  }, attributes, {
    className: rowClasses
  }), children));
  return rowComponent;
};
Row.defaultProps = {
  tag: "div"
};
function _templateObject$X() {
  var data = _taggedTemplateLiteralLoose([""]);
  _templateObject$X = function _templateObject() {
    return data;
  };
  return data;
}
var Component$V = styled.select(_templateObject$X());
var Select = function Select(props) {
  var className = props.className,
    options = props.options,
    optionClassName = props.optionClassName,
    selected = props.selected,
    attributes = _objectWithoutPropertiesLoose(props, ["className", "options", "optionClassName", "selected"]);
  var selectClasses = classNames("custom-select", className);
  var optionClasses = classNames(optionClassName);
  var selectComponent = React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$V, Object.assign({
    "data-test": "select"
  }, attributes, {
    className: selectClasses
  }), React.createElement("option", null, selected), options && options.map(function (optionType, index) {
    return React.createElement("option", {
      key: optionType.value,
      className: optionClasses
    }, optionType.text);
  })));
  return selectComponent;
};
Select.defaultProps = {
  tag: "div"
};
function _templateObject4$7() {
  var data = _taggedTemplateLiteralLoose(["\n\tfont-size:0.9em;\n"]);
  _templateObject4$7 = function _templateObject4() {
    return data;
  };
  return data;
}
function _templateObject3$e() {
  var data = _taggedTemplateLiteralLoose(["\n\tcolor:#000;\n\tbackground:#fff;\n\tmax-height:0px;\n\toverflow-y:scroll;\n\ttransition: inherit;\n\n\t&.display {\n\t\tmax-height:150px;\n\t}\n\n\t::-webkit-scrollbar {\n\t\twidth:3px;\n\t\tbackground-color:transparent;\n\t}\n\n\t::-webkit-scrollbar-thumb {\n\t\tborder-radius:5px;\n\t\tbackground-color: #ccc;\n\t}\n\n\tdiv {\n\t\tdisplay:flex;\n\t\talign-items:center;\n\t\tpadding:10px;\n\t\tcursor:pointer;\n\t\ttransition: all 0.1s ease-in-out;\n\n\t\t:hover{\n\t\t\tbackground-color:#ddd;\n\t\t\t", "\n\t\t}\n\t}\n"]);
  _templateObject3$e = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$j() {
  var data = _taggedTemplateLiteralLoose(["\n\tdisplay:flex;\n\tpadding:10px;\n\tcursor:pointer;\n\ttransition: inherit;\n\tborder-width:1px;\n\tborder-style:solid;\n\tbox-sizing:border-box;\n\talign-items:center;\n\tjustify-content:space-between;\n\n\t.icon {\n    \tmargin:0;\n    \topacity: 0.5;\n    \ttransition: inherit;\n\t}\n\n\t&.active{\n\t\t.icon{\n\t\t\topacity:1;\n\t\t\ttransform: rotate(-180deg);\n\t\t}\n\t}\n"]);
  _templateObject2$j = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$Y() {
  var data = _taggedTemplateLiteralLoose(["\n\twidth:100%;\n\ttransition: all 0.3s ease-in-out;\n\t", "\n\n\ti {\n\t\tmargin-right:5px;\n\t}\n\n\t&.disabled {\n\t\tpointer-events:none;\n\t\topacity:0.6;\n\t}\n"]);
  _templateObject$Y = function _templateObject() {
    return data;
  };
  return data;
}
var Component$W = styled.div(_templateObject$Y(), variant({
  prop: "colors",
  scale: "lightStyle"
}));
var Div$1 = styled.div(_templateObject2$j());
var Options$1 = styled.div(_templateObject3$e(), variant({
  prop: "colors",
  scale: "lightStyle"
}));
var Option = styled.span(_templateObject4$7());
var Select2 = function Select2(props) {
  var tag = props.tag,
    color = props.color,
    options = props.options,
    disabled = props.disabled,
    iconSize = props.iconSize,
    iconBrand = props.iconBrand,
    iconClass = props.iconClass,
    iconLight = props.iconLight,
    iconRegular = props.iconRegular,
    selected = props.selected,
    className = props.className,
    hoverColor = props.hoverColor,
    optionClassName = props.optionClassName,
    attributes = _objectWithoutPropertiesLoose(props, ["tag", "color", "options", "disabled", "iconSize", "iconBrand", "iconClass", "iconLight", "iconRegular", "selected", "className", "hoverColor", "optionClassName"]);
  var _useState = useState(false),
    show = _useState[0],
    setShow = _useState[1];
  var _useState2 = useState([selected]),
    active = _useState2[0],
    setActive = _useState2[1];
  var wrapperRef = useRef(null);
  var selectClasses = classNames(show && "shadow", {
    disabled: disabled
  }, className);
  var optionClasses = classNames(show && "display", optionClassName);
  var divClass = classNames(show && "active");
  var display = function display(e) {
    e.stopPropagation();
    setShow(!show);
  };
  var setSelected = function setSelected(text, icon) {
    setActive([text, icon]);
    setShow(!show);
  };
  var useOutsideClose = function useOutsideClose(ref) {
    useEffect(function () {
      function handleClickOutside(event) {
        if (ref.current && !ref.current.contains(event.target)) {
          setShow(false);
        }
      }
      document.addEventListener("mousedown", handleClickOutside);
      return function () {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [ref]);
  };
  var selectComponent = React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$W, Object.assign({
    "data-test": "select",
    colors: color,
    className: selectClasses,
    ref: wrapperRef,
    onClick: useOutsideClose(wrapperRef),
    as: tag
  }, attributes), React.createElement(Div$1, {
    className: divClass,
    onClick: display
  }, React.createElement(Option, {
    onClick: display
  }, active[1] && React.createElement(Icon, {
    size: iconSize,
    brand: iconBrand,
    light: iconLight,
    regular: iconRegular,
    className: iconClass,
    icon: active[1]
  }), active[0]), React.createElement(Icon, {
    fas: true,
    icon: "angle-down",
    onClick: display,
    className: "icon"
  })), React.createElement(Options$1, {
    colors: hoverColor && color,
    className: optionClasses
  }, options && options.map(function (optionType, index) {
    var icon = optionType.icon,
      text = optionType.text;
    return React.createElement("div", {
      key: index,
      onClick: function onClick() {
        return setSelected(text, icon);
      }
    }, icon && React.createElement(Icon, {
      size: iconSize,
      brand: iconBrand,
      light: iconLight,
      regular: iconRegular,
      className: iconClass,
      icon: icon
    }), React.createElement(Option, null, text));
  }))));
  return selectComponent;
};
Select2.defaultProps = {
  tag: "div"
};
function _templateObject3$f() {
  var data = _taggedTemplateLiteralLoose(["\n  height: 100%;\n  overflow-y: auto;\n  overflow-x: hidden;\n  position: relative;\n  display: flex;\n  flex-direction: column;\n  z-index: 101;\n"]);
  _templateObject3$f = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$k() {
  var data = _taggedTemplateLiteralLoose(["\n  height: 100%;\n  position: relative;\n  z-index: 101;\n"]);
  _templateObject2$k = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$Z() {
  var data = _taggedTemplateLiteralLoose(["\n  background: ", ";\n  color: ", ";\n  height: 100%;\n  width: ", ";\n  min-width: ", ";\n  text-align: left;\n  transition: width, left, right, 0.3s;\n  position: relative;\n  z-index: 1009;\n\n  &.toggled {\n    width: ", ";\n    min-width: ", ";\n  }\n"]);
  _templateObject$Z = function _templateObject() {
    return data;
  };
  return data;
}
var SidebarContainer = styled.div(_templateObject$Z(), function (props) {
  return props.backgroundColor;
}, function (props) {
  return props.textColor;
}, function (_ref) {
  var maxWidth = _ref.maxWidth;
  return maxWidth;
}, function (_ref2) {
  var maxWidth = _ref2.maxWidth;
  return maxWidth;
}, function (_ref3) {
  var minWidth = _ref3.minWidth;
  return minWidth;
}, function (_ref4) {
  var minWidth = _ref4.minWidth;
  return minWidth;
});
var SidebarInner = styled.div(_templateObject2$k());
var SidebarLayout = styled.div(_templateObject3$f());
var SidebarContext = createContext({
  toggled: false,
  handleToggleSidebar: function handleToggleSidebar() {},
  textColor: '',
  backgroundColor: '',
  breakpoint: 0
});
var Sidebar = forwardRef(function (props, ref) {
  var className = props.className,
    minWidth = props.minWidth,
    maxWidth = props.maxWidth,
    children = props.children,
    toggled = props.toggled,
    textColor = props.textColor,
    backgroundColor = props.backgroundColor,
    breakpoint = props.breakpoint,
    rest = _objectWithoutPropertiesLoose(props, ["className", "minWidth", "maxWidth", "children", "toggled", "textColor", "backgroundColor", "breakpoint"]);
  var handleToggleSidebar = function handleToggleSidebar(toggled) {
    setSidebarState(_extends({}, sidebarState, {
      toggled: !toggled
    }));
  };
  var _useState = useState({
      toggled: toggled,
      handleToggleSidebar: handleToggleSidebar,
      textColor: textColor,
      backgroundColor: backgroundColor,
      breakpoint: breakpoint
    }),
    sidebarState = _useState[0],
    setSidebarState = _useState[1];
  useEffect(function () {
    handleToggleSidebar(!toggled);
  }, [toggled]);
  var sidebarRef = ref ? ref : React.createRef();
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(SidebarContext.Provider, {
    value: sidebarState
  }, React.createElement(SidebarContainer, Object.assign({}, rest, {
    ref: sidebarRef,
    className: classNames('pro-sidebar', className, {
      toggled: sidebarState.toggled
    }),
    textColor: textColor,
    backgroundColor: backgroundColor,
    minWidth: minWidth,
    maxWidth: maxWidth
  }), React.createElement(SidebarInner, null, React.createElement(SidebarLayout, null, children)))));
});
Sidebar.defaultProps = {
  textColor: '#ffffff',
  backgroundColor: theme.colors.dark900,
  breakpoint: 720,
  toggled: false,
  minWidth: '80px',
  maxWidth: '270px',
  children: null
};
Sidebar.displayName = 'Sidebar';
function _templateObject$_() {
  var data = _taggedTemplateLiteralLoose(["\n    flex-grow: 1;\n    padding-top: 15px;\n"]);
  _templateObject$_ = function _templateObject() {
    return data;
  };
  return data;
}
var ContentContainer = styled.div(_templateObject$_());
var SidebarContent = forwardRef(function (props, ref) {
  var children = props.children,
    className = props.className,
    rest = _objectWithoutPropertiesLoose(props, ["children", "className"]);
  var _useContext = useContext(SidebarContext),
    handleToggleSidebar = _useContext.handleToggleSidebar,
    breakpoint = _useContext.breakpoint;
  var _useState = useState(0),
    width = _useState[0],
    setWidth = _useState[1];
  var finalbreakpoint = breakpoint || 720;
  useEffect(function () {
    var handleWindowResize = function handleWindowResize() {
      return setWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleWindowResize);
    if (width < finalbreakpoint) {
      handleToggleSidebar(false);
    }
    if (width > finalbreakpoint) {
      handleToggleSidebar(true);
    }
    return function () {
      window.removeEventListener("resize", handleWindowResize);
    };
  }, [handleToggleSidebar, width, finalbreakpoint]);
  var sidebarContentRef = ref ? ref : React.createRef();
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(ContentContainer, Object.assign({}, rest, {
    ref: sidebarContentRef,
    className: classNames("pro-sidebar-content", className)
  }), children));
});
var SidebarFooter = forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    rest = _objectWithoutPropertiesLoose(_ref, ["children", "className"]);
  var sidebarFooterRef = ref ? ref : React.createRef();
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement("div", Object.assign({}, rest, {
    ref: sidebarFooterRef,
    className: classNames("pro-sidebar-footer", className)
  }), children));
});
function _templateObject$$() {
  var data = _taggedTemplateLiteralLoose(["\n  border-bottom: 1px solid hsla(0, 0%, 67.8%, 0.2);\n\n  .head-div {\n    padding: 24px 30px;\n    font-weight: bold;\n    font-size: 17px;\n    letter-spacing: 1px;\n    overflow: hidden;\n    display: flex;\n    justify-content: space-between;\n    white-space: nowrap;\n\n    .icon-suffix {\n      cursor: pointer;\n    }\n\n    &.toggled {\n      justify-content: center;\n      .head-text {\n        display: none;\n      }\n      .icon-suffix {\n        margin: 0px;\n      }\n    }\n  }\n"]);
  _templateObject$$ = function _templateObject() {
    return data;
  };
  return data;
}
var HeaderContainer = styled.div(_templateObject$$());
var SidebarHeader = forwardRef(function (_ref, ref) {
  var children = _ref.children,
    prefix = _ref.prefix,
    className = _ref.className,
    rest = _objectWithoutPropertiesLoose(_ref, ["children", "prefix", "className"]);
  var sidebarHeaderRef = ref ? ref : React.createRef();
  var _useContext = useContext(SidebarContext),
    toggled = _useContext.toggled,
    handleToggleSidebar = _useContext.handleToggleSidebar;
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(HeaderContainer, Object.assign({}, rest, {
    ref: sidebarHeaderRef,
    className: classNames(className)
  }), React.createElement("div", {
    className: classNames("head-div", {
      toggled: toggled
    })
  }, React.createElement("span", {
    className: "head-text"
  }, children), prefix ? React.createElement("span", {
    className: "icon-suffix",
    onClick: function onClick() {
      return handleToggleSidebar(toggled);
    }
  }, prefix) : null)));
});
function _templateObject2$l() {
  var data = _taggedTemplateLiteralLoose(["\n    list-style-type: none;\n    padding: 0;\n    margin: 0;\n\n    a {\n        color: ", ";\n        text-decoration:none;\n    }\n    .activeClicked {\n      >li{\n            background: ", ";\n            color: ", ";\n        }\n    }\n    \n"]);
  _templateObject2$l = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$10() {
  var data = _taggedTemplateLiteralLoose(["\n    padding-top: 10px;\n    padding-bottom: 10px;\n"]);
  _templateObject$10 = function _templateObject() {
    return data;
  };
  return data;
}
var MenuNav = styled.nav(_templateObject$10());
var MenuUl = styled.ul(_templateObject2$l(), function (props) {
  return props.textColor;
}, function (props) {
  return props.textColor;
}, function (props) {
  return props.backgroundColor;
});
var SidebarMenu = forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    popperArrow = _ref.popperArrow,
    rest = _objectWithoutPropertiesLoose(_ref, ["children", "className", "popperArrow"]);
  var menuRef = ref ? ref : React.createRef();
  var _useContext = useContext(SidebarContext),
    textColor = _useContext.textColor,
    backgroundColor = _useContext.backgroundColor;
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(MenuNav, Object.assign({}, rest, {
    ref: menuRef,
    className: classNames("pro-menu", className)
  }), React.createElement(MenuUl, {
    textColor: textColor,
    backgroundColor: backgroundColor
  }, React.Children.map(children, function (child) {
    return React.cloneElement(child, {
      firstchild: 1,
      popperarrow: popperArrow === true ? 1 : 0
    });
  }))));
});
function _templateObject2$m() {
  var data = _taggedTemplateLiteralLoose(["\n  list-style: none;\n  margin: 10px 15px;\n  &.toggled {\n    position: relative;\n    margin-top: 0px;\n    margin-bottom: 0px;\n    &::before {\n      content: \"\";\n      display: inline-block;\n      position: absolute;\n      top: 0;\n      bottom: 0;\n      left: 0;\n      right: 0;\n      cursor: pointer;\n    }\n  }\n"]);
  _templateObject2$m = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$11() {
  var data = _taggedTemplateLiteralLoose(["\n  position: relative;\n  display: flex;\n  align-items: center;\n  padding: 8px 2px 8px 15px;\n  height: 45px;\n  cursor: pointer;\n  outline: none;\n\n  .side-icon {\n    margin-right: 10px;\n    width: 30px;\n  }\n  &.active {\n    background: white;\n    color: black;\n  }\n\n  .item-content {\n    flex-grow: 1;\n    flex-shrink: 1;\n    overflow: hidden;\n    text-overflow: ellipsis;\n    white-space: nowrap;\n    font-size: ", ";\n  }\n  .suffix-wrapper {\n    margin-right: 23px;\n  }\n  &.toggled {\n    justify-content: center;\n    .item-content {\n      width: 0px;\n      display: none;\n    }\n    .suffix-wrapper {\n      opacity: 0;\n      display: none;\n    }\n  }\n"]);
  _templateObject$11 = function _templateObject() {
    return data;
  };
  return data;
}
var InnerMenuItem = styled.div(_templateObject$11(), function (_ref) {
  var fontSize = _ref.fontSize;
  return fontSize;
});
var InnerMenuLi = styled.li(_templateObject2$m());
var SidebarMenuItem = forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    icon = _ref.icon,
    iconSize = _ref.iconSize,
    iconType = _ref.iconType,
    iconClassName = _ref.iconClassName,
    textFontSize = _ref.textFontSize,
    active = _ref.active,
    suffix = _ref.suffix,
    rest = _objectWithoutPropertiesLoose(_ref, ["children", "className", "icon", "iconSize", "iconType", "iconClassName", "textFontSize", "active", "suffix", "firstChild", "popperArrow"]);
  var menuItemRef = ref ? ref : React.createRef();
  var _useContext = useContext(SidebarContext),
    toggled = _useContext.toggled;
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(InnerMenuLi, Object.assign({}, rest, {
    ref: menuItemRef,
    className: classNames(className, {
      active: active
    }, {
      toggled: toggled
    })
  }), React.createElement(InnerMenuItem, {
    className: classNames({
      active: active
    }, {
      toggled: toggled
    }),
    tabIndex: 0,
    fontSize: textFontSize,
    role: 'button',
    toggled: toggled
  }, icon && React.createElement(Icon, {
    icon: icon,
    size: iconSize,
    className: classNames(iconClassName, 'side-icon', iconType && "fa-" + iconType)
  }), React.createElement("span", {
    className: 'item-content'
  }, children), suffix ? React.createElement("span", {
    className: 'suffix-wrapper'
  }, suffix) : null)));
});
SidebarMenuItem.defaultProps = {
  iconSize: 'md'
};
function _templateObject4$8() {
  var data = _taggedTemplateLiteralLoose(["\n  list-style: none;\n  margin: 10px 15px;\n\n  ", " {\n    .item-content {\n      display: block !important;\n    }\n  }\n  .react-slidedown {\n    height: 0;\n    transition-property: none;\n    transition-duration: 0.2s;\n    transition-timing-function: ease-in-out;\n  }\n\n  .react-slidedown.transitioning {\n    overflow-y: hidden;\n  }\n\n  .react-slidedown.closed {\n    display: none;\n  }\n  .popper-inner {\n    .pro-item-content {\n      display: block !important;\n    }\n  }\n  &.toggled {\n    .pro-item-content {\n      display: none;\n    }\n  }\n  .pro-item-content {\n    flex-grow: 1;\n    flex-shrink: 1;\n    overflow: hidden;\n    text-overflow: ellipsis;\n    white-space: nowrap;\n  }\n\n  &.open {\n    .pro-inner-item {\n      > .pro-arrow-wrapper {\n        .pro-arrow {\n          transform: rotate(45deg);\n        }\n      }\n    }\n  }\n\n  &.toggled {\n    position: relative;\n    .pro-inner-list-item {\n      z-index: 111;\n    }\n\n    &:hover {\n      .pro-inner-list-item {\n        visibility: visible;\n      }\n      .pro-icon-wrapper {\n        .pro-icon {\n          animation: swing ease-in-out 0.5s 1 alternate;\n        }\n      }\n    }\n    .pro-inner-list-item {\n      .pro-sub-menu-item,\n      .pro-inner-item {\n        padding: 8px 30px 8px 5px;\n      }\n    }\n  }\n"]);
  _templateObject4$8 = function _templateObject4() {
    return data;
  };
  return data;
}
function _templateObject3$g() {
  var data = _taggedTemplateLiteralLoose(["\n  ", ";\n  max-height: 100vh;\n  overflow-y: auto;\n  background-color: ", ";\n  padding-left: 20px;\n  border-radius: 4px;\n  ul {\n    padding-top: 8px;\n    padding-bottom: 8px;\n    padding-left: 0px;\n    li {\n      margin: 0px 5px;\n      div {\n        height: auto;\n        pointer-events: auto !important;\n      }\n    }\n  }\n"]);
  _templateObject3$g = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$n() {
  var data = _taggedTemplateLiteralLoose(["\n  height: auto;\n  position: fixed;\n  visibility: hidden;\n  min-width: 220px;\n  max-width: 270px;\n  background-color: transparent;\n  max-height: 100%;\n  padding-left: 3px;\n  &.has-arrow {\n    padding-left: 10px;\n  }\n  .pro-inner-item {\n    &:before {\n      content: \"\";\n      display: inline-block;\n      width: 8px;\n      min-width: 8px;\n      height: 8px;\n      border: 1px solid ", ";\n      border-radius: 50%;\n      margin-right: 4px;\n      position: relative;\n      margin-left: 0px;\n    }\n  }\n"]);
  _templateObject2$n = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$12() {
  var data = _taggedTemplateLiteralLoose(["\n  position: relative;\n  display: flex;\n  align-items: center;\n  padding: 8px 2px 8px 15px;\n  height: 45px;\n  cursor: pointer;\n  outline: none;\n  .side-icon {\n    margin-right: 15px;\n    width: 30px;\n  }\n\n  .item-content {\n    flex-grow: 1;\n    flex-shrink: 1;\n    overflow: hidden;\n    text-overflow: ellipsis;\n    white-space: nowrap;\n  }\n  &.toggled {\n    .item-content {\n      display: block !important;\n    }\n  }\n  &.active {\n    background: ", ";\n    color: ", ";\n    .pro-arrow-wrapper {\n      .pro-arrow {\n        border-color: ", ";\n      }\n    }\n  }\n\n  &:before {\n    background: ", ";\n  }\n\n  .pro-arrow-wrapper {\n    position: absolute;\n    right: 13px;\n    top: 50%;\n    transform: translateY(-50%);\n\n    .pro-arrow {\n      display: inline-block;\n      border-style: solid;\n\n      border-width: 0 2px 2px 0;\n      padding: 2.5px;\n      vertical-align: middle;\n      transition: transform 0.3s;\n      transform: rotate(-45deg);\n    }\n  }\n  .suffix-wrapper {\n    right: 10%;\n    position: absolute;\n    opacity: 1;\n  }\n  &.toggled {\n    pointer-events: none;\n    padding-left: 9px;\n    .pro-arrow-wrapper {\n      display: none;\n    }\n    .suffix-wrapper {\n      opacity: 0;\n    }\n  }\n"]);
  _templateObject$12 = function _templateObject() {
    return data;
  };
  return data;
}
var InnerMenu = styled.div(_templateObject$12(), function (props) {
  return props.textColor;
}, function (props) {
  return props.backgroundColor;
}, function (props) {
  return props.backgroundColor;
}, function (props) {
  return props.textColor;
});
var PopperElement = styled.div(_templateObject2$n(), function (props) {
  return props.backgroundColor;
});
var PopperInner = styled.div(_templateObject3$g(), PopperElement, function (props) {
  return props.backgroundColor;
});
var SubMenuContainerLi = styled.li(_templateObject4$8(), InnerMenuItem);
var SidebarSubMenu = forwardRef(function (_ref, ref) {
  var children = _ref.children,
    icon = _ref.icon,
    iconClassName = _ref.iconClassName,
    iconSize = _ref.iconSize,
    className = _ref.className,
    title = _ref.title,
    _ref$defaultOpen = _ref.defaultOpen,
    defaultOpen = _ref$defaultOpen === void 0 ? false : _ref$defaultOpen,
    open = _ref.open,
    suffix = _ref.suffix,
    firstChild = _ref.firstChild,
    popperArrow = _ref.popperArrow,
    rest = _objectWithoutPropertiesLoose(_ref, ["children", "icon", "iconClassName", "iconSize", "className", "title", "defaultOpen", "open", "suffix", "firstChild", "popperArrow"]);
  var popperInstance = useRef(null);
  var _useContext = useContext(SidebarContext),
    toggled = _useContext.toggled,
    textColor = _useContext.textColor,
    backgroundColor = _useContext.backgroundColor;
  var _useState = useState(!defaultOpen),
    closed = _useState[0],
    setClosed = _useState[1];
  var popperElRef = useRef(null);
  var referenceElement = useRef(null);
  var popperElement = useRef(null);
  var handleToggleSubMenu = function handleToggleSubMenu() {
    setClosed(!closed);
  };
  useEffect(function () {
    if (firstChild) {
      if (toggled) {
        if (referenceElement.current && popperElement.current) {
          popperInstance.current = createPopper(referenceElement.current, popperElement.current, {
            placement: 'right',
            strategy: 'fixed',
            modifiers: [{
              name: 'computeStyles',
              options: {
                adaptive: false
              }
            }]
          });
        }
        if (popperElRef.current) {
          var ro = new ResizeObserver(function () {
            if (popperInstance.current) {
              popperInstance.current.update();
            }
          });
          ro.observe(popperElRef.current);
          ro.observe(referenceElement.current);
        }
        setTimeout(function () {
          if (popperInstance.current) {
            popperInstance.current.update();
          }
        }, 3);
      }
    }
    return function () {
      if (popperInstance.current) {
        popperInstance.current.destroy();
        popperInstance.current = null;
      }
    };
  }, [firstChild, toggled]);
  var subMenuRef = ref || React.createRef();
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(SubMenuContainerLi, {
    ref: subMenuRef,
    className: classNames('pro-menu-item pro-sub-menu', className, {
      open: typeof open === 'undefined' ? !closed : open
    }, {
      toggled: toggled
    })
  }, React.createElement(InnerMenu, Object.assign({}, rest, {
    ref: referenceElement,
    onClick: handleToggleSubMenu,
    onKeyPress: handleToggleSubMenu,
    role: 'button',
    tabIndex: 0,
    className: classNames('pro-inner-item', className, {
      active: typeof open === 'undefined' ? !closed : open
    }, {
      toggled: toggled
    }),
    textColor: textColor,
    backgroundColor: backgroundColor
  }), icon && React.createElement(Icon, {
    icon: icon,
    className: classNames(iconClassName, 'side-icon'),
    size: iconSize
  }), React.createElement("span", {
    className: 'pro-item-content'
  }, title), suffix ? React.createElement("span", {
    className: 'suffix-wrapper'
  }, suffix) : null, React.createElement("span", {
    className: 'pro-arrow-wrapper'
  }, React.createElement("span", {
    className: 'pro-arrow'
  }))), firstChild && toggled ? React.createElement(PopperElement, {
    ref: popperElement,
    className: classNames('pro-inner-list-item popper-element', {
      'has-arrow': popperArrow
    }),
    backgroundColor: backgroundColor
  }, React.createElement(PopperInner, {
    className: 'popper-inner',
    ref: popperElRef,
    backgroundColor: backgroundColor
  }, React.createElement("ul", null, children)), popperArrow ? React.createElement("div", {
    className: 'popper-arrow',
    "data-popper-arrow": true
  }) : null) : React.createElement(SlideDown, {
    closed: !open ? closed : !open,
    className: 'pro-inner-list-item'
  }, React.createElement("div", null, React.createElement("ul", null, children)))));
});
SidebarSubMenu.defaultProps = {
  iconSize: 'md'
};
function _templateObject3$h() {
  var data = _taggedTemplateLiteralLoose([""]);
  _templateObject3$h = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$o() {
  var data = _taggedTemplateLiteralLoose([""]);
  _templateObject2$o = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$13() {
  var data = _taggedTemplateLiteralLoose(["\n  position: relative;\n  ", "\n  box-shadow: 0 4px 10px 0 rgba(0, 0, 0, 0.1);\n  background: ", ";\n  margin: 10px;\n  box-sizing: border-box;\n  border-radius: 10px;\n  position: relative;\n  padding: 15px;\n  padding-top: 30px;\n  display: flex;\n  flex-direction: column;\n  align-items: center;\n  justify-content: center;\n  height: auto;\n  * {\n    box-sizing: border-box;\n  }\n  p {\n    font-weight: 200;\n    font-size: 12px;\n    color: ", ";\n  }\n  .cta-content {\n    display: flex;\n    width: 100%;\n    flex-direction: column;\n    align-items: center;\n  }\n  .close-icon {\n    position: absolute;\n    top: 12px;\n    width: 17.5px;\n    right: 12px;\n    height: 17.5px;\n    border-radius: 50%;\n    box-sizing: border-box;\n    display: flex;\n    justify-content: center;\n    align-items: center;\n    cursor: pointer;\n    &:hover,\n    &:focus {\n      background: ", ";\n      svg {\n        fill: ", ";\n      }\n    }\n    svg {\n      width: 12.5px;\n      height: 12.5px;\n      fill: ", ";\n    }\n  }\n"]);
  _templateObject$13 = function _templateObject() {
    return data;
  };
  return data;
}
var CTA = styled.div(_templateObject$13(), function (_ref) {
  var mode = _ref.mode;
  return mode === 'light' ? css(_templateObject2$o()) : css(_templateObject3$h());
}, function (props) {
  return props.backgroundColor;
}, function (props) {
  return props.textColor;
}, function (props) {
  return props.textColor;
}, function (props) {
  return props.backgroundColor;
}, function (props) {
  return props.textColor;
});
var SidebarCTA = function SidebarCTA(propss) {
  var mode = propss.mode,
    children = propss.children,
    image = propss.image,
    text = propss.text,
    backgroundColor = propss.backgroundColor,
    textColor = propss.textColor,
    props = _objectWithoutPropertiesLoose(propss, ["mode", "children", "image", "text", "backgroundColor", "textColor"]);
  var _useState = useState(true),
    show = _useState[0],
    setShow = _useState[1];
  var _useContext = useContext(SidebarContext),
    toggled = _useContext.toggled;
  var color = tinycolor(backgroundColor);
  var CTABackgroundColor = mode === 'dark' ? color.lighten(10).toString() : color.darken(10).toString();
  console.log(CTABackgroundColor);
  return React.createElement(ThemeProvider, {
    theme: theme
  }, !toggled && show && React.createElement(CTA, Object.assign({
    mode: mode,
    backgroundColor: CTABackgroundColor,
    textColor: textColor
  }, props), React.createElement("i", {
    className: 'close-icon',
    onClick: function onClick() {
      return setShow(false);
    }
  }, React.createElement("svg", {
    className: 'w-4 h-4',
    fill: 'currentColor',
    viewBox: '0 0 20 20',
    xmlns: 'http://www.w3.org/2000/svg'
  }, React.createElement("path", {
    "fill-rule": 'evenodd',
    d: 'M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z',
    "clip-rule": 'evenodd'
  }))), children ? {
    children: children
  } : React.createElement("div", {
    className: 'cta-content'
  }, image, React.createElement("p", null, text))));
};
SidebarCTA.defaultProps = {
  mode: 'dark'
};
var Slider = function Slider(props) {
  var value = props.value,
    setValue = props.setValue,
    className = props.className,
    attributes = _objectWithoutPropertiesLoose(props, ["value", "setValue", "className"]);
  var sliderClasses = classNames("slider", className);
  var sliderComponent = React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(RangeSlider, Object.assign({
    value: value,
    onChange: function onChange(changeEvent) {
      return setValue(changeEvent.target.value);
    },
    className: sliderClasses
  }, attributes)));
  return sliderComponent;
};
Slider.defaultProps = {
  min: 0,
  max: 100,
  step: 1
};
var SmoothScroll = function SmoothScroll(props) {
  var children = props.children,
    attributes = _objectWithoutPropertiesLoose(props, ["children"]);
  var smoothScrollComp = React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Link$2, Object.assign({}, attributes), children));
  return smoothScrollComp;
};
SmoothScroll.defaultProps = {
  offset: -70,
  duration: 500,
  smooth: true,
  spy: true
};
function _templateObject7$3() {
  var data = _taggedTemplateLiteralLoose(["\n          ", " 1.4s ease-in-out infinite\n        "]);
  _templateObject7$3 = function _templateObject7() {
    return data;
  };
  return data;
}
function _templateObject6$3() {
  var data = _taggedTemplateLiteralLoose(["\n          ", " 1.4s ease-in-out infinite, ", " 5.6s ease-in-out infinite\n        "]);
  _templateObject6$3 = function _templateObject6() {
    return data;
  };
  return data;
}
function _templateObject5$5() {
  var data = _taggedTemplateLiteralLoose(["\n  stroke: ", ";\n\n  stroke-dasharray: 187;\n  stroke-dashoffset: 0;\n  transform-origin: center;\n  animation: ", ";\n"]);
  _templateObject5$5 = function _templateObject5() {
    return data;
  };
  return data;
}
function _templateObject4$9() {
  var data = _taggedTemplateLiteralLoose(["\n  stroke-width: 5;\n  animation-name: ", ";\n  animation-duration: 1.4s;\n  animation-iteration-count: infinite;\n"]);
  _templateObject4$9 = function _templateObject4() {
    return data;
  };
  return data;
}
function _templateObject3$i() {
  var data = _taggedTemplateLiteralLoose(["\n 0% { stroke-dashoffset: 187; }\n  50%{stroke-dashoffset: 46.75;\ntransform: rotate(135deg)} \n 100% {   stroke-dashoffset: 187;\ntransform: rotate(450deg)}"]);
  _templateObject3$i = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$p() {
  var data = _taggedTemplateLiteralLoose(["\n 0% { stroke: #4285f4; }\n 25%{stroke: #de3e35;} 50%{stroke: #f7c223} 75%{stroke: #1b9a59}\n 100% {  stroke: #4285f4}"]);
  _templateObject2$p = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$14() {
  var data = _taggedTemplateLiteralLoose(["\n 0% { transform: rotate(0deg) }\n 100% { transform: rotate(360deg) }"]);
  _templateObject$14 = function _templateObject() {
    return data;
  };
  return data;
}
var rotator = keyframes(_templateObject$14());
var colors$1 = keyframes(_templateObject2$p());
var dash = keyframes(_templateObject3$i());
var Component$X = styled.svg(_templateObject4$9(), rotator);
var Circle = styled.circle(_templateObject5$5(), function (props) {
  return props.warning ? "#FFE975" : props.success ? "#05944F" : props.secondary ? "#7356BF" : props.dark ? "#000000" : props.danger ? "#E11900" : props.info ? "#17A2B8" : "#276EF1";
}, function (props) {
  return props.multicolor ? css(_templateObject6$3(), dash, colors$1) : css(_templateObject7$3(), dash);
});
var Spinner = function Spinner(props) {
  var tag = props.tag,
    size = props.size,
    className = props.className,
    secondary = props.secondary,
    info = props.info,
    success = props.success,
    warning = props.warning,
    danger = props.danger,
    dark = props.dark,
    multicolor = props.multicolor,
    attributes = _objectWithoutPropertiesLoose(props, ["color", "tag", "size", "className", "secondary", "info", "success", "warning", "danger", "dark", "multicolor"]);
  var spinnerClasses = classNames("spinner", className);
  var sizeValue = size === "big" ? "100px" : size === "small" ? "30px" : "65px";
  var spinnerComponent = React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$X, Object.assign({
    className: spinnerClasses,
    as: tag,
    size: size
  }, attributes, {
    width: sizeValue,
    height: sizeValue,
    viewBox: "0 0 66 66",
    xmlns: "http://www.w3.org/2000/svg"
  }), React.createElement(Circle, {
    fill: "none",
    "stroke-width": "6",
    "stroke-linecap": "round",
    cx: "33",
    cy: "33",
    r: "30",
    danger: danger,
    success: success,
    info: info,
    secondary: secondary,
    dark: dark,
    warning: warning,
    multicolor: multicolor
  })));
  return spinnerComponent;
};
Spinner.defaultProps = {
  tag: "svg"
};
function _templateObject3$j() {
  var data = _taggedTemplateLiteralLoose(["\n      flex-direction: column;\n      .step-row-2 {\n        flex-direction: column;\n        margin-top: 22px;\n        overflow-x: hidden;\n        .step-content {\n          overflow: auto;\n          min-width: 500px;\n          justify-content: space-between !important;\n          display: flex !important;\n          width: 100%;\n        }\n        &-webkit-scrollbar {\n          height: 8px;\n        }\n        &-webkit-scrollbar-thumb {\n          background: #ccc;\n        }\n        &-webkit-scrollbar-track {\n        }\n      }\n    "]);
  _templateObject3$j = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$q() {
  var data = _taggedTemplateLiteralLoose(["\n      .step-row-2 {\n        height: 100%;\n        flex-direction: row;\n        .step-content {\n          display: flex;\n          -ms-flex-align: center;\n          flex-direction: column;\n          align-items: center;\n          height: 100%;\n          justify-content: space-between !important;\n        }\n      }\n    "]);
  _templateObject2$q = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$15() {
  var data = _taggedTemplateLiteralLoose(["\n  display: flex;\n  position: relative;\n  width: 100%;\n  padding: 10px;\n  ", "\n  ", "\n\n  .steps {\n    display: table-cell;\n    text-align: center;\n    position: relative;\n  }\n"]);
  _templateObject$15 = function _templateObject() {
    return data;
  };
  return data;
}
var Component$Y = styled.div(_templateObject$15(), function (props) {
  return props.direction === "vertical" && css(_templateObject2$q());
}, function (props) {
  return props.direction === "horizontal" && css(_templateObject3$j());
});
function _templateObject3$k() {
  var data = _taggedTemplateLiteralLoose(["\n      width: 100%;\n    "]);
  _templateObject3$k = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$r() {
  var data = _taggedTemplateLiteralLoose(["\n      width: calc(100% - 150px);\n    "]);
  _templateObject2$r = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$16() {
  var data = _taggedTemplateLiteralLoose(["\n  display: flex;\n  ", "\n  ", "\n    .content-container {\n    width: 100%;\n    padding: 15px 10px;\n  }\n  .content-transition {\n    width: 100%;\n  }\n  .fade-in {\n    animation: fadeIn 0.5s;\n  }\n  @keyframes fadeIn {\n    0% {\n      opacity: 0;\n      margin-top: -20px;\n    }\n    100% {\n      opacity: 1;\n      margin-top: 0px;\n    }\n  }\n"]);
  _templateObject$16 = function _templateObject() {
    return data;
  };
  return data;
}
var Component$Z = styled.div(_templateObject$16(), function (_ref) {
  var direction = _ref.direction;
  return direction === 'vertical' && css(_templateObject2$r());
}, function (_ref2) {
  var direction = _ref2.direction;
  return direction === 'horizontal' && css(_templateObject3$k());
});
var StepperContent = function StepperContent() {
  var _useContext = useContext(StepperContext),
    content = _useContext.content,
    direction = _useContext.direction,
    active = _useContext.active;
  return React.createElement(Component$Z, {
    direction: direction
  }, content.map(function (item) {
    if (active === item.id) {
      return React.createElement("div", {
        className: 'content-container fade-in'
      }, item.node);
    }
  }));
};
var StepperContext = createContext({});
var Stepper = function Stepper(props) {
  var direction = props.direction,
    children = props.children,
    mainColor = props.mainColor,
    stepSize = props.stepSize,
    showTooltip = props.showTooltip,
    showTitle = props.showTitle,
    showIndex = props.showIndex,
    activeColor = props.activeColor,
    completeColor = props.completeColor,
    incompleteColor = props.incompleteColor,
    dividerStepCompleteColor = props.dividerStepCompleteColor,
    dividerStepIncompleteColor = props.dividerStepIncompleteColor,
    tooltipColor = props.tooltipColor,
    attributes = _objectWithoutPropertiesLoose(props, ["direction", "children", "mainColor", "stepSize", "showTooltip", "showTitle", "showIndex", "activeColor", "completeColor", "incompleteColor", "dividerStepCompleteColor", "dividerStepIncompleteColor", "tooltipColor"]);
  var containerRef = useRef(null);
  var _useState = useState(0),
    width = _useState[0],
    setWidth = _useState[1];
  var _useState2 = useState(0),
    height = _useState2[0],
    setHeight = _useState2[1];
  var _useState3 = useState([]),
    content = _useState3[0],
    setContent = _useState3[1];
  var _useState4 = useState(1),
    active = _useState4[0],
    setActive = _useState4[1];
  useEffect(function () {
    window.addEventListener('resize', function () {
      var _containerRef$current, _containerRef$current2;
      setWidth(containerRef === null || containerRef === void 0 ? void 0 : (_containerRef$current = containerRef.current) === null || _containerRef$current === void 0 ? void 0 : _containerRef$current.offsetWidth);
      setHeight(containerRef === null || containerRef === void 0 ? void 0 : (_containerRef$current2 = containerRef.current) === null || _containerRef$current2 === void 0 ? void 0 : _containerRef$current2.offsetHeight);
    });
  }, []);
  useEffect(function () {
    var contentArray = [];
    children.forEach(function (child, index) {
      contentArray.push({
        node: child.props.component,
        id: index + 1
      });
    });
    setContent(contentArray);
  }, []);
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(StepperContext.Provider, {
    value: {
      direction: direction,
      mainColor: mainColor,
      width: width,
      height: height,
      stepSize: stepSize,
      children: children,
      content: content,
      active: active,
      setActive: setActive,
      showTooltip: showTooltip,
      showTitle: showTitle,
      showIndex: showIndex,
      activeColor: activeColor,
      completeColor: completeColor,
      incompleteColor: incompleteColor,
      dividerStepCompleteColor: dividerStepCompleteColor,
      dividerStepIncompleteColor: dividerStepIncompleteColor,
      tooltipColor: tooltipColor
    }
  }, React.createElement(Component$Y, Object.assign({
    direction: direction
  }, attributes, {
    background: mainColor,
    ref: containerRef
  }), React.createElement("div", {
    className: 'step-row-2'
  }, React.createElement("div", {
    className: 'step-content'
  }, children.map(function (item) {
    return item;
  }))), React.createElement(StepperContent, null))));
};
Stepper.defaultProps = {
  direction: 'horizontal',
  currentStepNumber: 1,
  mainColor: theme.colors.dark900,
  stepSize: 45,
  showTooltip: true,
  showTitle: true
};
function _templateObject6$4() {
  var data = _taggedTemplateLiteralLoose(["\n      span:nth-child(1) {\n        background-color: ", ";\n      }\n      span:nth-child(2) {\n        background-color: ", ";\n      }\n    "]);
  _templateObject6$4 = function _templateObject6() {
    return data;
  };
  return data;
}
function _templateObject5$6() {
  var data = _taggedTemplateLiteralLoose(["\n      span:nth-child(1) {\n        background-color: ", ";\n      }\n      span:nth-child(2) {\n        background-color: ", ";\n      }\n    "]);
  _templateObject5$6 = function _templateObject5() {
    return data;
  };
  return data;
}
function _templateObject4$a() {
  var data = _taggedTemplateLiteralLoose(["\n      span:nth-child(1) {\n        background-color: ", ";\n      }\n      span:nth-child(2) {\n        background-color: ", ";\n      }\n    "]);
  _templateObject4$a = function _templateObject4() {
    return data;
  };
  return data;
}
function _templateObject3$l() {
  var data = _taggedTemplateLiteralLoose(["\n      flex-direction: row;\n      justify-content: center;\n      align-items: center;\n      margin-left: 10px;\n      span {\n        margin: 0 2px;\n        border-radius: 6px;\n        transition: 0.4;\n      }\n      span:nth-child(1) {\n        width: 6px;\n        height: 6px;\n      }\n      span:nth-child(2) {\n        width: 50px;\n        height: 6px;\n      }\n    "]);
  _templateObject3$l = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$s() {
  var data = _taggedTemplateLiteralLoose(["\n      width: ", "px;\n      flex-direction: column;\n      justify-content: center;\n      align-items: center;\n      margin-top: 10px;\n      margin-bottom: ", ";\n      span {\n        margin: 2px 0;\n        border-radius: 6px;\n        transition: 0.4;\n      }\n      span:nth-child(1) {\n        width: 6px;\n        height: 6px;\n      }\n      span:nth-child(2) {\n        width: 6px;\n        height: 50px;\n      }\n    "]);
  _templateObject2$s = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$17() {
  var data = _taggedTemplateLiteralLoose(["\n  display: flex;\n  ", "\n  ", "\n  ", ";\n  ", ";\n  ", "\n"]);
  _templateObject$17 = function _templateObject() {
    return data;
  };
  return data;
}
var Component$_ = styled.div(_templateObject$17(), function (_ref) {
  var direction = _ref.direction,
    size = _ref.size,
    showTitle = _ref.showTitle,
    showIndex = _ref.showIndex;
  return direction === "vertical" && css(_templateObject2$s(), size, !showTitle && !showIndex && "10px");
}, function (_ref2) {
  var direction = _ref2.direction;
  return direction === "horizontal" && css(_templateObject3$l());
}, function (_ref3) {
  var status = _ref3.status,
    dividerStepCompleteColor = _ref3.dividerStepCompleteColor,
    completeColor = _ref3.completeColor;
  return status === "prev" && css(_templateObject4$a(), dividerStepCompleteColor ? dividerStepCompleteColor : completeColor ? completeColor : "#666666", dividerStepCompleteColor ? dividerStepCompleteColor : completeColor ? completeColor : "#666666");
}, function (_ref4) {
  var status = _ref4.status,
    dividerStepCompleteColor = _ref4.dividerStepCompleteColor,
    dividerStepIncompleteColor = _ref4.dividerStepIncompleteColor,
    activeColor = _ref4.activeColor;
  return status === "active" && css(_templateObject5$6(), dividerStepCompleteColor ? dividerStepCompleteColor : activeColor ? activeColor : "#666666", dividerStepIncompleteColor ? dividerStepIncompleteColor : activeColor ? activeColor : "#cccccc");
}, function (_ref5) {
  var status = _ref5.status,
    dividerStepIncompleteColor = _ref5.dividerStepIncompleteColor,
    incompleteColor = _ref5.incompleteColor;
  return status === "next" && css(_templateObject6$4(), dividerStepIncompleteColor ? dividerStepIncompleteColor : incompleteColor ? incompleteColor : "#cccccc", dividerStepIncompleteColor ? dividerStepIncompleteColor : incompleteColor ? incompleteColor : "#cccccc");
});
var StepperDivider = function StepperDivider(_ref6) {
  var status = _ref6.status,
    activeColor = _ref6.activeColor,
    completeColor = _ref6.completeColor,
    incompleteColor = _ref6.incompleteColor;
  var containerRef = useRef(null);
  var _useContext = useContext(StepperContext),
    direction = _useContext.direction,
    mainColor = _useContext.mainColor,
    stepSize = _useContext.stepSize,
    showTitle = _useContext.showTitle,
    showIndex = _useContext.showIndex,
    dividerStepCompleteColor = _useContext.dividerStepCompleteColor,
    dividerStepIncompleteColor = _useContext.dividerStepIncompleteColor;
  return React.createElement(Component$_, {
    direction: direction,
    mainColor: mainColor,
    background: mainColor,
    ref: containerRef,
    size: stepSize,
    status: status,
    showTitle: showTitle,
    showIndex: showIndex,
    dividerStepCompleteColor: dividerStepCompleteColor,
    dividerStepIncompleteColor: dividerStepIncompleteColor,
    activeColor: activeColor,
    completeColor: completeColor,
    incompleteColor: incompleteColor
  }, React.createElement("span", null), React.createElement("span", null));
};
function _templateObject11$2() {
  var data = _taggedTemplateLiteralLoose(["\n        color: ", ";\n        background-color: ", ";\n      "]);
  _templateObject11$2 = function _templateObject11() {
    return data;
  };
  return data;
}
function _templateObject10$2() {
  var data = _taggedTemplateLiteralLoose(["\n        background-color: #fff;\n        color: ", ";\n        border: 2px solid ", ";\n        position: relative;\n      "]);
  _templateObject10$2 = function _templateObject10() {
    return data;
  };
  return data;
}
function _templateObject9$2() {
  var data = _taggedTemplateLiteralLoose(["\n        background-color: ", ";\n        color: ", ";\n        border: 2px solid #cccccc;\n      "]);
  _templateObject9$2 = function _templateObject9() {
    return data;
  };
  return data;
}
function _templateObject8$3() {
  var data = _taggedTemplateLiteralLoose(["\n        flex-direction: column;\n      "]);
  _templateObject8$3 = function _templateObject8() {
    return data;
  };
  return data;
}
function _templateObject7$4() {
  var data = _taggedTemplateLiteralLoose(["\n        flex-direction: row;\n      "]);
  _templateObject7$4 = function _templateObject7() {
    return data;
  };
  return data;
}
function _templateObject6$5() {
  var data = _taggedTemplateLiteralLoose(["\n        opacity: 0.6;\n      "]);
  _templateObject6$5 = function _templateObject6() {
    return data;
  };
  return data;
}
function _templateObject5$7() {
  var data = _taggedTemplateLiteralLoose(["\n        opacity: 1;\n      "]);
  _templateObject5$7 = function _templateObject5() {
    return data;
  };
  return data;
}
function _templateObject4$b() {
  var data = _taggedTemplateLiteralLoose(["\n        opacity: 1;\n      "]);
  _templateObject4$b = function _templateObject4() {
    return data;
  };
  return data;
}
function _templateObject3$m() {
  var data = _taggedTemplateLiteralLoose(["\n      min-width: 150px;\n      max-width: 150px;\n    "]);
  _templateObject3$m = function _templateObject3() {
    return data;
  };
  return data;
}
function _templateObject2$t() {
  var data = _taggedTemplateLiteralLoose(["\n      flex-direction: column;\n    "]);
  _templateObject2$t = function _templateObject2() {
    return data;
  };
  return data;
}
function _templateObject$18() {
  var data = _taggedTemplateLiteralLoose(["\n  display: flex;\n  ", "\n  ", "\n  .step-title {\n    display: flex;\n    align-items: center;\n    margin-left: 5px;\n    padding: 10px 0;\n    ", ";\n    ", ";\n    ", "\n    .step-number {\n      font-size: 30px;\n      font-weight: 600;\n    }\n    .step-text {\n      font-size: 14px;\n      line-height: 18px;\n      font-weight: 600;\n      margin-left: 10px;\n      text-align: start;\n      max-width: 150px;\n      word-wrap: break-word;\n    }\n  }\n  .step-indicators {\n    display: flex;\n    width: fit-content;\n    ", "\n    ", "\n  }\n  .default-node {\n    cursor: pointer;\n    width: ", ";\n    height: ", ";\n    color: #000;\n    font-size: 16px;\n    border-radius: 50%;\n    display: flex;\n    justify-content: center;\n    transition: width 0.3;\n    align-items: center;\n    * {\n      position: relative;\n    }\n    ", ";\n    ", ";\n    ", "\n  }\n  .__react_component_tooltip {\n    background: ", ";\n    font-size: 10px;\n    * {\n      font-size: inherit;\n    }\n    padding: 2px 5.5px;\n    border-radius: 4px;\n    &:before,\n    &:after {\n      display: none !important;\n    }\n  }\n"]);
  _templateObject$18 = function _templateObject() {
    return data;
  };
  return data;
}
var Component$$ = styled.div(_templateObject$18(), function (_ref) {
  var direction = _ref.direction;
  return direction === "horizontal" && css(_templateObject2$t());
}, function (_ref2) {
  var direction = _ref2.direction;
  return direction === "vertical" && css(_templateObject3$m());
}, function (_ref3) {
  var status = _ref3.status;
  return status === "prev" && css(_templateObject4$b());
}, function (_ref4) {
  var status = _ref4.status;
  return status === "active" && css(_templateObject5$7());
}, function (_ref5) {
  var status = _ref5.status;
  return status === "next" && css(_templateObject6$5());
}, function (_ref6) {
  var direction = _ref6.direction;
  return direction === "horizontal" && css(_templateObject7$4());
}, function (_ref7) {
  var direction = _ref7.direction;
  return direction === "vertical" && css(_templateObject8$3());
}, function (_ref8) {
  var size = _ref8.size;
  return size + "px";
}, function (_ref9) {
  var size = _ref9.size;
  return size + "px";
}, function (_ref10) {
  var status = _ref10.status,
    completeStepColor = _ref10.completeStepColor,
    completeTextColor = _ref10.completeTextColor,
    background = _ref10.background;
  return status === "prev" && css(_templateObject9$2(), completeStepColor ? completeStepColor : background ? background : "#333333", completeTextColor ? completeTextColor : "#fff");
}, function (_ref11) {
  var status = _ref11.status,
    activeStepColor = _ref11.activeStepColor,
    activeTextColor = _ref11.activeTextColor,
    background = _ref11.background;
  return status === "active" && css(_templateObject10$2(), activeTextColor ? activeTextColor : "", activeStepColor ? activeStepColor : background ? background : "#333333");
}, function (_ref12) {
  var status = _ref12.status,
    inactiveStepColor = _ref12.inactiveStepColor,
    inactiveTextColor = _ref12.inactiveTextColor;
  return status === "next" && css(_templateObject11$2(), inactiveTextColor ? inactiveTextColor : "#fff", inactiveStepColor ? inactiveStepColor : "#9f9d9d");
}, function (props) {
  return props.tooltipColor;
});
var Step = function Step(props) {
  var name = props.name,
    icon = props.icon,
    far = props.far,
    fab = props.fab,
    fas = props.fas,
    handleClick = props.handleClick,
    active = props.active,
    id = props.id,
    activeBgColor = props.activeBgColor,
    completeBgColor = props.completeBgColor,
    incompleteBgColor = props.incompleteBgColor,
    incompleteTextColor = props.incompleteTextColor,
    completeTextColor = props.completeTextColor,
    activeTextColor = props.activeTextColor,
    attributes = _objectWithoutPropertiesLoose(props, ["name", "icon", "children", "far", "fab", "fas", "handleClick", "active", "id", "component", "activeBgColor", "completeBgColor", "incompleteBgColor", "incompleteTextColor", "completeTextColor", "activeTextColor"]);
  var _useContext = useContext(StepperContext),
    mainColor = _useContext.mainColor,
    mainChildren = _useContext.children,
    direction = _useContext.direction,
    stepSize = _useContext.stepSize,
    setActive = _useContext.setActive,
    showTooltip = _useContext.showTooltip,
    showTitle = _useContext.showTitle,
    showIndex = _useContext.showIndex,
    activeColor = _useContext.activeColor,
    completeColor = _useContext.completeColor,
    incompleteColor = _useContext.incompleteColor,
    tooltipColor = _useContext.tooltipColor;
  var stepClasses = classNames('default-node', id === active && 'active');
  var status = id < active ? 'prev' : id === active ? 'active' : 'next';
  var iconPrefix = far ? 'far' : fas ? 'fas' : fab ? 'fab' : 'fa';
  var iconClass = classNames(iconPrefix, icon ? "fa-" + icon : false);
  useEffect(function () {
    setActive && setActive(active);
  }, [active]);
  var stepIcon = icon ? React.createElement("i", {
    className: iconClass
  }) : null;
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$$, Object.assign({
    className: 'steps',
    key: name,
    icon: icon
  }, attributes, {
    tooltipBackground: mainColor,
    background: mainColor,
    active: active,
    status: status,
    direction: direction,
    size: stepSize,
    activeStepColor: activeBgColor || activeColor,
    activeTextColor: activeTextColor,
    inactiveStepColor: incompleteBgColor || incompleteColor,
    inactiveTextColor: incompleteTextColor,
    completeStepColor: completeBgColor || completeColor,
    completeTextColor: completeTextColor,
    tooltipColor: tooltipColor
  }), showTitle || showIndex && React.createElement("div", {
    className: 'step-title'
  }, React.createElement("span", {
    className: 'step-number'
  }, "0", id), showTitle && React.createElement("div", {
    className: 'step-text'
  }, name)), React.createElement("div", {
    className: 'step-indicators',
    "data-for": "tip " + name,
    key: name,
    "data-tip": true
  }, React.createElement("div", {
    className: stepClasses,
    onClick: function onClick() {
      return handleClick && handleClick();
    }
  }, icon ? stepIcon : "0" + id), id < (mainChildren === null || mainChildren === void 0 ? void 0 : mainChildren.length) && React.createElement(StepperDivider, {
    status: status,
    activeColor: activeBgColor || activeColor,
    incompleteColor: incompleteBgColor || incompleteColor,
    completeColor: completeBgColor || completeColor
  }), showTooltip && React.createElement(ReactTooltip, {
    id: "tip " + name,
    place: 'top',
    effect: 'solid'
  }, name))));
};
Step.defaultProps = {
  component: null
};
function _templateObject$19() {
  var data = _taggedTemplateLiteralLoose(["\n  .switch-container {\n  }\n\n  input[type=\"checkbox\"].switch {\n    position:absolute;\n    opacity: 0;\n  }\n\n  input[type=\"checkbox\"].switch + div {\n    vertical-align: middle;\n    width: 40px;\n    height: 20px;\n    border-radius: 100px;\n    background-color: #333;\n    cursor: pointer;\n  }\n\n  input[type=\"checkbox\"].switch:checked + div {\n    background-color: #80ff80;\n  }\n\n  input[type=\"checkbox\"].switch + div > div {\n    margin:0;\n    width: 50%;\n    height: 100%;\n    background: #f2f2f2;\n    border-radius: inherit;\n    transition:margin 0.3s linear;\n  }\n\n  input[type=\"checkbox\"].switch:checked + div > div {\n    margin-left:auto;\n    // transition:all 0.s linear;\n    // -webkit-transform: translate3d(23px, 0, 0);\n    // transform: translate3d(23px, 0, 0);\n  }\n"]);
  _templateObject$19 = function _templateObject() {
    return data;
  };
  return data;
}
var Component$10 = styled.div(_templateObject$19());
var Switch = function Switch(props) {
  var checked = props.checked,
    className = props.className,
    attributes = _objectWithoutPropertiesLoose(props, ["checked", "className"]);
  var _useState = useState(checked),
    isChecked = _useState[0],
    setIsChecked = _useState[1];
  var switchClassName = classNames(className);
  var handleChange = function handleChange() {
    setIsChecked(!isChecked);
  };
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$10, Object.assign({
    className: switchClassName
  }, attributes), React.createElement("div", {
    className: "switch-container"
  }, React.createElement("label", null, React.createElement("input", {
    checked: isChecked,
    onChange: handleChange,
    className: "switch",
    type: "checkbox"
  }), React.createElement("div", null, React.createElement("div", null))))));
};
Switch.defaultProps = {
  tag: "div",
  checked: false
};
function _templateObject$1a() {
  var data = _taggedTemplateLiteralLoose(["\n.tab-content{\n  margin-top: 30px;\n}\n  .tab-content .tab-pane {\n    display: none;\n    transition: opacity 300ms linear;\n  }\n\n  .tab-content .tab-pane.active {\n    display: block;\n  }\n"]);
  _templateObject$1a = function _templateObject() {
    return data;
  };
  return data;
}
var Component$11 = styled.div(_templateObject$1a());
var TabContext = createContext({
  activeItemId: null
});
var TabContent = function TabContent(props) {
  var className = props.className;
  var _useState = useState({
    activeItem: props.activeItem
  });
  var attributes = omit(props, Object.keys(propTypes));
  var classes = classNames("tab-content", className);
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(TabContext.Provider, {
    value: {
      activeItemId: props.activeItem
    }
  }, React.createElement(Component$11, null, React.createElement("div", Object.assign({
    "data-test": "tabContent"
  }, attributes, {
    className: classes
  })))));
};
var propTypes = {
  activeItem: PropTypes.any,
  className: PropTypes.string
};
function _templateObject$1b() {
  var data = _taggedTemplateLiteralLoose(["\n  .tab-link {\n    margin-top: 5px;\n    border: none;\n    trabsition: 1s;\n    color: #000000;\n    border-bottom: 5px solid #e2e2e2;\n    font-weight: bold;\n    transition:all 300ms ease-in-out;\n  }\n  .tab-link.active {\n    border-bottom: 5px solid #000000;\n  }\n"]);
  _templateObject$1b = function _templateObject() {
    return data;
  };
  return data;
}
var Component$12 = styled.span(_templateObject$1b());
var TabLink = function TabLink(props) {
  var children = props.children,
    className = props.className,
    active = props.active,
    text = props.text,
    link = props.link,
    to = props.to,
    tag = props.tag,
    attributes = _objectWithoutPropertiesLoose(props, ["children", "className", "active", "text", "link", "to", "tag"]);
  var Tag = link ? Link : NavLink$1;
  var itemClasses = classNames("nav-item", active && "active", text && "navbar-text", className);
  var linkClasses = classNames("tab-link", active && "active", className);
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$12, Object.assign({
    "data-test": "nav-item",
    as: tag
  }, attributes, {
    className: itemClasses
  }), React.createElement(Tag, {
    to: to,
    className: linkClasses
  }, children)));
};
function _templateObject$1c() {
  var data = _taggedTemplateLiteralLoose(["\n  /* appear - on page load */\n  .fade-appear {\n    display: none;\n    opacity: 0;\n    z-index: 1;\n  }\n  .fade-appear.fade-appear-active {\n    opacity: 1;\n    display: block;\n    transition: opacity 300ms linear;\n  }\n\n  /* enter */\n  .fade-enter {\n    display: none;\n    opacity: 0;\n    z-index: 1;\n  }\n  .fade-enter.fade-enter-active {\n    display: block;\n    opacity: 1;\n    transition: opacity 300ms linear 300ms;\n  }\n\n  /* exit */\n  .fade-exit {\n    display: block;\n    opacity: 1;\n  }\n  .fade-exit.fade-exit-active {\n    display: none;\n    opacity: 0;\n    transition: opacity 300ms linear;\n  }\n  .fade-exit-done {\n    opacity: 0;\n    display: none;\n  }\n"]);
  _templateObject$1c = function _templateObject() {
    return data;
  };
  return data;
}
var Component$13 = styled.div(_templateObject$1c());
var TabPane = function TabPane(props) {
  var className = props.className,
    tabId = props.tabId,
    attributes = _objectWithoutPropertiesLoose(props, ["className", "tabId"]);
  var _useContext = useContext(TabContext),
    activeItemId = _useContext.activeItemId;
  var classes = classNames('tab-pane', {
    active: tabId === activeItemId
  }, className);
  var activeId = tabId === activeItemId ? true : false;
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$13, null, React.createElement(TransitionGroup, null, React.createElement(CSSTransition, {
    timeout: 300,
    key: tabId,
    in: activeId,
    appear: true,
    classNames: 'fade'
  }, React.createElement("div", Object.assign({
    "data-test": 'tab-pane'
  }, attributes, {
    className: classes,
    role: 'tabpanel'
  }))))));
};
var gradients = [{
  "name": "Noon to Dusk",
  "colors": ["#ff6e7f", "#bfe9ff"]
}, {
  "name": "YouTube",
  "colors": ["#e52d27", "#b31217"]
}, {
  "name": "Cool Brown",
  "colors": ["#603813", "#b29f94"]
}, {
  "name": "Harmonic Energy",
  "colors": ["#16A085", "#F4D03F"]
}, {
  "name": "Playing with Reds",
  "colors": ["#D31027", "#EA384D"]
}, {
  "name": "Sunny Days",
  "colors": ["#EDE574", "#E1F5C4"]
}, {
  "name": "Green Beach",
  "colors": ["#02AAB0", "#00CDAC"]
}, {
  "name": "Intuitive Purple",
  "colors": ["#DA22FF", "#9733EE"]
}, {
  "name": "Emerald Water",
  "colors": ["#348F50", "#56B4D3"]
}, {
  "name": "Lemon Twist",
  "colors": ["#3CA55C", "#B5AC49"]
}, {
  "name": "Horizon",
  "colors": ["#003973", "#E5E5BE"]
}, {
  "name": "Rose Water",
  "colors": ["#E55D87", "#5FC3E4"]
}, {
  "name": "Frozen",
  "colors": ["#403B4A", "#E7E9BB"]
}, {
  "name": "Mango Pulp",
  "colors": ["#F09819", "#EDDE5D"]
}, {
  "name": "Bloody Mary",
  "colors": ["#FF512F", "#DD2476"]
}, {
  "name": "Aubergine",
  "colors": ["#AA076B", "#61045F"]
}, {
  "name": "Aqua Marine",
  "colors": ["#1A2980", "#26D0CE"]
}, {
  "name": "Sunrise",
  "colors": ["#FF512F", "#F09819"]
}, {
  "name": "Purple Paradise",
  "colors": ["#1D2B64", "#F8CDDA"]
}, {
  "name": "Sea Weed",
  "colors": ["#4CB8C4", "#3CD3AD"]
}, {
  "name": "Pinky",
  "colors": ["#DD5E89", "#F7BB97"]
}, {
  "name": "Cherry",
  "colors": ["#EB3349", "#F45C43"]
}, {
  "name": "Mojito",
  "colors": ["#1D976C", "#93F9B9"]
}, {
  "name": "Juicy Orange",
  "colors": ["#FF8008", "#FFC837"]
}, {
  "name": "Mirage",
  "colors": ["#16222A", "#3A6073"]
}, {
  "name": "Steel Gray",
  "colors": ["#1F1C2C", "#928DAB"]
}, {
  "name": "Kashmir",
  "colors": ["#614385", "#516395"]
}, {
  "name": "Electric Violet",
  "colors": ["#4776E6", "#8E54E9"]
}, {
  "name": "Venice Blue",
  "colors": ["#085078", "#85D8CE"]
}, {
  "name": "Bora Bora",
  "colors": ["#2BC0E4", "#EAECC6"]
}, {
  "name": "Moss",
  "colors": ["#134E5E", "#71B280"]
}, {
  "name": "Shroom Haze",
  "colors": ["#5C258D", "#4389A2"]
}, {
  "name": "Mystic",
  "colors": ["#757F9A", "#D7DDE8"]
}, {
  "name": "Midnight City",
  "colors": ["#232526", "#414345"]
}, {
  "name": "Sea Blizz",
  "colors": ["#1CD8D2", "#93EDC7"]
}, {
  "name": "Opa",
  "colors": ["#3D7EAA", "#FFE47A"]
}, {
  "name": "Titanium",
  "colors": ["#283048", "#859398"]
}, {
  "name": "Mantle",
  "colors": ["#24C6DC", "#514A9D"]
}, {
  "name": "Dracula",
  "colors": ["#DC2424", "#4A569D"]
}, {
  "name": "Peach",
  "colors": ["#ED4264", "#FFEDBC"]
}, {
  "name": "Moonrise",
  "colors": ["#DAE2F8", "#D6A4A4"]
}, {
  "name": "Clouds",
  "colors": ["#ECE9E6", "#FFFFFF"]
}, {
  "name": "Stellar",
  "colors": ["#7474BF", "#348AC7"]
}, {
  "name": "Bourbon",
  "colors": ["#EC6F66", "#F3A183"]
}, {
  "name": "Calm Darya",
  "colors": ["#5f2c82", "#49a09d"]
}, {
  "name": "Influenza",
  "colors": ["#C04848", "#480048"]
}, {
  "name": "Shrimpy",
  "colors": ["#e43a15", "#e65245"]
}, {
  "name": "Army",
  "colors": ["#414d0b", "#727a17"]
}, {
  "name": "Miaka",
  "colors": ["#FC354C", "#0ABFBC"]
}, {
  "name": "Pinot Noir",
  "colors": ["#4b6cb7", "#182848"]
}, {
  "name": "Day Tripper",
  "colors": ["#f857a6", "#ff5858"]
}, {
  "name": "Namn",
  "colors": ["#a73737", "#7a2828"]
}, {
  "name": "Blurry Beach",
  "colors": ["#d53369", "#cbad6d"]
}, {
  "name": "Vasily",
  "colors": ["#e9d362", "#333333"]
}, {
  "name": "A Lost Memory",
  "colors": ["#DE6262", "#FFB88C"]
}, {
  "name": "Petrichor",
  "colors": ["#666600", "#999966"]
}, {
  "name": "Jonquil",
  "colors": ["#FFEEEE", "#DDEFBB"]
}, {
  "name": "Sirius Tamed",
  "colors": ["#EFEFBB", "#D4D3DD"]
}, {
  "name": "Kyoto",
  "colors": ["#c21500", "#ffc500"]
}, {
  "name": "Misty Meadow",
  "colors": ["#215f00", "#e4e4d9"]
}, {
  "name": "Aqualicious",
  "colors": ["#50C9C3", "#96DEDA"]
}, {
  "name": "Moor",
  "colors": ["#616161", "#9bc5c3"]
}, {
  "name": "Almost",
  "colors": ["#ddd6f3", "#faaca8"]
}, {
  "name": "Forever Lost",
  "colors": ["#5D4157", "#A8CABA"]
}, {
  "name": "Winter",
  "colors": ["#E6DADA", "#274046"]
}, {
  "name": "Autumn",
  "colors": ["#DAD299", "#B0DAB9"]
}, {
  "name": "Candy",
  "colors": ["#D3959B", "#BFE6BA"]
}, {
  "name": "Reef",
  "colors": ["#00d2ff", "#3a7bd5"]
}, {
  "name": "The Strain",
  "colors": ["#870000", "#190A05"]
}, {
  "name": "Dirty Fog",
  "colors": ["#B993D6", "#8CA6DB"]
}, {
  "name": "Earthly",
  "colors": ["#649173", "#DBD5A4"]
}, {
  "name": "Virgin",
  "colors": ["#C9FFBF", "#FFAFBD"]
}, {
  "name": "Ash",
  "colors": ["#606c88", "#3f4c6b"]
}, {
  "name": "Shadow Night",
  "colors": ["#000000", "#53346D"]
}, {
  "name": "Cherryblossoms",
  "colors": ["#FBD3E9", "#BB377D"]
}, {
  "name": "Parklife",
  "colors": ["#ADD100", "#7B920A"]
}, {
  "name": "Dance To Forget",
  "colors": ["#FF4E50", "#F9D423"]
}, {
  "name": "Starfall",
  "colors": ["#F0C27B", "#4B1248"]
}, {
  "name": "Red Mist",
  "colors": ["#000000", "#e74c3c"]
}, {
  "name": "Teal Love",
  "colors": ["#AAFFA9", "#11FFBD"]
}, {
  "name": "Neon Life",
  "colors": ["#B3FFAB", "#12FFF7"]
}, {
  "name": "Man of Steel",
  "colors": ["#780206", "#061161"]
}, {
  "name": "Amethyst",
  "colors": ["#9D50BB", "#6E48AA"]
}, {
  "name": "Cheer Up Emo Kid",
  "colors": ["#556270", "#FF6B6B"]
}, {
  "name": "Shore",
  "colors": ["#70e1f5", "#ffd194"]
}, {
  "name": "Facebook Messenger",
  "colors": ["#00c6ff", "#0072ff"]
}, {
  "name": "SoundCloud",
  "colors": ["#fe8c00", "#f83600"]
}, {
  "name": "Behongo",
  "colors": ["#52c234", "#061700"]
}, {
  "name": "ServQuick",
  "colors": ["#485563", "#29323c"]
}, {
  "name": "Friday",
  "colors": ["#83a4d4", "#b6fbff"]
}, {
  "name": "Martini",
  "colors": ["#FDFC47", "#24FE41"]
}, {
  "name": "Metallic Toad",
  "colors": ["#abbaab", "#ffffff"]
}, {
  "name": "Between The Clouds",
  "colors": ["#73C8A9", "#373B44"]
}, {
  "name": "Crazy Orange I",
  "colors": ["#D38312", "#A83279"]
}, {
  "name": "Hersheys",
  "colors": ["#1e130c", "#9a8478"]
}, {
  "name": "Talking To Mice Elf",
  "colors": ["#948E99", "#2E1437"]
}, {
  "name": "Purple Bliss",
  "colors": ["#360033", "#0b8793"]
}, {
  "name": "Predawn",
  "colors": ["#FFA17F", "#00223E"]
}, {
  "name": "Endless River",
  "colors": ["#43cea2", "#185a9d"]
}];

/**
 * Removes all key-value entries from the list cache.
 *
 * @private
 * @name clear
 * @memberOf ListCache
 */
function listCacheClear() {
  this.__data__ = [];
  this.size = 0;
}
var _listCacheClear = listCacheClear;

/**
 * Performs a
 * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
 * comparison between two values to determine if they are equivalent.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to compare.
 * @param {*} other The other value to compare.
 * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
 * @example
 *
 * var object = { 'a': 1 };
 * var other = { 'a': 1 };
 *
 * _.eq(object, object);
 * // => true
 *
 * _.eq(object, other);
 * // => false
 *
 * _.eq('a', 'a');
 * // => true
 *
 * _.eq('a', Object('a'));
 * // => false
 *
 * _.eq(NaN, NaN);
 * // => true
 */
function eq(value, other) {
  return value === other || value !== value && other !== other;
}
var eq_1 = eq;

/**
 * Gets the index at which the `key` is found in `array` of key-value pairs.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {*} key The key to search for.
 * @returns {number} Returns the index of the matched value, else `-1`.
 */
function assocIndexOf(array, key) {
  var length = array.length;
  while (length--) {
    if (eq_1(array[length][0], key)) {
      return length;
    }
  }
  return -1;
}
var _assocIndexOf = assocIndexOf;

/** Used for built-in method references. */
var arrayProto = Array.prototype;

/** Built-in value references. */
var splice = arrayProto.splice;

/**
 * Removes `key` and its value from the list cache.
 *
 * @private
 * @name delete
 * @memberOf ListCache
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function listCacheDelete(key) {
  var data = this.__data__,
    index = _assocIndexOf(data, key);
  if (index < 0) {
    return false;
  }
  var lastIndex = data.length - 1;
  if (index == lastIndex) {
    data.pop();
  } else {
    splice.call(data, index, 1);
  }
  --this.size;
  return true;
}
var _listCacheDelete = listCacheDelete;

/**
 * Gets the list cache value for `key`.
 *
 * @private
 * @name get
 * @memberOf ListCache
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function listCacheGet(key) {
  var data = this.__data__,
    index = _assocIndexOf(data, key);
  return index < 0 ? undefined : data[index][1];
}
var _listCacheGet = listCacheGet;

/**
 * Checks if a list cache value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf ListCache
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function listCacheHas(key) {
  return _assocIndexOf(this.__data__, key) > -1;
}
var _listCacheHas = listCacheHas;

/**
 * Sets the list cache `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf ListCache
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the list cache instance.
 */
function listCacheSet(key, value) {
  var data = this.__data__,
    index = _assocIndexOf(data, key);
  if (index < 0) {
    ++this.size;
    data.push([key, value]);
  } else {
    data[index][1] = value;
  }
  return this;
}
var _listCacheSet = listCacheSet;

/**
 * Creates an list cache object.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function ListCache(entries) {
  var index = -1,
    length = entries == null ? 0 : entries.length;
  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

// Add methods to `ListCache`.
ListCache.prototype.clear = _listCacheClear;
ListCache.prototype['delete'] = _listCacheDelete;
ListCache.prototype.get = _listCacheGet;
ListCache.prototype.has = _listCacheHas;
ListCache.prototype.set = _listCacheSet;
var _ListCache = ListCache;

/**
 * Removes all key-value entries from the stack.
 *
 * @private
 * @name clear
 * @memberOf Stack
 */
function stackClear() {
  this.__data__ = new _ListCache();
  this.size = 0;
}
var _stackClear = stackClear;

/**
 * Removes `key` and its value from the stack.
 *
 * @private
 * @name delete
 * @memberOf Stack
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function stackDelete(key) {
  var data = this.__data__,
    result = data['delete'](key);
  this.size = data.size;
  return result;
}
var _stackDelete = stackDelete;

/**
 * Gets the stack value for `key`.
 *
 * @private
 * @name get
 * @memberOf Stack
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function stackGet(key) {
  return this.__data__.get(key);
}
var _stackGet = stackGet;

/**
 * Checks if a stack value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf Stack
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function stackHas(key) {
  return this.__data__.has(key);
}
var _stackHas = stackHas;
var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};
function createCommonjsModule(fn, module) {
  return module = {
    exports: {}
  }, fn(module, module.exports), module.exports;
}

/** Detect free variable `global` from Node.js. */
var freeGlobal = typeof commonjsGlobal == 'object' && commonjsGlobal && commonjsGlobal.Object === Object && commonjsGlobal;
var _freeGlobal = freeGlobal;

/** Detect free variable `self`. */
var freeSelf = typeof self == 'object' && self && self.Object === Object && self;

/** Used as a reference to the global object. */
var root = _freeGlobal || freeSelf || Function('return this')();
var _root = root;

/** Built-in value references. */
var Symbol = _root.Symbol;
var _Symbol = Symbol;

/** Used for built-in method references. */
var objectProto = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty = objectProto.hasOwnProperty;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString = objectProto.toString;

/** Built-in value references. */
var symToStringTag = _Symbol ? _Symbol.toStringTag : undefined;

/**
 * A specialized version of `baseGetTag` which ignores `Symbol.toStringTag` values.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the raw `toStringTag`.
 */
function getRawTag(value) {
  var isOwn = hasOwnProperty.call(value, symToStringTag),
    tag = value[symToStringTag];
  try {
    value[symToStringTag] = undefined;
    var unmasked = true;
  } catch (e) {}
  var result = nativeObjectToString.call(value);
  if (unmasked) {
    if (isOwn) {
      value[symToStringTag] = tag;
    } else {
      delete value[symToStringTag];
    }
  }
  return result;
}
var _getRawTag = getRawTag;

/** Used for built-in method references. */
var objectProto$1 = Object.prototype;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString$1 = objectProto$1.toString;

/**
 * Converts `value` to a string using `Object.prototype.toString`.
 *
 * @private
 * @param {*} value The value to convert.
 * @returns {string} Returns the converted string.
 */
function objectToString(value) {
  return nativeObjectToString$1.call(value);
}
var _objectToString = objectToString;

/** `Object#toString` result references. */
var nullTag = '[object Null]',
  undefinedTag = '[object Undefined]';

/** Built-in value references. */
var symToStringTag$1 = _Symbol ? _Symbol.toStringTag : undefined;

/**
 * The base implementation of `getTag` without fallbacks for buggy environments.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the `toStringTag`.
 */
function baseGetTag(value) {
  if (value == null) {
    return value === undefined ? undefinedTag : nullTag;
  }
  return symToStringTag$1 && symToStringTag$1 in Object(value) ? _getRawTag(value) : _objectToString(value);
}
var _baseGetTag = baseGetTag;

/**
 * Checks if `value` is the
 * [language type](http://www.ecma-international.org/ecma-262/7.0/#sec-ecmascript-language-types)
 * of `Object`. (e.g. arrays, functions, objects, regexes, `new Number(0)`, and `new String('')`)
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an object, else `false`.
 * @example
 *
 * _.isObject({});
 * // => true
 *
 * _.isObject([1, 2, 3]);
 * // => true
 *
 * _.isObject(_.noop);
 * // => true
 *
 * _.isObject(null);
 * // => false
 */
function isObject(value) {
  var type = typeof value;
  return value != null && (type == 'object' || type == 'function');
}
var isObject_1 = isObject;

/** `Object#toString` result references. */
var asyncTag = '[object AsyncFunction]',
  funcTag = '[object Function]',
  genTag = '[object GeneratorFunction]',
  proxyTag = '[object Proxy]';

/**
 * Checks if `value` is classified as a `Function` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a function, else `false`.
 * @example
 *
 * _.isFunction(_);
 * // => true
 *
 * _.isFunction(/abc/);
 * // => false
 */
function isFunction(value) {
  if (!isObject_1(value)) {
    return false;
  }
  // The use of `Object#toString` avoids issues with the `typeof` operator
  // in Safari 9 which returns 'object' for typed arrays and other constructors.
  var tag = _baseGetTag(value);
  return tag == funcTag || tag == genTag || tag == asyncTag || tag == proxyTag;
}
var isFunction_1 = isFunction;

/** Used to detect overreaching core-js shims. */
var coreJsData = _root['__core-js_shared__'];
var _coreJsData = coreJsData;

/** Used to detect methods masquerading as native. */
var maskSrcKey = function () {
  var uid = /[^.]+$/.exec(_coreJsData && _coreJsData.keys && _coreJsData.keys.IE_PROTO || '');
  return uid ? 'Symbol(src)_1.' + uid : '';
}();

/**
 * Checks if `func` has its source masked.
 *
 * @private
 * @param {Function} func The function to check.
 * @returns {boolean} Returns `true` if `func` is masked, else `false`.
 */
function isMasked(func) {
  return !!maskSrcKey && maskSrcKey in func;
}
var _isMasked = isMasked;

/** Used for built-in method references. */
var funcProto = Function.prototype;

/** Used to resolve the decompiled source of functions. */
var funcToString = funcProto.toString;

/**
 * Converts `func` to its source code.
 *
 * @private
 * @param {Function} func The function to convert.
 * @returns {string} Returns the source code.
 */
function toSource(func) {
  if (func != null) {
    try {
      return funcToString.call(func);
    } catch (e) {}
    try {
      return func + '';
    } catch (e) {}
  }
  return '';
}
var _toSource = toSource;

/**
 * Used to match `RegExp`
 * [syntax characters](http://ecma-international.org/ecma-262/7.0/#sec-patterns).
 */
var reRegExpChar = /[\\^$.*+?()[\]{}|]/g;

/** Used to detect host constructors (Safari). */
var reIsHostCtor = /^\[object .+?Constructor\]$/;

/** Used for built-in method references. */
var funcProto$1 = Function.prototype,
  objectProto$2 = Object.prototype;

/** Used to resolve the decompiled source of functions. */
var funcToString$1 = funcProto$1.toString;

/** Used to check objects for own properties. */
var hasOwnProperty$1 = objectProto$2.hasOwnProperty;

/** Used to detect if a method is native. */
var reIsNative = RegExp('^' + funcToString$1.call(hasOwnProperty$1).replace(reRegExpChar, '\\$&').replace(/hasOwnProperty|(function).*?(?=\\\()| for .+?(?=\\\])/g, '$1.*?') + '$');

/**
 * The base implementation of `_.isNative` without bad shim checks.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a native function,
 *  else `false`.
 */
function baseIsNative(value) {
  if (!isObject_1(value) || _isMasked(value)) {
    return false;
  }
  var pattern = isFunction_1(value) ? reIsNative : reIsHostCtor;
  return pattern.test(_toSource(value));
}
var _baseIsNative = baseIsNative;

/**
 * Gets the value at `key` of `object`.
 *
 * @private
 * @param {Object} [object] The object to query.
 * @param {string} key The key of the property to get.
 * @returns {*} Returns the property value.
 */
function getValue$1(object, key) {
  return object == null ? undefined : object[key];
}
var _getValue = getValue$1;

/**
 * Gets the native function at `key` of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {string} key The key of the method to get.
 * @returns {*} Returns the function if it's native, else `undefined`.
 */
function getNative(object, key) {
  var value = _getValue(object, key);
  return _baseIsNative(value) ? value : undefined;
}
var _getNative = getNative;

/* Built-in method references that are verified to be native. */
var Map = _getNative(_root, 'Map');
var _Map = Map;

/* Built-in method references that are verified to be native. */
var nativeCreate = _getNative(Object, 'create');
var _nativeCreate = nativeCreate;

/**
 * Removes all key-value entries from the hash.
 *
 * @private
 * @name clear
 * @memberOf Hash
 */
function hashClear() {
  this.__data__ = _nativeCreate ? _nativeCreate(null) : {};
  this.size = 0;
}
var _hashClear = hashClear;

/**
 * Removes `key` and its value from the hash.
 *
 * @private
 * @name delete
 * @memberOf Hash
 * @param {Object} hash The hash to modify.
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function hashDelete(key) {
  var result = this.has(key) && delete this.__data__[key];
  this.size -= result ? 1 : 0;
  return result;
}
var _hashDelete = hashDelete;

/** Used to stand-in for `undefined` hash values. */
var HASH_UNDEFINED = '__lodash_hash_undefined__';

/** Used for built-in method references. */
var objectProto$3 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$2 = objectProto$3.hasOwnProperty;

/**
 * Gets the hash value for `key`.
 *
 * @private
 * @name get
 * @memberOf Hash
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function hashGet(key) {
  var data = this.__data__;
  if (_nativeCreate) {
    var result = data[key];
    return result === HASH_UNDEFINED ? undefined : result;
  }
  return hasOwnProperty$2.call(data, key) ? data[key] : undefined;
}
var _hashGet = hashGet;

/** Used for built-in method references. */
var objectProto$4 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$3 = objectProto$4.hasOwnProperty;

/**
 * Checks if a hash value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf Hash
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function hashHas(key) {
  var data = this.__data__;
  return _nativeCreate ? data[key] !== undefined : hasOwnProperty$3.call(data, key);
}
var _hashHas = hashHas;

/** Used to stand-in for `undefined` hash values. */
var HASH_UNDEFINED$1 = '__lodash_hash_undefined__';

/**
 * Sets the hash `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf Hash
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the hash instance.
 */
function hashSet(key, value) {
  var data = this.__data__;
  this.size += this.has(key) ? 0 : 1;
  data[key] = _nativeCreate && value === undefined ? HASH_UNDEFINED$1 : value;
  return this;
}
var _hashSet = hashSet;

/**
 * Creates a hash object.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function Hash(entries) {
  var index = -1,
    length = entries == null ? 0 : entries.length;
  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

// Add methods to `Hash`.
Hash.prototype.clear = _hashClear;
Hash.prototype['delete'] = _hashDelete;
Hash.prototype.get = _hashGet;
Hash.prototype.has = _hashHas;
Hash.prototype.set = _hashSet;
var _Hash = Hash;

/**
 * Removes all key-value entries from the map.
 *
 * @private
 * @name clear
 * @memberOf MapCache
 */
function mapCacheClear() {
  this.size = 0;
  this.__data__ = {
    'hash': new _Hash(),
    'map': new (_Map || _ListCache)(),
    'string': new _Hash()
  };
}
var _mapCacheClear = mapCacheClear;

/**
 * Checks if `value` is suitable for use as unique object key.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is suitable, else `false`.
 */
function isKeyable(value) {
  var type = typeof value;
  return type == 'string' || type == 'number' || type == 'symbol' || type == 'boolean' ? value !== '__proto__' : value === null;
}
var _isKeyable = isKeyable;

/**
 * Gets the data for `map`.
 *
 * @private
 * @param {Object} map The map to query.
 * @param {string} key The reference key.
 * @returns {*} Returns the map data.
 */
function getMapData(map, key) {
  var data = map.__data__;
  return _isKeyable(key) ? data[typeof key == 'string' ? 'string' : 'hash'] : data.map;
}
var _getMapData = getMapData;

/**
 * Removes `key` and its value from the map.
 *
 * @private
 * @name delete
 * @memberOf MapCache
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function mapCacheDelete(key) {
  var result = _getMapData(this, key)['delete'](key);
  this.size -= result ? 1 : 0;
  return result;
}
var _mapCacheDelete = mapCacheDelete;

/**
 * Gets the map value for `key`.
 *
 * @private
 * @name get
 * @memberOf MapCache
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function mapCacheGet(key) {
  return _getMapData(this, key).get(key);
}
var _mapCacheGet = mapCacheGet;

/**
 * Checks if a map value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf MapCache
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function mapCacheHas(key) {
  return _getMapData(this, key).has(key);
}
var _mapCacheHas = mapCacheHas;

/**
 * Sets the map `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf MapCache
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the map cache instance.
 */
function mapCacheSet(key, value) {
  var data = _getMapData(this, key),
    size = data.size;
  data.set(key, value);
  this.size += data.size == size ? 0 : 1;
  return this;
}
var _mapCacheSet = mapCacheSet;

/**
 * Creates a map cache object to store key-value pairs.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function MapCache(entries) {
  var index = -1,
    length = entries == null ? 0 : entries.length;
  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

// Add methods to `MapCache`.
MapCache.prototype.clear = _mapCacheClear;
MapCache.prototype['delete'] = _mapCacheDelete;
MapCache.prototype.get = _mapCacheGet;
MapCache.prototype.has = _mapCacheHas;
MapCache.prototype.set = _mapCacheSet;
var _MapCache = MapCache;

/** Used as the size to enable large array optimizations. */
var LARGE_ARRAY_SIZE = 200;

/**
 * Sets the stack `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf Stack
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the stack cache instance.
 */
function stackSet(key, value) {
  var data = this.__data__;
  if (data instanceof _ListCache) {
    var pairs = data.__data__;
    if (!_Map || pairs.length < LARGE_ARRAY_SIZE - 1) {
      pairs.push([key, value]);
      this.size = ++data.size;
      return this;
    }
    data = this.__data__ = new _MapCache(pairs);
  }
  data.set(key, value);
  this.size = data.size;
  return this;
}
var _stackSet = stackSet;

/**
 * Creates a stack cache object to store key-value pairs.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function Stack(entries) {
  var data = this.__data__ = new _ListCache(entries);
  this.size = data.size;
}

// Add methods to `Stack`.
Stack.prototype.clear = _stackClear;
Stack.prototype['delete'] = _stackDelete;
Stack.prototype.get = _stackGet;
Stack.prototype.has = _stackHas;
Stack.prototype.set = _stackSet;
var _Stack = Stack;

/** Used to stand-in for `undefined` hash values. */
var HASH_UNDEFINED$2 = '__lodash_hash_undefined__';

/**
 * Adds `value` to the array cache.
 *
 * @private
 * @name add
 * @memberOf SetCache
 * @alias push
 * @param {*} value The value to cache.
 * @returns {Object} Returns the cache instance.
 */
function setCacheAdd(value) {
  this.__data__.set(value, HASH_UNDEFINED$2);
  return this;
}
var _setCacheAdd = setCacheAdd;

/**
 * Checks if `value` is in the array cache.
 *
 * @private
 * @name has
 * @memberOf SetCache
 * @param {*} value The value to search for.
 * @returns {number} Returns `true` if `value` is found, else `false`.
 */
function setCacheHas(value) {
  return this.__data__.has(value);
}
var _setCacheHas = setCacheHas;

/**
 *
 * Creates an array cache object to store unique values.
 *
 * @private
 * @constructor
 * @param {Array} [values] The values to cache.
 */
function SetCache(values) {
  var index = -1,
    length = values == null ? 0 : values.length;
  this.__data__ = new _MapCache();
  while (++index < length) {
    this.add(values[index]);
  }
}

// Add methods to `SetCache`.
SetCache.prototype.add = SetCache.prototype.push = _setCacheAdd;
SetCache.prototype.has = _setCacheHas;
var _SetCache = SetCache;

/**
 * A specialized version of `_.some` for arrays without support for iteratee
 * shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} predicate The function invoked per iteration.
 * @returns {boolean} Returns `true` if any element passes the predicate check,
 *  else `false`.
 */
function arraySome(array, predicate) {
  var index = -1,
    length = array == null ? 0 : array.length;
  while (++index < length) {
    if (predicate(array[index], index, array)) {
      return true;
    }
  }
  return false;
}
var _arraySome = arraySome;

/**
 * Checks if a `cache` value for `key` exists.
 *
 * @private
 * @param {Object} cache The cache to query.
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function cacheHas(cache, key) {
  return cache.has(key);
}
var _cacheHas = cacheHas;

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG = 1,
  COMPARE_UNORDERED_FLAG = 2;

/**
 * A specialized version of `baseIsEqualDeep` for arrays with support for
 * partial deep comparisons.
 *
 * @private
 * @param {Array} array The array to compare.
 * @param {Array} other The other array to compare.
 * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
 * @param {Function} customizer The function to customize comparisons.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Object} stack Tracks traversed `array` and `other` objects.
 * @returns {boolean} Returns `true` if the arrays are equivalent, else `false`.
 */
function equalArrays(array, other, bitmask, customizer, equalFunc, stack) {
  var isPartial = bitmask & COMPARE_PARTIAL_FLAG,
    arrLength = array.length,
    othLength = other.length;
  if (arrLength != othLength && !(isPartial && othLength > arrLength)) {
    return false;
  }
  // Check that cyclic values are equal.
  var arrStacked = stack.get(array);
  var othStacked = stack.get(other);
  if (arrStacked && othStacked) {
    return arrStacked == other && othStacked == array;
  }
  var index = -1,
    result = true,
    seen = bitmask & COMPARE_UNORDERED_FLAG ? new _SetCache() : undefined;
  stack.set(array, other);
  stack.set(other, array);

  // Ignore non-index properties.
  while (++index < arrLength) {
    var arrValue = array[index],
      othValue = other[index];
    if (customizer) {
      var compared = isPartial ? customizer(othValue, arrValue, index, other, array, stack) : customizer(arrValue, othValue, index, array, other, stack);
    }
    if (compared !== undefined) {
      if (compared) {
        continue;
      }
      result = false;
      break;
    }
    // Recursively compare arrays (susceptible to call stack limits).
    if (seen) {
      if (!_arraySome(other, function (othValue, othIndex) {
        if (!_cacheHas(seen, othIndex) && (arrValue === othValue || equalFunc(arrValue, othValue, bitmask, customizer, stack))) {
          return seen.push(othIndex);
        }
      })) {
        result = false;
        break;
      }
    } else if (!(arrValue === othValue || equalFunc(arrValue, othValue, bitmask, customizer, stack))) {
      result = false;
      break;
    }
  }
  stack['delete'](array);
  stack['delete'](other);
  return result;
}
var _equalArrays = equalArrays;

/** Built-in value references. */
var Uint8Array = _root.Uint8Array;
var _Uint8Array = Uint8Array;

/**
 * Converts `map` to its key-value pairs.
 *
 * @private
 * @param {Object} map The map to convert.
 * @returns {Array} Returns the key-value pairs.
 */
function mapToArray(map) {
  var index = -1,
    result = Array(map.size);
  map.forEach(function (value, key) {
    result[++index] = [key, value];
  });
  return result;
}
var _mapToArray = mapToArray;

/**
 * Converts `set` to an array of its values.
 *
 * @private
 * @param {Object} set The set to convert.
 * @returns {Array} Returns the values.
 */
function setToArray(set) {
  var index = -1,
    result = Array(set.size);
  set.forEach(function (value) {
    result[++index] = value;
  });
  return result;
}
var _setToArray = setToArray;

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG$1 = 1,
  COMPARE_UNORDERED_FLAG$1 = 2;

/** `Object#toString` result references. */
var boolTag = '[object Boolean]',
  dateTag = '[object Date]',
  errorTag = '[object Error]',
  mapTag = '[object Map]',
  numberTag = '[object Number]',
  regexpTag = '[object RegExp]',
  setTag = '[object Set]',
  stringTag = '[object String]',
  symbolTag = '[object Symbol]';
var arrayBufferTag = '[object ArrayBuffer]',
  dataViewTag = '[object DataView]';

/** Used to convert symbols to primitives and strings. */
var symbolProto = _Symbol ? _Symbol.prototype : undefined,
  symbolValueOf = symbolProto ? symbolProto.valueOf : undefined;

/**
 * A specialized version of `baseIsEqualDeep` for comparing objects of
 * the same `toStringTag`.
 *
 * **Note:** This function only supports comparing values with tags of
 * `Boolean`, `Date`, `Error`, `Number`, `RegExp`, or `String`.
 *
 * @private
 * @param {Object} object The object to compare.
 * @param {Object} other The other object to compare.
 * @param {string} tag The `toStringTag` of the objects to compare.
 * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
 * @param {Function} customizer The function to customize comparisons.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Object} stack Tracks traversed `object` and `other` objects.
 * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
 */
function equalByTag(object, other, tag, bitmask, customizer, equalFunc, stack) {
  switch (tag) {
    case dataViewTag:
      if (object.byteLength != other.byteLength || object.byteOffset != other.byteOffset) {
        return false;
      }
      object = object.buffer;
      other = other.buffer;
    case arrayBufferTag:
      if (object.byteLength != other.byteLength || !equalFunc(new _Uint8Array(object), new _Uint8Array(other))) {
        return false;
      }
      return true;
    case boolTag:
    case dateTag:
    case numberTag:
      // Coerce booleans to `1` or `0` and dates to milliseconds.
      // Invalid dates are coerced to `NaN`.
      return eq_1(+object, +other);
    case errorTag:
      return object.name == other.name && object.message == other.message;
    case regexpTag:
    case stringTag:
      // Coerce regexes to strings and treat strings, primitives and objects,
      // as equal. See http://www.ecma-international.org/ecma-262/7.0/#sec-regexp.prototype.tostring
      // for more details.
      return object == other + '';
    case mapTag:
      var convert = _mapToArray;
    case setTag:
      var isPartial = bitmask & COMPARE_PARTIAL_FLAG$1;
      convert || (convert = _setToArray);
      if (object.size != other.size && !isPartial) {
        return false;
      }
      // Assume cyclic values are equal.
      var stacked = stack.get(object);
      if (stacked) {
        return stacked == other;
      }
      bitmask |= COMPARE_UNORDERED_FLAG$1;

      // Recursively compare objects (susceptible to call stack limits).
      stack.set(object, other);
      var result = _equalArrays(convert(object), convert(other), bitmask, customizer, equalFunc, stack);
      stack['delete'](object);
      return result;
    case symbolTag:
      if (symbolValueOf) {
        return symbolValueOf.call(object) == symbolValueOf.call(other);
      }
  }
  return false;
}
var _equalByTag = equalByTag;

/**
 * Appends the elements of `values` to `array`.
 *
 * @private
 * @param {Array} array The array to modify.
 * @param {Array} values The values to append.
 * @returns {Array} Returns `array`.
 */
function arrayPush(array, values) {
  var index = -1,
    length = values.length,
    offset = array.length;
  while (++index < length) {
    array[offset + index] = values[index];
  }
  return array;
}
var _arrayPush = arrayPush;

/**
 * Checks if `value` is classified as an `Array` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an array, else `false`.
 * @example
 *
 * _.isArray([1, 2, 3]);
 * // => true
 *
 * _.isArray(document.body.children);
 * // => false
 *
 * _.isArray('abc');
 * // => false
 *
 * _.isArray(_.noop);
 * // => false
 */
var isArray = Array.isArray;
var isArray_1 = isArray;

/**
 * The base implementation of `getAllKeys` and `getAllKeysIn` which uses
 * `keysFunc` and `symbolsFunc` to get the enumerable property names and
 * symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {Function} keysFunc The function to get the keys of `object`.
 * @param {Function} symbolsFunc The function to get the symbols of `object`.
 * @returns {Array} Returns the array of property names and symbols.
 */
function baseGetAllKeys(object, keysFunc, symbolsFunc) {
  var result = keysFunc(object);
  return isArray_1(object) ? result : _arrayPush(result, symbolsFunc(object));
}
var _baseGetAllKeys = baseGetAllKeys;

/**
 * A specialized version of `_.filter` for arrays without support for
 * iteratee shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} predicate The function invoked per iteration.
 * @returns {Array} Returns the new filtered array.
 */
function arrayFilter(array, predicate) {
  var index = -1,
    length = array == null ? 0 : array.length,
    resIndex = 0,
    result = [];
  while (++index < length) {
    var value = array[index];
    if (predicate(value, index, array)) {
      result[resIndex++] = value;
    }
  }
  return result;
}
var _arrayFilter = arrayFilter;

/**
 * This method returns a new empty array.
 *
 * @static
 * @memberOf _
 * @since 4.13.0
 * @category Util
 * @returns {Array} Returns the new empty array.
 * @example
 *
 * var arrays = _.times(2, _.stubArray);
 *
 * console.log(arrays);
 * // => [[], []]
 *
 * console.log(arrays[0] === arrays[1]);
 * // => false
 */
function stubArray() {
  return [];
}
var stubArray_1 = stubArray;

/** Used for built-in method references. */
var objectProto$5 = Object.prototype;

/** Built-in value references. */
var propertyIsEnumerable = objectProto$5.propertyIsEnumerable;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeGetSymbols = Object.getOwnPropertySymbols;

/**
 * Creates an array of the own enumerable symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of symbols.
 */
var getSymbols = !nativeGetSymbols ? stubArray_1 : function (object) {
  if (object == null) {
    return [];
  }
  object = Object(object);
  return _arrayFilter(nativeGetSymbols(object), function (symbol) {
    return propertyIsEnumerable.call(object, symbol);
  });
};
var _getSymbols = getSymbols;

/**
 * The base implementation of `_.times` without support for iteratee shorthands
 * or max array length checks.
 *
 * @private
 * @param {number} n The number of times to invoke `iteratee`.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns the array of results.
 */
function baseTimes(n, iteratee) {
  var index = -1,
    result = Array(n);
  while (++index < n) {
    result[index] = iteratee(index);
  }
  return result;
}
var _baseTimes = baseTimes;

/**
 * Checks if `value` is object-like. A value is object-like if it's not `null`
 * and has a `typeof` result of "object".
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is object-like, else `false`.
 * @example
 *
 * _.isObjectLike({});
 * // => true
 *
 * _.isObjectLike([1, 2, 3]);
 * // => true
 *
 * _.isObjectLike(_.noop);
 * // => false
 *
 * _.isObjectLike(null);
 * // => false
 */
function isObjectLike(value) {
  return value != null && typeof value == 'object';
}
var isObjectLike_1 = isObjectLike;

/** `Object#toString` result references. */
var argsTag = '[object Arguments]';

/**
 * The base implementation of `_.isArguments`.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an `arguments` object,
 */
function baseIsArguments(value) {
  return isObjectLike_1(value) && _baseGetTag(value) == argsTag;
}
var _baseIsArguments = baseIsArguments;

/** Used for built-in method references. */
var objectProto$6 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$4 = objectProto$6.hasOwnProperty;

/** Built-in value references. */
var propertyIsEnumerable$1 = objectProto$6.propertyIsEnumerable;

/**
 * Checks if `value` is likely an `arguments` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an `arguments` object,
 *  else `false`.
 * @example
 *
 * _.isArguments(function() { return arguments; }());
 * // => true
 *
 * _.isArguments([1, 2, 3]);
 * // => false
 */
var isArguments = _baseIsArguments(function () {
  return arguments;
}()) ? _baseIsArguments : function (value) {
  return isObjectLike_1(value) && hasOwnProperty$4.call(value, 'callee') && !propertyIsEnumerable$1.call(value, 'callee');
};
var isArguments_1 = isArguments;

/**
 * This method returns `false`.
 *
 * @static
 * @memberOf _
 * @since 4.13.0
 * @category Util
 * @returns {boolean} Returns `false`.
 * @example
 *
 * _.times(2, _.stubFalse);
 * // => [false, false]
 */
function stubFalse() {
  return false;
}
var stubFalse_1 = stubFalse;
var isBuffer_1 = createCommonjsModule(function (module, exports) {
  /** Detect free variable `exports`. */
  var freeExports = exports && !exports.nodeType && exports;

  /** Detect free variable `module`. */
  var freeModule = freeExports && 'object' == 'object' && module && !module.nodeType && module;

  /** Detect the popular CommonJS extension `module.exports`. */
  var moduleExports = freeModule && freeModule.exports === freeExports;

  /** Built-in value references. */
  var Buffer = moduleExports ? _root.Buffer : undefined;

  /* Built-in method references for those with the same name as other `lodash` methods. */
  var nativeIsBuffer = Buffer ? Buffer.isBuffer : undefined;

  /**
   * Checks if `value` is a buffer.
   *
   * @static
   * @memberOf _
   * @since 4.3.0
   * @category Lang
   * @param {*} value The value to check.
   * @returns {boolean} Returns `true` if `value` is a buffer, else `false`.
   * @example
   *
   * _.isBuffer(new Buffer(2));
   * // => true
   *
   * _.isBuffer(new Uint8Array(2));
   * // => false
   */
  var isBuffer = nativeIsBuffer || stubFalse_1;
  module.exports = isBuffer;
});

/** Used as references for various `Number` constants. */
var MAX_SAFE_INTEGER = 9007199254740991;

/** Used to detect unsigned integer values. */
var reIsUint = /^(?:0|[1-9]\d*)$/;

/**
 * Checks if `value` is a valid array-like index.
 *
 * @private
 * @param {*} value The value to check.
 * @param {number} [length=MAX_SAFE_INTEGER] The upper bounds of a valid index.
 * @returns {boolean} Returns `true` if `value` is a valid index, else `false`.
 */
function isIndex(value, length) {
  var type = typeof value;
  length = length == null ? MAX_SAFE_INTEGER : length;
  return !!length && (type == 'number' || type != 'symbol' && reIsUint.test(value)) && value > -1 && value % 1 == 0 && value < length;
}
var _isIndex = isIndex;

/** Used as references for various `Number` constants. */
var MAX_SAFE_INTEGER$1 = 9007199254740991;

/**
 * Checks if `value` is a valid array-like length.
 *
 * **Note:** This method is loosely based on
 * [`ToLength`](http://ecma-international.org/ecma-262/7.0/#sec-tolength).
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a valid length, else `false`.
 * @example
 *
 * _.isLength(3);
 * // => true
 *
 * _.isLength(Number.MIN_VALUE);
 * // => false
 *
 * _.isLength(Infinity);
 * // => false
 *
 * _.isLength('3');
 * // => false
 */
function isLength(value) {
  return typeof value == 'number' && value > -1 && value % 1 == 0 && value <= MAX_SAFE_INTEGER$1;
}
var isLength_1 = isLength;

/** `Object#toString` result references. */
var argsTag$1 = '[object Arguments]',
  arrayTag = '[object Array]',
  boolTag$1 = '[object Boolean]',
  dateTag$1 = '[object Date]',
  errorTag$1 = '[object Error]',
  funcTag$1 = '[object Function]',
  mapTag$1 = '[object Map]',
  numberTag$1 = '[object Number]',
  objectTag = '[object Object]',
  regexpTag$1 = '[object RegExp]',
  setTag$1 = '[object Set]',
  stringTag$1 = '[object String]',
  weakMapTag = '[object WeakMap]';
var arrayBufferTag$1 = '[object ArrayBuffer]',
  dataViewTag$1 = '[object DataView]',
  float32Tag = '[object Float32Array]',
  float64Tag = '[object Float64Array]',
  int8Tag = '[object Int8Array]',
  int16Tag = '[object Int16Array]',
  int32Tag = '[object Int32Array]',
  uint8Tag = '[object Uint8Array]',
  uint8ClampedTag = '[object Uint8ClampedArray]',
  uint16Tag = '[object Uint16Array]',
  uint32Tag = '[object Uint32Array]';

/** Used to identify `toStringTag` values of typed arrays. */
var typedArrayTags = {};
typedArrayTags[float32Tag] = typedArrayTags[float64Tag] = typedArrayTags[int8Tag] = typedArrayTags[int16Tag] = typedArrayTags[int32Tag] = typedArrayTags[uint8Tag] = typedArrayTags[uint8ClampedTag] = typedArrayTags[uint16Tag] = typedArrayTags[uint32Tag] = true;
typedArrayTags[argsTag$1] = typedArrayTags[arrayTag] = typedArrayTags[arrayBufferTag$1] = typedArrayTags[boolTag$1] = typedArrayTags[dataViewTag$1] = typedArrayTags[dateTag$1] = typedArrayTags[errorTag$1] = typedArrayTags[funcTag$1] = typedArrayTags[mapTag$1] = typedArrayTags[numberTag$1] = typedArrayTags[objectTag] = typedArrayTags[regexpTag$1] = typedArrayTags[setTag$1] = typedArrayTags[stringTag$1] = typedArrayTags[weakMapTag] = false;

/**
 * The base implementation of `_.isTypedArray` without Node.js optimizations.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
 */
function baseIsTypedArray(value) {
  return isObjectLike_1(value) && isLength_1(value.length) && !!typedArrayTags[_baseGetTag(value)];
}
var _baseIsTypedArray = baseIsTypedArray;

/**
 * The base implementation of `_.unary` without support for storing metadata.
 *
 * @private
 * @param {Function} func The function to cap arguments for.
 * @returns {Function} Returns the new capped function.
 */
function baseUnary(func) {
  return function (value) {
    return func(value);
  };
}
var _baseUnary = baseUnary;
var _nodeUtil = createCommonjsModule(function (module, exports) {
  /** Detect free variable `exports`. */
  var freeExports = exports && !exports.nodeType && exports;

  /** Detect free variable `module`. */
  var freeModule = freeExports && 'object' == 'object' && module && !module.nodeType && module;

  /** Detect the popular CommonJS extension `module.exports`. */
  var moduleExports = freeModule && freeModule.exports === freeExports;

  /** Detect free variable `process` from Node.js. */
  var freeProcess = moduleExports && _freeGlobal.process;

  /** Used to access faster Node.js helpers. */
  var nodeUtil = function () {
    try {
      // Use `util.types` for Node.js 10+.
      var types = freeModule && freeModule.require && freeModule.require('util').types;
      if (types) {
        return types;
      }

      // Legacy `process.binding('util')` for Node.js < 10.
      return freeProcess && freeProcess.binding && freeProcess.binding('util');
    } catch (e) {}
  }();
  module.exports = nodeUtil;
});

/* Node.js helper references. */
var nodeIsTypedArray = _nodeUtil && _nodeUtil.isTypedArray;

/**
 * Checks if `value` is classified as a typed array.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
 * @example
 *
 * _.isTypedArray(new Uint8Array);
 * // => true
 *
 * _.isTypedArray([]);
 * // => false
 */
var isTypedArray = nodeIsTypedArray ? _baseUnary(nodeIsTypedArray) : _baseIsTypedArray;
var isTypedArray_1 = isTypedArray;

/** Used for built-in method references. */
var objectProto$7 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$5 = objectProto$7.hasOwnProperty;

/**
 * Creates an array of the enumerable property names of the array-like `value`.
 *
 * @private
 * @param {*} value The value to query.
 * @param {boolean} inherited Specify returning inherited property names.
 * @returns {Array} Returns the array of property names.
 */
function arrayLikeKeys(value, inherited) {
  var isArr = isArray_1(value),
    isArg = !isArr && isArguments_1(value),
    isBuff = !isArr && !isArg && isBuffer_1(value),
    isType = !isArr && !isArg && !isBuff && isTypedArray_1(value),
    skipIndexes = isArr || isArg || isBuff || isType,
    result = skipIndexes ? _baseTimes(value.length, String) : [],
    length = result.length;
  for (var key in value) {
    if ((inherited || hasOwnProperty$5.call(value, key)) && !(skipIndexes && (
    // Safari 9 has enumerable `arguments.length` in strict mode.
    key == 'length' ||
    // Node.js 0.10 has enumerable non-index properties on buffers.
    isBuff && (key == 'offset' || key == 'parent') ||
    // PhantomJS 2 has enumerable non-index properties on typed arrays.
    isType && (key == 'buffer' || key == 'byteLength' || key == 'byteOffset') ||
    // Skip index properties.
    _isIndex(key, length)))) {
      result.push(key);
    }
  }
  return result;
}
var _arrayLikeKeys = arrayLikeKeys;

/** Used for built-in method references. */
var objectProto$8 = Object.prototype;

/**
 * Checks if `value` is likely a prototype object.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a prototype, else `false`.
 */
function isPrototype(value) {
  var Ctor = value && value.constructor,
    proto = typeof Ctor == 'function' && Ctor.prototype || objectProto$8;
  return value === proto;
}
var _isPrototype = isPrototype;

/**
 * Creates a unary function that invokes `func` with its argument transformed.
 *
 * @private
 * @param {Function} func The function to wrap.
 * @param {Function} transform The argument transform.
 * @returns {Function} Returns the new function.
 */
function overArg(func, transform) {
  return function (arg) {
    return func(transform(arg));
  };
}
var _overArg = overArg;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeKeys = _overArg(Object.keys, Object);
var _nativeKeys = nativeKeys;

/** Used for built-in method references. */
var objectProto$9 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$6 = objectProto$9.hasOwnProperty;

/**
 * The base implementation of `_.keys` which doesn't treat sparse arrays as dense.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 */
function baseKeys(object) {
  if (!_isPrototype(object)) {
    return _nativeKeys(object);
  }
  var result = [];
  for (var key in Object(object)) {
    if (hasOwnProperty$6.call(object, key) && key != 'constructor') {
      result.push(key);
    }
  }
  return result;
}
var _baseKeys = baseKeys;

/**
 * Checks if `value` is array-like. A value is considered array-like if it's
 * not a function and has a `value.length` that's an integer greater than or
 * equal to `0` and less than or equal to `Number.MAX_SAFE_INTEGER`.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is array-like, else `false`.
 * @example
 *
 * _.isArrayLike([1, 2, 3]);
 * // => true
 *
 * _.isArrayLike(document.body.children);
 * // => true
 *
 * _.isArrayLike('abc');
 * // => true
 *
 * _.isArrayLike(_.noop);
 * // => false
 */
function isArrayLike(value) {
  return value != null && isLength_1(value.length) && !isFunction_1(value);
}
var isArrayLike_1 = isArrayLike;

/**
 * Creates an array of the own enumerable property names of `object`.
 *
 * **Note:** Non-object values are coerced to objects. See the
 * [ES spec](http://ecma-international.org/ecma-262/7.0/#sec-object.keys)
 * for more details.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category Object
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 *   this.b = 2;
 * }
 *
 * Foo.prototype.c = 3;
 *
 * _.keys(new Foo);
 * // => ['a', 'b'] (iteration order is not guaranteed)
 *
 * _.keys('hi');
 * // => ['0', '1']
 */
function keys(object) {
  return isArrayLike_1(object) ? _arrayLikeKeys(object) : _baseKeys(object);
}
var keys_1 = keys;

/**
 * Creates an array of own enumerable property names and symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names and symbols.
 */
function getAllKeys(object) {
  return _baseGetAllKeys(object, keys_1, _getSymbols);
}
var _getAllKeys = getAllKeys;

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG$2 = 1;

/** Used for built-in method references. */
var objectProto$a = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$7 = objectProto$a.hasOwnProperty;

/**
 * A specialized version of `baseIsEqualDeep` for objects with support for
 * partial deep comparisons.
 *
 * @private
 * @param {Object} object The object to compare.
 * @param {Object} other The other object to compare.
 * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
 * @param {Function} customizer The function to customize comparisons.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Object} stack Tracks traversed `object` and `other` objects.
 * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
 */
function equalObjects(object, other, bitmask, customizer, equalFunc, stack) {
  var isPartial = bitmask & COMPARE_PARTIAL_FLAG$2,
    objProps = _getAllKeys(object),
    objLength = objProps.length,
    othProps = _getAllKeys(other),
    othLength = othProps.length;
  if (objLength != othLength && !isPartial) {
    return false;
  }
  var index = objLength;
  while (index--) {
    var key = objProps[index];
    if (!(isPartial ? key in other : hasOwnProperty$7.call(other, key))) {
      return false;
    }
  }
  // Check that cyclic values are equal.
  var objStacked = stack.get(object);
  var othStacked = stack.get(other);
  if (objStacked && othStacked) {
    return objStacked == other && othStacked == object;
  }
  var result = true;
  stack.set(object, other);
  stack.set(other, object);
  var skipCtor = isPartial;
  while (++index < objLength) {
    key = objProps[index];
    var objValue = object[key],
      othValue = other[key];
    if (customizer) {
      var compared = isPartial ? customizer(othValue, objValue, key, other, object, stack) : customizer(objValue, othValue, key, object, other, stack);
    }
    // Recursively compare objects (susceptible to call stack limits).
    if (!(compared === undefined ? objValue === othValue || equalFunc(objValue, othValue, bitmask, customizer, stack) : compared)) {
      result = false;
      break;
    }
    skipCtor || (skipCtor = key == 'constructor');
  }
  if (result && !skipCtor) {
    var objCtor = object.constructor,
      othCtor = other.constructor;

    // Non `Object` object instances with different constructors are not equal.
    if (objCtor != othCtor && 'constructor' in object && 'constructor' in other && !(typeof objCtor == 'function' && objCtor instanceof objCtor && typeof othCtor == 'function' && othCtor instanceof othCtor)) {
      result = false;
    }
  }
  stack['delete'](object);
  stack['delete'](other);
  return result;
}
var _equalObjects = equalObjects;

/* Built-in method references that are verified to be native. */
var DataView = _getNative(_root, 'DataView');
var _DataView = DataView;

/* Built-in method references that are verified to be native. */
var Promise$1 = _getNative(_root, 'Promise');
var _Promise = Promise$1;

/* Built-in method references that are verified to be native. */
var Set = _getNative(_root, 'Set');
var _Set = Set;

/* Built-in method references that are verified to be native. */
var WeakMap = _getNative(_root, 'WeakMap');
var _WeakMap = WeakMap;

/** `Object#toString` result references. */
var mapTag$2 = '[object Map]',
  objectTag$1 = '[object Object]',
  promiseTag = '[object Promise]',
  setTag$2 = '[object Set]',
  weakMapTag$1 = '[object WeakMap]';
var dataViewTag$2 = '[object DataView]';

/** Used to detect maps, sets, and weakmaps. */
var dataViewCtorString = _toSource(_DataView),
  mapCtorString = _toSource(_Map),
  promiseCtorString = _toSource(_Promise),
  setCtorString = _toSource(_Set),
  weakMapCtorString = _toSource(_WeakMap);

/**
 * Gets the `toStringTag` of `value`.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the `toStringTag`.
 */
var getTag = _baseGetTag;

// Fallback for data views, maps, sets, and weak maps in IE 11 and promises in Node.js < 6.
if (_DataView && getTag(new _DataView(new ArrayBuffer(1))) != dataViewTag$2 || _Map && getTag(new _Map()) != mapTag$2 || _Promise && getTag(_Promise.resolve()) != promiseTag || _Set && getTag(new _Set()) != setTag$2 || _WeakMap && getTag(new _WeakMap()) != weakMapTag$1) {
  getTag = function (value) {
    var result = _baseGetTag(value),
      Ctor = result == objectTag$1 ? value.constructor : undefined,
      ctorString = Ctor ? _toSource(Ctor) : '';
    if (ctorString) {
      switch (ctorString) {
        case dataViewCtorString:
          return dataViewTag$2;
        case mapCtorString:
          return mapTag$2;
        case promiseCtorString:
          return promiseTag;
        case setCtorString:
          return setTag$2;
        case weakMapCtorString:
          return weakMapTag$1;
      }
    }
    return result;
  };
}
var _getTag = getTag;

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG$3 = 1;

/** `Object#toString` result references. */
var argsTag$2 = '[object Arguments]',
  arrayTag$1 = '[object Array]',
  objectTag$2 = '[object Object]';

/** Used for built-in method references. */
var objectProto$b = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$8 = objectProto$b.hasOwnProperty;

/**
 * A specialized version of `baseIsEqual` for arrays and objects which performs
 * deep comparisons and tracks traversed objects enabling objects with circular
 * references to be compared.
 *
 * @private
 * @param {Object} object The object to compare.
 * @param {Object} other The other object to compare.
 * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
 * @param {Function} customizer The function to customize comparisons.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Object} [stack] Tracks traversed `object` and `other` objects.
 * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
 */
function baseIsEqualDeep(object, other, bitmask, customizer, equalFunc, stack) {
  var objIsArr = isArray_1(object),
    othIsArr = isArray_1(other),
    objTag = objIsArr ? arrayTag$1 : _getTag(object),
    othTag = othIsArr ? arrayTag$1 : _getTag(other);
  objTag = objTag == argsTag$2 ? objectTag$2 : objTag;
  othTag = othTag == argsTag$2 ? objectTag$2 : othTag;
  var objIsObj = objTag == objectTag$2,
    othIsObj = othTag == objectTag$2,
    isSameTag = objTag == othTag;
  if (isSameTag && isBuffer_1(object)) {
    if (!isBuffer_1(other)) {
      return false;
    }
    objIsArr = true;
    objIsObj = false;
  }
  if (isSameTag && !objIsObj) {
    stack || (stack = new _Stack());
    return objIsArr || isTypedArray_1(object) ? _equalArrays(object, other, bitmask, customizer, equalFunc, stack) : _equalByTag(object, other, objTag, bitmask, customizer, equalFunc, stack);
  }
  if (!(bitmask & COMPARE_PARTIAL_FLAG$3)) {
    var objIsWrapped = objIsObj && hasOwnProperty$8.call(object, '__wrapped__'),
      othIsWrapped = othIsObj && hasOwnProperty$8.call(other, '__wrapped__');
    if (objIsWrapped || othIsWrapped) {
      var objUnwrapped = objIsWrapped ? object.value() : object,
        othUnwrapped = othIsWrapped ? other.value() : other;
      stack || (stack = new _Stack());
      return equalFunc(objUnwrapped, othUnwrapped, bitmask, customizer, stack);
    }
  }
  if (!isSameTag) {
    return false;
  }
  stack || (stack = new _Stack());
  return _equalObjects(object, other, bitmask, customizer, equalFunc, stack);
}
var _baseIsEqualDeep = baseIsEqualDeep;

/**
 * The base implementation of `_.isEqual` which supports partial comparisons
 * and tracks traversed objects.
 *
 * @private
 * @param {*} value The value to compare.
 * @param {*} other The other value to compare.
 * @param {boolean} bitmask The bitmask flags.
 *  1 - Unordered comparison
 *  2 - Partial comparison
 * @param {Function} [customizer] The function to customize comparisons.
 * @param {Object} [stack] Tracks traversed `value` and `other` objects.
 * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
 */
function baseIsEqual(value, other, bitmask, customizer, stack) {
  if (value === other) {
    return true;
  }
  if (value == null || other == null || !isObjectLike_1(value) && !isObjectLike_1(other)) {
    return value !== value && other !== other;
  }
  return _baseIsEqualDeep(value, other, bitmask, customizer, baseIsEqual, stack);
}
var _baseIsEqual = baseIsEqual;

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG$4 = 1,
  COMPARE_UNORDERED_FLAG$2 = 2;

/**
 * The base implementation of `_.isMatch` without support for iteratee shorthands.
 *
 * @private
 * @param {Object} object The object to inspect.
 * @param {Object} source The object of property values to match.
 * @param {Array} matchData The property names, values, and compare flags to match.
 * @param {Function} [customizer] The function to customize comparisons.
 * @returns {boolean} Returns `true` if `object` is a match, else `false`.
 */
function baseIsMatch(object, source, matchData, customizer) {
  var index = matchData.length,
    length = index,
    noCustomizer = !customizer;
  if (object == null) {
    return !length;
  }
  object = Object(object);
  while (index--) {
    var data = matchData[index];
    if (noCustomizer && data[2] ? data[1] !== object[data[0]] : !(data[0] in object)) {
      return false;
    }
  }
  while (++index < length) {
    data = matchData[index];
    var key = data[0],
      objValue = object[key],
      srcValue = data[1];
    if (noCustomizer && data[2]) {
      if (objValue === undefined && !(key in object)) {
        return false;
      }
    } else {
      var stack = new _Stack();
      if (customizer) {
        var result = customizer(objValue, srcValue, key, object, source, stack);
      }
      if (!(result === undefined ? _baseIsEqual(srcValue, objValue, COMPARE_PARTIAL_FLAG$4 | COMPARE_UNORDERED_FLAG$2, customizer, stack) : result)) {
        return false;
      }
    }
  }
  return true;
}
var _baseIsMatch = baseIsMatch;

/**
 * Checks if `value` is suitable for strict equality comparisons, i.e. `===`.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` if suitable for strict
 *  equality comparisons, else `false`.
 */
function isStrictComparable(value) {
  return value === value && !isObject_1(value);
}
var _isStrictComparable = isStrictComparable;

/**
 * Gets the property names, values, and compare flags of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the match data of `object`.
 */
function getMatchData(object) {
  var result = keys_1(object),
    length = result.length;
  while (length--) {
    var key = result[length],
      value = object[key];
    result[length] = [key, value, _isStrictComparable(value)];
  }
  return result;
}
var _getMatchData = getMatchData;

/**
 * A specialized version of `matchesProperty` for source values suitable
 * for strict equality comparisons, i.e. `===`.
 *
 * @private
 * @param {string} key The key of the property to get.
 * @param {*} srcValue The value to match.
 * @returns {Function} Returns the new spec function.
 */
function matchesStrictComparable(key, srcValue) {
  return function (object) {
    if (object == null) {
      return false;
    }
    return object[key] === srcValue && (srcValue !== undefined || key in Object(object));
  };
}
var _matchesStrictComparable = matchesStrictComparable;

/**
 * The base implementation of `_.matches` which doesn't clone `source`.
 *
 * @private
 * @param {Object} source The object of property values to match.
 * @returns {Function} Returns the new spec function.
 */
function baseMatches(source) {
  var matchData = _getMatchData(source);
  if (matchData.length == 1 && matchData[0][2]) {
    return _matchesStrictComparable(matchData[0][0], matchData[0][1]);
  }
  return function (object) {
    return object === source || _baseIsMatch(object, source, matchData);
  };
}
var _baseMatches = baseMatches;

/** `Object#toString` result references. */
var symbolTag$1 = '[object Symbol]';

/**
 * Checks if `value` is classified as a `Symbol` primitive or object.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a symbol, else `false`.
 * @example
 *
 * _.isSymbol(Symbol.iterator);
 * // => true
 *
 * _.isSymbol('abc');
 * // => false
 */
function isSymbol(value) {
  return typeof value == 'symbol' || isObjectLike_1(value) && _baseGetTag(value) == symbolTag$1;
}
var isSymbol_1 = isSymbol;

/** Used to match property names within property paths. */
var reIsDeepProp = /\.|\[(?:[^[\]]*|(["'])(?:(?!\1)[^\\]|\\.)*?\1)\]/,
  reIsPlainProp = /^\w*$/;

/**
 * Checks if `value` is a property name and not a property path.
 *
 * @private
 * @param {*} value The value to check.
 * @param {Object} [object] The object to query keys on.
 * @returns {boolean} Returns `true` if `value` is a property name, else `false`.
 */
function isKey(value, object) {
  if (isArray_1(value)) {
    return false;
  }
  var type = typeof value;
  if (type == 'number' || type == 'symbol' || type == 'boolean' || value == null || isSymbol_1(value)) {
    return true;
  }
  return reIsPlainProp.test(value) || !reIsDeepProp.test(value) || object != null && value in Object(object);
}
var _isKey = isKey;

/** Error message constants. */
var FUNC_ERROR_TEXT = 'Expected a function';

/**
 * Creates a function that memoizes the result of `func`. If `resolver` is
 * provided, it determines the cache key for storing the result based on the
 * arguments provided to the memoized function. By default, the first argument
 * provided to the memoized function is used as the map cache key. The `func`
 * is invoked with the `this` binding of the memoized function.
 *
 * **Note:** The cache is exposed as the `cache` property on the memoized
 * function. Its creation may be customized by replacing the `_.memoize.Cache`
 * constructor with one whose instances implement the
 * [`Map`](http://ecma-international.org/ecma-262/7.0/#sec-properties-of-the-map-prototype-object)
 * method interface of `clear`, `delete`, `get`, `has`, and `set`.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Function
 * @param {Function} func The function to have its output memoized.
 * @param {Function} [resolver] The function to resolve the cache key.
 * @returns {Function} Returns the new memoized function.
 * @example
 *
 * var object = { 'a': 1, 'b': 2 };
 * var other = { 'c': 3, 'd': 4 };
 *
 * var values = _.memoize(_.values);
 * values(object);
 * // => [1, 2]
 *
 * values(other);
 * // => [3, 4]
 *
 * object.a = 2;
 * values(object);
 * // => [1, 2]
 *
 * // Modify the result cache.
 * values.cache.set(object, ['a', 'b']);
 * values(object);
 * // => ['a', 'b']
 *
 * // Replace `_.memoize.Cache`.
 * _.memoize.Cache = WeakMap;
 */
function memoize(func, resolver) {
  if (typeof func != 'function' || resolver != null && typeof resolver != 'function') {
    throw new TypeError(FUNC_ERROR_TEXT);
  }
  var memoized = function () {
    var args = arguments,
      key = resolver ? resolver.apply(this, args) : args[0],
      cache = memoized.cache;
    if (cache.has(key)) {
      return cache.get(key);
    }
    var result = func.apply(this, args);
    memoized.cache = cache.set(key, result) || cache;
    return result;
  };
  memoized.cache = new (memoize.Cache || _MapCache)();
  return memoized;
}

// Expose `MapCache`.
memoize.Cache = _MapCache;
var memoize_1 = memoize;

/** Used as the maximum memoize cache size. */
var MAX_MEMOIZE_SIZE = 500;

/**
 * A specialized version of `_.memoize` which clears the memoized function's
 * cache when it exceeds `MAX_MEMOIZE_SIZE`.
 *
 * @private
 * @param {Function} func The function to have its output memoized.
 * @returns {Function} Returns the new memoized function.
 */
function memoizeCapped(func) {
  var result = memoize_1(func, function (key) {
    if (cache.size === MAX_MEMOIZE_SIZE) {
      cache.clear();
    }
    return key;
  });
  var cache = result.cache;
  return result;
}
var _memoizeCapped = memoizeCapped;

/** Used to match property names within property paths. */
var rePropName = /[^.[\]]+|\[(?:(-?\d+(?:\.\d+)?)|(["'])((?:(?!\2)[^\\]|\\.)*?)\2)\]|(?=(?:\.|\[\])(?:\.|\[\]|$))/g;

/** Used to match backslashes in property paths. */
var reEscapeChar = /\\(\\)?/g;

/**
 * Converts `string` to a property path array.
 *
 * @private
 * @param {string} string The string to convert.
 * @returns {Array} Returns the property path array.
 */
var stringToPath = _memoizeCapped(function (string) {
  var result = [];
  if (string.charCodeAt(0) === 46 /* . */) {
    result.push('');
  }
  string.replace(rePropName, function (match, number, quote, subString) {
    result.push(quote ? subString.replace(reEscapeChar, '$1') : number || match);
  });
  return result;
});
var _stringToPath = stringToPath;

/**
 * A specialized version of `_.map` for arrays without support for iteratee
 * shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns the new mapped array.
 */
function arrayMap(array, iteratee) {
  var index = -1,
    length = array == null ? 0 : array.length,
    result = Array(length);
  while (++index < length) {
    result[index] = iteratee(array[index], index, array);
  }
  return result;
}
var _arrayMap = arrayMap;

/** Used as references for various `Number` constants. */
var INFINITY = 1 / 0;

/** Used to convert symbols to primitives and strings. */
var symbolProto$1 = _Symbol ? _Symbol.prototype : undefined,
  symbolToString = symbolProto$1 ? symbolProto$1.toString : undefined;

/**
 * The base implementation of `_.toString` which doesn't convert nullish
 * values to empty strings.
 *
 * @private
 * @param {*} value The value to process.
 * @returns {string} Returns the string.
 */
function baseToString(value) {
  // Exit early for strings to avoid a performance hit in some environments.
  if (typeof value == 'string') {
    return value;
  }
  if (isArray_1(value)) {
    // Recursively convert values (susceptible to call stack limits).
    return _arrayMap(value, baseToString) + '';
  }
  if (isSymbol_1(value)) {
    return symbolToString ? symbolToString.call(value) : '';
  }
  var result = value + '';
  return result == '0' && 1 / value == -INFINITY ? '-0' : result;
}
var _baseToString = baseToString;

/**
 * Converts `value` to a string. An empty string is returned for `null`
 * and `undefined` values. The sign of `-0` is preserved.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to convert.
 * @returns {string} Returns the converted string.
 * @example
 *
 * _.toString(null);
 * // => ''
 *
 * _.toString(-0);
 * // => '-0'
 *
 * _.toString([1, 2, 3]);
 * // => '1,2,3'
 */
function toString(value) {
  return value == null ? '' : _baseToString(value);
}
var toString_1 = toString;

/**
 * Casts `value` to a path array if it's not one.
 *
 * @private
 * @param {*} value The value to inspect.
 * @param {Object} [object] The object to query keys on.
 * @returns {Array} Returns the cast property path array.
 */
function castPath(value, object) {
  if (isArray_1(value)) {
    return value;
  }
  return _isKey(value, object) ? [value] : _stringToPath(toString_1(value));
}
var _castPath = castPath;

/** Used as references for various `Number` constants. */
var INFINITY$1 = 1 / 0;

/**
 * Converts `value` to a string key if it's not a string or symbol.
 *
 * @private
 * @param {*} value The value to inspect.
 * @returns {string|symbol} Returns the key.
 */
function toKey(value) {
  if (typeof value == 'string' || isSymbol_1(value)) {
    return value;
  }
  var result = value + '';
  return result == '0' && 1 / value == -INFINITY$1 ? '-0' : result;
}
var _toKey = toKey;

/**
 * The base implementation of `_.get` without support for default values.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {Array|string} path The path of the property to get.
 * @returns {*} Returns the resolved value.
 */
function baseGet(object, path) {
  path = _castPath(path, object);
  var index = 0,
    length = path.length;
  while (object != null && index < length) {
    object = object[_toKey(path[index++])];
  }
  return index && index == length ? object : undefined;
}
var _baseGet = baseGet;

/**
 * Gets the value at `path` of `object`. If the resolved value is
 * `undefined`, the `defaultValue` is returned in its place.
 *
 * @static
 * @memberOf _
 * @since 3.7.0
 * @category Object
 * @param {Object} object The object to query.
 * @param {Array|string} path The path of the property to get.
 * @param {*} [defaultValue] The value returned for `undefined` resolved values.
 * @returns {*} Returns the resolved value.
 * @example
 *
 * var object = { 'a': [{ 'b': { 'c': 3 } }] };
 *
 * _.get(object, 'a[0].b.c');
 * // => 3
 *
 * _.get(object, ['a', '0', 'b', 'c']);
 * // => 3
 *
 * _.get(object, 'a.b.c', 'default');
 * // => 'default'
 */
function get(object, path, defaultValue) {
  var result = object == null ? undefined : _baseGet(object, path);
  return result === undefined ? defaultValue : result;
}
var get_1 = get;

/**
 * The base implementation of `_.hasIn` without support for deep paths.
 *
 * @private
 * @param {Object} [object] The object to query.
 * @param {Array|string} key The key to check.
 * @returns {boolean} Returns `true` if `key` exists, else `false`.
 */
function baseHasIn(object, key) {
  return object != null && key in Object(object);
}
var _baseHasIn = baseHasIn;

/**
 * Checks if `path` exists on `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {Array|string} path The path to check.
 * @param {Function} hasFunc The function to check properties.
 * @returns {boolean} Returns `true` if `path` exists, else `false`.
 */
function hasPath(object, path, hasFunc) {
  path = _castPath(path, object);
  var index = -1,
    length = path.length,
    result = false;
  while (++index < length) {
    var key = _toKey(path[index]);
    if (!(result = object != null && hasFunc(object, key))) {
      break;
    }
    object = object[key];
  }
  if (result || ++index != length) {
    return result;
  }
  length = object == null ? 0 : object.length;
  return !!length && isLength_1(length) && _isIndex(key, length) && (isArray_1(object) || isArguments_1(object));
}
var _hasPath = hasPath;

/**
 * Checks if `path` is a direct or inherited property of `object`.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Object
 * @param {Object} object The object to query.
 * @param {Array|string} path The path to check.
 * @returns {boolean} Returns `true` if `path` exists, else `false`.
 * @example
 *
 * var object = _.create({ 'a': _.create({ 'b': 2 }) });
 *
 * _.hasIn(object, 'a');
 * // => true
 *
 * _.hasIn(object, 'a.b');
 * // => true
 *
 * _.hasIn(object, ['a', 'b']);
 * // => true
 *
 * _.hasIn(object, 'b');
 * // => false
 */
function hasIn(object, path) {
  return object != null && _hasPath(object, path, _baseHasIn);
}
var hasIn_1 = hasIn;

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG$5 = 1,
  COMPARE_UNORDERED_FLAG$3 = 2;

/**
 * The base implementation of `_.matchesProperty` which doesn't clone `srcValue`.
 *
 * @private
 * @param {string} path The path of the property to get.
 * @param {*} srcValue The value to match.
 * @returns {Function} Returns the new spec function.
 */
function baseMatchesProperty(path, srcValue) {
  if (_isKey(path) && _isStrictComparable(srcValue)) {
    return _matchesStrictComparable(_toKey(path), srcValue);
  }
  return function (object) {
    var objValue = get_1(object, path);
    return objValue === undefined && objValue === srcValue ? hasIn_1(object, path) : _baseIsEqual(srcValue, objValue, COMPARE_PARTIAL_FLAG$5 | COMPARE_UNORDERED_FLAG$3);
  };
}
var _baseMatchesProperty = baseMatchesProperty;

/**
 * This method returns the first argument it receives.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category Util
 * @param {*} value Any value.
 * @returns {*} Returns `value`.
 * @example
 *
 * var object = { 'a': 1 };
 *
 * console.log(_.identity(object) === object);
 * // => true
 */
function identity(value) {
  return value;
}
var identity_1 = identity;

/**
 * The base implementation of `_.property` without support for deep paths.
 *
 * @private
 * @param {string} key The key of the property to get.
 * @returns {Function} Returns the new accessor function.
 */
function baseProperty(key) {
  return function (object) {
    return object == null ? undefined : object[key];
  };
}
var _baseProperty = baseProperty;

/**
 * A specialized version of `baseProperty` which supports deep paths.
 *
 * @private
 * @param {Array|string} path The path of the property to get.
 * @returns {Function} Returns the new accessor function.
 */
function basePropertyDeep(path) {
  return function (object) {
    return _baseGet(object, path);
  };
}
var _basePropertyDeep = basePropertyDeep;

/**
 * Creates a function that returns the value at `path` of a given object.
 *
 * @static
 * @memberOf _
 * @since 2.4.0
 * @category Util
 * @param {Array|string} path The path of the property to get.
 * @returns {Function} Returns the new accessor function.
 * @example
 *
 * var objects = [
 *   { 'a': { 'b': 2 } },
 *   { 'a': { 'b': 1 } }
 * ];
 *
 * _.map(objects, _.property('a.b'));
 * // => [2, 1]
 *
 * _.map(_.sortBy(objects, _.property(['a', 'b'])), 'a.b');
 * // => [1, 2]
 */
function property(path) {
  return _isKey(path) ? _baseProperty(_toKey(path)) : _basePropertyDeep(path);
}
var property_1 = property;

/**
 * The base implementation of `_.iteratee`.
 *
 * @private
 * @param {*} [value=_.identity] The value to convert to an iteratee.
 * @returns {Function} Returns the iteratee.
 */
function baseIteratee(value) {
  // Don't store the `typeof` result in a variable to avoid a JIT bug in Safari 9.
  // See https://bugs.webkit.org/show_bug.cgi?id=156034 for more details.
  if (typeof value == 'function') {
    return value;
  }
  if (value == null) {
    return identity_1;
  }
  if (typeof value == 'object') {
    return isArray_1(value) ? _baseMatchesProperty(value[0], value[1]) : _baseMatches(value);
  }
  return property_1(value);
}
var _baseIteratee = baseIteratee;

/**
 * Creates a `_.find` or `_.findLast` function.
 *
 * @private
 * @param {Function} findIndexFunc The function to find the collection index.
 * @returns {Function} Returns the new find function.
 */
function createFind(findIndexFunc) {
  return function (collection, predicate, fromIndex) {
    var iterable = Object(collection);
    if (!isArrayLike_1(collection)) {
      var iteratee = _baseIteratee(predicate);
      collection = keys_1(collection);
      predicate = function (key) {
        return iteratee(iterable[key], key, iterable);
      };
    }
    var index = findIndexFunc(collection, predicate, fromIndex);
    return index > -1 ? iterable[iteratee ? collection[index] : index] : undefined;
  };
}
var _createFind = createFind;

/**
 * The base implementation of `_.findIndex` and `_.findLastIndex` without
 * support for iteratee shorthands.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {Function} predicate The function invoked per iteration.
 * @param {number} fromIndex The index to search from.
 * @param {boolean} [fromRight] Specify iterating from right to left.
 * @returns {number} Returns the index of the matched value, else `-1`.
 */
function baseFindIndex(array, predicate, fromIndex, fromRight) {
  var length = array.length,
    index = fromIndex + (fromRight ? 1 : -1);
  while (fromRight ? index-- : ++index < length) {
    if (predicate(array[index], index, array)) {
      return index;
    }
  }
  return -1;
}
var _baseFindIndex = baseFindIndex;

/** Used as references for various `Number` constants. */
var NAN = 0 / 0;

/** Used to match leading and trailing whitespace. */
var reTrim = /^\s+|\s+$/g;

/** Used to detect bad signed hexadecimal string values. */
var reIsBadHex = /^[-+]0x[0-9a-f]+$/i;

/** Used to detect binary string values. */
var reIsBinary = /^0b[01]+$/i;

/** Used to detect octal string values. */
var reIsOctal = /^0o[0-7]+$/i;

/** Built-in method references without a dependency on `root`. */
var freeParseInt = parseInt;

/**
 * Converts `value` to a number.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to process.
 * @returns {number} Returns the number.
 * @example
 *
 * _.toNumber(3.2);
 * // => 3.2
 *
 * _.toNumber(Number.MIN_VALUE);
 * // => 5e-324
 *
 * _.toNumber(Infinity);
 * // => Infinity
 *
 * _.toNumber('3.2');
 * // => 3.2
 */
function toNumber(value) {
  if (typeof value == 'number') {
    return value;
  }
  if (isSymbol_1(value)) {
    return NAN;
  }
  if (isObject_1(value)) {
    var other = typeof value.valueOf == 'function' ? value.valueOf() : value;
    value = isObject_1(other) ? other + '' : other;
  }
  if (typeof value != 'string') {
    return value === 0 ? value : +value;
  }
  value = value.replace(reTrim, '');
  var isBinary = reIsBinary.test(value);
  return isBinary || reIsOctal.test(value) ? freeParseInt(value.slice(2), isBinary ? 2 : 8) : reIsBadHex.test(value) ? NAN : +value;
}
var toNumber_1 = toNumber;

/** Used as references for various `Number` constants. */
var INFINITY$2 = 1 / 0,
  MAX_INTEGER = 1.7976931348623157e+308;

/**
 * Converts `value` to a finite number.
 *
 * @static
 * @memberOf _
 * @since 4.12.0
 * @category Lang
 * @param {*} value The value to convert.
 * @returns {number} Returns the converted number.
 * @example
 *
 * _.toFinite(3.2);
 * // => 3.2
 *
 * _.toFinite(Number.MIN_VALUE);
 * // => 5e-324
 *
 * _.toFinite(Infinity);
 * // => 1.7976931348623157e+308
 *
 * _.toFinite('3.2');
 * // => 3.2
 */
function toFinite(value) {
  if (!value) {
    return value === 0 ? value : 0;
  }
  value = toNumber_1(value);
  if (value === INFINITY$2 || value === -INFINITY$2) {
    var sign = value < 0 ? -1 : 1;
    return sign * MAX_INTEGER;
  }
  return value === value ? value : 0;
}
var toFinite_1 = toFinite;

/**
 * Converts `value` to an integer.
 *
 * **Note:** This method is loosely based on
 * [`ToInteger`](http://www.ecma-international.org/ecma-262/7.0/#sec-tointeger).
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to convert.
 * @returns {number} Returns the converted integer.
 * @example
 *
 * _.toInteger(3.2);
 * // => 3
 *
 * _.toInteger(Number.MIN_VALUE);
 * // => 0
 *
 * _.toInteger(Infinity);
 * // => 1.7976931348623157e+308
 *
 * _.toInteger('3.2');
 * // => 3
 */
function toInteger(value) {
  var result = toFinite_1(value),
    remainder = result % 1;
  return result === result ? remainder ? result - remainder : result : 0;
}
var toInteger_1 = toInteger;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeMax = Math.max;

/**
 * This method is like `_.find` except that it returns the index of the first
 * element `predicate` returns truthy for instead of the element itself.
 *
 * @static
 * @memberOf _
 * @since 1.1.0
 * @category Array
 * @param {Array} array The array to inspect.
 * @param {Function} [predicate=_.identity] The function invoked per iteration.
 * @param {number} [fromIndex=0] The index to search from.
 * @returns {number} Returns the index of the found element, else `-1`.
 * @example
 *
 * var users = [
 *   { 'user': 'barney',  'active': false },
 *   { 'user': 'fred',    'active': false },
 *   { 'user': 'pebbles', 'active': true }
 * ];
 *
 * _.findIndex(users, function(o) { return o.user == 'barney'; });
 * // => 0
 *
 * // The `_.matches` iteratee shorthand.
 * _.findIndex(users, { 'user': 'fred', 'active': false });
 * // => 1
 *
 * // The `_.matchesProperty` iteratee shorthand.
 * _.findIndex(users, ['active', false]);
 * // => 0
 *
 * // The `_.property` iteratee shorthand.
 * _.findIndex(users, 'active');
 * // => 2
 */
function findIndex(array, predicate, fromIndex) {
  var length = array == null ? 0 : array.length;
  if (!length) {
    return -1;
  }
  var index = fromIndex == null ? 0 : toInteger_1(fromIndex);
  if (index < 0) {
    index = nativeMax(length + index, 0);
  }
  return _baseFindIndex(array, _baseIteratee(predicate), index);
}
var findIndex_1 = findIndex;

/**
 * Iterates over elements of `collection`, returning the first element
 * `predicate` returns truthy for. The predicate is invoked with three
 * arguments: (value, index|key, collection).
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Collection
 * @param {Array|Object} collection The collection to inspect.
 * @param {Function} [predicate=_.identity] The function invoked per iteration.
 * @param {number} [fromIndex=0] The index to search from.
 * @returns {*} Returns the matched element, else `undefined`.
 * @example
 *
 * var users = [
 *   { 'user': 'barney',  'age': 36, 'active': true },
 *   { 'user': 'fred',    'age': 40, 'active': false },
 *   { 'user': 'pebbles', 'age': 1,  'active': true }
 * ];
 *
 * _.find(users, function(o) { return o.age < 40; });
 * // => object for 'barney'
 *
 * // The `_.matches` iteratee shorthand.
 * _.find(users, { 'age': 1, 'active': true });
 * // => object for 'pebbles'
 *
 * // The `_.matchesProperty` iteratee shorthand.
 * _.find(users, ['active', false]);
 * // => object for 'fred'
 *
 * // The `_.property` iteratee shorthand.
 * _.find(users, 'active');
 * // => object for 'barney'
 */
var find = _createFind(findIndex_1);
var find_1 = find;
function _templateObject$1d() {
  var data = _taggedTemplateLiteralLoose(["\n  .timepicker-backdrop {\n    position: fixed;\n    width: 100%;\n    height: 100%;\n    max-width: 100%;\n    max-height: 100%;\n    top: 0;\n    left: 0;\n    padding: 0;\n    margin: 0;\n    z-index: 100000;\n    background: rgba(0, 0, 0, 0.49);\n  }\n\n  .timepicker-modal {\n    font-family: \"Helvetica\";\n    width: 50%;\n    max-width: 400px;\n    min-width: 300px;\n    background: #fff;\n    position: fixed;\n    top: 50%;\n    z-index: 100001;\n    left: 50%;\n    -webkit-transform: translateX(-50%) translateY(-50%);\n    transform: translateX(-50%) translateY(-50%);\n    box-shadow: rgba(0, 0, 0, 0.25) 0px 14px 45px,\n      rgba(0, 0, 0, 0.22) 0px 10px 18px;\n    border-radius: 6px;\n    overflow: hidden;\n  }\n\n  .timepicker-modal .timepicker-header {\n    height: 120px;\n    display: -webkit-box;\n    display: -ms-flexbox;\n    display: flex;\n    -webkit-box-pack: center;\n    -ms-flex-pack: center;\n    justify-content: center;\n    -webkit-box-align: center;\n    -ms-flex-align: center;\n    align-items: center;\n    color: #fff;\n    -webkit-user-select: none;\n    -moz-user-select: none;\n    -ms-user-select: none;\n    user-select: none;\n  }\n\n  .timepicker-modal .am-pm-input {\n    width: 0.1px;\n    height: 0.1px;\n    opacity: 0;\n    overflow: hidden;\n    position: absolute;\n    z-index: -1;\n  }\n\n  .text-shadow {\n    text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.13);\n  }\n\n  .timepicker-modal .am-pm-label {\n    color: rgba(255, 255, 255, 0.7);\n    font-size: 14px;\n    margin-left: 12px;\n    cursor: pointer;\n    text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.13);\n  }\n\n  .timepicker-modal .timepicker-time-container {\n    font-size: 42px;\n    color: inherit;\n    letter-spacing: 2px;\n    cursor: pointer;\n    font-weight: bold;\n  }\n\n  .timepicker-modal .timepicker-time-container .is-not-selected {\n    color: rgba(255, 255, 255, 0.7);\n  }\n\n  .timepicker-modal .am-pm-input:checked + .am-pm-label {\n    color: inherit;\n    display: block;\n  }\n\n  .timepicker-modal .timepicker-main {\n    display: -webkit-box;\n    display: -ms-flexbox;\n    display: flex;\n    -webkit-box-pack: center;\n    -ms-flex-pack: center;\n    justify-content: center;\n    -webkit-box-align: center;\n    -ms-flex-align: center;\n    align-items: center;\n    padding: 20px;\n    box-sizing: border-box;\n  }\n\n  .timepicker-modal .hours-container {\n    width: 260px;\n    height: 260px;\n    position: relative;\n    display: -webkit-box;\n    display: -ms-flexbox;\n    display: flex;\n    -webkit-box-pack: center;\n    -ms-flex-pack: center;\n    justify-content: center;\n    -webkit-box-align: center;\n    -ms-flex-align: center;\n    align-items: center;\n    background: #efefef;\n    border-radius: 50%;\n    -webkit-user-select: none;\n    -moz-user-select: none;\n    -ms-user-select: none;\n    user-select: none;\n  }\n\n  .timepicker-modal .hand {\n    -webkit-user-select: none;\n    -moz-user-select: none;\n    -ms-user-select: none;\n    user-select: none;\n    width: 45%;\n    position: absolute;\n    left: 50%;\n    bottom: 50%;\n    -webkit-transform-origin: center left 0px;\n    transform-origin: center left 0px;\n    pointer-events: none;\n    -webkit-transform: rotateZ(0deg);\n    transform: rotateZ(0deg);\n    height: 2px;\n  }\n\n  .timepicker-modal .hand:after {\n    position: absolute;\n    content: \"\";\n    top: -5px;\n    border-radius: 50%;\n    right: 0;\n    width: 12px;\n    height: 12px;\n    background: inherit;\n  }\n\n  .timepicker-modal .circle {\n    -webkit-user-select: none;\n    -moz-user-select: none;\n    -ms-user-select: none;\n    user-select: none;\n    font-size: 16px;\n    display: -webkit-box;\n    display: -ms-flexbox;\n    display: flex;\n    -webkit-box-pack: center;\n    -ms-flex-pack: center;\n    justify-content: center;\n    -webkit-box-align: center;\n    -ms-flex-align: center;\n    align-items: center;\n    cursor: pointer;\n    width: 25px;\n    height: 25px;\n    position: absolute;\n    border-radius: 50%;\n    top: 50%;\n    left: 50%;\n  }\n\n  .timepicker-modal .circle.selected {\n    color: #fff;\n  }\n\n  .timepicker-modal .circle:nth-of-type(1) {\n    -webkit-transform: translate3d(0px, -110px, 0) ", ";\n    transform: translate3d(0px, -110px, 0) ", ";\n  }\n\n  .timepicker-modal .circle:nth-of-type(2) {\n    transform: translate3d(55px, -95px, 0) ", ";\n    -webkit-transform: translate3d(55px, -95px, 0) ", ";\n  }\n\n  .timepicker-modal .circle:nth-of-type(3) {\n    -webkit-transform: translate3d(95px, -55px, 0) ", ";\n    transform: translate3d(95px, -55px, 0) ", ";\n  }\n\n  .timepicker-modal .circle:nth-of-type(4) {\n    transform: translate3d(110px, 0px, 0) ", ";\n    -webkit-transform: translate3d(110px, 0px, 0) ", ";\n  }\n\n  .timepicker-modal .circle:nth-of-type(5) {\n    transform: translate3d(95px, 55px, 0) ", ";\n    -webkit-transform: translate3d(95px, 55px, 0) ", ";\n  }\n\n  .timepicker-modal .circle:nth-of-type(6) {\n    transform: translate3d(55px, 95px, 0) ", ";\n    -webkit-transform: translate3d(55px, 95px, 0) ", ";\n  }\n\n  .timepicker-modal .circle:nth-of-type(7) {\n    transform: translate3d(0px, 110px, 0) ", ";\n    -webkit-transform: translate3d(0px, 110px, 0) ", ";\n  }\n\n  .timepicker-modal .circle:nth-of-type(8) {\n    transform: translate3d(-55px, 95px, 0) ", ";\n    -webkit-transform: translate3d(-55px, 95px, 0) ", ";\n  }\n\n  .timepicker-modal .circle:nth-of-type(9) {\n    transform: translate3d(-95px, 55px, 0) ", ";\n    -webkit-transform: translate3d(-95px, 55px, 0) ", ";\n  }\n\n  .timepicker-modal .circle:nth-of-type(10) {\n    transform: translate3d(-110px, 0px, 0) ", ";\n    -webkit-transform: translate3d(-110px, 0px, 0) ", ";\n  }\n\n  .timepicker-modal .circle:nth-of-type(11) {\n    transform: translate3d(-95px, -55px, 0) ", ";\n    -webkit-transform: translate3d(-95px, -55px, 0) ", ";\n  }\n\n  .timepicker-modal .circle:nth-of-type(12) {\n    transform: translate3d(-55px, -95px, 0) ", ";\n    -webkit-transform: translate3d(-55px, -95px, 0) ", ";\n  }\n\n  .timepicker-modal .mask {\n    width: 100%;\n    height: 100%;\n  }\n\n  .timepicker-modal footer {\n    text-align: right;\n    padding: 20px;\n  }\n\n  .timepicker-modal .timepicker-button {\n    border: 0;\n    background: white;\n    text-transform: uppercase;\n    cursor: pointer;\n    font-size: 14px;\n    margin-left: 12px;\n    font-weight: bold;\n    color: #ffffff;\n    padding: 10px 21px;\n    border-radius: 21px;\n  }\n\n  .timepicker-modal .timepicker-button.close {\n    background: white;\n  }\n"]);
  _templateObject$1d = function _templateObject() {
    return data;
  };
  return data;
}
var DEFAULT_PRIMARY_COLOR = "#F26B83";
var style = "\n    react-timepicker{\n        color: black\n    }\n";
function getColorStyles(props) {
  var themeSelector = props.themeSelector,
    theme = props.theme,
    color1 = props.color1,
    _props$headerColor = props.headerColor,
    headerColor = _props$headerColor === void 0 ? color1 : _props$headerColor;
  var backgroundColor = "";
  var primaryColor = "";
  if (theme) {
    var selectedGradient = find_1(gradients, function getTheme(o) {
      return o.name === theme;
    });
    if (selectedGradient) {
      var colors = selectedGradient.colors;
      primaryColor = color1 ? color1 : colors[0];
      backgroundColor = "\n      background: " + colors[0] + ";\n      background: -webkit-linear-gradient(to left," + colors[0] + "  , " + colors[1] + ");\n      background: linear-gradient(to left, " + colors[0] + " ," + colors[1] + ");";
    } else {
      backgroundColor = "background-color: " + DEFAULT_PRIMARY_COLOR + ";";
      primaryColor = DEFAULT_PRIMARY_COLOR;
    }
  } else {
    primaryColor = color1 || DEFAULT_PRIMARY_COLOR;
    backgroundColor = "background-color: " + (headerColor || primaryColor) + ";";
  }
  return "\n    .react-timepicker-background-color-" + themeSelector + " {\n      " + backgroundColor + ";\n     }\n    .react-timepicker-primary-color-background-" + themeSelector + " {\n      background: " + primaryColor + " !important;\n    }\n    .react-timepicker-primary-color-color-" + themeSelector + " {\n      color: " + primaryColor + " !important;\n    }\n    ";
}
var transformLeftRightMiddle = "translateX(-50%) translateY(-50%);";
var Component$14 = styled.div(_templateObject$1d(), transformLeftRightMiddle, transformLeftRightMiddle, transformLeftRightMiddle, transformLeftRightMiddle, transformLeftRightMiddle, transformLeftRightMiddle, transformLeftRightMiddle, transformLeftRightMiddle, transformLeftRightMiddle, transformLeftRightMiddle, transformLeftRightMiddle, transformLeftRightMiddle, transformLeftRightMiddle, transformLeftRightMiddle, transformLeftRightMiddle, transformLeftRightMiddle, transformLeftRightMiddle, transformLeftRightMiddle, transformLeftRightMiddle, transformLeftRightMiddle, transformLeftRightMiddle, transformLeftRightMiddle, transformLeftRightMiddle, transformLeftRightMiddle);
function appendZero(val) {
  return val < 10 ? '0' + val : String(val);
}
function isMousePressed(event) {
  if (typeof event.buttons === 'undefined') {
    return event.nativeEvent.which !== 1;
  }
  return event.buttons !== 1;
}
function format12to24(hour, minute, isAmSelected) {
  if (isAmSelected && hour === 12) {
    hour -= 12;
    return appendZero(hour) + ':' + appendZero(minute);
  }
  if (!isAmSelected && hour !== 12) {
    hour += 12;
    return appendZero(hour) + ':' + appendZero(minute);
  }
  return appendZero(hour) + ':' + appendZero(minute);
}
function format24to12(hour, minute) {
  if (hour === 0) {
    hour += 12;
    return appendZero(hour) + ':' + appendZero(minute) + ' AM';
  }
  if (hour === 12) {
    return appendZero(hour) + ':' + appendZero(minute) + ' PM';
  }
  if (hour > 0 && hour < 12) {
    return appendZero(hour) + ':' + appendZero(minute) + ' AM';
  }
  hour -= 12;
  return appendZero(hour) + ':' + appendZero(minute) + ' PM';
}
function getFormat12(hour, minute, isAmSelected) {
  return appendZero(hour) + ':' + appendZero(minute) + ' ' + (isAmSelected ? 'AM' : 'PM');
}
var TimePicker = function TimePicker(props) {
  var time = props.time,
    attributes = _objectWithoutPropertiesLoose(props, ["time", "color1", "headerColor", "placeholder", "className", "style", "degree"]);
  var _format12 = "";
  if (time) {
    var timeArr = time.split(":");
    _format12 = format24to12(Number(timeArr[0]), Number(timeArr[1]));
  }
  var _useState = useState({
      x: 0,
      y: 0
    }),
    center = _useState[0],
    setCenter = _useState[1];
  var _useState2 = useState({
      x: 0,
      y: 0
    }),
    containerPos = _useState2[0],
    setContainerPos = _useState2[1];
  var _useState3 = useState({
      x: 0,
      y: 0
    }),
    basePoint = _useState3[0],
    setBasePoint = _useState3[1];
  var mask = useRef(null);
  var closeButton = useRef(null);
  var inputField = useRef(null);
  var _useState4 = useState(false),
    mounted = _useState4[0],
    setMounted = _useState4[1];
  var _useState5 = useState({
      toShow: false,
      time: {
        format12: _format12,
        format24: ""
      },
      degree: 0,
      hour: 0,
      minute: 0,
      toShowHourContainer: false,
      selectedIndexDegree: 0,
      isAmSelected: false
    }),
    state = _useState5[0],
    setState = _useState5[1];
  if (!mounted) {
    addStyles();
  }
  useEffect(function () {
    setMounted(true);
  }, []);
  useEffect(function () {
    if (!mounted) {
      return;
    }
    var format12 = "";
    var format24 = "";
    var time = props.time;
    if (time) {
      var temp = time.split(":");
      format12 = format24to12(Number(temp[0]), Number(temp[1]));
      format24 = time;
    }
    setState(function (prevState) {
      return _extends({}, prevState, {
        time: {
          format12: format12,
          format24: format24
        }
      });
    });
  }, [props.time]);
  function addStyles() {
    var commonSelector = "react-timepicker-common-style";
    var themeSelector = getThemeSelector(props.theme, props.color1);
    var theme = props.theme,
      color1 = props.color1,
      headerColor = props.headerColor;
    var head = document.head || document.getElementsByTagName("head")[0];
    if (!document.getElementById(commonSelector)) {
      var _style = document.createElement("style");
      _style.type = "text/css";
      _style.id = commonSelector;
      if (_style.sheet) {
        _style.sheet.insertRule(style);
      } else {
        _style.appendChild(document.createTextNode(style));
      }
      head.appendChild(_style);
    }
    if (!document.getElementById(themeSelector)) {
      var themeStyles = getColorStyles({
        themeSelector: themeSelector,
        theme: theme,
        color1: color1,
        headerColor: headerColor
      });
      var _style2 = document.createElement("style");
      _style2.type = "text/css";
      _style2.id = themeSelector;
      if (_style2.sheet) {
        _style2.sheet.insertRule(themeStyles);
      } else {
        _style2.appendChild(document.createTextNode(themeStyles));
      }
      head.appendChild(_style2);
    }
  }
  function removeEventListener() {
    window.removeEventListener("keydown", handleKeyPress);
  }
  function addEventListener() {
    window.addEventListener("keydown", handleKeyPress);
  }
  var handleKeyPress = function handleKeyPress(e) {
    switch (e.which) {
      case 13:
        if (e.target !== closeButton.current) {
          handleSet();
        }
        break;
      case 27:
        toggleToShow(false);
        break;
      case 38:
        state.toShowHourContainer ? setHour((state.degree + 30) % 360 || 360) : setMinute((state.degree + 6) % 360 || 360);
        break;
      case 40:
        state.toShowHourContainer ? setHour((state.degree - 30) % 360 || 360) : setMinute((state.degree - 6) % 360 || 360);
        break;
    }
  };
  var handleMove = function handleMove(event) {
    event.preventDefault();
    if (isMousePressed(event)) return;
    changeClock(event.nativeEvent.clientX, event.nativeEvent.clientY);
  };
  var handleTouchMove = function handleTouchMove(event) {
    event.preventDefault();
    changeClock(event.changedTouches[0].clientX, event.changedTouches[0].clientY);
  };
  var handleTouchUp = function handleTouchUp(event) {
    if (event.target === mask.current) return;
    changeClock(event.changedTouches[0].clientX, event.changedTouches[0].clientY);
  };
  var handleMoveUp = function handleMoveUp(event) {
    if (event.target === mask.current) return;
    changeClock(event.nativeEvent.clientX, event.nativeEvent.clientY);
  };
  var changeClock = function changeClock(clientX, clientY) {
    var x = clientX - containerPos.x;
    var y = clientY - containerPos.y;
    state.toShowHourContainer ? setHour(getDegree(x, y)) : setMinute(getDegree(x, y));
  };
  var toggleHourOrMinuteContainer = function toggleHourOrMinuteContainer(toShowHourContainer) {
    var _ref = toShowHourContainer ? getSelectedIndexDegreeAndDegreeForHour(Number(state.hour)) : getSelectedIndexDegreeAndDegreeForMinute(Number(state.minute)),
      degree = _ref.degree,
      selectedIndexDegree = _ref.selectedIndexDegree;
    console.log({
      degree: degree
    }, {
      selectedIndexDegree: selectedIndexDegree
    }, {
      toShowHourContainer: toShowHourContainer
    });
    setState(function (prevState) {
      return _extends({}, prevState, {
        toShowHourContainer: toShowHourContainer,
        degree: degree,
        selectedIndexDegree: selectedIndexDegree
      });
    });
  };
  var toggleAmPm = function toggleAmPm(isAmSelected) {
    setState(function (prevState) {
      return _extends({}, prevState, {
        isAmSelected: isAmSelected
      });
    });
  };
  var handleFocus = function handleFocus(e) {
    e.preventDefault();
    var format24 = props.time;
    var _getInitialConfig = getInitialConfig(format24),
      hour = _getInitialConfig.hour,
      minute = _getInitialConfig.minute,
      degree = _getInitialConfig.degree,
      selectedIndexDegree = _getInitialConfig.selectedIndexDegree,
      isAmSelected = _getInitialConfig.isAmSelected;
    toggleToShow();
    var _hour = parseInt(hour);
    var _minute = parseInt(minute);
    Promise.resolve().then(function () {
      return setState(function (prevState) {
        return _extends({}, prevState, {
          degree: degree,
          hour: _hour,
          minute: _minute,
          toShowHourContainer: true,
          selectedIndexDegree: selectedIndexDegree,
          isAmSelected: isAmSelected
        });
      });
    }).then(function () {
      init();
      console.log("From focus:", {
        containerPos: containerPos
      }, {
        center: center
      }, {
        basePoint: basePoint
      });
    });
  };
  function toggleToShow(toShow) {
    if (toShow === void 0) {
      toShow = true;
    }
    setState(function (prevState) {
      return _extends({}, prevState, {
        toShow: toShow
      });
    });
    toShow ? addEventListener() : removeEventListener();
  }
  var handleSet = function handleSet() {
    var allFormat = getTime(Number(state.hour), Number(state.minute), state.isAmSelected);
    setState(function (prevState) {
      return _extends({}, prevState, {
        time: allFormat
      });
    });
    toggleToShow(false);
    inputField.current.blur();
  };
  function init() {
    setCenter({
      x: 130,
      y: 130
    });
    var maskPosition = mask.current.getBoundingClientRect();
    setContainerPos({
      y: maskPosition.top,
      x: maskPosition.left
    });
    setBasePoint({
      x: 130,
      y: 0
    });
  }
  function getThemeSelector(theme, color1) {
    var _color1 = color1;
    if (color1 && color1.indexOf("#") === 0) {
      _color1 = color1.substr(1);
    }
    if (theme) {
      if (color1) {
        return theme.split(" ").join("-") + _color1;
      }
      return theme.split(" ").join("-");
    }
    if (_color1) {
      return _color1;
    }
    return "react-time-picker-theme";
  }
  function getHour(val) {
    return appendZero(val % 12 || "12");
  }
  function getMinute(val) {
    return appendZero(val % 60 || "0");
  }
  function getInitialConfig(time) {
    var date = new Date();
    time = time ? time : date.getHours() + ":" + date.getMinutes();
    var temp = time.split(":");
    var hour24 = Number(temp[0]);
    var minute24 = Number(temp[1]);
    var _getSelectedIndexDegr = getSelectedIndexDegreeAndDegreeForHour(hour24),
      degree = _getSelectedIndexDegr.degree,
      selectedIndexDegree = _getSelectedIndexDegr.selectedIndexDegree;
    return {
      hour: getHour(hour24),
      minute: getMinute(minute24),
      degree: degree,
      selectedIndexDegree: selectedIndexDegree,
      isAmSelected: Number(temp[0]) <= 12
    };
  }
  function getSelectedIndexDegreeAndDegreeForHour(val) {
    var degree = val * 30 % 360;
    return {
      selectedIndexDegree: getSelectedIndexDegree(degree),
      degree: degree
    };
  }
  function getSelectedIndexDegreeAndDegreeForMinute(val) {
    var degree = val * 6 % 360;
    return {
      selectedIndexDegree: getSelectedIndexDegree(degree),
      degree: degree
    };
  }
  function getSelectedIndexDegree(degree) {
    return degree / 30 % 12;
  }
  function getDegree(offsetX, offsetY) {
    var x = offsetX - center.x;
    var y = offsetY - center.y;
    var cx = basePoint.x - center.x;
    var cy = basePoint.y - center.y;
    var atan = Math.atan2(cx, cy) - Math.atan2(x, y);
    return atan * 57.29577951308232 % 360;
  }
  function getClock(className) {
    var hours = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
    var minutes = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];
    var hoursOrMinutes = state.toShowHourContainer ? hours : minutes;
    return hoursOrMinutes.map(function (i, j) {
      return React.createElement("span", {
        key: i,
        className: "circle " + (state.selectedIndexDegree === j ? "selected " + className : "")
      }, i);
    });
  }
  function getTime(hour, minute, isAmSelected) {
    var format12 = getFormat12(hour, minute, isAmSelected);
    var format24 = format12to24(hour, minute, isAmSelected);
    return {
      format12: format12,
      format24: format24
    };
  }
  function setMinute(degree) {
    var base = Math.round(degree / 6);
    degree = base * 6;
    var minute = parseInt(getMinute(base));
    var selectedIndexDegree = getSelectedIndexDegree(degree);
    var toReturn = {
      degree: degree,
      minute: minute,
      selectedIndexDegree: selectedIndexDegree
    };
    setState(function (prevState) {
      return _extends({}, prevState, toReturn);
    });
    return toReturn;
  }
  function setHour(degree) {
    var base = Math.round(degree / 30);
    degree = base * 30;
    var hour = parseInt(getHour(base));
    var selectedIndexDegree = getSelectedIndexDegree(degree);
    var toReturn = {
      degree: degree,
      hour: hour,
      selectedIndexDegree: selectedIndexDegree
    };
    setState(function (prevState) {
      return _extends({}, prevState, toReturn);
    });
    return toReturn;
  }
  function getBody() {
    if (!state.toShow) return false;
    var themeSelector = getThemeSelector(props.theme, props.color1);
    var primaryColorColorClassName = "react-timepicker-primary-color-color-" + themeSelector;
    var primaryColorBackgroundClassName = "react-timepicker-primary-color-background-" + themeSelector;
    var backgroundColorClassName = "react-timepicker-background-color-" + themeSelector;
    return React.createElement("div", null, React.createElement("div", {
      className: "timepicker-backdrop",
      onClick: function onClick() {
        return toggleToShow(false);
      }
    }), React.createElement("div", {
      className: "timepicker-modal"
    }, React.createElement("header", {
      className: "timepicker-header " + backgroundColorClassName
    }, React.createElement("div", {
      className: "timepicker-time-container"
    }, React.createElement("span", {
      className: "text-shadow " + (!state.toShowHourContainer ? "is-not-selected" : ""),
      onClick: function onClick() {
        return toggleHourOrMinuteContainer(true);
      }
    }, state.hour), React.createElement("span", {
      className: "text-shadow"
    }, ":"), React.createElement("span", {
      className: "text-shadow " + (state.toShowHourContainer ? "is-not-selected" : ""),
      onClick: function onClick() {
        return toggleHourOrMinuteContainer(false);
      }
    }, state.minute)), React.createElement("div", {
      className: "timepicker-am-pm-container"
    }, React.createElement("input", {
      className: "am-pm-input",
      type: "radio",
      id: "am",
      name: "am-pm",
      value: "am",
      onChange: function onChange() {
        return toggleAmPm(true);
      },
      defaultChecked: state.isAmSelected
    }), React.createElement("label", {
      className: "am-pm-label",
      htmlFor: "am"
    }, "AM"), React.createElement("input", {
      className: "am-pm-input",
      type: "radio",
      id: "pm",
      name: "am-pm",
      value: "pm",
      onChange: function onChange() {
        return toggleAmPm(false);
      },
      defaultChecked: !state.isAmSelected
    }), React.createElement("label", {
      className: "am-pm-label",
      htmlFor: "pm"
    }, "PM"))), React.createElement("main", {
      className: "timepicker-main"
    }, React.createElement("div", {
      className: "hours-container",
      ref: mask,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchUp,
      onMouseMove: handleMove,
      onMouseUp: handleMoveUp
    }, React.createElement("div", {
      className: primaryColorBackgroundClassName + " hand",
      style: {
        transform: "rotate(" + (state.degree - 90) + "deg)",
        WebkitTransform: "rotate(" + (state.degree - 90) + "deg)"
      }
    }, "\xA0"), getClock(primaryColorBackgroundClassName))), React.createElement("footer", null, React.createElement("button", {
      type: "button",
      className: primaryColorColorClassName + " timepicker-button close",
      onClick: function onClick() {
        return toggleToShow(false);
      },
      ref: closeButton
    }, "Close"), React.createElement("button", {
      type: "button",
      className: primaryColorBackgroundClassName + " timepicker-button",
      onClick: handleSet
    }, "Set"))));
  }
  return React.createElement(ThemeProvider, {
    theme: theme
  }, React.createElement(Component$14, {
    className: "timepicker-container"
  }, React.createElement("input", {
    readOnly: true,
    type: "text",
    placeholder: props.placeholder,
    className: props.className,
    value: state.time.format12,
    onFocus: handleFocus,
    ref: inputField,
    style: props.style
  }), getBody()));
};
export { Accordion as CDBAccordion, Alert as CDBAlert, Animation as CDBAnimation, Autocomplete as CDBAutocomplete, Badge as CDBBadge, Box as CDBBox, Breadcrumb as CDBBreadcrumb, Button$1 as CDBBtn, ButtonGroup as CDBBtnGrp, ButtonToolbar as CDBBtnTb, Card as CDBCard, CardBody as CDBCardBody, CardImage as CDBCardImage, CardText as CDBCardText, CardTitle as CDBCardTitle, Carousel as CDBCarousel, CarouselInner as CDBCarouselInner, CarouselItem as CDBCarouselItem, Checkbox as CDBCheckbox, CircularProgress as CDBCircularProgress, CloseIcon as CDBCloseIcon, Col as CDBCol, Collapse as CDBCollapse, Container$1 as CDBContainer, DataTable as CDBDataTable, DatePicker as CDBDatePicker, DropDown as CDBDropDown, DropDownItem as CDBDropDownItem, DropDownMenu as CDBDropDownMenu, DropdownToggle as CDBDropDownToggle, EditableTable as CDBEditableTable, FileUploader as CDBFileUploader, Footer as CDBFooter, FooterLink as CDBFooterLink, HamburgerToggler as CDBHamburgerToggler, Icon as CDBIcon, Iframe as CDBIframe, Input as CDBInput, InputGroup as CDBInputGroup, Jumbotron as CDBJumbotron, Link as CDBLink, ListGroup as CDBListGroup, ListGroupItem as CDBListGroupItem, Mask as CDBMask, Modal as CDBModal, ModalBody as CDBModalBody, ModalFooter as CDBModalFooter, ModalHeader as CDBModalHeader, Multiselect as CDBMultiselect, Nav as CDBNav, NavBrand as CDBNavBrand, NavItem as CDBNavItem, NavLink as CDBNavLink, NavToggle as CDBNavToggle, Navbar as CDBNavbar, NavbarNav as CDBNavbarNav, Notification as CDBNotification, PageItem as CDBPageItem, PageLink as CDBPageLink, Pagination as CDBPagination, Pane as CDBPane, PaneImage as CDBPaneImage, Panel as CDBPanel, PanelText as CDBPanelText, PanelTitle as CDBPanelTitle, PopoverBody as CDBPopoverBody, PopoverHeader as CDBPopoverHeader, Popover as CDBPopper, Progress as CDBProgress, Radio as CDBRadio, RadioGroup as CDBRadioGroup, Rating as CDBRating, Row as CDBRow, Select as CDBSelect, Select2 as CDBSelect2, Sidebar as CDBSidebar, SidebarCTA as CDBSidebarCTA, SidebarContent as CDBSidebarContent, SidebarFooter as CDBSidebarFooter, SidebarHeader as CDBSidebarHeader, SidebarMenu as CDBSidebarMenu, SidebarMenuItem as CDBSidebarMenuItem, SidebarSubMenu as CDBSidebarSubMenu, Slider as CDBSlider, SmoothScroll as CDBSmoothScroll, Spinner as CDBSpinner, Step as CDBStep, Stepper as CDBStepper, Switch as CDBSwitch, TabContent as CDBTabContent, TabLink as CDBTabLink, TabPane as CDBTabPane, Table$1 as CDBTable, TableBody as CDBTableBody, TableHeader as CDBTableHeader, TimePicker as CDBTimePicker, View as CDBView };